import z, { z as z$1 } from "zod";
// src/zodConfig.ts
z$1.config({ jitless: true });
// src/agents/dottieFeedbackReasons.ts
/**
* SIM-2345 — closed reason taxonomy for a Dottie thumbs-down, plus an optional free-text correction
* (SIM-2309). One source of truth: the contract validates against it, the handler persists `code`s
* and never labels, the portal renders `label`s. A `code` is the STABLE persisted enum reaching
* analytics — never rename or repurpose one; labels are display-only.
*/
const DOTTIE_FEEDBACK_REASONS = [
	{
		code: "wrong_data",
		label: "Wrong numbers or data"
	},
	{
		code: "not_what_i_asked",
		label: "Not what I asked for"
	},
	{
		code: "wrong_format",
		label: "Wrong chart or format"
	},
	{
		code: "incorrect_incomplete",
		label: "Incorrect or incomplete"
	},
	{
		code: "unclear",
		label: "Confusing or unclear"
	}
];
/**
* The stable persisted codes, derived from the taxonomy so the code list, the
* union type, and the Zod schema below all share one source.
*/
const DOTTIE_FEEDBACK_REASON_CODES = DOTTIE_FEEDBACK_REASONS.map((r) => r.code);
/**
* Runtime validator for a single reason code — shared by the platform contract
* (`feedback.contract.ts`) so type + runtime validation cannot drift.
*/
const dottieFeedbackReasonCodeSchema = z$1.enum(DOTTIE_FEEDBACK_REASONS.map((r) => r.code));
/** True iff `value` is one of the closed reason codes. */
function isDottieFeedbackReasonCode(value) {
	return typeof value === "string" && DOTTIE_FEEDBACK_REASON_CODES.includes(value);
}
// src/agents/semanticRoles.ts
/**
* SIM-1703 Slice 0 — closed-enum semantic roles for `AgentFieldDefinition.semanticRole`. Tenants
* name the same field differently (`ada_pct`, `attendance_pct`, `pct_present`), so a Universal
* Skill asks for "the field whose role is `attendance.pct_present` here" and acts on whatever
* column that tenant tagged. Closed on purpose: a Skill need not be defensive.
* @see docs/architecture/dottie-dashboard-aware.md §4, §10
*/
const SEMANTIC_ROLES = [
	"identity:student",
	"identity:school",
	"identity:teacher",
	"identity:section",
	"time.snapshot_date",
	"time.school_year",
	"grade.level",
	"attendance.pct_present",
	"attendance.days_absent",
	"achievement.score",
	"achievement.proficiency_label"
];
function isSemanticRole(value) {
	return typeof value === "string" && SEMANTIC_ROLES.includes(value);
}
// src/agents/sessionContext.ts
/**
* SIM-1703 Slice 0 — the T4 session-context envelope: a flat JSON projection of what the user is
* looking at (T1 code-bundled, T2 per-tenant rows, T3 live warehouse schema, T4 this). Structured
* `{ dashboard: { config, state } }` so config, stable across a conversation, can later be cached
* while only state busts the prompt cache. `focused` is the full union now.
* @see docs/architecture/dottie-dashboard-aware.md
*/
/**
* A field bound to a card, with optional semantic-role hints (resolved from the tenant's
* `AgentFieldDefinition.semanticRole` rows at serializer time — see {@link "./semanticRoles"}).
* `boundAs` answers the card-vocabulary question ("what part of the chart is this field?");
* `semanticRole` answers the cross-tenant one ("what concept does it play in this warehouse?").
*/
const cardBindingSchema = z$1.object({
	field: z$1.string(),
	datasetId: z$1.string(),
	/** Card-vocabulary role — how the field is used by this specific card. */
	boundAs: z$1.enum([
		"metric",
		"dimension",
		"breakdown",
		"filter",
		"sort",
		"row-key",
		"value",
		"other"
	]),
	/** Cross-tenant semantic role, if the tenant has tagged this field. */
	semanticRole: z$1.string().nullable().optional()
});
const datasetReferenceSchema = z$1.object({
	datasetId: z$1.string(),
	projectId: z$1.string(),
	bqDatasetId: z$1.string(),
	tableId: z$1.string(),
	/** Subset of fields actually bound on the current page. */
	boundFields: z$1.array(z$1.string())
});
const filterValueSchema = z$1.discriminatedUnion("kind", [
	z$1.object({
		kind: z$1.literal("equals"),
		value: z$1.string()
	}),
	z$1.object({
		kind: z$1.literal("in"),
		values: z$1.array(z$1.string())
	}),
	z$1.object({
		kind: z$1.literal("range"),
		min: z$1.string().optional(),
		max: z$1.string().optional()
	}),
	z$1.object({
		kind: z$1.literal("dateRange"),
		from: z$1.string(),
		to: z$1.string()
	})
]);
const activeFilterSchema = z$1.object({
	fieldName: z$1.string(),
	datasetId: z$1.string(),
	value: filterValueSchema
});
const focusedSchema = z$1.discriminatedUnion("kind", [
	z$1.object({ kind: z$1.literal("none") }),
	z$1.object({
		kind: z$1.literal("card"),
		cardId: z$1.string(),
		/** From `packages/core/src/spark/componentTypes`. */
		cardType: z$1.string(),
		/** Normalized binding view — what universal Skills read first. */
		dataSpec: z$1.object({
			datasets: z$1.array(datasetReferenceSchema),
			bindings: z$1.array(cardBindingSchema)
		}),
		/**
		* Verbatim card config preserved for card-type-specific Skills (e.g.
		* "redo this chart broken down by X" — needs the full chart spec to
		* mutate). Stored as JSON; consumers narrow per `cardType`. Tightened
		* in Slice 2 once the per-type shapes settle.
		*/
		cardConfig: z$1.unknown()
	}),
	z$1.object({
		kind: z$1.literal("row"),
		cardId: z$1.string(),
		/** Field declared as the row identity on the card's dataset binding. */
		identityField: z$1.string(),
		identityValue: z$1.string(),
		/**
		* Snapshot of the row's displayed columns. Scrubbed before Sentry
		* (see `packages/agents/src/sentryScrubber.ts`). Slice 3 only.
		*/
		rowSnapshot: z$1.record(z$1.string(), z$1.unknown())
	})
]);
/**
* v1 of the session-context envelope. Bumped to v2 when a breaking shape
* change is required; consumers dispatch on `version` and the prompt-stitcher
* carries one formatter per version.
*/
const sessionContextV1Schema = z$1.object({
	version: z$1.literal("v1"),
	dashboard: z$1.object({
		/**
		* Stable across a conversation — anything that doesn't change without the
		* user navigating to a different dashboard. Eligible for prompt-cache.
		*/
		config: z$1.object({
			id: z$1.string(),
			name: z$1.string(),
			pageId: z$1.string(),
			pageTitle: z$1.string(),
			boundDatasets: z$1.array(datasetReferenceSchema)
		}),
		/**
		* Per-message dynamic — busts the prompt cache. Today's v0 sends with
		* every message (see [docs/architecture/dottie-dashboard-aware.md §10.1]).
		*/
		state: z$1.object({
			activeFilters: z$1.array(activeFilterSchema),
			focused: focusedSchema
		})
	})
});
// src/brand.ts
/**
* Canonical product/brand name — one source of truth for every surface that renders or asserts it.
* It was once duplicated across the portal shell title, its runtime `document.title` and a platform
* E2E assertion; a rebrand updated the portal but not the CI-only literal, so it merged green and
* broke main. History: SIM-1976, SIM-1928, SIM-1935.
*/
const BRAND_NAME = "SimplySpark";
/** Document-title suffix for portal routes, e.g. `"Dashboards | SimplySpark"`. */
const PORTAL_TITLE_SUFFIX = ` | ${BRAND_NAME}`;
// src/cascadeLayers.ts
/** The ordered cascade layer sequence. Single source of truth for BOTH the
* scheduled runner path (apps/runner runWithPlan) and the adhoc path
* (apps/platform buildCascadeRunPlan) so they can never diverge (SIM-2114). */
const CASCADE_LAYER_SEQUENCE = [
	"silver-canon",
	"gold",
	"diamond"
];
// src/dnaResources.ts
const DNA_LIST_RESOURCES = [
	"Sites",
	"GradeLevels",
	"Terms",
	"Courses",
	"Students",
	"Students2",
	"StudentContacts",
	"StudentCounselors",
	"StudentSpecialEd",
	"ProgramCode",
	"Enrollment",
	"Users",
	"UserRoles",
	"MasterSchedule",
	"Roster",
	"Assessments",
	"AssessmentAggregateStudentResponses",
	"AssessmentAggregateStudentResponsesGroup",
	"AssessmentAggregateStudentResponsesStandard",
	"PoolAssessmentAggregateStudentResponses",
	"PoolAssessmentAggregateStudentResponsesStandard",
	"GradebookScores",
	"GradebookOverallScores",
	"GradebookCategoryScores",
	"GradebookStandardsScores",
	"GradebookScoresWithSection",
	"GradebookOverallScoresWithSection",
	"GradebookCategoryScoresWithSection",
	"GradebookStandardsScoresWithSection",
	"Attendance/Daily",
	"Attendance/Section",
	"Behavior",
	"GraduationEligibility"
];
/** Lowercase bronze table name for a DnA resource, e.g. `Attendance/Daily` → `attendance_daily`. */
function dnaTableNameFor(resource) {
	return resource.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
/** The known list endpoint matching `value` case-insensitively, in DnA's own casing. */
function canonicalDnaResource(value) {
	const lower = value.toLowerCase();
	return DNA_LIST_RESOURCES.find((resource) => resource.toLowerCase() === lower);
}
// src/formatZodError.ts
const DEFAULT_MULTI_PREFIX = "We couldn't save your changes:";
const DEFAULT_UNKNOWN = "Something went wrong. Please try again.";
function formatZodError(error, options = {}) {
	const issues = extractIssues(error);
	if (issues === null) {
		if (error instanceof Error) return error.message;
		if (typeof error === "string") return error;
		return options.unknownFallback ?? DEFAULT_UNKNOWN;
	}
	if (issues.length === 0) return options.unknownFallback ?? DEFAULT_UNKNOWN;
	if (issues.length === 1) return formatIssue(issues[0], options.overrides);
	return `${options.multiIssuePrefix ?? DEFAULT_MULTI_PREFIX}\n${issues.map((issue) => `  • ${formatIssue(issue, options.overrides)}`).join("\n")}`;
}
function extractIssues(error) {
	if (!error || typeof error !== "object") return null;
	const e = error;
	if (Array.isArray(e.issues) && isIssueArray(e.issues)) return e.issues;
	if (e.data && typeof e.data === "object") {
		const data = e.data;
		if (Array.isArray(data.issues) && isIssueArray(data.issues)) return data.issues;
	}
	return null;
}
function isIssueArray(arr) {
	return arr.every((x) => {
		if (!x || typeof x !== "object") return false;
		const i = x;
		return typeof i.code === "string" && Array.isArray(i.path);
	});
}
function applyOverride(issue, overrides) {
	const exactKey = issue.path.join(".");
	const wildcardKey = issue.path.map((p) => typeof p === "number" ? "*" : p).join(".");
	const override = overrides[exactKey] ?? overrides[wildcardKey];
	if (override === void 0) return void 0;
	return typeof override === "function" ? override(issue) : override;
}
function formatInvalidType(issue, label) {
	if (issue.received === "undefined" || /received undefined/i.test(issue.message ?? "")) return `${label} is required.`;
	return `${label} has the wrong type (expected ${issue.expected ?? "different value"}).`;
}
function formatTooSmall(issue, label) {
	const min = issue.minimum;
	if (issue.origin === "string") {
		if (min === 1) return `Please enter ${pronoun(label)} ${labelLower(label)}.`;
		return `${label} must be at least ${min} characters.`;
	}
	if (issue.origin === "array") return `Please add at least ${min} ${min === 1 ? "item" : "items"} to ${labelLower(label)}.`;
	return `${label} must be at least ${min}.`;
}
function formatTooBig(issue, label) {
	const max = issue.maximum;
	if (issue.origin === "string") return `${label} is longer than ${max} characters.`;
	if (issue.origin === "array") return `${label} has more than ${max} ${max === 1 ? "item" : "items"}.`;
	return `${label} is larger than the allowed maximum (${max}).`;
}
function formatInvalidFormat(issue, label) {
	const fmt = issue.format;
	if (fmt === "email") return `Please enter a valid email address.`;
	if (fmt === "url") return `Please enter a valid URL.`;
	if (fmt === "uuid") return `${label} must be a valid identifier.`;
	if (fmt === "regex") return `${label} is not in the expected format.`;
	return `${label} is not in the expected format${fmt ? ` (${fmt})` : ""}.`;
}
function formatInvalidValue(issue, label) {
	const values = issue.values ?? [];
	if (values.length > 0) return `${label} must be one of: ${values.join(", ")}.`;
	return `${label} has an invalid value.`;
}
function formatUnrecognizedKeys(issue) {
	const keys = issue.keys ?? [];
	if (keys.length === 1) return `Unexpected field "${keys[0]}".`;
	return `Unexpected fields: ${keys.join(", ")}.`;
}
const ISSUE_HANDLERS = {
	invalid_type: formatInvalidType,
	too_small: formatTooSmall,
	too_big: formatTooBig,
	invalid_format: formatInvalidFormat,
	invalid_value: formatInvalidValue,
	unrecognized_keys: (issue) => formatUnrecognizedKeys(issue),
	invalid_union: (_issue, label) => `${label} doesn't match any of the allowed shapes.`,
	custom: (issue, label) => issue.message ?? `${label} is not valid.`
};
function formatIssue(issue, overrides) {
	if (overrides) {
		const fromOverride = applyOverride(issue, overrides);
		if (fromOverride !== void 0) return fromOverride;
	}
	const label = humanizeFieldName(issue.path);
	const handler = ISSUE_HANDLERS[issue.code];
	if (handler) return handler(issue, label);
	return issue.message ? `${label}: ${issue.message}` : `${label} is not valid.`;
}
/**
* Convert a Zod path to a human-readable field name: `["spark","layout",0,"w"]` becomes "Spark
* layout 1 w" and `[]` becomes "This value". Best-effort only — camelCase and snake_case become
* spaced words, known abbreviations are expanded (id, url, uuid), the result is sentence-cased, and
* numeric indices become 1-based ordinals.
*/
function humanizeFieldName(path) {
	if (path.length === 0) return "This value";
	return path.map((segment, idx) => {
		if (typeof segment === "number") return String(segment + 1);
		return humanizeSegment(segment, idx === 0);
	}).join(" ");
}
const UPPERCASE_ABBREVIATIONS = /* @__PURE__ */ new Set([
	"id",
	"url",
	"uri",
	"uuid",
	"api",
	"sql",
	"csv",
	"json",
	"ui",
	"ip"
]);
function humanizeSegment(segment, isFirst) {
	return segment.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").trim().split(/\s+/).map((w, wi) => {
		const lower = w.toLowerCase();
		if (UPPERCASE_ABBREVIATIONS.has(lower)) return lower.toUpperCase();
		if (isFirst && wi === 0) return lower.charAt(0).toUpperCase() + lower.slice(1);
		return lower;
	}).join(" ");
}
function labelLower(label) {
	if (label === "This value") return "a value";
	return label.charAt(0).toLowerCase() + label.slice(1);
}
/**
* Pick "a" or "an" for the indefinite article. Crude — just looks at the first
* sound of the word.
*/
function pronoun(label) {
	const first = labelLower(label).trim().charAt(0).toLowerCase();
	return [
		"a",
		"e",
		"i",
		"o",
		"u"
	].includes(first) ? "an" : "a";
}
/**
* Convenience helper for the common case where you have a `try { ... } catch (err)`
* around a fetch + Zod-validated mutation and want a polite toast description.
*/
function describeApiError(err, options) {
	return formatZodError(err, options);
}
// src/inheritFromParents.ts
/**
* Walk the `inheritFrom.parent` chain in `knowledge` from every name in `scheduledNames` and return
* the lowercased parents NOT already scheduled. Handles transitive chains and is cycle-safe.
* SIM-1293: when the schedule filter excludes an inheritFrom parent the runner crashes at
* `findTable(parent)`, unable to project the IN-list (contract from SIM-953/SIM-1042).
*/
function transitiveInheritFromParents(scheduledNames, knowledge) {
	const scheduledSet = new Set(scheduledNames.map((n) => n.toLowerCase()));
	const visited = /* @__PURE__ */ new Set();
	const parents = /* @__PURE__ */ new Set();
	const stack = [...scheduledSet];
	while (stack.length > 0) {
		const name = stack.pop();
		if (visited.has(name)) continue;
		visited.add(name);
		const parent = knowledge[name]?.inheritFrom?.parent?.toLowerCase();
		if (parent && !scheduledSet.has(parent) && !parents.has(parent)) {
			parents.add(parent);
			stack.push(parent);
		}
	}
	return Array.from(parents);
}
/**
* SIM-3139: the cascade only runs when the connector has project-level
* scoping — `PowerSchoolConnector.deriveInheritChunk` bails on the same check
* before it collects any parent ids. Appending a parent without it adds an
* unbounded full-table pull on the child's cadence whose rows nothing reads.
*/
function hasProjectLevelScoping(vars) {
	return (vars.filters?.schoolids?.length ?? 0) > 0;
}
// src/volumeBand.ts
/**
* Below this, a band is noise fitted to a handful of points and would fire on
* ordinary variation. Five is the floor the original design named and it is
* kept — the change from that design is the partition key, not the floor.
*/
const MIN_VOLUME_SAMPLES = 5;
/**
* How many MADs from the median before a count is called anomalous. 3 is the conventional
* robust-outlier threshold, deliberately not tuned against our own data: a threshold fitted to a
* fleet containing known-broken tables is how a detector learns to accept what it exists to catch.
*/
const VOLUME_BAND_MADS = 3;
/**
* A zero-rate at or above this means zero is the ordinary outcome for this scope, so a fresh zero
* is not news. The SAFE direction of a self-history inference — it only ever downgrades an alarm.
* The opposite ("zeros are rare here, so this one is a defect") is not drawn: see `empty`.
*/
const ZERO_IS_NORMAL_RATE = .5;
function median(sorted) {
	if (sorted.length === 0) return 0;
	const mid = Math.floor(sorted.length / 2);
	if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
	return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}
/**
* Median and MAD over a scope partition. MAD rather than standard deviation: a single 150,987-row
* backfill in an otherwise 400-row history would inflate an SD-based band enough to swallow every
* subsequent anomaly. The median absolute deviation does not move.
*/
function computeVolumeStats(samples) {
	const counts = samples.map((s) => s.recordsFetched);
	const med = median([...counts].sort((a, b) => a - b));
	const deviations = counts.map((c) => Math.abs(c - med)).sort((a, b) => a - b);
	const zeros = counts.filter((c) => c === 0).length;
	return {
		samples: counts.length,
		median: med,
		mad: median(deviations),
		zeroRate: counts.length === 0 ? 0 : zeros / counts.length
	};
}
/**
* Where one count sits against its partition. `not-judgeable` in three cases: too few samples (a
* band on <5 points is noise); a ZERO count (the `empty` question, routed by `zeroRate` upstream —
* self-history cannot answer it in the alarming direction); and a flat history, where MAD 0 would
* make an ordinary ±1 an anomaly.
*/
function classifyVolume(count, stats) {
	if (stats.samples < 5) return "not-judgeable";
	if (count === 0) return "not-judgeable";
	if (stats.mad === 0) return "not-judgeable";
	if (Math.abs(count - stats.median) / stats.mad <= 3) return "in-band";
	return count < stats.median ? "drop" : "spike";
}
/** True when zero is the ordinary outcome for this partition. */
function zeroIsNormal(stats) {
	return stats.samples >= 5 && stats.zeroRate >= .5;
}
// src/pipelineStatus.ts
/** Cadence a write came from. `adhoc` covers Run-now / one-table plans. */
const runCadenceSchema = z$1.enum([
	"hourly",
	"daily",
	"weekly",
	"adhoc",
	"other"
]);
/**
* The verdict, ordered by precedence — first match wins, and the order is load-bearing: `paused`
* beats `failed` so a deliberately-off connector does not send someone to debug it. `empty` and
* `suspect` are defined but never emitted today; they are declared here because adding a member to
* a registry-protected enum later gets worked around on the consumer side.
*/
const pipelineStatusSchema = z$1.enum([
	"unscheduled",
	"paused",
	"running",
	"failed",
	"empty",
	"never",
	"unknown",
	"late",
	"suspect",
	"fresh"
]);
/**
* Why the status is what it is. Always present.
*
* This is where the seven `RowCountStatus` members and the `freshnessStatus` pair land, so retiring
* those types loses nothing. Members marked NOT YET EMITTED accompany `empty` / `suspect`.
*/
const pipelineReasonSchema = z$1.enum([
	"no-writer",
	"external-live-read",
	"project-paused",
	"project-offboarded",
	"definition-disabled",
	"in-window",
	"attempt-stalled",
	"run-failed",
	"window-missed",
	"no-successful-run",
	"instrument-stale",
	"insufficient-history",
	"scope-not-recorded",
	"zero-in-scope",
	"zero-unexpected",
	"no-new-records",
	"count-drop",
	"count-spike",
	"count-shrink",
	"level-shifted",
	"stale-source-file",
	"mode-mismatch"
]);
/**
* Display severity, worst first, for rolling many statuses into one badge. Deliberately NOT the
* enum's precedence order: precedence decides one row, severity decides what a group header shows.
* `paused` has high precedence and the lowest severity. `unknown` sits above `fresh` because a wave
* where nothing can be judged should not present as healthy.
*/
const PIPELINE_STATUS_SEVERITY = {
	failed: 90,
	late: 80,
	empty: 70,
	suspect: 60,
	never: 50,
	unknown: 40,
	running: 30,
	fresh: 20,
	unscheduled: 10,
	paused: 0
};
/**
* The worst status among a set — what a wave-group header shows.
*
* Returns `unknown` for an empty set rather than `fresh`: no children is not
* evidence of health.
*/
function worstPipelineStatus(statuses) {
	let worst = null;
	for (const s of statuses) if (worst == null || PIPELINE_STATUS_SEVERITY[s] > PIPELINE_STATUS_SEVERITY[worst]) worst = s;
	return worst ?? "unknown";
}
/** Expected interval per cadence, in minutes. Null = no interval expectation. */
const CADENCE_MINUTES = {
	hourly: 60,
	daily: 1440,
	weekly: 10080,
	adhoc: null,
	other: null
};
/**
* Grace beyond the cadence before a write counts as late, in minutes. Absolute per cadence, not a
* multiplier: the old `GRACE_FACTOR = 1.5` gave the loosest schedules the most extra slack. Weekly
* was the bad one — at 10.5 days a weekly table could miss a firing and stay green for three more.
* Hourly stays at 30 min so a miss is flagged before the next firing stacks up.
*/
const PIPELINE_GRACE_MINUTES = {
	hourly: 30,
	daily: 480,
	weekly: 1440,
	adhoc: 0,
	other: 0
};
/** The window a write is judged against: cadence + its grace. Null when no cadence applies. */
function cadenceWindowMinutes(cadence) {
	const base = CADENCE_MINUTES[cadence];
	return base == null ? null : base + PIPELINE_GRACE_MINUTES[cadence];
}
/**
* The lateness window for a cadence, from a bare interval in minutes — some callers hold minutes
* rather than a cadence name. The reverse lookup is unambiguous because `CADENCE_MINUTES` is
* injective over the three real cadences. An unrecognised interval returns itself, i.e. zero grace:
* widening the window is what hides a missed run.
*/
function cadenceWindowFromMinutes(cadenceMinutes) {
	for (const cadence of Object.keys(CADENCE_MINUTES)) if (CADENCE_MINUTES[cadence] === cadenceMinutes) return cadenceMinutes + PIPELINE_GRACE_MINUTES[cadence];
	return cadenceMinutes;
}
/** The tightest cadence with a real interval — what the thing is actually held to. */
function strictestCadence(cadences) {
	return cadences.filter((c) => CADENCE_MINUTES[c] != null).sort((a, b) => (CADENCE_MINUTES[a] ?? 0) - (CADENCE_MINUTES[b] ?? 0))[0] ?? null;
}
function pausedReason(skipReason) {
	return skipReason === "offboarded" ? "project-offboarded" : "project-paused";
}
/**
* States that mean "no verdict is owed here" — a deliberate off switch, or activity already under
* way. Returns null when the object must be judged on its run history instead. Split out so the
* precedence chain stays inside the complexity cap; the order within it is still load-bearing and
* documented on the enum.
*/
function suppressionVerdict(input, strictest, windowMinutes) {
	const nil = {
		windowMinutes: null,
		overdueByMinutes: null
	};
	if (input.enabled === false) return {
		status: "paused",
		reason: "definition-disabled",
		...nil
	};
	if (input.skipReason != null) return {
		status: "paused",
		reason: pausedReason(input.skipReason),
		...nil
	};
	if (input.inFlight && !input.inFlight.late) return {
		status: "running",
		reason: "in-window",
		windowMinutes,
		overdueByMinutes: null
	};
	if (strictest == null && input.cadenceSource !== "observed") return {
		status: "unscheduled",
		reason: "no-writer",
		...nil
	};
	return null;
}
/**
* Which cadence's outcome decides the verdict: only the strictest scheduled one, because an adhoc
* run is a real write but must not make a dead schedule look alive. With no cadence at all
* (transforms, `cadenceSource: "observed"`) there is nothing to be late against, so the newest
* write of any cadence decides merely whether it ran.
*/
function decidingOutcome(perCadence, strictest) {
	if (strictest != null) return perCadence.find((c) => c.cadence === strictest);
	return perCadence.find((c) => c.at != null) ?? perCadence[0];
}
/**
* Which reason accompanies an on-time write. Ordered most-explanatory first: a fact that fully
* accounts for the count beats one that merely says we could not judge it. Every branch either
* explains a zero or declines to judge — none raises an alarm, which is what makes the chain safe
* to ship before the volume band exists.
*/
function volumeReason(volume) {
	if (volume == null) return "in-window";
	if (volume.recordsFetched === 0 && volume.mode === "incremental") return "no-new-records";
	if (!volume.scopeRecorded) return "scope-not-recorded";
	const stats = volume.stats;
	if (stats == null) return "insufficient-history";
	if (volume.recordsFetched === 0) return zeroIsNormal(stats) ? "zero-in-scope" : "insufficient-history";
	const band = classifyVolume(volume.planTotal ?? volume.recordsFetched, stats);
	if (band === "drop") return "count-drop";
	if (band === "spike") return "count-spike";
	if (band === "not-judgeable") return "insufficient-history";
	return "in-window";
}
/** Reasons that mean the write landed on time but does not look right. */
const SUSPECT_REASONS = /* @__PURE__ */ new Set(["count-drop", "count-spike"]);
function computePipelineStatus(input) {
	const strictest = strictestCadence(input.scheduledCadences);
	const windowMinutes = strictest ? cadenceWindowMinutes(strictest) : null;
	const suppressed = suppressionVerdict(input, strictest, windowMinutes);
	if (suppressed) return suppressed;
	const own = decidingOutcome(input.perCadence, strictest);
	if (own?.status === "failed") return {
		status: "failed",
		reason: "run-failed",
		windowMinutes,
		overdueByMinutes: null
	};
	if (own?.at == null) return {
		status: "never",
		reason: "no-successful-run",
		windowMinutes,
		overdueByMinutes: null
	};
	const ageMinutes = Math.round((input.nowMs - Date.parse(own.at)) / 6e4);
	if (windowMinutes != null && ageMinutes > windowMinutes) return {
		status: "late",
		reason: input.inFlight?.late ? "attempt-stalled" : "window-missed",
		windowMinutes,
		overdueByMinutes: ageMinutes - windowMinutes
	};
	const reason = volumeReason(input.volume);
	return {
		status: SUSPECT_REASONS.has(reason) ? "suspect" : "fresh",
		reason,
		windowMinutes,
		overdueByMinutes: null
	};
}
// src/powerschoolEnrollmentSchema.ts
const POWERSCHOOL_ENROLLMENT_SCHEMA_KNOWLEDGE = {
	submissionrecords: { inheritFrom: {
		parent: "publishedactions",
		paramKey: "publishedActionId"
	} },
	rosterrecords: { inheritFrom: {
		parent: "publishedactions",
		paramKey: "publishedActionId"
	} }
};
/**
* Case-insensitive lookup of the metadata entry for a PSE table. Returns
* `undefined` when the table has no entry in the knowledge map (which is
* fine — it just means the table syncs straight through without cascade).
*/
function getEnrollmentTableMetadata(tableName) {
	return POWERSCHOOL_ENROLLMENT_SCHEMA_KNOWLEDGE[tableName.toLowerCase()];
}
/**
* The `inheritFrom` descriptor for a PSE table, or `null` when it has none (no entry at all, or an
* entry without the clause). Case-insensitive. Used by the runner when expanding one schedule entry
* into N cascade-fanned `SyncTableOptions`: `null` means "no cascade, run the entry as-is".
*/
function getInheritDescriptor(tableName) {
	const meta = getEnrollmentTableMetadata(tableName);
	if (!meta?.inheritFrom) return null;
	return meta.inheritFrom;
}
// src/powerschoolSchema.ts
/**
* Covers the tables Leadership Prep syncs plus the incident family and the sections-cascade entries
* used for FK-inheritance; every other table falls back to the heuristic. Expand this map
* (case-insensitively keyed by lowercase PS table name) as clients onboard or need explicit policy.
*/
const POWERSCHOOL_SCHEMA_KNOWLEDGE = {
	attendance: {
		schoolColumns: ["SchoolID"],
		studentColumns: ["StudentID"],
		primaryDateColumn: "Att_Date"
	},
	attendance_code: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	students: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	studentrace: {
		schoolColumns: [],
		studentColumns: ["studentid"],
		primaryDateColumn: null
	},
	pgfinalgrades: {
		schoolColumns: [],
		studentColumns: ["studentid"],
		primaryDateColumn: "lastgradeupdate"
	},
	courses: {
		schoolColumns: ["SchoolID"],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	sections: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		inheritFrom: {
			parent: "cc",
			joinColumn: "id",
			parentColumn: "SectionID"
		}
	},
	terms: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "FirstDay"
	},
	storedgrades: {
		schoolColumns: ["SchoolID"],
		studentColumns: ["StudentID"],
		primaryDateColumn: "DateStored"
	},
	cc: {
		schoolColumns: ["SchoolID"],
		studentColumns: ["StudentID"],
		primaryDateColumn: "DateEnrolled"
	},
	reenrollments: {
		schoolColumns: ["SchoolID"],
		studentColumns: ["StudentID"],
		primaryDateColumn: null
	},
	calendar_day: {
		schoolColumns: ["SchoolID"],
		studentColumns: [],
		primaryDateColumn: null
	},
	schools: {
		schoolColumns: ["school_number"],
		studentColumns: [],
		primaryDateColumn: null
	},
	incident: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Incident_TS",
		inheritFrom: {
			parent: "schools",
			joinColumn: "School_Number",
			parentColumn: "school_number"
		}
	},
	incident_action: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident",
			joinColumn: "Incident_ID"
		}
	},
	incident_action_attribute: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident_action",
			joinColumn: "Incident_Action_ID"
		}
	},
	incident_detail: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident",
			joinColumn: "Incident_ID"
		}
	},
	incident_object: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident",
			joinColumn: "Incident_ID"
		}
	},
	incident_object_person: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident_object",
			joinColumn: "Incident_Object_ID"
		}
	},
	incident_person_action: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident_action",
			joinColumn: "Incident_Action_ID"
		}
	},
	incident_person_detail: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident_person_role",
			joinColumn: "Incident_Person_Role_ID"
		}
	},
	incident_personal: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Last_Modified_TS",
		inheritFrom: {
			parent: "incident",
			joinColumn: "Incident_ID"
		}
	},
	incident_person_role: {
		schoolColumns: [],
		studentColumns: ["StudentID"],
		primaryDateColumn: "Last_Modified_TS"
	},
	incident_lu_code: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	incident_lu_sub_code: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	assignmentsection: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "Transaction_date",
		inheritFrom: {
			parent: "sections",
			joinColumn: "SectionsDCID",
			parentColumn: "id"
		}
	},
	assignmentcategoryassoc: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "TRANSACTION_DATE",
		inheritFrom: {
			parent: "assignmentsection",
			joinColumn: "ASSIGNMENTSECTIONID",
			parentColumn: "AssignmentSectionID"
		}
	},
	assignmentscore: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: "ScoreEntryDate"
	},
	assignment: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null
	},
	standard: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	standardscore: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	standardgradesection: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	messagekeymap: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	gen: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	configitem: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	gradescaleitem: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	gradescalescorecode: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	districtteachercategory: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	s_stu_crdc_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_tn_stu_crdc_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_tn_stu_easyiep_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	studentcorefields: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	assignmentstandardassoc: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		inheritFrom: {
			parent: "assignment",
			joinColumn: "AssignmentId",
			parentColumn: "AssignmentID"
		}
	},
	codeset: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	teachercategory: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	test: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	testscore: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	gradescaleschoolassoc: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	studenttestscore: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		treatAs: "reference"
	},
	activities: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_in_stu_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_la_stu_504sped_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_la_stu_language_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_ma_stu_sped_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_ma_stu_demographic_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_ny_stu_crdc_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_stu_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	u_def_ext_students: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	u_student_custom_alert_info: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	gradescalesectionstudent: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: null
	},
	s_nm_stu_x: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: "arrivaldate"
	},
	s_ny_stu_assessments_c: {
		schoolColumns: [],
		studentColumns: ["StudentsDCID"],
		primaryDateColumn: "DateOfAdministration"
	},
	cycle_day: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	period: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	scheduleperiods: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	schoolstaff: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	users: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: null,
		inheritFrom: {
			parent: "schoolstaff",
			joinColumn: "DCID",
			parentColumn: "Users_DCID"
		}
	},
	u_mba_app_records: {
		schoolColumns: ["schoolid"],
		studentColumns: [],
		primaryDateColumn: null
	},
	studenttest: {
		schoolColumns: [],
		studentColumns: [],
		primaryDateColumn: "test_date"
	}
};
// src/projectStatus.ts
/**
* True when the project should be suppressed from scheduled execution and health reporting.
*
* `statusUntil` takes a `Date`, an ISO string, or null. An unparseable string is treated as "no
* expiry" so a malformed value fails safe toward suppression rather than silently un-pausing.
*/
function isProjectSuppressed(input, now) {
	if (input.status === "offboarded") return true;
	if (input.status !== "paused") return false;
	if (input.statusUntil == null) return true;
	const untilMs = input.statusUntil instanceof Date ? input.statusUntil.getTime() : new Date(input.statusUntil).getTime();
	if (Number.isNaN(untilMs)) return true;
	return untilMs > now.getTime();
}
// src/runPlan.ts
const runStepPhaseSchema = z$1.enum([
	"sync",
	"transform",
	"cascade",
	"cleanup",
	"archive",
	"metadata"
]);
const runStepStatusSchema = z$1.enum([
	"pending",
	"in-progress",
	"completed",
	"failed",
	"skipped"
]);
const runStepKindSchema = z$1.enum(["transform", "cleanup"]);
/**
* SIM-1657: shared sync-mode enum. Previously inlined as `z.enum(["full",
* "incremental"])` in two separate places in core (`streamStateSchema.mode`
* in schemas.ts; `runStepSyncStateSchema.mode` here) plus six oRPC contract
* shadows. One canonical, every consumer imports.
*/
const syncModeSchema = z$1.enum(["full", "incremental"]);
const runStepSyncStateSchema = z$1.object({
	currentPage: z$1.number().optional(),
	mode: syncModeSchema.optional(),
	streamStateKey: z$1.string().optional()
});
const runStepSchema = z$1.object({
	phase: runStepPhaseSchema,
	table: z$1.string(),
	dataset: z$1.string().optional(),
	connectorId: z$1.string().optional(),
	sql: z$1.string().optional(),
	layer: z$1.string().optional(),
	kind: runStepKindSchema.optional(),
	status: runStepStatusSchema,
	startedAt: z$1.number().optional(),
	/**
	* SIM-3422: when a later leg picks this step back up. `startedAt` keeps the
	* FIRST attempt, so a hard-killed step's own clock survives its recovery —
	* overwriting it is what made every post-mortem read the recovery leg's time
	* as the failure's.
	*/
	resumedAt: z$1.number().optional(),
	completedAt: z$1.number().optional(),
	error: z$1.string().optional(),
	bytesProcessed: z$1.number().optional(),
	syncState: runStepSyncStateSchema.optional(),
	/**
	* SIM-1702: TransformDefinition.id for cascade steps, populated only by `buildCascadeSteps` —
	* `transform` and `cleanup` phases generate SQL with no editable row, so it stays undefined
	* there. The runner re-resolves the latest SQL from this id at execution time on a continuation
	* leg, preventing snapshot-stale cascade reverts when a user edits the transform mid-run.
	*/
	definitionId: z$1.string().optional(),
	/**
	* SIM-2416: Skyward multi-entity ingestion. When set, this sync step pulls its table for exactly
	* this entity id; `buildSyncSteps` emits one step per (table, entityId) when the connector
	* declares `vars.entityIds`. Persisted so each pair rides the per-step continuation machinery
	* with its own streamStateKey across RunPlan round-trips. Undefined for every other connector.
	*/
	entityId: z$1.number().optional(),
	/**
	* SIM-3559: a single-row query the runner runs after the step and records on
	* `QueryRun.checkResult`. Set on the seen-ledger diff's cleanup step and on a cut-over
	* table's silver `__latest` step, where it rides beside the built-in duplicate check.
	*/
	checkSql: z$1.string().optional(),
	/**
	* SIM-2273: on an adhoc plan's `sync` step, the schedule whose settings this sync runs with —
	* the runner fetches config as that schedule would. Never the plan's own `schedule`
	* (`adhoc-cascade`): config for an unknown schedule holds no tables.
	*/
	asSchedule: z$1.string().optional()
});
const runPlanStatusSchema = z$1.enum([
	"running",
	"completed",
	"failed",
	"paused",
	"superseded"
]);
/** Statuses from which a plan can never be resumed. */
const TERMINAL_RUN_PLAN_STATUSES = [
	"completed",
	"failed",
	"superseded"
];
const runPlanSchema = z$1.object({
	id: z$1.string().optional(),
	projectId: z$1.string(),
	functionName: z$1.string(),
	schedule: z$1.string(),
	steps: z$1.array(runStepSchema),
	currentStepIndex: z$1.number(),
	status: runPlanStatusSchema,
	startedAt: z$1.number(),
	updatedAt: z$1.number()
});
const runExecutionStatusSchema = z$1.enum([
	"started",
	"completed",
	"continued",
	"failed",
	"timed_out",
	"abandoned",
	"skipped"
]);
/**
* SIM-1656: which logical layer of the bronze/silver/gold/diamond pipeline a transform produces.
* Bronze is excluded — it is raw connector output, not a transform target, so transforms produce
* silver-canon, gold or diamond. Lifted from three inline duplicates (SIM-1563).
*/
const transformLayerSchema = z$1.enum([
	"silver-canon",
	"gold",
	"diamond"
]);
const tableRunErrorKindSchema = z$1.enum([
	"ps_maintenance",
	"upstream_html",
	"invalid_json",
	"auth",
	"not_allowed",
	"not_found",
	"rate_limit",
	"upstream_4xx",
	"upstream_5xx",
	"network",
	"timeout",
	"bq_insert",
	"other"
]);
const tableRunScopeSchema = z$1.object({
	/**
	* The date predicate, when one applied. `token` is the configured form (`P-1D`,
	* `SCHOOLYEARSTART`) and `value` its resolution at run time — the token explains
	* intent, the value explains the count. SIM-3297: the run covered every row whose
	* stored `column` satisfies `value <= v < until`; `until` absent or null is unbounded.
	*/
	date: z$1.object({
		column: z$1.string(),
		value: z$1.string(),
		token: z$1.string().nullable(),
		until: z$1.string().nullable().optional()
	}).nullable(),
	/**
	* School scoping: how many ids were emitted, and how many the project declares. SIM-3297:
	* `column` and `ids` are present only when the run filtered on that column, so the run
	* covered every row whose stored `column` is in `ids`. School ids are not personal data.
	*/
	school: z$1.object({
		count: z$1.number().int(),
		of: z$1.number().int().nullable(),
		column: z$1.string().optional(),
		ids: z$1.array(z$1.string()).optional()
	}).nullable(),
	/**
	* 1c student-IN scoping. PRESENCE is the signal; `count` is always 0 and must
	* never be shown as a pool size — the stamp is side-effect-free and the real
	* size is only known after the ids have been fetched. Kept numeric so a later
	* stamp-after-the-fact can fill it without a schema change (SIM-2913 D2c).
	*/
	student: z$1.object({ count: z$1.number().int() }).nullable(),
	/** Slice F FK cascade: which parent the IN-list came from, and its size. */
	cascade: z$1.object({
		parent: z$1.string(),
		joinColumn: z$1.string(),
		count: z$1.number().int().nullable()
	}).nullable(),
	/**
	* `true` claims the walk read every row from row 0 (SIM-3585). For PowerSchool it is the
	* `overrides.filters.unfiltered` bypass, so an incremental or floored run stamps `false`.
	*/
	unfiltered: z$1.boolean(),
	/** `overrides.filters.disable` — dimensions suppressed by config. */
	disabled: z$1.array(z$1.string()),
	/** `treatAs: "reference"`: district-wide by knowledge-file policy, not config. */
	reference: z$1.boolean(),
	mode: syncModeSchema.nullable(),
	/**
	* Stamped as a **deprecation signal**, not a feature (SIM-1408). A pagination floor silently
	* drops the first N records forever, so it must be visible wherever a low count is read — but it
	* is being retired, and the stamp gives that retirement a live worklist of which tables still
	* carry one. Display it as a warning, never as a neutral filter row.
	*/
	minStartIndex: z$1.number().int().nullable(),
	/**
	* SIM-3568: one leg of a per-entity fan-out — this leg's entity and every entity the plan walks.
	* Not a narrowing: the legs together cover the table, so a sweep needs each of `of` to finish.
	*/
	entity: z$1.object({
		id: z$1.number(),
		of: z$1.array(z$1.number())
	}).nullable().optional()
});
/**
* Sentinel table value for metadata steps. Required by `RunStep.table` which
* is non-optional, but a metadata step has no per-table semantics — it
* triggers a project-level collection POST.
*/
const METADATA_STEP_TABLE = "__metadata__";
/**
* SIM-1377: append a metadata-collection step to an existing plan. Returns a
* new plan object (callers may mutate the original; this helper is the
* supported boundary). Used by adhoc "Run now" handlers — scheduled paths
* gate the same step in `buildPlanFromConfig` via `checkPending`.
*/
function appendMetadataStep(plan) {
	return {
		...plan,
		steps: [...plan.steps, {
			phase: "metadata",
			table: METADATA_STEP_TABLE,
			status: "pending"
		}],
		updatedAt: Date.now()
	};
}
// src/runStatus.ts
/**
* SIM-2913: the row indicator is now `pipelineStatusSchema`, shared with the
* transform rows, the pipeline DAG, and the health reports. `RunDot` was one of
* eight competing vocabularies for the same question — see `pipelineStatus.ts`
* for the inventory and why the verdict is computed server-side only.
*/
const runDotSchema = pipelineStatusSchema;
const cadenceWriteSchema = z$1.object({
	cadence: runCadenceSchema,
	/**
	* ISO; null = never. On a table row: the silver step's completion in a plan where this table's
	* fetch completed (any row count, zero included) and silver completed — a silver rebuild after a
	* failed fetch is not a write (SIM-3729). On a transform row: its cascade step's completion.
	*/
	at: z$1.string().nullable(),
	status: z$1.enum([
		"completed",
		"failed",
		"skipped",
		"in-progress",
		"pending"
	]),
	error: z$1.string().nullable()
});
const tableRunFactsSchema = z$1.object({
	/** `TableRun.startedAt`, ISO. */
	at: z$1.string(),
	/** `TableRun.endedAt`, ISO. Null while the run never reported a terminal state. */
	endedAt: z$1.string().nullable(),
	/** `TableRun.status` — the Prisma `RunStatus` enum, as written by the runner. */
	status: z$1.string(),
	/**
	* Resolved from the owning `RunPlan.schedule`, never from
	* `ProjectRun.functionName`: on multi-school projects the entrypoint encodes
	* the school (`runHourly_GBR`, `runHourly_GMC`, …) and keying cadence off it
	* splits one cadence into four.
	*/
	cadence: runCadenceSchema,
	recordsFetched: z$1.number(),
	/**
	* The honest bronze-insert signal. A BigQuery `lastModifiedTime` advances on
	* any DML, so it cannot distinguish an insert from a cleanup overwrite; a
	* `completed` run's insert count can.
	*/
	recordsInserted: z$1.number(),
	/** `full` | `incremental`. Null on runs that predate the field or omit it. */
	mode: z$1.string().nullable(),
	/**
	* Bounded failure classification from `tableRunErrorKindSchema` (SIM-2742).
	* Deliberately this and not a message: error text is provider-controlled and
	* unbounded, and these rows outlive Sentry retention. See SIM-2915.
	*/
	errorKind: z$1.string().nullable(),
	errorStatus: z$1.number().nullable(),
	/**
	* `ProjectRun.skipReason` (`"paused"` | `"offboarded"`) when the scheduled run
	* was skipped by design (SIM-1792). Never a failure — a paused connector
	* reported as broken sends someone to debug something that is off on purpose.
	*/
	skipReason: z$1.string().nullable(),
	/**
	* SIM-2914: the resolved filters this fetch ran under. Null is permanent and normal (runs
	* predating the stamp, non-PowerSchool connectors, paused clients), so consumers render "not
	* recorded" rather than inferring one. Never recomputed from current config — that renders
	* today's filters against yesterday's count, lying in the exact case the stamp exists for.
	*/
	scope: tableRunScopeSchema.nullable()
});
const tableRunStatusSchema = z$1.object({
	/** Owning connector, resolved server-side from the step's dataset. */
	connectorId: z$1.string(),
	/** Config-row table name (base name, e.g. `attendance`). */
	table: z$1.string(),
	bronzeDataset: z$1.string().nullable(),
	bronzeTable: z$1.string().nullable(),
	silverDataset: z$1.string().nullable(),
	silverTable: z$1.string().nullable(),
	/**
	* Newest write across every cadence: the silver step's completion in a plan where this table's
	* fetch completed (any row count, zero included) and silver completed. ISO; null = never written.
	*/
	lastWrittenAt: z$1.string().nullable(),
	/** Which cadence produced `lastWrittenAt` — so a manual run cannot pass as a schedule. */
	lastWrittenBy: runCadenceSchema.nullable(),
	/** Newest-first, one entry per cadence that has ever written this table. */
	perCadence: z$1.array(cadenceWriteSchema),
	/** Set when a plan is running right now and this table's step has not finished. */
	inFlight: z$1.object({
		cadence: runCadenceSchema,
		startedAt: z$1.string(),
		late: z$1.boolean()
	}).nullable(),
	/** Cadences the config schedules this table under, strictest first. */
	scheduledCadences: z$1.array(runCadenceSchema),
	dot: runDotSchema,
	/**
	* SIM-2913: why the status is what it is. Always present. Carries the signals
	* the old separate vocabularies held (row-count band, source-file freshness),
	* so the dot stays one word while the explanation stays specific.
	*/
	reason: pipelineReasonSchema,
	/** The window the verdict was made against (cadence + grace), for display. */
	windowMinutes: z$1.number().nullable(),
	/** How far past the window the last write is. Null when inside it or n/a. */
	overdueByMinutes: z$1.number().nullable(),
	/**
	* SIM-2791: the newest `TableRun` for this table, or null when the lookback window holds none.
	* Null is normal, not an error — a table that has not run inside the window, a connector that
	* emits no `TableRun` rows, and a paused client all land here, so consumers must render the
	* absence rather than assume a run exists.
	*/
	lastRun: tableRunFactsSchema.nullable(),
	/**
	* Newest-first, up to `RUNS_PER_TABLE`. Includes `lastRun` as its first entry
	* so the popover can show a short history without a second request.
	*/
	recentRuns: z$1.array(tableRunFactsSchema)
});
const transformRunStatusSchema = z$1.object({
	definitionId: z$1.string(),
	name: z$1.string(),
	/** The definition's declared layer — `gold` / `diamond` / `silver-canon`. */
	layer: z$1.string(),
	outputDataset: z$1.string(),
	outputTable: z$1.string(),
	enabled: z$1.boolean(),
	/** Newest **completed** run, ISO. Null = none inside the lookback window. */
	lastRunAt: z$1.string().nullable(),
	/** Status of the newest run attempt, which may be a later, failed one. */
	lastAttemptStatus: z$1.string().nullable(),
	lastAttemptAt: z$1.string().nullable(),
	lastError: z$1.string().nullable(),
	rowsAffected: z$1.number().nullable(),
	/**
	* The definition was edited after its last completed run — "you changed this
	* and it has not executed since". The ticket's motivating case: currently
	* undetectable anywhere in the UI.
	*/
	editedSinceLastRun: z$1.boolean(),
	editedAt: z$1.string(),
	/** Cadences this transform has been *observed* running under (not declared —
	*  transforms carry no schedule of their own), strictest first. */
	observedCadences: z$1.array(runCadenceSchema),
	perCadence: z$1.array(cadenceWriteSchema),
	inFlight: z$1.object({
		cadence: runCadenceSchema,
		startedAt: z$1.string(),
		late: z$1.boolean()
	}).nullable(),
	dot: runDotSchema,
	/**
	* SIM-2913: why the status is what it is. Always present. Carries the signals
	* the old separate vocabularies held (row-count band, source-file freshness),
	* so the dot stays one word while the explanation stays specific.
	*/
	reason: pipelineReasonSchema,
	windowMinutes: z$1.number().nullable(),
	overdueByMinutes: z$1.number().nullable()
});
const runStatusSchema = z$1.object({
	projectId: z$1.string(),
	/** When this was computed, so the client never measures against its own clock. */
	computedAt: z$1.string(),
	/** How far back run history was read. A transform idle longer than this
	*  reports no run rather than its true last run — bounded on purpose, the
	*  unbounded window cost 642ms on the largest project. */
	lookbackDays: z$1.number(),
	tables: z$1.array(tableRunStatusSchema),
	transforms: z$1.array(transformRunStatusSchema)
});
// src/safeString.ts
/**
* SIM-1540 — stringify-from-unknown that JSON-serialises the object case instead of falling through
* to `"[object Object]"`. Used at boundaries where a `Record<string, unknown>` row value, query
* param or BQ cell must become a string: callers expect primitives at runtime but the typesystem
* cannot prove it, and behaviour for primitives is unchanged. Core: SIM-1733.
*/
function safeString(value) {
	if (value === null || value === void 0) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value);
	if (value instanceof Date) return value.toString();
	try {
		return JSON.stringify(value);
	} catch {
		return "[unstringifiable]";
	}
}
// src/scheduleMembership.ts
function isNamedEntry(entry) {
	return typeof entry === "object" && entry !== null && typeof entry.name === "string";
}
/**
* SIM-2273: the schedules a connector's table is on, in `vars.schedules` key order. Matched
* case-insensitively, as `applySchedule` matches schedule entries to base tables.
*/
function scheduleMembershipsForTable(vars, table) {
	const schedules = vars?.schedules;
	if (typeof schedules !== "object" || schedules === null) return [];
	const wanted = table.toLowerCase();
	const memberships = [];
	for (const [schedule, list] of Object.entries(schedules)) {
		if (!Array.isArray(list)) continue;
		const entries = list.filter((e) => isNamedEntry(e) && e.name.toLowerCase() === wanted);
		if (entries.length > 0) memberships.push({
			schedule,
			instances: entries.length,
			entries
		});
	}
	return memberships;
}
// src/scheduleTimes.ts
/**
* The timezone every schedule entry's `at` is interpreted in (SIM-3119). `at` is a bare `HH:MM`,
* which means nothing without a zone, and one zone fleet-wide is enough for now: every US zone
* shifts on the same DST dates, so a fleet pinned to one stays internally consistent across them.
* Per-client zones (`Project.timezone`) are SIM-3118.
*/
const SCHEDULE_TIMEZONE = "America/New_York";
const HOUR_MINUTES = 60;
const DAY_MINUTES = 1440;
const WEEK_MINUTES = 10080;
/**
* The circle each cadence's firings live on.
*
* Monthly stays on the day circle: its `at` is still a time of day. Measuring an
* hourly chain on the 24h circle spreads a punctual `:28` across the whole clock.
*/
const CADENCE_PERIOD_MINUTES = {
	hourly: 60,
	daily: DAY_MINUTES,
	weekly: WEEK_MINUTES,
	monthly: DAY_MINUTES
};
/** The cadence's period, defaulting to a day for an unrecognised key. */
function cadencePeriodMinutes(cadence) {
	return CADENCE_PERIOD_MINUTES[cadence] ?? 1440;
}
/** Indexed by `Date.getUTCDay()`, and by the weekday part of a weekly phase. */
const WEEKDAY_NAMES = [
	"Sun",
	"Mon",
	"Tue",
	"Wed",
	"Thu",
	"Fri",
	"Sat"
];
const zonedFormatter = new Intl.DateTimeFormat("en-CA", {
	timeZone: SCHEDULE_TIMEZONE,
	weekday: "short",
	hour: "2-digit",
	minute: "2-digit",
	second: "2-digit",
	hourCycle: "h23"
});
/**
* The SCHEDULE_TIMEZONE wall clock for an instant. GAS triggers are set in a
* wall clock, so a chain pinned to 02:00 ET is steady here and wobbles by an
* hour in UTC across a DST boundary — reading `at` in the zone keeps it steady.
* Seconds are kept: the thresholds that consume this are finer than a minute.
*/
function zonedWallClock(date) {
	const parts = zonedFormatter.formatToParts(date);
	const get = (type) => parts.find((p) => p.type === type)?.value ?? "0";
	const hour = Number.parseInt(get("hour"), 10) % 24;
	const minute = Number.parseInt(get("minute"), 10);
	const second = Number.parseInt(get("second"), 10);
	const weekday = WEEKDAY_NAMES.indexOf(get("weekday").slice(0, 3));
	return {
		weekday: weekday === -1 ? 0 : weekday,
		minutesIntoDay: hour * 60 + minute + second / 60
	};
}
/** A wall clock's position on its cadence's circle — see CADENCE_PERIOD_MINUTES. */
function phaseMinutes(wall, cadence) {
	const period = cadencePeriodMinutes(cadence);
	if (period === 60) return wall.minutesIntoDay % 60;
	if (period === 10080) return wall.weekday * DAY_MINUTES + wall.minutesIntoDay;
	return wall.minutesIntoDay;
}
/** The instant's phase on its cadence's circle, read in SCHEDULE_TIMEZONE. */
function phaseMinutesOf(date, cadence) {
	return phaseMinutes(zonedWallClock(date), cadence);
}
const HOURLY_AT = /^:([0-5]\d)$/;
const DAILY_AT = /^([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKLY_AT = /^([A-Z][a-z]{2}) ([01]\d|2[0-3]):([0-5]\d)$/;
/**
* An `at` value as phase minutes FOR THE GIVEN CADENCE, null when the shape does
* not match it. `schedules` is a `z.record`, so an entry cannot see its own
* bucket key and every shape validates anywhere — the cadence is therefore the
* key, never the string: a `:45` filed under `daily` is unusable, not 00:45.
*/
function parseAtPhase(at, cadence) {
	const period = CADENCE_PERIOD_MINUTES[cadence];
	if (period === void 0) return null;
	if (period === 60) {
		const hourly = HOURLY_AT.exec(at);
		return hourly ? Number.parseInt(hourly[1], 10) : null;
	}
	if (period === 10080) {
		const weekly = WEEKLY_AT.exec(at);
		if (!weekly) return null;
		const weekday = WEEKDAY_NAMES.indexOf(weekly[1]);
		if (weekday === -1) return null;
		return weekday * DAY_MINUTES + Number.parseInt(weekly[2], 10) * 60 + Number.parseInt(weekly[3], 10);
	}
	const daily = DAILY_AT.exec(at);
	return daily ? Number.parseInt(daily[1], 10) * 60 + Number.parseInt(daily[2], 10) : null;
}
/** Tolerance for "landed on the requested phase" — Date truncates fractional ms. */
const PHASE_EPSILON_MINUTES = 1 / 60;
/** How far `actual` runs ahead of `target` on the circle, in (-period/2, period/2]. */
function signedPhaseDiff(actual, target, period) {
	const forward = ((actual - target) % period + period) % period;
	return forward > period / 2 ? forward - period : forward;
}
/**
* Wall-clock minutes are not elapsed minutes across a DST boundary, so a candidate
* reached by elapsed arithmetic is corrected once by the hour it missed by. A second
* miss means that wall clock never occurred: null, and the caller falls back.
*/
function settleOnPhase(candidate, target, cadence, period) {
	const drift = signedPhaseDiff(phaseMinutesOf(candidate, cadence), target, period);
	if (Math.abs(drift) <= PHASE_EPSILON_MINUTES) return candidate;
	const corrected = /* @__PURE__ */ new Date(candidate.getTime() - drift * 6e4);
	const settled = signedPhaseDiff(phaseMinutesOf(corrected, cadence), target, period);
	return Math.abs(settled) > PHASE_EPSILON_MINUTES ? null : corrected;
}
/**
* The most recent instant at `phase` on `cadence`'s circle, at or before `now`,
* in SCHEDULE_TIMEZONE. Null when that wall clock never occurred (spring forward).
*/
function previousSlotStart(now, phase, cadence) {
	const period = cadencePeriodMinutes(cadence);
	const target = (phase % period + period) % period;
	const delta = ((phaseMinutesOf(now, cadence) - target) % period + period) % period;
	const candidate = settleOnPhase(/* @__PURE__ */ new Date(now.getTime() - delta * 6e4), target, cadence, period);
	if (candidate === null) return null;
	candidate.setUTCMilliseconds(0);
	return candidate.getTime() > now.getTime() ? null : candidate;
}
/**
* The earliest instant at `phase` on `cadence`'s circle strictly after `now` — the
* boundary at which `previousSlotStart` next moves. Null when that wall clock never
* occurs (spring forward), exactly as `previousSlotStart` would answer then.
*/
function nextSlotStart(now, phase, cadence) {
	const period = cadencePeriodMinutes(cadence);
	const target = (phase % period + period) % period;
	const ahead = ((target - phaseMinutesOf(now, cadence)) % period + period) % period;
	const candidate = settleOnPhase(new Date(now.getTime() + (ahead || period) * 6e4), target, cadence, period);
	if (candidate === null) return null;
	candidate.setUTCMilliseconds(0);
	let next = candidate;
	for (let step = 0; step < 3; step++) {
		const earlier = previousSlotStart(/* @__PURE__ */ new Date(next.getTime() - 1e3), phase, cadence);
		if (earlier === null || earlier.getTime() <= now.getTime()) break;
		next = earlier;
	}
	return next.getTime() > now.getTime() ? next : null;
}
// src/schemas.ts
const connectorSchema = z.object({
	id: z.string(),
	name: z.string(),
	type: z.string(),
	prefix: z.string(),
	projectId: z.string(),
	vars: z.record(z.string(), z.any()),
	secrets: z.record(z.string(), z.string()),
	createdAt: z.date(),
	updatedAt: z.date(),
	archivedAt: z.date().nullable()
});
const baseConnectorConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	type: z.string(),
	prefix: z.string(),
	vars: z.record(z.string(), z.any()),
	secrets: z.record(z.string(), z.string())
});
/** Per-table transform flags controlling silver SQL generation. */
const tableTransformFlags = {
	trackFirstCaptured: z.boolean().optional(),
	trackDeleted: z.boolean().optional(),
	seenLedgerCutover: z.boolean().optional(),
	strategy: z.enum(["replace", "merge"]).optional(),
	cleanupAfterDays: z.number().nullable().optional()
};
/** Optional connector-level dataset overrides — allows per-connector control over BQ target datasets. */
const connectorDatasetOverrides = {
	bronzeDataset: z.string().optional(),
	silverDataset: z.string().optional()
};
/**
* The phase within a cadence period, wall-clock in SCHEDULE_TIMEZONE: `:45` hourly,
* `05:45` daily, `Sun 05:45` weekly. One definition, used by every schema that
* accepts one — it was written out twice and the copies are a standing footgun
* (SIM-3376). The cadence comes from the bucket key, so the shape is unambiguous.
*/
const atSchema = z.string().regex(/^(:[0-5]\d|([01]\d|2[0-3]):[0-5]\d|(Sun|Mon|Tue|Wed|Thu|Fri|Sat) ([01]\d|2[0-3]):[0-5]\d)$/);
/**
* SIM-3376: the schedule itself, defined once per cadence rather than on every table entry;
* the `schedules` bucket of the same name is what attaches tables to it. The only source of
* `at` — the per-entry override it once deferred to is gone, so a schedule with no definition
* resolves nothing (SIM-3380).
*/
const scheduleDefsSchema = z.record(z.string(), z.object({
	cadence: z.string().optional(),
	at: atSchema.optional(),
	notBefore: z.iso.datetime({ offset: true }).optional()
})).optional();
/**
* Per-schedule table sync configs, keyed by schedule name ("hourly", "daily"); each value is an
* array of table configs with optional sync options (mode, minStartIndex, …). `params` and
* `filters` are first-class schedule-entry fields so required-param tables (PSE submissionrecords)
* can be scheduled — Zod strips unknown keys, so before that they vanished at save-time (SIM-1369).
*/
const schedulesSchema = z.record(z.string(), z.array(z.object({
	name: z.string(),
	mode: syncModeSchema.optional(),
	minStartIndex: z.number().optional(),
	lookbackRows: z.number().optional(),
	lookbackDays: z.number().optional(),
	params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
	filters: z.record(z.string(), z.union([z.string(), z.number()])).optional()
}))).optional();
const powerschoolFiltersShape = {
	schoolids: z.array(z.string()).optional(),
	sinceDate: z.string().optional(),
	schoolYearStart: z.string().optional(),
	studentPoolFromReenrollments: z.boolean().optional()
};
const powerschoolFiltersSchema = z.object(powerschoolFiltersShape).optional();
/**
* `overrides.filters` at the table or per-cadence level, adding three override-only knobs:
* `unfiltered: true` bypasses ALL scoping for this table (subsuming SIM-952's 1d escape hatch),
* `disable: [...]` suppresses named dimensions, and `scopeBy: "student"` (SIM-2810) scopes by the
* schoolid-derived student pool rather than the table's own school column.
*/
const powerschoolOverrideFiltersSchema = z.object({
	...powerschoolFiltersShape,
	unfiltered: z.boolean().optional(),
	disable: z.array(z.enum(["schoolids", "sinceDate"])).optional(),
	scopeBy: z.enum(["school", "student"]).optional()
}).optional();
/**
* Optional per-entry overrides container, used on both `vars.tables[]` and `vars.schedules[*][]`.
* The platform's `applySchedule()` merges schedule entries onto the matching base table entry, and
* object-spread precedence means a schedule entry's `overrides` replaces the base table's outright.
* The runner therefore reads only `table.overrides` post-merge — see `resolveEffectiveFilters()`.
*/
const powerschoolOverridesSchema = z.object({ filters: powerschoolOverrideFiltersSchema }).optional();
/**
* PowerSchool-specific `vars.schedules` shape. Adds `overrides` on top of the
* generic cadence-entry fields so per-cadence filter policy can be expressed.
* Other connectors keep using the generic `schedulesSchema`.
*/
const powerschoolSchedulesSchema = z.record(z.string(), z.array(z.object({
	name: z.string(),
	mode: syncModeSchema.optional(),
	minStartIndex: z.number().optional(),
	lookbackRows: z.number().optional(),
	lookbackDays: z.number().optional(),
	params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
	filters: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
	overrides: powerschoolOverridesSchema
}))).optional();
const powerschoolsConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("powerschools"),
	vars: z.object({
		baseUrl: z.string(),
		filters: powerschoolFiltersSchema,
		tables: z.array(z.object({
			name: z.string(),
			fields: z.array(z.string()),
			primaryKey: z.string(),
			/** Map of field name → column alias (e.g. `{ "Name.FirstName": "first_name" }`). */
			fieldAliases: z.record(z.string(), z.string()).optional(),
			overrides: powerschoolOverridesSchema,
			...tableTransformFlags
		})),
		schedules: powerschoolSchedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({
		clientId: z.string(),
		clientSecret: z.string()
	})
});
const powerschoolEnrollmentConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("powerschool-enrollment"),
	vars: z.object({
		/**
		* Base API URL including version, e.g. "https://enrollment.powerschool.com/api/v1"
		*/
		baseUrl: z.string(),
		tables: z.array(z.object({
			/**
			* Table name is the Enrollment API resource identifier.
			* (We intentionally avoid a separate redundant `resource` field.)
			*/
			name: z.string(),
			/** Primary key field(s). Can be a single field name or an array for composite keys */
			primaryKey: z.union([z.string(), z.array(z.string())]),
			fields: z.array(z.string()).optional(),
			/**
			* Names of runtime params this table requires at sync time; values arrive via
			* `SyncTableOptions.params` (GAS overrides or schedule entries). Distinct from the
			* schedule-entry `params: Record<string, ...>` channel: this field names declared keys,
			* that one carries values. See history: SIM-1378, SIM-1369.
			*/
			requiredParams: z.array(z.string()).optional(),
			/**
			* Default query params to apply on every request for this table (merged with runtime sync filters).
			* e.g. { status: "Approved" }
			*/
			defaultFilters: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({ apiKey: z.string() })
});
const skywardConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("skyward"),
	vars: z.object({
		baseUrl: z.string(),
		tables: z.array(z.object({
			name: z.string(),
			fields: z.array(z.string()),
			primaryKey: z.string(),
			/** Map of field name → column alias (e.g. `{ "Name.FirstName": "first_name" }`). */
			fieldAliases: z.record(z.string(), z.string()).optional(),
			...tableTransformFlags
		})),
		entityId: z.number(),
		/**
		* SIM-2416: multi-entity ingestion. When present and non-empty the runner pulls each configured
		* table IN FULL for EACH entity id, appending every row to the same bronze snapshot with
		* per-entity provenance (`__source_entity_id`). Absent or empty keeps the legacy single-entity
		* behavior driven by the scalar `entityId`.
		*/
		entityIds: z.array(z.number()).optional(),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({
		clientId: z.string(),
		clientSecret: z.string()
	})
});
/**
* Renaissance DnA (formerly Illuminate "Data and Assessment"). OAuth2
* client-credentials against `{baseUrl}/Api/<Resource>/`, page/limit paginated.
* See `docs/architecture/dna-connector.md` for the measured API shape.
*/
const dnaConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("dna"),
	vars: z.object({
		/**
		* Site root including the instance directory, e.g.
		* "https://impact.illuminateed.com/live". `rest_server.php` is appended for
		* REST calls; a URL that already carries it is accepted unchanged. A bare
		* host with no directory falls back to `/live`, the only one observed.
		*/
		baseUrl: z.string(),
		/**
		* Token endpoint. DnA serves it from the site root and reads `grant_type`
		* only from the request body. Defaults to the site root plus
		* `?OAuth2_AccessToken`; set only if an instance differs.
		*/
		oauthUrl: z.string().optional(),
		tables: z.array(z.object({
			/**
			* Bronze table name. Forced lowercase on parse — BigQuery table ids
			* are case-sensitive, and mixed case has already produced the same
			* logical PS table under two ids across clients (SIM-3181).
			*/
			name: z.string(),
			/**
			* API resource path segment, e.g. "AssessmentAggregateStudentResponses".
			* DnA's routes ARE case-sensitive, so this preserves the vendor's
			* casing while `name` goes lowercase. Defaults to `name` as entered,
			* captured before the lowercasing below.
			*/
			resource: z.string().optional(),
			/** Single field or composite. A too-wide key is safe; a too-narrow one collides. */
			primaryKey: z.union([z.string(), z.array(z.string())]),
			/** Omit to keep every field the endpoint returns. */
			fields: z.array(z.string()).optional(),
			/** Query params applied on every request for this table. */
			defaultFilters: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
			/**
			* Endpoints differ: most return the `{page, num_pages, results[]}`
			* envelope, but `Sites` and `Courses` return a bare array. "auto"
			* detects per response and is correct for both.
			*/
			responseShape: z.enum([
				"auto",
				"envelope",
				"array"
			]).default("auto").optional(),
			/**
			* Opt-in (SIM-3596): collect every distinct `idField` value across the
			* `sources` list endpoints, then GET `resource` — which must contain
			* `{id}` — once per value, `batchSize` requests per fetch call.
			*/
			fanOut: z.object({
				idField: z.string().min(1),
				sources: z.array(z.string().min(1)).min(1),
				batchSize: z.number().int().min(1).max(50).default(10)
			}).optional(),
			...tableTransformFlags
		}).refine((table) => !table.fanOut || (table.resource ?? "").includes("{id}"), {
			message: "fanOut tables need a resource containing {id}",
			path: ["resource"]
		}).transform((table) => ({
			...table,
			resource: table.resource ?? table.name,
			name: table.name.toLowerCase()
		}))),
		/** Rows per request. DnA silently caps at 1000. */
		pageSize: z.number().max(1e3).optional(),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({
		accessKey: z.string(),
		accessSecret: z.string()
	})
});
const exampleConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("example"),
	vars: z.object({
		tables: z.array(z.object({
			name: z.string(),
			fields: z.array(z.string()),
			records: z.array(z.any()),
			primaryKey: z.string(),
			/**
			* SIM-3297: a narrowing the fake vendor applies and stamps on `TableRun.scope`, so the
			* pulse rig can exercise a scoped sweep. `date` keeps `since <= day < until`.
			*/
			scope: z.object({
				date: z.object({
					column: z.string(),
					since: z.string(),
					until: z.string().optional()
				}).optional(),
				school: z.object({
					column: z.string(),
					ids: z.array(z.string())
				}).optional()
			}).optional()
		})),
		...connectorDatasetOverrides
	})
});
const deanslistConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("deanslist"),
	vars: z.object({
		domain: z.string(),
		lookbackDays: z.number().optional(),
		/** For fast-rest tables, max days of data per request. Default 1. */
		maxDaysPerRequest: z.number().optional(),
		tables: z.array(z.object({
			name: z.string(),
			primaryKey: z.string(),
			api: z.enum([
				"graphql",
				"standard-rest",
				"fast-rest"
			]).default("graphql"),
			fields: z.array(z.string()),
			/** Map of field name → column alias (e.g. `{ "Name.FirstName": "first_name" }`). */
			fieldAliases: z.record(z.string(), z.string()).optional(),
			/** Override lookbackDays for this specific table (falls back to connector-level, then 30). */
			lookbackDays: z.number().optional(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({ apiKey: z.string() })
});
const ireadySftpConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("iready-sftp"),
	vars: z.object({
		/** Optional override for the Cloud Function URL */
		sftpWrapperUrl: z.string().optional(),
		/** GCP project ID where the SFTP wrapper function is deployed */
		projectId: z.string(),
		/** GCP region where the SFTP wrapper function is deployed */
		region: z.string().default("us-central1"),
		/** SFTP server hostname */
		host: z.string().default("sftp.i-ready.com"),
		/** SFTP server port */
		port: z.number().default(22),
		/** SFTP username */
		username: z.string(),
		/** Table definitions */
		tables: z.array(z.object({
			name: z.string(),
			/** Primary key field(s). Can be a single field name or an array for composite keys */
			primaryKey: z.union([z.string(), z.array(z.string())]),
			fields: z.array(z.string()).optional(),
			/** Directory to sync files from for this table (default: "exports/Current_Year/") */
			directory: z.string().default("exports/Current_Year/"),
			/** Regex pattern to filter files for this table (default: "diagnostic_results_.*\\.csv$") */
			filePattern: z.string(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({ password: z.string() })
});
const schoolmintConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("schoolmint"),
	vars: z.object({
		/** SchoolMint domain, e.g. "superschools.schoolmint.net" */
		domain: z.string(),
		/** Super admin email for SchoolMint API requests */
		accountEmail: z.string(),
		/** Matches the terraform sftp-server module's `site_name` variable.
		*  Used to derive the GCS bucket: `${siteName}-simplicity-sftp-server-files` */
		siteName: z.string(),
		/** The SFTP username SchoolMint uploads as.
		*  Used to derive the GCS prefix: `users/${sftpUsername}/` */
		sftpUsername: z.string(),
		/** Override the derived GCS bucket name */
		gcsBucketOverride: z.string().optional(),
		/** Override the derived GCS object prefix */
		gcsPrefixOverride: z.string().optional(),
		/** Milliseconds between GCS poll attempts (default 30s) */
		pollIntervalMs: z.number().default(3e4),
		/** Maximum number of poll attempts before timing out (default 40) */
		pollMaxAttempts: z.number().default(40),
		tables: z.array(z.object({
			name: z.string(),
			primaryKey: z.union([z.string(), z.array(z.string())]),
			fields: z.array(z.string()).optional(),
			/** Regex pattern to match the report file name in GCS */
			filePattern: z.string(),
			/** Map of field name → column alias (e.g. `{ "Accepted Date": "accepted_date" }`). */
			fieldAliases: z.record(z.string(), z.string()).optional(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({ 
	/** SchoolMint Api-Token used for all demand_exports requests */
api_token: z.string() })
});
const avelaConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("avela"),
	vars: z.object({
		/** Matches the terraform sftp-server module's `site_name` variable.
		*  Used to derive the GCS bucket: `${siteName}-simplicity-sftp-server-files` */
		siteName: z.string(),
		/** The SFTP username Avela uploads as.
		*  Used to derive the GCS prefix: `users/${sftpUsername}/` */
		sftpUsername: z.string(),
		/** Override the derived GCS bucket name */
		gcsBucketOverride: z.string().optional(),
		/** Override the derived GCS object prefix */
		gcsPrefixOverride: z.string().optional(),
		tables: z.array(z.object({
			name: z.string(),
			primaryKey: z.union([z.string(), z.array(z.string())]),
			fields: z.array(z.string()).optional(),
			/** Regex pattern to match CSV files in GCS */
			filePattern: z.string(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({})
});
const schoolmintEnrollConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("schoolmint-enroll"),
	vars: z.object({
		/** Matches the terraform sftp-server module's `site_name` variable.
		*  Used to derive the GCS bucket: `${siteName}-simplicity-sftp-server-files` */
		siteName: z.string(),
		/** The SFTP username SchoolMint Enroll uploads as.
		*  Used to derive the GCS prefix: `users/${sftpUsername}/` */
		sftpUsername: z.string(),
		/** Override the derived GCS bucket name */
		gcsBucketOverride: z.string().optional(),
		/** Override the derived GCS object prefix */
		gcsPrefixOverride: z.string().optional(),
		tables: z.array(z.object({
			name: z.string(),
			primaryKey: z.union([z.string(), z.array(z.string())]),
			fields: z.array(z.string()).optional(),
			/** Regex pattern to match CSV files in GCS */
			filePattern: z.string(),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({})
});
const fileDropConnectorConfigSchema = baseConnectorConfigSchema.extend({
	type: z.literal("file-drop"),
	vars: z.object({
		acquisition: z.discriminatedUnion("mode", [z.object({
			mode: z.literal("remote-sftp"),
			host: z.string(),
			port: z.number().default(22),
			username: z.string(),
			/** Optional override for the SFTP wrapper Cloud Function URL */
			sftpWrapperUrl: z.string().optional(),
			/** Set by the platform at delivery from the onsite workspace's GCP project; never saved. */
			projectId: z.string().optional(),
			/** Set by the platform at delivery from the onsite workspace's region; never saved. */
			region: z.string().optional()
		})]),
		tables: z.array(z.object({
			name: z.string(),
			fields: z.array(z.string()).optional(),
			/** Map of source header → column name, applied before field projection */
			fieldAliases: z.record(z.string(), z.string()).optional(),
			/** Directory to list files from ("" = SFTP root) */
			directory: z.string().default(""),
			/** Regex over bare file names; the newest match by modify time is the one loaded. */
			filePattern: z.string().default(".*\\.csv$"),
			/** CSV delimiter */
			delimiter: z.string().default(","),
			hasHeader: z.boolean().default(true),
			/** Only `overwrite` is supported this slice */
			loadMode: z.enum(["overwrite", "append"]).default("overwrite"),
			...tableTransformFlags
		})),
		schedules: schedulesSchema,
		scheduleDefs: scheduleDefsSchema,
		...connectorDatasetOverrides
	}),
	secrets: z.object({ password: z.string() })
});
const connectorConfigSchema = z.discriminatedUnion("type", [
	powerschoolsConnectorConfigSchema,
	powerschoolEnrollmentConnectorConfigSchema,
	skywardConnectorConfigSchema,
	exampleConnectorConfigSchema,
	deanslistConnectorConfigSchema,
	ireadySftpConnectorConfigSchema,
	schoolmintConnectorConfigSchema,
	avelaConnectorConfigSchema,
	schoolmintEnrollConnectorConfigSchema,
	fileDropConnectorConfigSchema,
	dnaConnectorConfigSchema
]);
const baseDestinationConfigSchema = z.object({
	id: z.string().optional(),
	name: z.string().optional(),
	createdAt: z.string().optional(),
	type: z.string(),
	vars: z.record(z.string(), z.any()),
	secrets: z.record(z.string(), z.string())
});
const bigQueryDestinationConfigSchema = baseDestinationConfigSchema.extend({
	type: z.literal("bigquery"),
	vars: z.object({
		projectId: z.string(),
		datasetId: z.string().optional()
	})
});
const bigQueryExperimentalDestinationConfigSchema = baseDestinationConfigSchema.extend({
	type: z.literal("bigquery-experimental"),
	vars: z.object({
		projectId: z.string(),
		datasetId: z.string().optional()
	})
});
const bigQueryNoDeduplicationDestinationConfigSchema = baseDestinationConfigSchema.extend({
	type: z.literal("bigquery-nodedup"),
	vars: z.object({
		projectId: z.string(),
		datasetId: z.string().optional()
	})
});
const exampleDestinationConfigSchema = baseDestinationConfigSchema.extend({ type: z.literal("example") });
const destinationConfigSchema = z.discriminatedUnion("type", [
	bigQueryDestinationConfigSchema,
	bigQueryExperimentalDestinationConfigSchema,
	bigQueryNoDeduplicationDestinationConfigSchema,
	exampleDestinationConfigSchema
]);
const jobSchema = z.object({
	id: z.string(),
	status: z.enum([
		"pending",
		"started",
		"completed",
		"failed"
	]),
	connectorId: z.string(),
	destinationId: z.string(),
	destinationTableName: z.string(),
	startState: z.record(z.string(), z.any()),
	endState: z.record(z.string(), z.any()).nullable()
});
/**
* Every destination type that has EVER existed in the database (SIM-2596). It can never shrink
* while archived rows carry old values: this is the OUTPUT schema for the admin destinations list
* and runner config, and oRPC validates outputs, so one unknown `type` throws `Output validation
* failed` for the whole response. To retire one, narrow the creatable enum.
*/
const destinationTypeSchema = z.enum([
	"bigquery",
	"bigquery-experimental",
	"bigquery-nodedup",
	"example"
]);
/**
* Destination types assignable on create/update (SIM-2596) — narrower than
* `destinationTypeSchema` on purpose. `bigquery` and `bigquery-experimental` are excluded because
* their runner implementations are gone, so selecting one produces a destination that throws
* `Unsupported destination type` on its first run.
*/
const creatableDestinationTypeSchema = z.enum(["bigquery-nodedup", "example"]);
/**
* Destination types that write to BigQuery, for classifying EXISTING rows (SIM-2596). Deliberately
* wide: callers use it to decide whether a stored destination is a BigQuery one, and archived rows
* still hold the legacy values.
*/
const BIGQUERY_DESTINATION_TYPES = [
	"bigquery",
	"bigquery-experimental",
	"bigquery-nodedup"
];
function isBigQueryDestinationType(type) {
	return BIGQUERY_DESTINATION_TYPES.includes(type);
}
const destinationSchema = z.object({
	id: z.string(),
	name: z.string(),
	type: destinationTypeSchema,
	projectId: z.string(),
	vars: z.record(z.string(), z.any()),
	secrets: z.record(z.string(), z.string()),
	createdAt: z.date(),
	updatedAt: z.date(),
	archivedAt: z.date().nullable()
});
const streamStateSchema = z.object({
	id: z.string(),
	connectorId: z.string(),
	key: z.string(),
	mode: syncModeSchema,
	startState: z.record(z.string(), z.any()),
	nextState: z.record(z.string(), z.any())
});
const archiveMappingSchema = z.object({
	sourceDataset: z.string().min(1),
	archiveDataset: z.string().min(1)
});
const archiveConfigSchema = z.object({
	enabled: z.boolean(),
	label: z.string().min(1),
	mappings: z.array(archiveMappingSchema),
	cadenceHours: z.number().int().min(1).default(24)
});
// src/spark/componentRegistry.ts
/**
* Canonical SimplySpark component-type registry — per-type capabilities (family, chart subtype,
* binding constraints, transition rules, menu order) as one source of truth; SIM-1262 migrates the
* scattered per-type tables onto it. To add a type, add the string to `COMPONENT_TYPES` and fix
* every compile error: the registry, both `renderComponent` switches and `ConfigPanel`.
*/
const COMPONENT_TYPES = [
	"kpi-card",
	"data-table",
	"bar-chart",
	"line-chart",
	"area-chart",
	"scatter-chart",
	"text-block",
	"pivot",
	"image",
	"rectangle",
	"tile-map"
];
const componentTypeSchema = z$1.enum(COMPONENT_TYPES);
const looseVisualConfig = z$1.object({}).passthrough();
/**
* Chart capability blocks shared across the four chart subtypes — they all
* require ≥1 dimension and ≥1 metric on the X/Y axes. Breakdown and the
* second-Y-axis toggle are honored by bar/line/area but not by scatter.
*/
const CHART_BINDING_WITH_BREAKDOWN = {
	dimensions: {
		min: 1,
		max: 1,
		heading: "X-Axis"
	},
	metrics: {
		min: 1,
		max: "many",
		heading: "Y-Axis"
	},
	supportsBreakdown: true,
	supportsTwoAxes: true
};
const COMPONENT_REGISTRY = {
	"kpi-card": {
		defaultTitle: "New KPI",
		defaultLayout: {
			w: 24,
			h: 12
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "kpi",
		dataBinding: {
			dimensions: {
				min: 0,
				max: 0,
				heading: "Group By"
			},
			metrics: {
				min: 1,
				max: 2,
				heading: "Metrics"
			},
			supportsBreakdown: false,
			supportsTwoAxes: false
		},
		addMenuOrder: 0
	},
	"data-table": {
		defaultTitle: "New Table",
		defaultLayout: {
			w: 60,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "table",
		dataBinding: {
			dimensions: {
				min: 0,
				max: "many",
				heading: "Group By"
			},
			metrics: {
				min: 0,
				max: "many",
				heading: "Aggregations"
			},
			supportsBreakdown: false,
			supportsTwoAxes: false
		},
		addMenuOrder: 1
	},
	"bar-chart": {
		defaultTitle: "New Bar Chart",
		defaultLayout: {
			w: 49,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "chart",
		chartSubtype: "bar",
		dataBinding: CHART_BINDING_WITH_BREAKDOWN,
		stripOnSubtypeSwitch: ["smooth", "areaFill"],
		addMenuOrder: 2
	},
	"line-chart": {
		defaultTitle: "New Line Chart",
		defaultLayout: {
			w: 49,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "chart",
		chartSubtype: "line",
		dataBinding: CHART_BINDING_WITH_BREAKDOWN,
		stripOnSubtypeSwitch: ["orientation"],
		addMenuOrder: 3
	},
	"area-chart": {
		defaultTitle: "New Area Chart",
		defaultLayout: {
			w: 49,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "chart",
		chartSubtype: "area",
		dataBinding: CHART_BINDING_WITH_BREAKDOWN,
		stripOnSubtypeSwitch: ["orientation"],
		addMenuOrder: 4
	},
	"scatter-chart": {
		defaultTitle: "New Scatter Chart",
		defaultLayout: {
			w: 49,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "chart",
		chartSubtype: "scatter",
		dataBinding: {
			dimensions: {
				min: 1,
				max: 1,
				heading: "X-Axis"
			},
			metrics: {
				min: 1,
				max: "many",
				heading: "Y-Axis"
			},
			supportsBreakdown: false,
			supportsTwoAxes: false
		},
		stripOnSubtypeSwitch: [
			"orientation",
			"stacking",
			"smooth",
			"areaFill"
		],
		addMenuOrder: 5
	},
	"text-block": {
		defaultTitle: "",
		defaultLayout: {
			w: 98,
			h: 10
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: false,
		family: "text",
		addMenuOrder: 9
	},
	pivot: {
		defaultTitle: "Pivot Table",
		defaultLayout: {
			w: 60,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "pivot",
		dataBinding: {
			dimensions: {
				min: 0,
				max: "many"
			},
			metrics: {
				min: 0,
				max: "many"
			},
			supportsBreakdown: false,
			supportsTwoAxes: false
		},
		addMenuOrder: 6
	},
	image: {
		defaultTitle: "Image",
		defaultLayout: {
			w: 40,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: false,
		family: "image",
		addMenuOrder: 7
	},
	rectangle: {
		defaultTitle: "",
		defaultLayout: {
			w: 12,
			h: 8
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: false,
		family: "rectangle",
		addMenuOrder: 8
	},
	"tile-map": {
		defaultTitle: "New Map",
		defaultLayout: {
			w: 49,
			h: 30
		},
		visualConfigSchema: looseVisualConfig,
		needsDataBinding: true,
		family: "tile-map",
		dataBinding: {
			dimensions: {
				min: 2,
				max: 4,
				heading: "Location"
			},
			metrics: {
				min: 1,
				max: 1,
				heading: "Encodings"
			},
			supportsBreakdown: false,
			supportsTwoAxes: false
		},
		addMenuOrder: 11
	}
};
const COMPONENT_META = {
	"kpi-card": {
		label: "KPI Card",
		icon: "Hash",
		description: "Single headline number with optional comparison"
	},
	"data-table": {
		label: "Data Table",
		icon: "Table2",
		description: "Tabular rows with sortable columns"
	},
	"bar-chart": {
		label: "Bar Chart",
		icon: "BarChart3",
		description: "Categorical comparison with bars"
	},
	"line-chart": {
		label: "Line Chart",
		icon: "LineChart",
		description: "Trend over time as a line"
	},
	"area-chart": {
		label: "Area Chart",
		icon: "ChartArea",
		description: "Trend over time with filled area"
	},
	"scatter-chart": {
		label: "Scatter Chart",
		icon: "ChartScatter",
		description: "Two-dimensional point distribution"
	},
	"text-block": {
		label: "Text Block",
		icon: "Type",
		description: "Rich-text content with formatting"
	},
	pivot: {
		label: "Pivot Table",
		icon: "Grid3x3",
		description: "Cross-tab with row + column groupings"
	},
	image: {
		label: "Image",
		icon: "Image",
		description: "Static image card with URL source"
	},
	rectangle: {
		label: "Rectangle",
		icon: "Square",
		description: "Background rectangle for visual grouping"
	},
	"tile-map": {
		label: "Map",
		icon: "MapPin",
		description: "Bubble map of geographic points on a Google Maps basemap (city-grained zoom)"
	}
};
// src/spark/conditionalRules.ts
const CONDITIONAL_OPERATORS = [
	"gt",
	"gte",
	"lt",
	"lte",
	"eq",
	"between",
	"eq_str",
	"neq_str",
	"contains",
	"starts_with",
	"ends_with",
	"is_empty",
	"is_not_empty"
];
const conditionalOperatorSchema = z$1.enum(CONDITIONAL_OPERATORS);
// src/spark/font.ts
const fontConfigSchema = z$1.object({
	color: z$1.string().optional(),
	size: z$1.number().int().min(8).max(72).optional(),
	weight: z$1.enum([
		"normal",
		"medium",
		"semibold",
		"bold"
	]).optional(),
	italic: z$1.boolean().optional()
}).strict();
const WEIGHT_TO_CSS = {
	normal: 400,
	medium: 500,
	semibold: 600,
	bold: 800
};
/**
* SIM-1165: shared defaults applied to data-table + pivot header cells. The
* renderer uses these via `applyFontStyle(headerFont, TABLE_HEADER_FONT_DEFAULTS)`
* so the rendered output matches what the FontEditor advertises (no-lying
* defaults — SIM-1120 pattern).
*/
const TABLE_HEADER_FONT_DEFAULTS = {
	size: 12,
	weight: "semibold",
	color: "var(--foreground)"
};
/**
* SIM-1165 — default background for table and pivot header rows, kept next to the font defaults so
* the editor swatch and the renderer share one constant. It MUST wrap in `hsl(...)`: ui-tokens are
* raw HSL channels, so a bare `var(--background)` is invalid CSS, silently ignored, and the sticky
* `<thead>` goes transparent as rows bleed through (SIM-1183).
*/
const TABLE_HEADER_BG_DEFAULT = "hsl(var(--background))";
/**
* SIM-1547 — ECharts-shaped sibling of `applyFontStyle`, so chart text uses the same `FontConfig`
* as KPI cards and table headers. `size` maps to `fontSize` as a NUMBER, not `"18px"`; `weight` to
* a numeric `fontWeight` mirroring `WEIGHT_TO_CSS`. Returns `{}` for an undefined config so callers
* can spread it. Typed `Record` to keep core ECharts-free.
*/
function chartTextStyle(config) {
	if (!config) return {};
	const style = {};
	if (config.color) style.color = config.color;
	if (config.size) style.fontSize = config.size;
	if (config.weight) style.fontWeight = WEIGHT_TO_CSS[config.weight];
	if (config.italic) style.fontStyle = "italic";
	return style;
}
/**
* Convert a `FontConfig` to an inline-style object for a React `style` prop. Returns `{}` when
* `config` is undefined and no `defaults` are supplied, so callers can spread it unconditionally.
* When `defaults` is given, a field absent from `config` is filled from it rather than omitted —
* the no-lying-defaults pattern (SIM-1165). Typed `Record`: core is React-free.
*/
function applyFontStyle(config, defaults) {
	const merged = {
		...defaults ?? {},
		...config ?? {}
	};
	const style = {};
	if (merged.color) style.color = merged.color;
	if (merged.size) style.fontSize = `${merged.size}px`;
	if (merged.weight) style.fontWeight = WEIGHT_TO_CSS[merged.weight];
	if (merged.italic) style.fontStyle = "italic";
	return style;
}
const columnWrapModeSchema = z$1.enum(["truncate", "wrap"]);
const tableStyleConfigSchema = z$1.object({
	headerFont: fontConfigSchema.optional(),
	bodyFont: fontConfigSchema.optional(),
	bodyBackground: z$1.string().optional(),
	columnWidths: z$1.record(z$1.string(), z$1.number()).optional(),
	columnWrap: z$1.record(z$1.string(), columnWrapModeSchema).optional()
}).partial();
// src/spark/metricComparison.ts
/**
* SIM-3793: Data Studio's "Comparison calculation" on a metric — its value set against the total
* or the maximum across every row the query groups. Absent means None.
*/
const metricComparisonSchema = z$1.enum([
	"pct_of_total",
	"diff_from_total",
	"pct_diff_from_total",
	"pct_of_max",
	"diff_from_max",
	"pct_diff_from_max"
]);
/** Editor labels, worded as Data Studio words them. */
const METRIC_COMPARISON_LABELS = {
	pct_of_total: "Percent of total",
	diff_from_total: "Difference from total",
	pct_diff_from_total: "Percent difference from total",
	pct_of_max: "Percent of max",
	diff_from_max: "Difference from max",
	pct_diff_from_max: "Percent difference from max"
};
const PERCENT_COMPARISONS = /* @__PURE__ */ new Set([
	"pct_of_total",
	"pct_diff_from_total",
	"pct_of_max",
	"pct_diff_from_max"
]);
/** The variants whose result is a ratio, so the editor defaults their format to percent. */
function isPercentComparison(comparison) {
	return comparison !== void 0 && PERCENT_COMPARISONS.has(comparison);
}
const COMPARISON_SQL = {
	pct_of_total: (v) => `SAFE_DIVIDE(${v}, SUM(${v}) OVER ())`,
	diff_from_total: (v) => `${v} - SUM(${v}) OVER ()`,
	pct_diff_from_total: (v) => `SAFE_DIVIDE(${v} - SUM(${v}) OVER (), SUM(${v}) OVER ())`,
	pct_of_max: (v) => `SAFE_DIVIDE(${v}, MAX(${v}) OVER ())`,
	diff_from_max: (v) => `${v} - MAX(${v}) OVER ()`,
	pct_diff_from_max: (v) => `SAFE_DIVIDE(${v} - MAX(${v}) OVER (), MAX(${v}) OVER ())`
};
/**
* Wrap a metric's aggregate SQL (`COUNT(DISTINCT \`x\`)`) in its comparison. BigQuery runs the
* window after GROUP BY and before ORDER BY / LIMIT, so the total covers every group — including
* rows the limit hides — while WHERE filters still apply, which is what Data Studio shows.
*/
function applyMetricComparison(aggregateSql, comparison) {
	return comparison ? COMPARISON_SQL[comparison](aggregateSql) : aggregateSql;
}
// src/spark/dataBinding.ts
/**
* SIM-1649: per-column / per-metric text alignment. `left` / `center` / `right`
* — applied to data-table body cells and pivot body cells / row headers /
* column headers. Default (absent) leaves cell alignment to the renderer's
* intrinsic rule (metric cells right-aligned, dimension cells left-aligned).
*/
const columnAlignSchema = z$1.enum([
	"left",
	"center",
	"right"
]);
/**
* SIM-1649: per-column / per-metric optional FontConfig, applied verbatim to data-table body cells
* and pivot body / row-header / column-header cells. Re-exported as `columnFontSchema` so the
* persisted-binding consumers (dimensionDefSchema, metricDefSchema, the pivot-value schema in
* `componentDef.ts`) point at the same canonical schema as `visualConfig.headerFont` (SIM-1112).
*/
const columnFontSchema = fontConfigSchema;
/**
* Canonical Zod schemas for SimplySpark `dataBinding` — the single source of truth for the wire
* shape. The platform contract and any client validation import these rather than re-declaring
* inline; keep the interface in `apps/portal/src/spark/types.ts` in step. `metricFormatSchema` is
* non-optional (wrap it at the use site); `dimensionDefSchema` takes a string or an object.
*/
/**
* SIM-3799: `date` renders a DATE / DATETIME / TIMESTAMP value through `pattern`, the token
* language `{{today:FORMAT}}` uses (`MMM D, YYYY h:mm A`). A TIMESTAMP is shown in Eastern time.
*/
const metricFormatSchema = z$1.object({
	type: z$1.enum([
		"number",
		"percent",
		"percent_raw",
		"compact",
		"currency",
		"date"
	]),
	decimals: z$1.number().int().min(0).max(10).optional(),
	prefix: z$1.string().optional(),
	suffix: z$1.string().optional(),
	pattern: z$1.string().optional()
});
/**
* Optional metadata captured from the BQ schema and a value-range sample when a metric/dimension is
* added in the editor. Lets downstream consumers (conditional formatting, format dropdowns,
* date-axis detection, chart numeric coercion) behave correctly per column type without
* round-tripping to BQ at render time (SIM-887).
*/
const columnMetadataSchema = z$1.object({
	bqType: z$1.string().optional(),
	valueSample: z$1.object({
		min: z$1.number().optional(),
		max: z$1.number().optional(),
		distinctCount: z$1.number().int().nonnegative().optional(),
		sampleSize: z$1.number().int().nonnegative()
	}).optional()
});
const dimensionDefSchema = z$1.union([z$1.string(), z$1.object({
	field: z$1.string(),
	label: z$1.string().optional(),
	/**
	* SIM-1649: optional per-column font config — applied to data-table
	* body cells and pivot row-label headers. Default (absent) leaves
	* typography to the surface's intrinsic style.
	*/
	font: columnFontSchema.optional(),
	/**
	* SIM-1649: optional per-column text alignment — applied to data-table
	* body cells and pivot row-label headers via Tailwind `text-{align}`.
	* Default (absent) preserves intrinsic alignment (left for dimensions).
	*/
	align: columnAlignSchema.optional(),
	/** SIM-3799: optional value format — today only `date`, for a date-typed column. */
	format: metricFormatSchema.optional()
}).merge(columnMetadataSchema)]);
/**
* SIM-1657: extracted from `metricDefSchema.aggregation` so the enum can be
* imported directly by oRPC contracts (and any other consumer) without going
* through `.shape.aggregation`. Same set of aggregations the BI query handler
* supports.
*/
const metricAggregationSchema = z$1.enum([
	"sum",
	"avg",
	"count",
	"count_distinct",
	"max",
	"min"
]);
/**
* SIM-3786: a stable opaque handle for one result column, so a visual can reference it by
* something that survives a rename of the derived result name. Optional and never re-derived
* once present; unique only within one card's binding.
*/
const resultColumnIdSchema = z$1.string().optional();
const metricDefSchema = z$1.object({
	id: resultColumnIdSchema,
	field: z$1.string(),
	aggregation: metricAggregationSchema,
	label: z$1.string().optional(),
	format: metricFormatSchema.optional(),
	/**
	* SIM-1104: optional per-metric "Tooltip note" — a short qualifier rendered below the metric's
	* value row in chart tooltips (italic, smaller font). Lets authors disclose caveats next to the
	* number itself ("Excludes withdrawn students", "SY24-25 baseline") without cluttering the
	* chart title or footer.
	*/
	note: z$1.string().optional(),
	/**
	* SIM-1214 — plot this metric against a second Y-axis on the right, so one chart can compare
	* metrics with different scales. Honoured by bar, line and area charts in non-stacked,
	* non-breakdown mode only: stacked charts need one shared axis and breakdown charts are all one
	* metric, so the editor hides the toggle and the renderer treats the flag as inert there.
	*/
	useSecondaryYAxis: z$1.boolean().optional(),
	/**
	* SIM-1649: optional per-metric font config — applied to data-table body
	* cells and pivot body cells (and the pivot column-header for the
	* matching value-row). Default (absent) leaves typography to the
	* surface's intrinsic style.
	*/
	font: columnFontSchema.optional(),
	/**
	* SIM-1649: optional per-metric text alignment — applied to data-table
	* body cells and pivot body cells / column headers via Tailwind
	* `text-{align}`. Default (absent) preserves intrinsic alignment
	* (right-aligned for metric cells).
	*/
	align: columnAlignSchema.optional(),
	/**
	* SIM-3793: Data Studio's "Comparison calculation" — percent of / difference from the total or
	* the max across every grouped row. Absent = None. Gives the metric its own result column.
	*/
	comparison: metricComparisonSchema.optional()
}).merge(columnMetadataSchema);
const filterDefSchema = z$1.object({
	field: z$1.string(),
	operator: z$1.string(),
	value: z$1.unknown().optional()
});
/**
* SIM-1809 — relative-date sentinel for a binding-level date filter. The platform compiler resolves
* it to `CURRENT_DATE()` at query time rather than binding a literal, so a saved filter rolls
* forward daily with no reload, evaluated in the warehouse rather than the viewer's clock. Contrast
* the page-filter `today` token, resolved client-side. Import it, never re-type.
*/
const BINDING_DATE_SENTINEL_TODAY = "@today";
/**
* Operators for which {@link BINDING_DATE_SENTINEL_TODAY} is honored. Scalar
* comparisons only — `in` / `not_in` / `like` / `is_null` treat the token as an
* ordinary string. Typed `readonly string[]` so `.includes()` accepts a plain
* `string` operator at every call site.
*/
const SENTINEL_DATE_OPERATORS = [
	"=",
	"!=",
	">",
	">=",
	"<",
	"<="
];
/**
* True when this `(operator, value)` pair is a relative-date sentinel the
* platform compiler resolves to a SQL function. Used by the editor to gate the
* "Today" affordance and by any consumer rendering a filter summary.
*/
function isBindingDateSentinel(operator, value) {
	return value === "@today" && SENTINEL_DATE_OPERATORS.includes(operator);
}
/**
* SIM-1657: extracted from `sortDefSchema.direction` so oRPC contracts and
* other consumers can import the enum directly.
*/
const sortDirectionSchema = z$1.enum(["asc", "desc"]);
const sortDefSchema = z$1.object({
	field: z$1.string(),
	direction: sortDirectionSchema,
	/**
	* SIM-2975 slice 2: which aggregation of `field` to sort by, when the binding
	* carries more than one. Optional and additive — a sort saved without it
	* resolves to the first metric on the field, exactly as it did before.
	*/
	aggregation: metricAggregationSchema.optional()
});
/**
* Row ceiling for every BI query, plus the default when a binding omits `limit`. The cap guards the
* *browser*, not BigQuery: a data table fetches every row, holds the set in memory, and client-side
* sorts all of it on each header click. Enforced at three layers — this schema, the platform query
* contract, the compiler clamp — all importing these constants (SIM-2953).
*/
const BI_DEFAULT_ROW_LIMIT = 1e3;
const BI_MAX_ROW_LIMIT = 1e4;
/**
* SIM-3400: a post-aggregation expression that becomes a result column beside the
* metrics. `expression` is authored source in the allowlisted grammar from
* `./expression`, never SQL — an expression cannot be parameterised, so that
* grammar is the only available control and the compiler re-parses server-side.
*/
const calculatedMetricDefSchema = z$1.object({
	id: resultColumnIdSchema,
	/** Result-column name. Must not collide with a dimension or a metric alias. */
	alias: z$1.string().min(1),
	expression: z$1.string().min(1),
	label: z$1.string().optional(),
	format: metricFormatSchema.optional(),
	font: columnFontSchema.optional(),
	align: columnAlignSchema.optional()
}).merge(columnMetadataSchema);
const dataBindingSchema = z$1.object({
	datasetId: z$1.string(),
	dimensions: z$1.array(dimensionDefSchema),
	metrics: z$1.array(metricDefSchema),
	calculatedMetrics: z$1.array(calculatedMetricDefSchema).optional(),
	breakdown: z$1.string().optional(),
	breakdownLabel: z$1.string().optional(),
	filters: z$1.array(filterDefSchema).optional(),
	sorts: z$1.array(sortDefSchema).optional(),
	limit: z$1.number().int().min(1).max(BI_MAX_ROW_LIMIT).optional()
});
/**
* SIM-3698: what a draft autosave may hold. A calculated metric's expression may still be empty
* while it is being written; every other field is the strict schema's, derived with `.extend` so
* none can go missing. Create, update and publish keep {@link dataBindingSchema}.
*/
const draftDataBindingSchema = dataBindingSchema.extend({ calculatedMetrics: z$1.array(calculatedMetricDefSchema.extend({ expression: z$1.string() })).optional() });
function qualifiedAlias(m) {
	const base = `${m.field}__${m.aggregation}`;
	return m.comparison ? `${base}__${m.comparison}` : base;
}
/**
* SIM-2975: the result-column name for each metric. Aliasing to the bare field name collided when
* one column was aggregated twice — BigQuery cannot resolve `ORDER BY` against the duplicate, and
* our renderers key columns by name and collapsed the pair. A field used once keeps its bare name
* and aliases ignore metric order, so existing bindings and saved column keys are unaffected.
*/
function resolveMetricAliases(metrics, opts) {
	const perField = /* @__PURE__ */ new Map();
	for (const m of metrics) perField.set(m.field, (perField.get(m.field) ?? 0) + 1);
	const taken = new Set(opts?.reserved ?? []);
	return metrics.map((m) => {
		const base = (perField.get(m.field) ?? 0) > 1 ? qualifiedAlias(m) : m.field;
		let alias = base;
		for (let n = 2; taken.has(alias); n++) alias = `${base}_${n}`;
		taken.add(alias);
		return alias;
	});
}
/**
* SIM-2975: the result column a sort should order by. An explicit
* `sort.aggregation` picks that metric; otherwise the first metric on the field
* wins (slice-1 behaviour), and a sort on a dimension or an unbound column
* falls through to the bare field name.
*/
function resolveSortColumn(sort, metrics, opts) {
	const aliases = resolveMetricAliases(metrics, opts);
	if (sort.aggregation !== void 0) {
		const exact = metrics.findIndex((m) => m.field === sort.field && m.aggregation === sort.aggregation);
		if (exact >= 0) return aliases[exact];
	}
	const first = metrics.findIndex((m) => m.field === sort.field);
	return first >= 0 ? aliases[first] : sort.field;
}
/**
* SIM-2975: the alias for one metric within its binding. Convenience wrapper
* over `resolveMetricAliases` for read-side call sites that hold a single
* metric; returns the bare field name if the metric is not in `metrics`.
*/
function metricAliasFor(metric, metrics) {
	const aliases = resolveMetricAliases(metrics);
	const byIdentity = metrics.indexOf(metric);
	if (byIdentity >= 0) return aliases[byIdentity];
	const byValue = metrics.findIndex((m) => m.field === metric.field && m.aggregation === metric.aggregation && m.comparison === metric.comparison);
	return byValue >= 0 ? aliases[byValue] : metric.field;
}
/**
* SIM-1262: the registry's `dataBinding` capabilities for a type, or `undefined` for types that
* take no data binding (text-block, image, rectangle) and for unknown strings. The `as const`
* registry literal preserves an absent property rather than `dataBinding?: undefined` on those leaf
* types, so the cast to a partial type is required for the optional read.
*/
function getBindingCapabilities(type) {
	return COMPONENT_REGISTRY[type]?.dataBinding;
}
/**
* Per-type minimum check. Returns a message string if invalid, undefined
* otherwise. Reads the per-type `dimensions.min` / `metrics.min` from
* COMPONENT_REGISTRY rather than maintaining a parallel set of type-keyed
* tables (SIM-1262 Slice 4).
*/
function checkTypeMinimum(type, dimCount, metricCount) {
	const binding = getBindingCapabilities(type);
	if (!binding) return void 0;
	const dimMin = binding.dimensions.min;
	const metricMin = binding.metrics.min;
	if (dimMin > 0 && metricMin > 0) {
		if (dimCount < dimMin || metricCount < metricMin) return "Choose an X-axis column and a Y-axis metric to display this chart.";
		return;
	}
	if (type === "kpi-card") return metricCount < metricMin ? "Choose a value as the primary metric." : void 0;
	if (type === "data-table") return dimCount === 0 && metricCount === 0 ? "Choose at least one dimension or metric column to display this table." : void 0;
}
/**
* SIM-3820: types whose renderer shows a calculated metric, so one meets the metric minimum.
* Charts are absent until they plot calculated metrics (SIM-3785).
*/
const CALCULATED_COUNTS_AS_METRIC = /* @__PURE__ */ new Set(["kpi-card", "data-table"]);
function validateMinimumBindings(component) {
	const type = component.type;
	const db = component.dataBinding;
	if (type === "text-block") return { valid: true };
	if (!db?.datasetId) return {
		valid: false,
		message: "Choose a dataset to display this component."
	};
	const calculatedCount = CALCULATED_COUNTS_AS_METRIC.has(type) ? db.calculatedMetrics?.length ?? 0 : 0;
	const metricCount = (db.metrics?.length ?? 0) + calculatedCount;
	const message = checkTypeMinimum(type, db.dimensions?.length ?? 0, metricCount);
	if (message) return {
		valid: false,
		message
	};
	return { valid: true };
}
// src/spark/exportTable.ts
/**
* SIM-836 / SIM-2953: the neutral tabular shape between a dashboard surface and an export writer.
* Adapters produce one, writers (Sheets, CSV) consume one, so a new surface costs one adapter and a
* new format one writer, not N×M. Writer-agnostic: no number formats, no styling, no A1 ranges.
* Core is bundled into `apps/runner`'s Apps Script file, so this file stays types + pure functions.
*/
/**
* One exported column. `key` is the **result-column key** — for a metric its resolved alias
* (`resolveMetricAliases`), not its bare field, so a field aggregated twice keeps two columns
* (`at_risk__sum` / `at_risk__avg`) rather than collapsing into one (SIM-2975). A writer that
* cannot express `format` or `align` (CSV) drops them without losing any value.
*/
const exportColumnSchema = z$1.object({
	key: z$1.string(),
	label: z$1.string(),
	format: metricFormatSchema.optional(),
	align: columnAlignSchema.optional()
});
/**
* A table ready to hand to a writer. `rows` hold the **raw stored values** keyed by
* `ExportColumn.key`, never display strings: sending `"63.4%"` instead of `0.634` makes the export
* unusable for arithmetic, which is the whole reason to export to a spreadsheet rather than a
* screenshot. Row order is the adapter's and is preserved as given.
*/
const exportTableSchema = z$1.object({
	title: z$1.string(),
	columns: z$1.array(exportColumnSchema),
	rows: z$1.array(z$1.record(z$1.string(), z$1.unknown()))
});
// src/spark/expression/emit.ts
/**
* Backtick-quote a BigQuery identifier. Mirrors `quoteIdentifier` in the
* platform's queryCompiler (SIM-1313): required for names containing `%`,
* spaces or hyphens, which iReady has in the wild ("% Correct").
*/
function quoteIdentifier(name) {
	if (name.includes("`")) throw new Error(`Identifier "${name}" contains a backtick character; not supported.`);
	return `\`${name}\``;
}
/** Single-quote a string literal, doubling any embedded quote. */
function quoteString(value) {
	return `'${value.replace(/'/g, "''")}'`;
}
function emitSql(node, opts = {}) {
	const resolve = opts.resolveColumn ?? ((n) => n);
	const go = (n) => {
		const leaf = emitLeaf(n, resolve);
		if (leaf !== null) return leaf;
		switch (n.type) {
			case "unary": return n.op === "-" ? `(-${go(n.operand)})` : `(NOT ${go(n.operand)})`;
			case "binary":
				if (n.op === "/") return `SAFE_DIVIDE(${go(n.left)}, ${go(n.right)})`;
				return `(${go(n.left)} ${normaliseOperator(n.op)} ${go(n.right)})`;
			case "isNull": return `(${go(n.operand)} IS ${n.negated ? "NOT " : ""}NULL)`;
			case "call": return emitCall(n, go);
			case "case": return `CASE ${n.branches.map((b) => `WHEN ${go(b.when)} THEN ${go(b.then)}`).join(" ")}${n.else ? ` ELSE ${go(n.else)}` : ""} END`;
			case "field":
			case "number":
			case "string":
			case "boolean":
			case "null": throw new Error(`Unhandled expression node: ${n.type}`);
		}
	};
	return go(node);
}
/** Terminal nodes, which carry no sub-expressions. `null` means "not a leaf". */
function emitLeaf(n, resolve) {
	switch (n.type) {
		case "field": return quoteIdentifier(resolve(n.name));
		case "number": return String(n.value);
		case "string": return quoteString(n.value);
		case "boolean": return n.value ? "TRUE" : "FALSE";
		case "null": return "NULL";
		case "unary":
		case "binary":
		case "isNull":
		case "call":
		case "case": return null;
	}
}
function emitCall(n, go) {
	if (n.distinct) return `COUNT(DISTINCT ${go(n.args[0])})`;
	return `${n.fn.name}(${n.args.map(go).join(", ")})`;
}
/** `<>` is valid BigQuery but `!=` is the idiom; normalise so emitted SQL is uniform. */
function normaliseOperator(op) {
	return op === "<>" ? "!=" : op;
}
// src/spark/expression/functions.ts
/**
* Slice-1 set, deliberately small: arithmetic, the six aggregations the product
* already exposes, and the conditional family. Text, date and regex functions
* are additions to this array and need no change anywhere else — that is the
* property to preserve.
*/
const FUNCTIONS = [
	{
		name: "SUM",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		description: "Total of the values in each group"
	},
	{
		name: "AVG",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		returns: "FLOAT64",
		description: "Mean of the values in each group"
	},
	{
		name: "MIN",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		description: "Smallest value in each group"
	},
	{
		name: "MAX",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		description: "Largest value in each group"
	},
	{
		name: "COUNT",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		returns: "INT64",
		description: "How many rows have a value"
	},
	{
		name: "COUNTIF",
		category: "aggregate",
		minArgs: 1,
		maxArgs: 1,
		returns: "INT64",
		description: "How many rows meet a condition"
	},
	{
		name: "ABS",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: 1,
		description: "Distance from zero, sign dropped"
	},
	{
		name: "CEIL",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: 1,
		description: "Round up to a whole number"
	},
	{
		name: "FLOOR",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: 1,
		description: "Round down to a whole number"
	},
	{
		name: "ROUND",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: 2,
		description: "Round, optionally to N decimal places"
	},
	{
		name: "MOD",
		category: "arithmetic",
		minArgs: 2,
		maxArgs: 2,
		description: "Remainder left after dividing"
	},
	{
		name: "POWER",
		category: "arithmetic",
		minArgs: 2,
		maxArgs: 2,
		description: "Raise a number to an exponent"
	},
	{
		name: "SQRT",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: 1,
		returns: "FLOAT64",
		description: "Square root of a number"
	},
	{
		name: "GREATEST",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: null,
		aliases: ["NARY_MAX"],
		description: "Largest of the values given"
	},
	{
		name: "LEAST",
		category: "arithmetic",
		minArgs: 1,
		maxArgs: null,
		aliases: ["NARY_MIN"],
		description: "Smallest of the values given"
	},
	{
		name: "IF",
		category: "conditional",
		minArgs: 3,
		maxArgs: 3,
		description: "Choose between two values on a test"
	},
	{
		name: "IFNULL",
		category: "conditional",
		minArgs: 2,
		maxArgs: 2,
		description: "Substitute a value where it is null"
	},
	{
		name: "NULLIF",
		category: "conditional",
		minArgs: 2,
		maxArgs: 2,
		description: "Null when two values are equal"
	},
	{
		name: "COALESCE",
		category: "conditional",
		minArgs: 1,
		maxArgs: null,
		description: "First of the values that is not null"
	}
];
const BY_NAME = /* @__PURE__ */ new Map();
for (const fn of FUNCTIONS) {
	BY_NAME.set(fn.name, fn);
	for (const alias of fn.aliases ?? []) BY_NAME.set(alias, fn);
}
/**
* Looker's spelling of `COUNT(DISTINCT x)`. Not a plain alias — the two differ
* in syntax, not just name — so the parser sets a `distinct` flag rather than
* renaming.
*/
const COUNT_DISTINCT_ALIAS = "COUNT_DISTINCT";
/** Resolve a written name (canonical or Looker alias) to its definition. */
function lookupFunction(written) {
	return BY_NAME.get(written.toUpperCase());
}
function isAggregateName(written) {
	const upper = written.toUpperCase();
	if (upper === "COUNT_DISTINCT") return true;
	return lookupFunction(upper)?.category === "aggregate";
}
/** Every name an author may write, for editor autocomplete and error messages. */
function allFunctionNames() {
	return [...BY_NAME.keys(), COUNT_DISTINCT_ALIAS].sort();
}
/**
* What the picker offers — canonical spellings only. Deliberately NOT
* `allFunctionNames`, which includes the Looker aliases it must accept on
* input: offering an author both `GREATEST` and `NARY_MAX` presents one
* function as two. See history: SIM-3430.
*/
function pickableFunctions() {
	return [...FUNCTIONS.map(({ name, category, description }) => ({
		name,
		category,
		description
	})), {
		name: COUNT_DISTINCT_ALIAS,
		category: "aggregate",
		description: "How many different values in each group"
	}];
}
// src/spark/expression/lexer.ts
const KEYWORD_SET = /* @__PURE__ */ new Set([
	"CASE",
	"WHEN",
	"THEN",
	"ELSE",
	"END",
	"AND",
	"OR",
	"NOT",
	"IS",
	"NULL",
	"TRUE",
	"FALSE",
	"DISTINCT"
]);
const OPERATORS = [
	"<=",
	">=",
	"!=",
	"<>",
	"||",
	"+",
	"-",
	"*",
	"/",
	"<",
	">",
	"="
];
var ExpressionSyntaxError = class extends Error {
	constructor(message, pos) {
		super(message);
		this.pos = pos;
		this.name = "ExpressionSyntaxError";
	}
};
const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;
/**
* `%` is deliberately absent from the operator set. It is BigQuery's modulo, but
* our live schemas contain columns like iReady's `% Correct` (SIM-1313), and
* accepting both makes a bare reference ambiguous. Field references are bracketed
* tokens precisely so this ambiguity cannot arise; modulo is spelled `MOD(x, y)`.
*/
function tokenize(source) {
	const tokens = [];
	let i = 0;
	while (i < source.length) {
		const ch = source[i];
		if (ch === " " || ch === "	" || ch === "\n" || ch === "\r") {
			i += 1;
			continue;
		}
		rejectComment(source, i);
		const scanned = scanToken(source, i);
		tokens.push(scanned.token);
		i = scanned.next;
	}
	tokens.push({
		kind: "eof",
		pos: source.length
	});
	return tokens;
}
/**
* `--` would otherwise lex as two unary minuses, so `SUM([a]) -- 1` compiles
* silently to `SUM(a) + 1` — valid SQL a dry-run cannot flag, meaning the
* opposite of what the author wrote.
*/
function rejectComment(source, i) {
	if (source[i] === "-" && source[i + 1] === "-") throw new ExpressionSyntaxError("Comments are not supported in expressions. Did you mean subtraction? Write `- -1` with a space to subtract a negative number.", i);
	if (source[i] === "/" && source[i + 1] === "*") throw new ExpressionSyntaxError("Comments are not supported in expressions", i);
}
function scanToken(source, i) {
	const ch = source[i];
	if (ch === "[") return scanField(source, i);
	if (ch === "'" || ch === "\"") {
		const { value, next } = readString(source, i, ch);
		return {
			token: {
				kind: "string",
				value,
				pos: i
			},
			next
		};
	}
	if (DIGIT.test(ch) || ch === "." && DIGIT.test(source[i + 1] ?? "")) return scanNumber(source, i);
	if (IDENT_START.test(ch)) return scanWord(source, i);
	if (isPunct(ch)) return {
		token: {
			kind: "punct",
			value: ch,
			pos: i
		},
		next: i + 1
	};
	const op = scanOperator(source, i);
	if (op) return op;
	throw new ExpressionSyntaxError(`Unexpected character '${ch}'`, i);
}
function isPunct(ch) {
	return ch === "(" || ch === ")" || ch === ",";
}
/** Two-character operators are tried first so `<=` never lexes as `<` then `=`. */
function scanOperator(source, i) {
	const two = source.slice(i, i + 2);
	if (OPERATORS.includes(two)) return {
		token: {
			kind: "operator",
			value: two,
			pos: i
		},
		next: i + 2
	};
	const one = source[i];
	if (OPERATORS.includes(one)) return {
		token: {
			kind: "operator",
			value: one,
			pos: i
		},
		next: i + 1
	};
	return null;
}
function scanField(source, i) {
	const close = source.indexOf("]", i + 1);
	if (close === -1) throw new ExpressionSyntaxError("Unclosed field reference — missing ']'", i);
	const name = source.slice(i + 1, close);
	if (name.length === 0) throw new ExpressionSyntaxError("Empty field reference '[]'", i);
	return {
		token: {
			kind: "field",
			name,
			pos: i
		},
		next: close + 1
	};
}
function scanNumber(source, start) {
	let i = start;
	while (i < source.length && DIGIT.test(source[i])) i += 1;
	if (source[i] === ".") {
		i += 1;
		while (i < source.length && DIGIT.test(source[i])) i += 1;
	}
	const raw = source.slice(start, i);
	const value = Number(raw);
	if (!Number.isFinite(value)) throw new ExpressionSyntaxError(`Invalid number '${raw}'`, start);
	return {
		token: {
			kind: "number",
			value,
			pos: start
		},
		next: i
	};
}
function scanWord(source, start) {
	let i = start;
	while (i < source.length && IDENT_PART.test(source[i])) i += 1;
	const word = source.slice(start, i);
	const upper = word.toUpperCase();
	return {
		token: KEYWORD_SET.has(upper) ? {
			kind: "keyword",
			value: upper,
			pos: start
		} : {
			kind: "identifier",
			value: word,
			pos: start
		},
		next: i
	};
}
/** BigQuery accepts both quote styles; a doubled quote escapes one of its own kind. */
function readString(source, start, quote) {
	let i = start + 1;
	let out = "";
	while (i < source.length) {
		if (source[i] === quote) {
			if (source[i + 1] === quote) {
				out += quote;
				i += 2;
				continue;
			}
			return {
				value: out,
				next: i + 1
			};
		}
		if (source[i] === "\\") throw new ExpressionSyntaxError("Backslash escapes are not supported in string literals", i);
		out += source[i];
		i += 1;
	}
	throw new ExpressionSyntaxError("Unterminated string literal", start);
}
// src/spark/expression/parser.ts
function parseExpression(source, opts) {
	try {
		if (source.trim() === "") throw new ExpressionSyntaxError("Expression is empty", 0);
		const parser = new Parser(tokenize(source));
		const ast = parser.parseExpr();
		parser.expectEof();
		validateScope(ast, opts.scope);
		const fields = collectFields(ast);
		if (opts.availableColumns) {
			const known = new Set(opts.availableColumns);
			const missing = fields.find((f) => !known.has(f));
			if (missing !== void 0) throw new ExpressionSyntaxError(`Unknown column '${missing}'`, 0);
		}
		return {
			ok: true,
			ast,
			fields
		};
	} catch (e) {
		if (e instanceof ExpressionSyntaxError) return {
			ok: false,
			message: e.message,
			pos: e.pos
		};
		throw e;
	}
}
/** Distinct column names an expression reads, in first-seen order. */
function collectFields(node) {
	const out = [];
	const seen = /* @__PURE__ */ new Set();
	walk(node, (n) => {
		if (n.type === "field" && !seen.has(n.name)) {
			seen.add(n.name);
			out.push(n.name);
		}
	});
	return out;
}
function walk(node, visit) {
	visit(node);
	for (const child of childrenOf(node)) walk(child, visit);
}
/** Sub-expressions of a node, in source order. Leaves return an empty array. */
function childrenOf(node) {
	switch (node.type) {
		case "unary": return [node.operand];
		case "binary": return [node.left, node.right];
		case "isNull": return [node.operand];
		case "call": return node.args;
		case "case": return [...node.branches.flatMap((b) => [b.when, b.then]), ...node.else ? [node.else] : []];
		case "field":
		case "number":
		case "string":
		case "boolean":
		case "null": return [];
	}
}
function validateScope(ast, scope) {
	if (scope === "row") {
		let offender = null;
		walk(ast, (n) => {
			if (offender === null && n.type === "call" && n.fn.category === "aggregate") offender = n.fn.name;
		});
		if (offender !== null) throw new ExpressionSyntaxError(`'${offender}' is an aggregate and cannot be used in a calculated column. Use a calculated metric instead, which runs after grouping.`, 0);
		return;
	}
	assertNoNestedAggregate(ast, false);
	const bare = findBareField(ast, false);
	if (bare !== null) throw new ExpressionSyntaxError(`Column '${bare}' must be inside an aggregate function such as SUM() or AVG(). A calculated metric is computed after grouping, so it cannot read an individual row.`, 0);
}
function assertNoNestedAggregate(node, insideAggregate) {
	if (node.type === "call" && node.fn.category === "aggregate") {
		if (insideAggregate) throw new ExpressionSyntaxError(`'${node.fn.name}' cannot be nested inside another aggregate function.`, 0);
		for (const a of node.args) assertNoNestedAggregate(a, true);
		return;
	}
	for (const child of childrenOf(node)) assertNoNestedAggregate(child, insideAggregate);
}
function findBareField(node, insideAggregate) {
	if (node.type === "field") return insideAggregate ? null : node.name;
	if (node.type === "call" && node.fn.category === "aggregate") return null;
	let found = null;
	for (const child of childrenOf(node)) found ??= findBareField(child, insideAggregate);
	return found;
}
/** Binding power per binary operator; higher binds tighter. */
const PRECEDENCE = {
	OR: 1,
	AND: 2,
	"=": 3,
	"!=": 3,
	"<>": 3,
	"<": 3,
	"<=": 3,
	">": 3,
	">=": 3,
	"||": 4,
	"+": 5,
	"-": 5,
	"*": 6,
	"/": 6
};
var Parser = class {
	constructor(tokens) {
		this.tokens = tokens;
		this.i = 0;
	}
	peek() {
		return this.tokens[this.i];
	}
	next() {
		return this.tokens[this.i++];
	}
	at(kind, value) {
		const t = this.peek();
		if (t.kind !== kind) return false;
		if (value === void 0) return true;
		return "value" in t && t.value === value;
	}
	expectEof() {
		const t = this.peek();
		if (t.kind !== "eof") throw new ExpressionSyntaxError(`Unexpected ${describe(t)} after end of expression`, t.pos);
	}
	parseExpr(minPrec = 0) {
		let left = this.parseUnary();
		for (;;) {
			const t = this.peek();
			let op = null;
			if (t.kind === "operator") op = t.value;
			else if (t.kind === "keyword" && (t.value === "AND" || t.value === "OR")) op = t.value;
			if (t.kind === "keyword" && t.value === "IS") {
				left = this.parseIsNull(left);
				continue;
			}
			if (op === null) break;
			const prec = PRECEDENCE[op];
			if (prec === void 0 || prec < minPrec) break;
			this.next();
			const right = this.parseExpr(prec + 1);
			left = {
				type: "binary",
				op,
				left,
				right
			};
		}
		return left;
	}
	parseIsNull(operand) {
		this.next();
		const negated = this.at("keyword", "NOT");
		if (negated) this.next();
		if (!this.at("keyword", "NULL")) throw new ExpressionSyntaxError("Expected NULL after IS", this.peek().pos);
		this.next();
		return {
			type: "isNull",
			operand,
			negated
		};
	}
	parseUnary() {
		if (this.at("operator", "-")) {
			this.next();
			return {
				type: "unary",
				op: "-",
				operand: this.parseUnary()
			};
		}
		if (this.at("keyword", "NOT")) {
			this.next();
			return {
				type: "unary",
				op: "NOT",
				operand: this.parseUnary()
			};
		}
		return this.parsePrimary();
	}
	parsePrimary() {
		const t = this.next();
		switch (t.kind) {
			case "field": return {
				type: "field",
				name: t.name
			};
			case "number": return {
				type: "number",
				value: t.value
			};
			case "string": return {
				type: "string",
				value: t.value
			};
			case "punct":
				if (t.value === "(") {
					const inner = this.parseExpr();
					this.expectPunct(")");
					return inner;
				}
				throw new ExpressionSyntaxError(`Unexpected '${t.value}'`, t.pos);
			case "keyword": return this.parseKeywordPrimary(t.value, t.pos);
			case "identifier": return this.parseCall(t.value, t.pos);
			case "operator":
			case "eof": throw new ExpressionSyntaxError(`Unexpected ${describe(t)}`, t.pos);
		}
	}
	parseKeywordPrimary(value, pos) {
		if (value === "TRUE") return {
			type: "boolean",
			value: true
		};
		if (value === "FALSE") return {
			type: "boolean",
			value: false
		};
		if (value === "NULL") return { type: "null" };
		if (value === "CASE") return this.parseCase(pos);
		throw new ExpressionSyntaxError(`Unexpected keyword '${value}'`, pos);
	}
	parseCall(written, pos) {
		if (!this.at("punct", "(")) throw new ExpressionSyntaxError(`Unknown name '${written}'. Columns must be inserted as fields, not typed.`, pos);
		const upper = written.toUpperCase();
		const isCountDistinct = upper === COUNT_DISTINCT_ALIAS;
		const fn = lookupFunction(isCountDistinct ? "COUNT" : upper);
		if (!fn) throw new ExpressionSyntaxError(`Unknown function '${written}'`, pos);
		this.next();
		let distinct = isCountDistinct;
		if (this.at("keyword", "DISTINCT")) {
			if (fn.name !== "COUNT") throw new ExpressionSyntaxError(`DISTINCT is only supported inside COUNT()`, pos);
			this.next();
			distinct = true;
		}
		const args = [];
		if (!this.at("punct", ")")) for (;;) {
			args.push(this.parseExpr());
			if (this.at("punct", ",")) {
				this.next();
				continue;
			}
			break;
		}
		this.expectPunct(")");
		if (args.length < fn.minArgs || fn.maxArgs !== null && args.length > fn.maxArgs) throw new ExpressionSyntaxError(arityMessage(written, fn, args.length), pos);
		return {
			type: "call",
			fn,
			args,
			distinct
		};
	}
	parseCase(pos) {
		const branches = [];
		if (!this.at("keyword", "WHEN")) throw new ExpressionSyntaxError("CASE must be followed by WHEN. Write `CASE WHEN <condition> THEN <value> … END`.", pos);
		while (this.at("keyword", "WHEN")) {
			this.next();
			const when = this.parseExpr();
			if (!this.at("keyword", "THEN")) throw new ExpressionSyntaxError("Expected THEN after WHEN condition", this.peek().pos);
			this.next();
			branches.push({
				when,
				then: this.parseExpr()
			});
		}
		let elseNode = null;
		if (this.at("keyword", "ELSE")) {
			this.next();
			elseNode = this.parseExpr();
		}
		if (!this.at("keyword", "END")) throw new ExpressionSyntaxError("Unterminated CASE — expected END", this.peek().pos);
		this.next();
		return {
			type: "case",
			branches,
			else: elseNode
		};
	}
	expectPunct(value) {
		if (!this.at("punct", value)) throw new ExpressionSyntaxError(`Expected '${value}'`, this.peek().pos);
		this.next();
	}
};
function arityMessage(written, fn, got) {
	const expected = fn.maxArgs === null ? `at least ${fn.minArgs}` : fn.minArgs === fn.maxArgs ? `${fn.minArgs}` : `${fn.minArgs} to ${fn.maxArgs}`;
	return `${written.toUpperCase()} takes ${expected} argument${fn.maxArgs === 1 ? "" : "s"}, got ${got}`;
}
function describe(t) {
	switch (t.kind) {
		case "eof": return "end of expression";
		case "field": return `field '${t.name}'`;
		case "string": return "string literal";
		case "number": return `number '${t.value}'`;
		case "identifier":
		case "keyword":
		case "operator":
		case "punct": return `'${String(t.value)}'`;
	}
}
// src/spark/expression/index.ts
/**
* Parse and emit in one step — the shape both the query compiler and the
* editor's pre-check want. Returns a failure rather than throwing, because an
* invalid expression is a storable editor state (SIM-3400), not an exception.
*/
function compileExpression(source, opts) {
	const parsed = parseExpression(source, opts);
	if (!parsed.ok) return {
		ok: false,
		message: parsed.message,
		pos: parsed.pos
	};
	return {
		ok: true,
		sql: emitSql(parsed.ast),
		fields: parsed.fields
	};
}
// src/spark/geo/usa.ts
/**
* SIM-1730 — the GeoJSON registered via `echarts.registerMap("USA", USA_GEOJSON)`, in core so the
* portal renderer and future consumers share one copy. Do NOT remove the leading `prettier-ignore`:
* formatting the expanded literal would explode this file into tens of thousands of lines and
* dramatically slow `tsc`, with no review benefit.
*/
const USA_GEOJSON = {
	"type": "FeatureCollection",
	"features": [
		{
			"type": "Feature",
			"id": "01",
			"properties": { "name": "Alabama" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [[[
					[-88.331, 30.2354],
					[-88.13, 30.262],
					[-88.1264, 30.2835],
					[-88.0762, 30.2491],
					[-88.1085, 30.2251],
					[-88.1444, 30.2388],
					[-88.209, 30.2371],
					[-88.2808, 30.2225],
					[-88.331, 30.2354]
				]], [[
					[-88.2018, 34.9954],
					[-88.2018, 35.0083],
					[-87.6634, 35.0032],
					[-87.4373, 35.0032],
					[-86.863, 34.992],
					[-86.5292, 34.9903],
					[-86.3964, 34.992],
					[-85.8257, 34.9886],
					[-85.6067, 34.9843],
					[-85.5529, 34.7085],
					[-85.4704, 34.3279],
					[-85.4057, 34.0024],
					[-85.3591, 33.7498],
					[-85.3052, 33.4826],
					[-85.1868, 32.8701],
					[-85.1545, 32.8426],
					[-85.1689, 32.8117],
					[-85.1222, 32.773],
					[-85.1042, 32.645],
					[-85.0684, 32.5832],
					[-85.0073, 32.5239],
					[-84.9966, 32.4535],
					[-84.9643, 32.4226],
					[-85.0002, 32.322],
					[-84.9356, 32.298],
					[-84.8925, 32.2636],
					[-84.9284, 32.219],
					[-84.9679, 32.219],
					[-84.9607, 32.1983],
					[-85.0109, 32.1803],
					[-85.0576, 32.1356],
					[-85.0468, 32.0875],
					[-85.054, 32.0136],
					[-85.0791, 31.9398],
					[-85.1294, 31.8779],
					[-85.1401, 31.8393],
					[-85.1186, 31.7327],
					[-85.1258, 31.6949],
					[-85.0576, 31.6202],
					[-85.0468, 31.5171],
					[-85.0719, 31.4681],
					[-85.0648, 31.4312],
					[-85.0935, 31.3625],
					[-85.0899, 31.2946],
					[-85.115, 31.2766],
					[-85.0971, 31.2328],
					[-85.1078, 31.1864],
					[-85.0361, 31.1082],
					[-85.0109, 31.0532],
					[-85.0037, 31.0008],
					[-85.2442, 31.0008],
					[-85.4991, 30.9965],
					[-85.8939, 30.9931],
					[-86.5184, 30.9931],
					[-87.003, 30.9991],
					[-87.5988, 30.9974],
					[-87.588, 30.9647],
					[-87.6347, 30.8659],
					[-87.6275, 30.8462],
					[-87.545, 30.7783],
					[-87.5342, 30.7431],
					[-87.405, 30.6744],
					[-87.3942, 30.6151],
					[-87.4481, 30.5275],
					[-87.4373, 30.4802],
					[-87.3655, 30.4364],
					[-87.4301, 30.4063],
					[-87.4588, 30.3359],
					[-87.4911, 30.323],
					[-87.466, 30.3024],
					[-87.5198, 30.2801],
					[-87.6562, 30.25],
					[-87.7998, 30.2294],
					[-88.0008, 30.2259],
					[-87.9362, 30.2612],
					[-87.8931, 30.2388],
					[-87.7675, 30.262],
					[-87.7567, 30.2912],
					[-87.8106, 30.3325],
					[-87.8357, 30.3694],
					[-87.9075, 30.4098],
					[-87.9326, 30.4871],
					[-87.9003, 30.5507],
					[-87.9111, 30.616],
					[-87.9362, 30.6572],
					[-88.008, 30.6847],
					[-88.0618, 30.6452],
					[-88.0546, 30.6125],
					[-88.087, 30.5635],
					[-88.0834, 30.5283],
					[-88.1049, 30.5008],
					[-88.1085, 30.3771],
					[-88.1372, 30.3204],
					[-88.191, 30.317],
					[-88.1875, 30.3479],
					[-88.2592, 30.3823],
					[-88.3131, 30.3685],
					[-88.331, 30.3883],
					[-88.3956, 30.3694],
					[-88.4064, 30.5601],
					[-88.4172, 30.8668],
					[-88.4387, 31.2302],
					[-88.4746, 31.8942],
					[-88.4423, 32.1373],
					[-88.3669, 32.7473],
					[-88.349, 32.9294],
					[-88.2305, 33.8915],
					[-88.209, 34.029],
					[-88.1408, 34.5814],
					[-88.0977, 34.8923],
					[-88.1551, 34.9224],
					[-88.2018, 34.9954]
				]]]
			}
		},
		{
			"type": "Feature",
			"id": "02",
			"properties": { "name": "Alaska" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-150.2428, 61.1375],
						[-150.2212, 61.1701],
						[-150.1566, 61.1607],
						[-150.2284, 61.1237],
						[-150.2428, 61.1375]
					]],
					[[
						[-150.774, 59.3326],
						[-150.6699, 59.4099],
						[-150.616, 59.3953],
						[-150.6196, 59.3558],
						[-150.7201, 59.2922],
						[-150.774, 59.3085],
						[-150.774, 59.3326]
					]],
					[[
						[-151.5277, 59.1479],
						[-151.4344, 59.1359],
						[-151.4452, 59.1049],
						[-151.5277, 59.1479]
					]],
					[[
						[-151.718, 59.1324],
						[-151.639, 59.1178],
						[-151.6641, 59.0895],
						[-151.7144, 59.1015],
						[-151.718, 59.1324]
					]],
					[[
						[-151.8615, 59.1659],
						[-151.7897, 59.1642],
						[-151.8077, 59.135],
						[-151.8615, 59.1659]
					]],
					[[
						[-151.8902, 58.2193],
						[-151.8615, 58.2579],
						[-151.7897, 58.2467],
						[-151.8185, 58.184],
						[-151.8723, 58.1694],
						[-151.8902, 58.2193]
					]],
					[[
						[-152.0733, 58.9245],
						[-152.0302, 58.9486],
						[-151.9548, 58.9142],
						[-152.0733, 58.9125],
						[-152.0733, 58.9245]
					]],
					[[
						[-152.0841, 60.3463],
						[-151.98, 60.4142],
						[-151.9513, 60.5104],
						[-151.84, 60.4855],
						[-151.9405, 60.4305],
						[-151.9728, 60.3884],
						[-151.9548, 60.3678],
						[-152.0841, 60.3463]
					]],
					[[
						[-152.3568, 58.9151],
						[-152.3066, 58.9623],
						[-152.242, 58.9383],
						[-152.1594, 58.9349],
						[-152.2779, 58.9065],
						[-152.3568, 58.9151]
					]],
					[[
						[-152.6404, 60.165],
						[-152.5794, 60.1702],
						[-152.5614, 60.0946],
						[-152.6404, 60.165]
					]],
					[[
						[-153.4193, 58.0638],
						[-153.3152, 58.1402],
						[-153.2614, 58.1454],
						[-153.168, 58.0887],
						[-153.1501, 58.1059],
						[-153.2255, 58.1617],
						[-153.2039, 58.2081],
						[-153.1716, 58.2167],
						[-153.0747, 58.1952],
						[-152.9993, 58.2115],
						[-153.1034, 58.2579],
						[-153.046, 58.306],
						[-152.9419, 58.2794],
						[-152.8881, 58.2828],
						[-152.9383, 58.3309],
						[-152.8952, 58.3455],
						[-152.8235, 58.3284],
						[-152.7732, 58.367],
						[-152.8378, 58.3722],
						[-152.8845, 58.4005],
						[-152.8127, 58.4031],
						[-152.7337, 58.4607],
						[-152.6117, 58.4761],
						[-152.6548, 58.5062],
						[-152.6655, 58.5646],
						[-152.5686, 58.6213],
						[-152.5435, 58.5947],
						[-152.3533, 58.6385],
						[-152.3281, 58.6325],
						[-152.3712, 58.5311],
						[-152.4179, 58.5156],
						[-152.5112, 58.4271],
						[-152.4932, 58.355],
						[-152.364, 58.3644],
						[-152.3568, 58.4237],
						[-152.2779, 58.416],
						[-152.2025, 58.355],
						[-152.1307, 58.3962],
						[-152.0877, 58.3679],
						[-152.1487, 58.2674],
						[-152.1164, 58.2485],
						[-152.0841, 58.3103],
						[-151.9872, 58.3507],
						[-151.962, 58.3292],
						[-151.9728, 58.2304],
						[-152.0805, 58.154],
						[-152.1953, 58.1737],
						[-152.2348, 58.2433],
						[-152.3102, 58.221],
						[-152.2635, 58.1359],
						[-152.3425, 58.1187],
						[-152.4825, 58.1299],
						[-152.5399, 58.0835],
						[-152.5686, 58.1145],
						[-152.5614, 58.178],
						[-152.5973, 58.1797],
						[-152.6296, 58.0818],
						[-152.6584, 58.0612],
						[-152.7696, 58.0466],
						[-152.7624, 58.0148],
						[-152.8199, 57.9959],
						[-152.9814, 57.9847],
						[-153.2901, 58.05],
						[-153.3654, 58.0389],
						[-153.4193, 58.0638]
					]],
					[[
						[-153.5593, 59.391],
						[-153.4121, 59.4151],
						[-153.3475, 59.3781],
						[-153.3654, 59.3369],
						[-153.4121, 59.3231],
						[-153.5162, 59.3206],
						[-153.5736, 59.3713],
						[-153.5593, 59.391]
					]],
					[[
						[-154.1587, 56.6918],
						[-154.0474, 56.7305],
						[-154.0187, 56.6893],
						[-154.1515, 56.6815],
						[-154.1587, 56.6918]
					]],
					[[
						[-154.3633, 56.5424],
						[-154.2915, 56.5956],
						[-154.2233, 56.6128],
						[-154.1838, 56.6034],
						[-154.0941, 56.618],
						[-154.0761, 56.5836],
						[-154.0079, 56.5518],
						[-153.8787, 56.5656],
						[-153.8859, 56.5338],
						[-153.9541, 56.5072],
						[-154.1192, 56.502],
						[-154.2341, 56.4908],
						[-154.3489, 56.5123],
						[-154.3633, 56.5424]
					]],
					[[
						[-154.7796, 57.3662],
						[-154.7078, 57.3808],
						[-154.6935, 57.4461],
						[-154.6289, 57.5105],
						[-154.5391, 57.5397],
						[-154.5212, 57.5775],
						[-154.4674, 57.5707],
						[-154.3453, 57.6308],
						[-154.2269, 57.6617],
						[-154.0869, 57.6488],
						[-153.9828, 57.6497],
						[-153.9828, 57.5535],
						[-153.8895, 57.5045],
						[-153.8751, 57.5432],
						[-153.8141, 57.5887],
						[-153.8787, 57.6299],
						[-153.8572, 57.6514],
						[-153.6669, 57.6394],
						[-153.6777, 57.6695],
						[-153.8644, 57.7073],
						[-153.9325, 57.7038],
						[-153.9361, 57.8129],
						[-153.8249, 57.8653],
						[-153.7208, 57.8902],
						[-153.649, 57.8799],
						[-153.57, 57.8318],
						[-153.5485, 57.7176],
						[-153.4947, 57.7287],
						[-153.4624, 57.7949],
						[-153.4803, 57.8421],
						[-153.3547, 57.8095],
						[-153.3295, 57.8499],
						[-153.4624, 57.8808],
						[-153.5377, 57.9306],
						[-153.5126, 57.9684],
						[-153.4516, 57.9633],
						[-153.2685, 57.8885],
						[-153.2362, 57.892],
						[-153.1285, 57.8567],
						[-153.0891, 57.8653],
						[-153.2003, 57.9298],
						[-153.2721, 57.959],
						[-153.2972, 57.9968],
						[-153.2362, 57.9968],
						[-153.1285, 57.9469],
						[-153.0245, 57.9572],
						[-152.8773, 57.9323],
						[-152.8522, 57.9744],
						[-152.723, 57.9873],
						[-152.7517, 57.9332],
						[-152.8917, 57.8421],
						[-152.9168, 57.7708],
						[-152.8486, 57.7399],
						[-152.8486, 57.8215],
						[-152.7912, 57.8585],
						[-152.7337, 57.8164],
						[-152.7158, 57.8559],
						[-152.6548, 57.8834],
						[-152.6368, 57.9186],
						[-152.5866, 57.9272],
						[-152.5507, 57.9005],
						[-152.4717, 57.9624],
						[-152.4107, 57.9693],
						[-152.425, 57.9306],
						[-152.3245, 57.9169],
						[-152.364, 57.8842],
						[-152.4035, 57.9014],
						[-152.4681, 57.8885],
						[-152.3999, 57.8576],
						[-152.4179, 57.8155],
						[-152.3497, 57.8344],
						[-152.3102, 57.7906],
						[-152.2133, 57.7914],
						[-152.2994, 57.7459],
						[-152.3568, 57.7743],
						[-152.443, 57.776],
						[-152.4968, 57.7347],
						[-152.4394, 57.727],
						[-152.4681, 57.6815],
						[-152.3748, 57.6746],
						[-152.4681, 57.5982],
						[-152.3856, 57.6128],
						[-152.3389, 57.6531],
						[-152.321, 57.6231],
						[-152.1523, 57.6196],
						[-152.163, 57.5844],
						[-152.2922, 57.5174],
						[-152.3389, 57.4229],
						[-152.4753, 57.4341],
						[-152.4825, 57.4676],
						[-152.5184, 57.4324],
						[-152.6009, 57.4684],
						[-152.6619, 57.4633],
						[-152.7445, 57.5054],
						[-152.7983, 57.4942],
						[-152.8845, 57.5105],
						[-152.9132, 57.471],
						[-152.8163, 57.4719],
						[-152.7624, 57.4573],
						[-152.6009, 57.3825],
						[-152.6296, 57.3224],
						[-152.7122, 57.2777],
						[-152.7517, 57.3087],
						[-152.8199, 57.2657],
						[-152.8881, 57.2915],
						[-152.9096, 57.3241],
						[-152.985, 57.3396],
						[-153.0568, 57.3293],
						[-153.1178, 57.2975],
						[-153.0137, 57.2992],
						[-152.9419, 57.2571],
						[-153.0783, 57.2116],
						[-153.2003, 57.2219],
						[-153.2147, 57.2047],
						[-153.125, 57.1755],
						[-153.0747, 57.1867],
						[-152.9491, 57.1875],
						[-152.8701, 57.1506],
						[-152.9096, 57.1265],
						[-152.9957, 57.1197],
						[-153.1178, 57.0913],
						[-153.1465, 57.1008],
						[-153.2147, 57.0758],
						[-153.2039, 57.0338],
						[-153.3008, 56.9908],
						[-153.3475, 57.008],
						[-153.3224, 57.0372],
						[-153.4049, 57.0801],
						[-153.3798, 57.1205],
						[-153.2829, 57.1738],
						[-153.3116, 57.1944],
						[-153.3654, 57.1764],
						[-153.4875, 57.0862],
						[-153.4982, 57.0655],
						[-153.5772, 57.093],
						[-153.6562, 57.0844],
						[-153.6741, 57.0544],
						[-153.5808, 57.0492],
						[-153.5449, 56.9951],
						[-153.6023, 56.9427],
						[-153.6921, 56.9255],
						[-153.6885, 56.8654],
						[-153.7782, 56.8302],
						[-153.8213, 56.8413],
						[-153.9038, 56.7709],
						[-153.972, 56.7451],
						[-154.0366, 56.7632],
						[-154.1479, 56.746],
						[-154.0689, 56.8456],
						[-154.0295, 56.8551],
						[-153.9361, 56.9161],
						[-153.85, 56.9427],
						[-153.8572, 56.9745],
						[-153.9756, 56.9547],
						[-153.9649, 56.9994],
						[-153.8859, 57.087],
						[-153.7818, 57.1317],
						[-153.8069, 57.1575],
						[-153.8608, 57.1188],
						[-153.9828, 57.0664],
						[-154.0761, 56.9702],
						[-154.1623, 56.9195],
						[-154.2125, 56.91],
						[-154.2305, 56.8723],
						[-154.2987, 56.8465],
						[-154.313, 56.9186],
						[-154.4063, 56.9685],
						[-154.5248, 56.9917],
						[-154.514, 57.0776],
						[-154.5284, 57.1686],
						[-154.575, 57.2399],
						[-154.6145, 57.2674],
						[-154.6971, 57.2846],
						[-154.794, 57.2889],
						[-154.7437, 57.3147],
						[-154.7976, 57.3465],
						[-154.7796, 57.3662]
					]],
					[[
						[-154.8047, 56.4341],
						[-154.7078, 56.5209],
						[-154.5248, 56.6042],
						[-154.4494, 56.6008],
						[-154.392, 56.5544],
						[-154.4351, 56.5346],
						[-154.6325, 56.4719],
						[-154.7366, 56.4041],
						[-154.8047, 56.4341]
					]],
					[[
						[-155.7487, 55.8259],
						[-155.6554, 55.8611],
						[-155.6375, 55.8938],
						[-155.5908, 55.9127],
						[-155.5621, 55.8852],
						[-155.5836, 55.8483],
						[-155.5657, 55.7898],
						[-155.5908, 55.7615],
						[-155.72, 55.7727],
						[-155.7487, 55.8259]
					]],
					[[
						[-156.7501, 56.0372],
						[-156.7322, 56.0776],
						[-156.6819, 56.0991],
						[-156.6353, 56.0484],
						[-156.6819, 55.9943],
						[-156.7501, 56.0372]
					]],
					[[
						[-156.804, 56.197],
						[-156.7717, 56.2271],
						[-156.7609, 56.1567],
						[-156.7968, 56.1558],
						[-156.804, 56.197]
					]],
					[[
						[-157.3244, 56.5407],
						[-157.2491, 56.5819],
						[-157.0768, 56.5793],
						[-156.987, 56.5329],
						[-157.1127, 56.5527],
						[-157.1701, 56.5303],
						[-157.3244, 56.5252],
						[-157.3244, 56.5407]
					]],
					[[
						[-157.9023, 56.3482],
						[-157.8341, 56.3697],
						[-157.7946, 56.3207],
						[-157.8269, 56.3113],
						[-157.9023, 56.3482]
					]],
					[[
						[-158.8965, 55.8259],
						[-158.8678, 55.8809],
						[-158.7996, 55.8912],
						[-158.7027, 55.8414],
						[-158.7314, 55.8259],
						[-158.8068, 55.8577],
						[-158.8965, 55.8259]
					]],
					[[
						[-159.1442, 55.8766],
						[-159.0868, 55.8345],
						[-159.1837, 55.8448],
						[-159.1442, 55.8766]
					]],
					[[
						[-159.3201, 54.8973],
						[-159.3237, 54.9282],
						[-159.277, 54.9488],
						[-159.2052, 54.9273],
						[-159.2375, 54.8767],
						[-159.3057, 54.8638],
						[-159.3201, 54.8973]
					]],
					[[
						[-159.3847, 55.7495],
						[-159.356, 55.807],
						[-159.3201, 55.8156],
						[-159.2806, 55.7623],
						[-159.3847, 55.7495]
					]],
					[[
						[-159.4601, 54.9488],
						[-159.417, 55.0218],
						[-159.4565, 55.0614],
						[-159.3452, 55.0596],
						[-159.3308, 54.9746],
						[-159.4278, 54.9419],
						[-159.4601, 54.9488]
					]],
					[[
						[-159.6036, 54.8139],
						[-159.5677, 54.8251],
						[-159.5103, 54.789],
						[-159.5139, 54.7598],
						[-159.5965, 54.7564],
						[-159.6036, 54.8139]
					]],
					[[
						[-159.6611, 55.1902],
						[-159.6, 55.1722],
						[-159.5821, 55.2143],
						[-159.5211, 55.2538],
						[-159.5031, 55.2349],
						[-159.5318, 55.1851],
						[-159.4995, 55.1739],
						[-159.496, 55.1361],
						[-159.5318, 55.0837],
						[-159.4852, 55.0579],
						[-159.5103, 55.0416],
						[-159.5713, 55.0605],
						[-159.5785, 55.0425],
						[-159.6503, 55.0493],
						[-159.6611, 55.1902]
					]],
					[[
						[-159.819, 54.8208],
						[-159.7436, 54.8423],
						[-159.7041, 54.8165],
						[-159.7759, 54.7942],
						[-159.819, 54.8208]
					]],
					[[
						[-159.8477, 55.0553],
						[-159.8154, 55.0888],
						[-159.7328, 55.1241],
						[-159.7149, 55.0931],
						[-159.7975, 55.0605],
						[-159.8298, 55.0107],
						[-159.8477, 55.0553]
					]],
					[[
						[-160.1133, 55.3337],
						[-160.0631, 55.3431],
						[-160.0451, 55.307],
						[-160.1133, 55.3337]
					]],
					[[
						[-160.2497, 54.9299],
						[-160.1313, 55.0141],
						[-160.0774, 55.0373],
						[-160.0954, 55.0528],
						[-160.1743, 55.0528],
						[-160.1887, 55.1181],
						[-160.1097, 55.161],
						[-160.0128, 55.1232],
						[-160.0164, 55.1558],
						[-160.0631, 55.2005],
						[-159.9554, 55.1894],
						[-159.8908, 55.2873],
						[-159.8477, 55.2675],
						[-159.8549, 55.2306],
						[-159.8872, 55.2289],
						[-159.9051, 55.1644],
						[-159.8621, 55.149],
						[-159.8621, 55.1773],
						[-159.8154, 55.1782],
						[-159.8298, 55.1266],
						[-159.8692, 55.0949],
						[-159.9482, 55.1052],
						[-159.9518, 55.0665],
						[-160.0523, 55.0055],
						[-160.1169, 54.9849],
						[-160.2282, 54.8638],
						[-160.2497, 54.9299]
					]],
					[[
						[-160.3179, 58.6943],
						[-160.3, 58.7295],
						[-160.2497, 58.6582],
						[-160.2748, 58.6368],
						[-160.3179, 58.6943]
					]],
					[[
						[-160.3466, 55.4265],
						[-160.2677, 55.4625],
						[-160.1384, 55.4505],
						[-160.142, 55.3835],
						[-160.3215, 55.3938],
						[-160.3466, 55.4265]
					]],
					[[
						[-160.4364, 58.684],
						[-160.4076, 58.7502],
						[-160.3897, 58.7012],
						[-160.4364, 58.684]
					]],
					[[
						[-160.8563, 55.3182],
						[-160.7989, 55.3818],
						[-160.7091, 55.4033],
						[-160.6445, 55.3835],
						[-160.6517, 55.344],
						[-160.6158, 55.3483],
						[-160.5727, 55.3887],
						[-160.5189, 55.3612],
						[-160.4256, 55.3388],
						[-160.3323, 55.3603],
						[-160.3071, 55.3036],
						[-160.3394, 55.2521],
						[-160.3897, 55.2864],
						[-160.4687, 55.289],
						[-160.5189, 55.3088],
						[-160.5297, 55.3431],
						[-160.5656, 55.3328],
						[-160.5656, 55.2735],
						[-160.5261, 55.2564],
						[-160.4866, 55.1816],
						[-160.5261, 55.1301],
						[-160.6553, 55.1601],
						[-160.6912, 55.2108],
						[-160.7558, 55.1954],
						[-160.8204, 55.1181],
						[-160.806, 55.1679],
						[-160.8419, 55.2048],
						[-160.8419, 55.2942],
						[-160.8563, 55.3182]
					]],
					[[
						[-161.0824, 58.5895],
						[-161.0573, 58.702],
						[-160.9173, 58.7467],
						[-160.8419, 58.7519],
						[-160.702, 58.8172],
						[-160.684, 58.8154],
						[-160.8814, 58.5809],
						[-160.9604, 58.5534],
						[-161.0752, 58.55],
						[-161.0824, 58.5895]
					]],
					[[
						[-161.427, 55.2168],
						[-161.3301, 55.2194],
						[-161.3444, 55.1584],
						[-161.4378, 55.1988],
						[-161.427, 55.2168]
					]],
					[[
						[-161.6926, 55.2177],
						[-161.6567, 55.2443],
						[-161.5275, 55.2512],
						[-161.5598, 55.2074],
						[-161.6926, 55.1988],
						[-161.6926, 55.2177]
					]],
					[[
						[-161.9044, 55.1172],
						[-161.89, 55.161],
						[-161.829, 55.1782],
						[-161.7393, 55.1619],
						[-161.6962, 55.137],
						[-161.5706, 55.1009],
						[-161.549, 55.0656],
						[-161.6567, 55.1034],
						[-161.7393, 55.0545],
						[-161.8398, 55.1181],
						[-161.8792, 55.0854],
						[-161.9044, 55.1172]
					]],
					[[
						[-162.4356, 54.9291],
						[-162.3279, 54.9858],
						[-162.2346, 54.9626],
						[-162.2346, 54.8913],
						[-162.2741, 54.8457],
						[-162.3207, 54.8277],
						[-162.4176, 54.8775],
						[-162.4356, 54.9291]
					]],
					[[
						[-162.6832, 63.5841],
						[-162.6151, 63.6219],
						[-162.5397, 63.6356],
						[-162.4535, 63.621],
						[-162.3746, 63.6262],
						[-162.3423, 63.5944],
						[-162.3459, 63.5514],
						[-162.6151, 63.5403],
						[-162.6797, 63.5566],
						[-162.6832, 63.5841]
					]],
					[[
						[-162.834, 54.4506],
						[-162.8304, 54.4944],
						[-162.5863, 54.448],
						[-162.5181, 54.4145],
						[-162.4248, 54.4145],
						[-162.3602, 54.3904],
						[-162.432, 54.3707],
						[-162.4679, 54.3423],
						[-162.5469, 54.3767],
						[-162.6079, 54.369],
						[-162.7191, 54.4007],
						[-162.7837, 54.3758],
						[-162.8627, 54.4248],
						[-162.834, 54.4506]
					]],
					[[
						[-163.1714, 55.429],
						[-163.1211, 55.4282],
						[-163.1391, 55.3955],
						[-163.1714, 55.429]
					]],
					[[
						[-164.8655, 54.2066],
						[-164.8225, 54.2255],
						[-164.765, 54.21],
						[-164.8404, 54.1782],
						[-164.8655, 54.2066]
					]],
					[[
						[-165.2209, 54.1018],
						[-165.1419, 54.131],
						[-165.0235, 54.1215],
						[-164.9373, 54.137],
						[-164.9194, 54.1164],
						[-164.9517, 54.0777],
						[-165.045, 54.0666],
						[-165.2137, 54.0898],
						[-165.2209, 54.1018]
					]],
					[[
						[-165.4829, 54.0752],
						[-165.2675, 54.0958],
						[-165.2352, 54.0657],
						[-165.2891, 54.0382],
						[-165.3357, 54.07],
						[-165.4578, 54.0666],
						[-165.4829, 54.0752]
					]],
					[[
						[-165.5762, 54.0416],
						[-165.5152, 54.0666],
						[-165.4901, 54.0356],
						[-165.5547, 54.0236],
						[-165.5762, 54.0416]
					]],
					[[
						[-165.6839, 54.2495],
						[-165.6372, 54.2976],
						[-165.587, 54.2848],
						[-165.5224, 54.3002],
						[-165.4793, 54.2822],
						[-165.5583, 54.2538],
						[-165.5116, 54.2126],
						[-165.4039, 54.2126],
						[-165.4003, 54.1774],
						[-165.4829, 54.18],
						[-165.5475, 54.1121],
						[-165.6301, 54.1327],
						[-165.6372, 54.1997],
						[-165.5798, 54.2298],
						[-165.6121, 54.247],
						[-165.6695, 54.2289],
						[-165.6839, 54.2495]
					]],
					[[
						[-166.1003, 54.1439],
						[-166.0823, 54.1748],
						[-165.9818, 54.2212],
						[-165.8741, 54.216],
						[-165.8634, 54.1662],
						[-165.7916, 54.1714],
						[-165.7413, 54.1585],
						[-165.7126, 54.1207],
						[-165.666, 54.1319],
						[-165.6731, 54.0966],
						[-165.7665, 54.0657],
						[-165.8095, 54.0752],
						[-165.8741, 54.0365],
						[-165.8957, 54.0605],
						[-165.9854, 54.0614],
						[-166.0464, 54.0442],
						[-166.111, 54.1224],
						[-166.1003, 54.1439]
					]],
					[[
						[-166.1972, 53.985],
						[-166.172, 53.9978],
						[-166.0751, 53.9695],
						[-166.19, 53.96],
						[-166.1972, 53.985]
					]],
					[[
						[-167.4427, 60.2097],
						[-167.317, 60.2312],
						[-167.1124, 60.2312],
						[-167.0335, 60.214],
						[-166.9509, 60.2226],
						[-166.9186, 60.202],
						[-166.8432, 60.2106],
						[-166.8073, 60.2363],
						[-166.8361, 60.269],
						[-166.714, 60.3274],
						[-166.5812, 60.3179],
						[-166.5812, 60.3497],
						[-166.4915, 60.3892],
						[-166.373, 60.3549],
						[-166.2869, 60.3824],
						[-166.2043, 60.3901],
						[-166.1505, 60.4365],
						[-166.0823, 60.3231],
						[-165.9854, 60.3145],
						[-165.8813, 60.3437],
						[-165.7772, 60.3265],
						[-165.6803, 60.2922],
						[-165.7234, 60.2363],
						[-165.6839, 60.1985],
						[-165.7234, 60.1642],
						[-165.6767, 60.1547],
						[-165.6624, 60.0989],
						[-165.709, 60.0662],
						[-165.6336, 60.0198],
						[-165.6265, 59.97],
						[-165.5511, 59.9786],
						[-165.5906, 59.9494],
						[-165.5834, 59.9082],
						[-165.8382, 59.8901],
						[-165.8598, 59.8609],
						[-165.9316, 59.8772],
						[-166.0249, 59.8618],
						[-166.1254, 59.812],
						[-166.0967, 59.757],
						[-166.19, 59.7501],
						[-166.2725, 59.8111],
						[-166.4089, 59.8515],
						[-166.6135, 59.848],
						[-166.6638, 59.8781],
						[-166.7643, 59.8936],
						[-166.8612, 59.9434],
						[-166.9868, 59.9795],
						[-167.1304, 59.9958],
						[-167.2452, 60.0576],
						[-167.335, 60.0662],
						[-167.3422, 60.1264],
						[-167.4427, 60.2097]
					]],
					[[
						[-167.8518, 53.3157],
						[-167.7908, 53.3355],
						[-167.7118, 53.381],
						[-167.6221, 53.3853],
						[-167.4893, 53.4197],
						[-167.457, 53.4429],
						[-167.3565, 53.4248],
						[-167.3063, 53.4412],
						[-167.2775, 53.479],
						[-167.1986, 53.4626],
						[-167.1878, 53.5245],
						[-167.1017, 53.515],
						[-167.1627, 53.6061],
						[-167.0909, 53.6336],
						[-167.0622, 53.6199],
						[-167.0083, 53.6353],
						[-167.073, 53.6654],
						[-167.0586, 53.6989],
						[-166.9976, 53.7186],
						[-166.8935, 53.7178],
						[-166.8612, 53.6594],
						[-166.8073, 53.6654],
						[-166.8325, 53.7083],
						[-166.7858, 53.735],
						[-166.976, 53.7753],
						[-167.0263, 53.7573],
						[-167.1412, 53.8269],
						[-167.1412, 53.8664],
						[-167.0299, 53.9454],
						[-166.8791, 53.9884],
						[-166.7499, 54.0159],
						[-166.7284, 54.003],
						[-166.6458, 54.0142],
						[-166.5884, 53.96],
						[-166.6458, 53.924],
						[-166.6207, 53.8939],
						[-166.5597, 53.8784],
						[-166.5094, 53.924],
						[-166.4879, 53.8956],
						[-166.4341, 53.9205],
						[-166.4376, 53.9557],
						[-166.373, 54.0099],
						[-166.3192, 53.96],
						[-166.2797, 53.9824],
						[-166.2079, 53.9291],
						[-166.2366, 53.881],
						[-166.3192, 53.8698],
						[-166.4053, 53.8097],
						[-166.5489, 53.7496],
						[-166.5417, 53.7161],
						[-166.4197, 53.7625],
						[-166.2115, 53.8174],
						[-166.1972, 53.8363],
						[-166.1182, 53.8552],
						[-166.0931, 53.8389],
						[-166.111, 53.7771],
						[-166.1685, 53.7332],
						[-166.2618, 53.704],
						[-166.2833, 53.6843],
						[-166.4448, 53.6405],
						[-166.5525, 53.6233],
						[-166.5094, 53.5838],
						[-166.5669, 53.5829],
						[-166.5812, 53.5305],
						[-166.6387, 53.5451],
						[-166.6638, 53.485],
						[-166.7356, 53.5065],
						[-166.7499, 53.4412],
						[-166.8791, 53.43],
						[-166.9581, 53.4558],
						[-166.994, 53.4291],
						[-167.0371, 53.4489],
						[-167.1124, 53.4171],
						[-167.134, 53.4266],
						[-167.2919, 53.3639],
						[-167.3027, 53.3372],
						[-167.3852, 53.3407],
						[-167.4427, 53.3209],
						[-167.4893, 53.2694],
						[-167.5898, 53.2891],
						[-167.6221, 53.2505],
						[-167.798, 53.2848],
						[-167.8518, 53.3157]
					]],
					[[
						[-168.1282, 65.6561],
						[-168.1031, 65.6853],
						[-167.981, 65.7283],
						[-167.5396, 65.8211],
						[-167.3134, 65.8846],
						[-167.1412, 65.9482],
						[-166.9796, 65.9963],
						[-166.8289, 66.0513],
						[-166.5274, 66.1415],
						[-166.33, 66.1896],
						[-165.8059, 66.3331],
						[-165.1886, 66.4654],
						[-164.7112, 66.5427],
						[-164.3989, 66.5813],
						[-163.9144, 66.5942],
						[-163.7277, 66.5848],
						[-163.6057, 66.5581],
						[-163.7529, 66.5487],
						[-163.7277, 66.5161],
						[-163.7636, 66.4551],
						[-163.8749, 66.3889],
						[-163.8713, 66.3288],
						[-163.8282, 66.2807],
						[-163.9036, 66.23],
						[-164.0795, 66.2016],
						[-164.0938, 66.1845],
						[-163.918, 66.1905],
						[-163.7672, 66.0608],
						[-163.6236, 66.0582],
						[-163.4944, 66.0857],
						[-163.3437, 66.0848],
						[-163.1678, 66.059],
						[-162.9991, 66.0771],
						[-162.7514, 66.09],
						[-162.6366, 66.0401],
						[-162.4571, 66.0582],
						[-162.3925, 66.029],
						[-162.2059, 66.0565],
						[-162.1413, 66.0788],
						[-161.8398, 66.023],
						[-161.7752, 66.0736],
						[-161.6818, 66.1114],
						[-161.549, 66.2403],
						[-161.4844, 66.2626],
						[-161.3409, 66.2549],
						[-161.3121, 66.2214],
						[-161.1973, 66.2111],
						[-161.086, 66.2343],
						[-160.9927, 66.2343],
						[-161.1075, 66.3288],
						[-161.3624, 66.376],
						[-161.5239, 66.3966],
						[-161.6962, 66.3958],
						[-161.9116, 66.3442],
						[-161.8649, 66.4405],
						[-161.8757, 66.5118],
						[-162.0982, 66.6097],
						[-162.1772, 66.6879],
						[-162.2705, 66.7179],
						[-162.4822, 66.73],
						[-162.511, 66.7781],
						[-162.6258, 66.8554],
						[-162.6007, 66.8983],
						[-162.5038, 66.9138],
						[-162.4679, 66.9507],
						[-162.4105, 66.919],
						[-162.3243, 66.943],
						[-162.2274, 66.8666],
						[-162.0982, 66.7884],
						[-162.0121, 66.7798],
						[-162.0802, 66.693],
						[-162.0695, 66.6458],
						[-161.969, 66.6028],
						[-161.7644, 66.4972],
						[-161.5742, 66.4387],
						[-161.4342, 66.4542],
						[-161.3265, 66.4783],
						[-161.2942, 66.5204],
						[-161.4557, 66.5221],
						[-161.4916, 66.5607],
						[-161.6926, 66.6209],
						[-161.8828, 66.7171],
						[-161.8469, 66.7617],
						[-161.8469, 66.8133],
						[-161.7967, 66.833],
						[-161.7931, 66.882],
						[-161.6747, 66.9619],
						[-161.567, 66.9344],
						[-161.488, 66.937],
						[-161.4844, 66.961],
						[-161.6208, 67.0083],
						[-161.7105, 67.0014],
						[-161.8111, 67.0504],
						[-162.1233, 67.0255],
						[-162.2382, 66.9937],
						[-162.3602, 66.9937],
						[-162.5002, 66.9774],
						[-162.6222, 66.992],
						[-162.694, 67.0289],
						[-162.755, 67.0152],
						[-162.8304, 67.0349],
						[-162.8448, 66.9911],
						[-162.974, 67.0246],
						[-163.3006, 67.0607],
						[-163.4944, 67.0873],
						[-163.6667, 67.1002],
						[-163.7457, 67.126],
						[-163.7421, 67.2102],
						[-163.8211, 67.3502],
						[-164.0077, 67.5358],
						[-164.0795, 67.5856],
						[-164.2087, 67.6388],
						[-164.5353, 67.7256],
						[-165.0558, 67.9215],
						[-165.1311, 67.9421],
						[-165.3142, 68.0168],
						[-165.4937, 68.0589],
						[-165.6875, 68.0907],
						[-165.8705, 68.1105],
						[-165.9746, 68.1405],
						[-166.0428, 68.2007],
						[-166.2366, 68.2728],
						[-166.3982, 68.3072],
						[-166.6889, 68.3424],
						[-166.8397, 68.3373],
						[-166.7068, 68.3716],
						[-166.3766, 68.4223],
						[-166.3048, 68.4618],
						[-166.2941, 68.5108],
						[-166.2259, 68.5761],
						[-166.1864, 68.7788],
						[-166.2151, 68.8793],
						[-165.5726, 68.8527],
						[-165.3286, 68.8579],
						[-164.8117, 68.8939],
						[-164.5281, 68.918],
						[-164.2518, 68.9309],
						[-163.9754, 68.985],
						[-163.8282, 69.0408],
						[-163.7241, 69.0666],
						[-163.5339, 69.1413],
						[-163.2432, 69.3063],
						[-163.1678, 69.3853],
						[-163.1427, 69.4532],
						[-163.1499, 69.613],
						[-163.0099, 69.8131],
						[-162.7084, 69.9703],
						[-162.4751, 70.1241],
						[-162.3423, 70.1903],
						[-161.8792, 70.3294],
						[-161.5347, 70.2994],
						[-161.287, 70.2968],
						[-161.0429, 70.3311],
						[-160.8132, 70.3767],
						[-160.4794, 70.466],
						[-160.2138, 70.5588],
						[-160.0559, 70.6327],
						[-159.6467, 70.7942],
						[-159.5283, 70.8208],
						[-159.1729, 70.8749],
						[-159.1478, 70.8345],
						[-158.9504, 70.7967],
						[-158.6561, 70.7864],
						[-158.412, 70.7993],
						[-158.3653, 70.8199],
						[-158.2505, 70.8174],
						[-158.0315, 70.832],
						[-157.7695, 70.8758],
						[-157.5039, 70.9488],
						[-157.2491, 71.0527],
						[-157.1198, 71.1283],
						[-156.8112, 71.2873],
						[-156.5671, 71.3526],
						[-156.524, 71.2941],
						[-156.3553, 71.2615],
						[-156.2153, 71.2598],
						[-156.0754, 71.2426],
						[-156.0179, 71.1722],
						[-155.921, 71.2074],
						[-155.5657, 71.1653],
						[-155.5118, 71.0811],
						[-155.7057, 71.0201],
						[-155.8313, 70.966],
						[-155.9784, 70.9625],
						[-155.9354, 70.8371],
						[-155.6446, 70.8242],
						[-155.4867, 70.8749],
						[-155.5118, 70.9402],
						[-155.4544, 70.9471],
						[-155.3431, 71.0046],
						[-155.2606, 71.015],
						[-155.1924, 70.9737],
						[-155.178, 71.0278],
						[-155.2749, 71.0648],
						[-155.1493, 71.112],
						[-155.0739, 71.0716],
						[-155.0847, 71.1275],
						[-155.0309, 71.1464],
						[-154.776, 71.0828],
						[-154.6145, 71.0261],
						[-154.5679, 70.99],
						[-154.6073, 70.9617],
						[-154.6037, 70.8681],
						[-154.5714, 70.826],
						[-154.3525, 70.8345],
						[-154.2269, 70.8028],
						[-154.1694, 70.7684],
						[-153.9972, 70.8217],
						[-153.8895, 70.8861],
						[-153.4875, 70.8861],
						[-153.2398, 70.9222],
						[-153.0496, 70.9127],
						[-152.906, 70.8835],
						[-152.6045, 70.8818],
						[-152.3856, 70.8543],
						[-152.224, 70.8242],
						[-152.1917, 70.795],
						[-152.2851, 70.7856],
						[-152.3784, 70.7143],
						[-152.4717, 70.6885],
						[-152.4215, 70.6086],
						[-152.3317, 70.6129],
						[-152.2635, 70.5923],
						[-152.102, 70.5871],
						[-152.0661, 70.5631],
						[-151.8185, 70.5459],
						[-151.7, 70.5536],
						[-151.761, 70.5167],
						[-151.7287, 70.4952],
						[-151.919, 70.4729],
						[-151.9477, 70.4523],
						[-151.8759, 70.4308],
						[-151.5564, 70.4359],
						[-151.298, 70.4007],
						[-151.2478, 70.3724],
						[-151.1473, 70.3767],
						[-150.9893, 70.4505],
						[-150.7847, 70.4634],
						[-150.6986, 70.448],
						[-150.6627, 70.4772],
						[-150.5191, 70.4832],
						[-150.415, 70.46],
						[-150.3612, 70.4093],
						[-150.2105, 70.4325],
						[-150.0741, 70.4394],
						[-149.8659, 70.5107],
						[-149.8192, 70.4918],
						[-149.6577, 70.509],
						[-149.5823, 70.4961],
						[-149.4603, 70.5184],
						[-149.4172, 70.4926],
						[-149.1803, 70.4858],
						[-148.9578, 70.4239],
						[-148.8573, 70.4231],
						[-148.7891, 70.4024],
						[-148.6958, 70.4291],
						[-148.6096, 70.4222],
						[-148.4768, 70.3595],
						[-148.4768, 70.3208],
						[-148.3512, 70.3045],
						[-148.204, 70.3483],
						[-148.2004, 70.2942],
						[-148.1071, 70.3432],
						[-147.8164, 70.277],
						[-147.7661, 70.2195],
						[-147.68, 70.1997],
						[-147.5041, 70.2006],
						[-147.3498, 70.1877],
						[-147.2457, 70.2186],
						[-147.1631, 70.1559],
						[-146.9909, 70.1473],
						[-146.8868, 70.186],
						[-146.7324, 70.1756],
						[-146.5099, 70.186],
						[-146.273, 70.1774],
						[-146.0971, 70.1447],
						[-145.9177, 70.1421],
						[-145.8566, 70.1662],
						[-145.6233, 70.0846],
						[-145.5228, 70.0777],
						[-145.408, 70.0313],
						[-145.2465, 70.0176],
						[-145.1747, 69.9918],
						[-145.0132, 69.9815],
						[-144.9521, 69.9592],
						[-144.8552, 69.9867],
						[-144.6183, 69.9695],
						[-144.4568, 70.0356],
						[-144.2845, 70.0408],
						[-144.2522, 70.058],
						[-143.9507, 70.1001],
						[-143.8861, 70.1207],
						[-143.5774, 70.1464],
						[-143.4267, 70.125],
						[-143.2544, 70.1525],
						[-143.2221, 70.137],
						[-142.9385, 70.0743],
						[-142.7483, 70.0425],
						[-142.4971, 69.9738],
						[-142.4037, 69.9162],
						[-142.2709, 69.9068],
						[-141.9587, 69.8234],
						[-141.7146, 69.7891],
						[-141.5279, 69.7358],
						[-141.431, 69.6954],
						[-141.2121, 69.6843],
						[-141.0039, 69.6456],
						[-141.0039, 61.9038],
						[-140.9967, 61.0696],
						[-141.0003, 60.3059],
						[-140.5337, 60.2243],
						[-140.4727, 60.3102],
						[-139.9881, 60.1856],
						[-139.6974, 60.3403],
						[-139.0872, 60.3575],
						[-139.0836, 60.324],
						[-139.2021, 60.0903],
						[-139.0334, 59.9941],
						[-138.7965, 59.9288],
						[-138.7032, 59.9099],
						[-138.6637, 59.8102],
						[-138.5596, 59.7415],
						[-137.6049, 59.2433],
						[-137.4972, 58.9864],
						[-137.5259, 58.9065],
						[-137.4469, 58.9099],
						[-137.2639, 59.0027],
						[-136.8655, 59.1385],
						[-136.826, 59.1582],
						[-136.5819, 59.1651],
						[-136.467, 59.2845],
						[-136.4742, 59.464],
						[-136.3665, 59.4477],
						[-136.3019, 59.464],
						[-136.2337, 59.525],
						[-136.2373, 59.5585],
						[-136.3522, 59.5989],
						[-136.1907, 59.6401],
						[-135.9466, 59.6642],
						[-135.4764, 59.7999],
						[-135.2539, 59.7011],
						[-135.1534, 59.6255],
						[-135.1139, 59.6238],
						[-135.0277, 59.5637],
						[-135.0277, 59.4743],
						[-135.0708, 59.4537],
						[-135.0672, 59.4219],
						[-135.0098, 59.3816],
						[-135.0277, 59.3455],
						[-134.9631, 59.2802],
						[-134.7011, 59.2476],
						[-134.6832, 59.1909],
						[-134.5683, 59.1281],
						[-134.4822, 59.1281],
						[-134.3781, 59.0354],
						[-134.3996, 58.9761],
						[-134.3063, 58.9589],
						[-134.3278, 58.9194],
						[-134.2489, 58.8584],
						[-133.8397, 58.7278],
						[-133.7248, 58.6256],
						[-133.5597, 58.5225],
						[-133.3803, 58.428],
						[-133.4628, 58.3859],
						[-133.3444, 58.2708],
						[-133.1757, 58.1505],
						[-133.0752, 57.9993],
						[-132.8706, 57.843],
						[-132.7557, 57.7047],
						[-132.648, 57.6093],
						[-132.5583, 57.5037],
						[-132.3681, 57.349],
						[-132.2532, 57.2159],
						[-132.3717, 57.0956],
						[-132.0522, 57.0509],
						[-132.1276, 56.8748],
						[-131.8727, 56.8052],
						[-131.9015, 56.7528],
						[-131.862, 56.7039],
						[-131.8369, 56.6016],
						[-131.582, 56.6137],
						[-131.4636, 56.5475],
						[-131.1692, 56.4487],
						[-131.0867, 56.4075],
						[-130.7816, 56.3671],
						[-130.6237, 56.2683],
						[-130.4657, 56.24],
						[-130.4263, 56.1403],
						[-130.3437, 56.1274],
						[-130.2468, 56.0965],
						[-130.1032, 56.1163],
						[-130.0027, 55.9934],
						[-130.0135, 55.9161],
						[-130.0853, 55.8242],
						[-130.1499, 55.7666],
						[-130.1463, 55.7151],
						[-130.1104, 55.6825],
						[-130.1284, 55.5811],
						[-130.0853, 55.4917],
						[-130.0458, 55.4522],
						[-130.0243, 55.338],
						[-129.9812, 55.2838],
						[-130.096, 55.1979],
						[-130.1822, 55.0931],
						[-130.1858, 55.0648],
						[-130.2576, 54.9875],
						[-130.3401, 54.9213],
						[-130.4729, 54.838],
						[-130.5698, 54.7907],
						[-130.638, 54.7787],
						[-130.6273, 54.7392],
						[-130.6955, 54.7194],
						[-130.7385, 54.7538],
						[-130.7421, 54.8019],
						[-130.7888, 54.8225],
						[-130.7924, 54.7847],
						[-130.8354, 54.7658],
						[-130.9001, 54.7813],
						[-130.9467, 54.826],
						[-130.9611, 54.9187],
						[-130.9503, 54.9677],
						[-131.0113, 54.9961],
						[-130.9826, 55.0845],
						[-131.0149, 55.0897],
						[-131.0723, 55.1378],
						[-131.0939, 55.1928],
						[-130.9862, 55.2469],
						[-130.9252, 55.301],
						[-130.8713, 55.2942],
						[-130.8821, 55.3586],
						[-130.9216, 55.429],
						[-130.8713, 55.5338],
						[-130.9036, 55.6971],
						[-130.9395, 55.7546],
						[-131.0939, 55.8955],
						[-131.1728, 55.9427],
						[-131.2159, 55.984],
						[-131.241, 55.9548],
						[-131.1585, 55.9015],
						[-131.0687, 55.8285],
						[-131.0436, 55.7666],
						[-130.9647, 55.6893],
						[-130.9288, 55.5768],
						[-130.9862, 55.5398],
						[-130.9934, 55.472],
						[-130.9682, 55.393],
						[-130.9288, 55.3397],
						[-130.9611, 55.3156],
						[-131.0006, 55.3981],
						[-131.0293, 55.4084],
						[-131.0185, 55.3483],
						[-131.0329, 55.2847],
						[-131.1621, 55.1971],
						[-131.2123, 55.1928],
						[-131.2985, 55.2349],
						[-131.2303, 55.2976],
						[-131.1908, 55.3603],
						[-131.2016, 55.393],
						[-131.2913, 55.3844],
						[-131.2554, 55.3225],
						[-131.2841, 55.2864],
						[-131.4026, 55.2383],
						[-131.4277, 55.2392],
						[-131.5102, 55.332],
						[-131.5497, 55.2933],
						[-131.6394, 55.3397],
						[-131.7005, 55.3552],
						[-131.7435, 55.3981],
						[-131.844, 55.4565],
						[-131.844, 55.5218],
						[-131.7328, 55.5484],
						[-131.6538, 55.5923],
						[-131.7256, 55.6326],
						[-131.7005, 55.6971],
						[-131.7328, 55.7306],
						[-131.6502, 55.7684],
						[-131.6394, 55.789],
						[-131.7041, 55.7898],
						[-131.7148, 55.8534],
						[-131.6897, 55.8903],
						[-131.8369, 55.8757],
						[-131.8153, 55.8371],
						[-131.7758, 55.8233],
						[-131.7794, 55.7916],
						[-131.8261, 55.7185],
						[-131.8297, 55.667],
						[-131.8979, 55.6043],
						[-131.9625, 55.6154],
						[-131.9374, 55.5356],
						[-131.9732, 55.4986],
						[-132.0594, 55.5433],
						[-132.1491, 55.5622],
						[-132.1814, 55.588],
						[-132.2245, 55.7014],
						[-132.2819, 55.7615],
						[-132.2066, 55.7366],
						[-132.1814, 55.801],
						[-132.0845, 55.8328],
						[-132.0414, 55.9591],
						[-132.0702, 56.0467],
						[-132.0343, 56.0957],
						[-132.0127, 56.0776],
						[-131.9697, 56.1137],
						[-131.9445, 56.1927],
						[-132.0199, 56.1833],
						[-132.0343, 56.1335],
						[-132.1025, 56.1077],
						[-132.1778, 56.0553],
						[-132.1312, 55.9582],
						[-132.1599, 55.9221],
						[-132.2783, 55.9247],
						[-132.3214, 55.8878],
						[-132.3106, 55.862],
						[-132.3717, 55.85],
						[-132.3968, 55.8792],
						[-132.3824, 55.9367],
						[-132.4506, 55.9565],
						[-132.4937, 56.0665],
						[-132.6086, 56.0518],
						[-132.6086, 56.0106],
						[-132.7091, 56.112],
						[-132.7198, 56.2176],
						[-132.6911, 56.2391],
						[-132.6014, 56.24],
						[-132.5763, 56.2967],
						[-132.5296, 56.3388],
						[-132.4219, 56.3491],
						[-132.3645, 56.2872],
						[-132.3394, 56.3414],
						[-132.3932, 56.4857],
						[-132.3609, 56.4883],
						[-132.2532, 56.4496],
						[-132.2389, 56.3989],
						[-132.1994, 56.3714],
						[-132.1778, 56.3912],
						[-132.2101, 56.4573],
						[-132.2891, 56.4874],
						[-132.3573, 56.5286],
						[-132.3681, 56.5742],
						[-132.4076, 56.5853],
						[-132.4506, 56.5638],
						[-132.4865, 56.6059],
						[-132.5691, 56.6334],
						[-132.587, 56.6824],
						[-132.5439, 56.7022],
						[-132.5583, 56.7571],
						[-132.6373, 56.7812],
						[-132.7701, 56.8379],
						[-132.8167, 56.8972],
						[-132.8706, 56.9255],
						[-132.9172, 56.9934],
						[-132.8131, 57.0303],
						[-132.8526, 57.0801],
						[-132.9172, 57.0398],
						[-132.9854, 57.0552],
						[-132.9926, 57.032],
						[-133.0752, 57.0819],
						[-133.1864, 57.0887],
						[-133.2259, 57.1368],
						[-133.3228, 57.1128],
						[-133.5166, 57.1781],
						[-133.5454, 57.2425],
						[-133.4915, 57.3052],
						[-133.4269, 57.2863],
						[-133.3085, 57.2898],
						[-133.2762, 57.331],
						[-133.3551, 57.3336],
						[-133.47, 57.3645],
						[-133.4628, 57.3946],
						[-133.5274, 57.5019],
						[-133.4808, 57.5715],
						[-133.5669, 57.5629],
						[-133.6207, 57.5793],
						[-133.6782, 57.6248],
						[-133.653, 57.7133],
						[-133.5813, 57.715],
						[-133.5238, 57.6841],
						[-133.4054, 57.6634],
						[-133.1793, 57.587],
						[-133.1613, 57.5999],
						[-133.251, 57.6497],
						[-133.3228, 57.666],
						[-133.5597, 57.7777],
						[-133.5705, 57.8593],
						[-133.6315, 57.8464],
						[-133.6387, 57.7906],
						[-133.7105, 57.7932],
						[-133.8504, 57.9358],
						[-134.0479, 58.062],
						[-134.0873, 58.1823],
						[-134.055, 58.2382],
						[-134.0622, 58.2828],
						[-133.9653, 58.318],
						[-134.012, 58.404],
						[-134.0407, 58.4022],
						[-134.073, 58.3292],
						[-134.1448, 58.3034],
						[-134.1448, 58.2674],
						[-134.1017, 58.2485],
						[-134.1484, 58.1995],
						[-134.1843, 58.2081],
						[-134.2632, 58.1909],
						[-134.353, 58.2004],
						[-134.4822, 58.2296],
						[-134.5073, 58.2167],
						[-134.6329, 58.2476],
						[-134.7765, 58.3971],
						[-134.7908, 58.4916],
						[-134.9416, 58.6118],
						[-134.9883, 58.6797],
						[-134.9201, 58.6806],
						[-134.9595, 58.83],
						[-135.0277, 58.7819],
						[-135.0241, 58.7321],
						[-135.1498, 58.8455],
						[-135.1785, 58.9976],
						[-135.2072, 59.0766],
						[-135.2826, 59.1926],
						[-135.3974, 59.2922],
						[-135.5267, 59.324],
						[-135.5446, 59.3103],
						[-135.4441, 59.2776],
						[-135.4297, 59.2424],
						[-135.3687, 59.2089],
						[-135.2969, 59.0964],
						[-135.3508, 59.1058],
						[-135.4728, 59.2261],
						[-135.5518, 59.257],
						[-135.6307, 59.2656],
						[-135.4836, 59.1866],
						[-135.4369, 59.1135],
						[-135.3759, 59.0886],
						[-135.401, 59.0053],
						[-135.3687, 58.9297],
						[-135.3221, 58.9099],
						[-135.34, 58.8893],
						[-135.2431, 58.7828],
						[-135.2323, 58.7356],
						[-135.1426, 58.6161],
						[-135.1354, 58.5887],
						[-135.1857, 58.5981],
						[-135.0888, 58.4229],
						[-135.0565, 58.3498],
						[-135.0529, 58.2906],
						[-135.0959, 58.2974],
						[-135.0995, 58.245],
						[-135.0565, 58.19],
						[-135.1605, 58.2098],
						[-135.2287, 58.2373],
						[-135.279, 58.2339],
						[-135.3436, 58.2708],
						[-135.3974, 58.3275],
						[-135.4333, 58.3997],
						[-135.5123, 58.3859],
						[-135.6307, 58.4289],
						[-135.7277, 58.3971],
						[-135.9071, 58.3808],
						[-135.8963, 58.4503],
						[-135.9861, 58.4641],
						[-136.0004, 58.4804],
						[-135.8928, 58.5139],
						[-135.9287, 58.5732],
						[-135.9107, 58.6187],
						[-136.0112, 58.7124],
						[-136.0112, 58.7433],
						[-136.0902, 58.8154],
						[-136.0435, 58.8361],
						[-136.0507, 58.9134],
						[-136.1189, 58.9684],
						[-136.162, 58.9769],
						[-136.1081, 58.8644],
						[-136.1512, 58.757],
						[-136.2481, 58.7527],
						[-136.3988, 58.8129],
						[-136.4922, 58.8386],
						[-136.546, 58.9675],
						[-136.5855, 58.9091],
						[-136.6322, 58.8902],
						[-136.7901, 58.9366],
						[-136.8403, 58.9194],
						[-136.8762, 58.9623],
						[-136.9193, 58.9469],
						[-136.9301, 58.9005],
						[-136.7685, 58.8704],
						[-136.7434, 58.8764],
						[-136.5819, 58.8386],
						[-136.4635, 58.7819],
						[-136.3558, 58.6926],
						[-136.3953, 58.6548],
						[-136.4814, 58.617],
						[-136.4599, 58.6076],
						[-136.3414, 58.6454],
						[-136.3163, 58.6711],
						[-136.2194, 58.672],
						[-136.1943, 58.5818],
						[-136.1009, 58.5148],
						[-136.0435, 58.3799],
						[-136.1117, 58.3421],
						[-136.266, 58.3146],
						[-136.3055, 58.3232],
						[-136.2732, 58.3636],
						[-136.3378, 58.3773],
						[-136.3809, 58.3627],
						[-136.3558, 58.3292],
						[-136.3917, 58.2974],
						[-136.546, 58.3163],
						[-136.5855, 58.2966],
						[-136.5675, 58.245],
						[-136.5963, 58.215],
						[-136.7003, 58.2193],
						[-136.7291, 58.2863],
						[-136.8583, 58.3163],
						[-136.8475, 58.3292],
						[-136.948, 58.3928],
						[-137.009, 58.4091],
						[-137.1131, 58.3928],
						[-137.2387, 58.4529],
						[-137.2962, 58.4658],
						[-137.4972, 58.5577],
						[-137.6802, 58.6222],
						[-137.6874, 58.6651],
						[-137.7951, 58.7252],
						[-137.9279, 58.7802],
						[-137.9458, 58.8043],
						[-137.9315, 58.8687],
						[-137.9853, 58.9099],
						[-138.1827, 59.0139],
						[-138.2904, 59.0543],
						[-138.635, 59.1307],
						[-138.7642, 59.1917],
						[-138.9185, 59.2484],
						[-139.2703, 59.3378],
						[-139.421, 59.3798],
						[-139.7477, 59.5036],
						[-139.8553, 59.5371],
						[-139.8374, 59.562],
						[-139.7261, 59.6384],
						[-139.64, 59.6264],
						[-139.6723, 59.6032],
						[-139.5861, 59.6058],
						[-139.5861, 59.6427],
						[-139.5108, 59.7029],
						[-139.5897, 59.7089],
						[-139.5826, 59.775],
						[-139.6328, 59.8747],
						[-139.6077, 59.9047],
						[-139.5287, 59.94],
						[-139.5467, 59.9692],
						[-139.4892, 59.9949],
						[-139.5359, 60.043],
						[-139.6041, 59.9992],
						[-139.6113, 59.9485],
						[-139.701, 59.9305],
						[-139.7764, 59.8687],
						[-139.78, 59.8266],
						[-140.164, 59.7407],
						[-140.2071, 59.7166],
						[-140.3076, 59.6925],
						[-140.6486, 59.7114],
						[-140.8891, 59.7424],
						[-140.9824, 59.7699],
						[-141.4239, 59.8772],
						[-141.4849, 59.9253],
						[-141.4203, 59.9168],
						[-141.2982, 59.9374],
						[-141.2588, 59.9984],
						[-141.3772, 60.1109],
						[-141.3664, 60.147],
						[-141.4382, 60.1324],
						[-141.5495, 60.1702],
						[-141.5279, 60.1272],
						[-141.3664, 60.0241],
						[-141.4023, 60.0061],
						[-141.5961, 59.9623],
						[-141.7361, 59.9623],
						[-141.912, 60.0095],
						[-142.131, 60.0302],
						[-142.2458, 60.0499],
						[-142.5365, 60.0843],
						[-142.6981, 60.0937],
						[-142.9098, 60.0903],
						[-143.136, 60.0619],
						[-143.4123, 60.0516],
						[-143.6995, 60.0276],
						[-143.8933, 59.9863],
						[-144.0333, 60.0198],
						[-144.1948, 59.9975],
						[-144.4245, 59.8944],
						[-144.5896, 59.7956],
						[-144.5681, 59.8515],
						[-144.4389, 59.94],
						[-144.2163, 60.0405],
						[-144.0512, 60.0422],
						[-144.1087, 60.0989],
						[-144.2845, 60.1401],
						[-144.3132, 60.1616],
						[-144.3491, 60.0911],
						[-144.4425, 60.1633],
						[-144.5358, 60.1891],
						[-144.5968, 60.1813],
						[-144.6542, 60.2045],
						[-144.9091, 60.2209],
						[-144.8768, 60.2449],
						[-144.7404, 60.2647],
						[-144.8911, 60.293],
						[-144.9593, 60.2879],
						[-144.9665, 60.3093],
						[-144.8337, 60.4434],
						[-144.8875, 60.4562],
						[-144.9342, 60.4253],
						[-145.0383, 60.4064],
						[-145.0419, 60.3575],
						[-145.1137, 60.3008],
						[-145.2536, 60.3111],
						[-145.5049, 60.3944],
						[-145.5013, 60.421],
						[-145.5946, 60.452],
						[-145.7346, 60.4743],
						[-145.8818, 60.4442],
						[-145.9607, 60.4674],
						[-145.9141, 60.4923],
						[-145.8028, 60.5198],
						[-145.7131, 60.5834],
						[-145.7633, 60.592],
						[-145.8279, 60.5499],
						[-146.1115, 60.47],
						[-146.2156, 60.4502],
						[-146.352, 60.4545],
						[-146.3484, 60.4081],
						[-146.133, 60.4313],
						[-146.0935, 60.4107],
						[-146.0864, 60.3652],
						[-146.1474, 60.3669],
						[-146.2479, 60.3351],
						[-146.3125, 60.3403],
						[-146.4596, 60.3076],
						[-146.6068, 60.2415],
						[-146.6499, 60.2432],
						[-146.6929, 60.2844],
						[-146.5242, 60.3506],
						[-146.6571, 60.3403],
						[-146.7181, 60.3497],
						[-146.7217, 60.3961],
						[-146.6391, 60.4674],
						[-146.5924, 60.4906],
						[-146.5278, 60.4923],
						[-146.4561, 60.4657],
						[-146.3699, 60.4803],
						[-146.2909, 60.5155],
						[-146.1545, 60.5267],
						[-145.95, 60.5765],
						[-145.7992, 60.5937],
						[-145.8315, 60.6152],
						[-145.8997, 60.6135],
						[-145.8961, 60.6744],
						[-145.9356, 60.6324],
						[-146.0038, 60.6152],
						[-146.0002, 60.6409],
						[-145.9105, 60.6968],
						[-145.9069, 60.7148],
						[-146.0253, 60.665],
						[-146.1869, 60.6246],
						[-146.255, 60.622],
						[-146.2694, 60.6478],
						[-146.0433, 60.744],
						[-146.0648, 60.7767],
						[-146.1689, 60.7251],
						[-146.3053, 60.7131],
						[-146.3484, 60.7354],
						[-146.4022, 60.6933],
						[-146.474, 60.6813],
						[-146.6678, 60.6925],
						[-146.7037, 60.7423],
						[-146.6068, 60.7586],
						[-146.5673, 60.7509],
						[-146.5027, 60.7724],
						[-146.3591, 60.7861],
						[-146.255, 60.8102],
						[-146.1725, 60.866],
						[-146.2622, 60.8677],
						[-146.3125, 60.8282],
						[-146.395, 60.8119],
						[-146.5566, 60.8102],
						[-146.6212, 60.8686],
						[-146.6642, 60.8712],
						[-146.7252, 60.8119],
						[-146.8006, 60.805],
						[-146.815, 60.8557],
						[-146.7576, 60.878],
						[-146.6965, 60.8729],
						[-146.736, 60.9107],
						[-146.7468, 60.9579],
						[-146.7001, 60.9871],
						[-146.6068, 61.0851],
						[-146.2622, 61.0902],
						[-146.2874, 61.1229],
						[-146.614, 61.1186],
						[-146.6894, 61.0645],
						[-146.7863, 61.0421],
						[-146.8616, 60.976],
						[-146.9729, 60.9347],
						[-147.0555, 60.9451],
						[-146.9837, 60.976],
						[-147.0196, 61.0731],
						[-147.0626, 61.1186],
						[-146.998, 61.1444],
						[-147.077, 61.1529],
						[-147.1272, 61.1332],
						[-147.1308, 61.0894],
						[-147.0878, 61.0353],
						[-147.138, 60.9811],
						[-147.1344, 60.9459],
						[-147.1811, 60.933],
						[-147.2744, 60.9743],
						[-147.2816, 60.9167],
						[-147.3785, 60.878],
						[-147.4539, 60.8969],
						[-147.4539, 60.9416],
						[-147.4934, 60.9579],
						[-147.5185, 60.8952],
						[-147.5508, 60.9081],
						[-147.5364, 61.0189],
						[-147.5041, 61.0722],
						[-147.558, 61.0816],
						[-147.619, 60.97],
						[-147.5867, 60.8746],
						[-147.601, 60.8497],
						[-147.6692, 60.842],
						[-147.6656, 60.8841],
						[-147.7626, 60.9133],
						[-147.7877, 60.8738],
						[-147.7302, 60.8179],
						[-147.7769, 60.811],
						[-147.9133, 60.8248],
						[-148.0353, 60.7835],
						[-148.1358, 60.7913],
						[-148.1538, 60.8179],
						[-148.1035, 60.8995],
						[-147.9528, 61.0292],
						[-147.9492, 61.0748],
						[-147.7733, 61.1804],
						[-147.7159, 61.25],
						[-147.7374, 61.2655],
						[-147.9994, 61.0842],
						[-148.003, 61.0542],
						[-148.0676, 61.0043],
						[-148.0964, 61.0112],
						[-148.1251, 61.0705],
						[-148.1681, 61.0696],
						[-148.1789, 61],
						[-148.2184, 60.9536],
						[-148.283, 60.9176],
						[-148.2938, 60.8626],
						[-148.3512, 60.8042],
						[-148.4266, 60.8274],
						[-148.4517, 60.7896],
						[-148.3656, 60.7655],
						[-148.3835, 60.6873],
						[-148.3476, 60.6805],
						[-148.2794, 60.7535],
						[-148.1466, 60.7586],
						[-148.1071, 60.7397],
						[-148.0928, 60.6616],
						[-148.143, 60.6229],
						[-148.2543, 60.5954],
						[-148.3333, 60.5301],
						[-148.3297, 60.476],
						[-148.2543, 60.4932],
						[-148.1933, 60.5576],
						[-148.0856, 60.5954],
						[-147.9779, 60.519],
						[-147.9779, 60.4812],
						[-147.942, 60.4442],
						[-148.0246, 60.2793],
						[-148.1179, 60.2793],
						[-148.1502, 60.324],
						[-148.2184, 60.3317],
						[-148.2148, 60.3008],
						[-148.3117, 60.263],
						[-148.3117, 60.2484],
						[-148.2112, 60.2604],
						[-148.1287, 60.2372],
						[-147.9097, 60.1307],
						[-147.8164, 60.0576],
						[-147.8702, 59.9915],
						[-147.9169, 59.9692],
						[-148.0389, 59.9391],
						[-148.0999, 59.9528],
						[-148.1502, 59.9374],
						[-148.2543, 59.9322],
						[-148.222, 59.976],
						[-148.3153, 60.0336],
						[-148.283, 60.0851],
						[-148.344, 60.1393],
						[-148.4014, 60.031],
						[-148.4014, 59.9778],
						[-148.4445, 59.9425],
						[-148.5127, 60.0018],
						[-148.545, 59.9949],
						[-148.5701, 59.9442],
						[-148.6348, 59.9159],
						[-148.7568, 59.9589],
						[-148.8609, 59.9245],
						[-148.9147, 59.9692],
						[-149.0116, 59.9511],
						[-149.0906, 59.9554],
						[-149.1229, 59.9795],
						[-149.0368, 60.043],
						[-149.0691, 60.0551],
						[-149.166, 60.0224],
						[-149.2701, 59.8721],
						[-149.3777, 59.836],
						[-149.41, 59.8515],
						[-149.3598, 59.8987],
						[-149.3131, 59.9709],
						[-149.367, 60.1186],
						[-149.4244, 60.1229],
						[-149.4423, 60.0989],
						[-149.4388, 60.0293],
						[-149.3921, 59.9838],
						[-149.428, 59.9803],
						[-149.4639, 59.9185],
						[-149.5249, 59.9279],
						[-149.5536, 59.9107],
						[-149.5716, 59.8523],
						[-149.611, 59.8377],
						[-149.5967, 59.7973],
						[-149.507, 59.7707],
						[-149.5285, 59.7071],
						[-149.6254, 59.7346],
						[-149.6685, 59.8437],
						[-149.6398, 59.8755],
						[-149.6792, 59.9425],
						[-149.7403, 59.9477],
						[-149.7187, 59.903],
						[-149.7474, 59.8609],
						[-149.7295, 59.6925],
						[-149.7474, 59.6376],
						[-149.8443, 59.7011],
						[-149.8479, 59.7381],
						[-149.9054, 59.7621],
						[-150.0274, 59.7888],
						[-150.0166, 59.7544],
						[-149.9305, 59.7235],
						[-149.9341, 59.6693],
						[-150.1817, 59.5336],
						[-150.2966, 59.4554],
						[-150.2966, 59.4245],
						[-150.354, 59.4159],
						[-150.3863, 59.342],
						[-150.4294, 59.3438],
						[-150.4402, 59.4039],
						[-150.3181, 59.5852],
						[-150.3181, 59.6101],
						[-150.4115, 59.5542],
						[-150.4796, 59.4589],
						[-150.5227, 59.4683],
						[-150.555, 59.5903],
						[-150.6017, 59.5456],
						[-150.6412, 59.5474],
						[-150.5837, 59.4941],
						[-150.6017, 59.4254],
						[-150.7417, 59.4254],
						[-150.7704, 59.373],
						[-150.835, 59.3524],
						[-150.914, 59.3051],
						[-150.8888, 59.2682],
						[-150.9427, 59.2329],
						[-151.0001, 59.2244],
						[-151.0001, 59.2707],
						[-151.0575, 59.3017],
						[-151.1042, 59.263],
						[-151.1078, 59.2175],
						[-151.1867, 59.2029],
						[-151.2729, 59.2295],
						[-151.3052, 59.2098],
						[-151.3806, 59.2424],
						[-151.4057, 59.2793],
						[-151.4488, 59.2484],
						[-151.5241, 59.2252],
						[-151.5026, 59.1951],
						[-151.5744, 59.1951],
						[-151.5923, 59.1616],
						[-151.7395, 59.1556],
						[-151.761, 59.2209],
						[-151.84, 59.2089],
						[-151.9154, 59.2278],
						[-151.9046, 59.2467],
						[-151.98, 59.2536],
						[-151.9907, 59.3137],
						[-151.962, 59.3446],
						[-151.9046, 59.3601],
						[-151.8867, 59.4211],
						[-151.7718, 59.4477],
						[-151.7395, 59.4383],
						[-151.6354, 59.4821],
						[-151.517, 59.4847],
						[-151.517, 59.5233],
						[-151.3985, 59.5413],
						[-151.2765, 59.5946],
						[-151.1652, 59.5869],
						[-151.2047, 59.6462],
						[-151.1257, 59.6685],
						[-151.115, 59.696],
						[-150.9822, 59.7836],
						[-151.0288, 59.7965],
						[-151.115, 59.7776],
						[-151.3303, 59.684],
						[-151.438, 59.6668],
						[-151.5026, 59.6333],
						[-151.6426, 59.647],
						[-151.7969, 59.7046],
						[-151.8508, 59.7389],
						[-151.8687, 59.7784],
						[-151.8041, 59.879],
						[-151.7574, 59.9176],
						[-151.7036, 60.0319],
						[-151.6067, 60.0997],
						[-151.5457, 60.1281],
						[-151.42, 60.2131],
						[-151.3806, 60.2973],
						[-151.3806, 60.3583],
						[-151.298, 60.3858],
						[-151.2801, 60.5198],
						[-151.2657, 60.543],
						[-151.3303, 60.5808],
						[-151.4093, 60.7208],
						[-151.2693, 60.7509],
						[-151.2513, 60.7741],
						[-151.0252, 60.7973],
						[-150.7058, 60.9382],
						[-150.3756, 61.0395],
						[-150.1925, 60.9012],
						[-150.0453, 60.8875],
						[-149.9987, 60.8643],
						[-149.8766, 60.9605],
						[-149.769, 60.9674],
						[-149.6398, 60.9287],
						[-149.5716, 60.9373],
						[-149.3454, 60.8961],
						[-149.1121, 60.8789],
						[-149.0044, 60.8317],
						[-149.008, 60.8549],
						[-149.087, 60.909],
						[-149.1911, 60.9399],
						[-149.3275, 60.9279],
						[-149.5105, 60.982],
						[-149.5967, 60.9811],
						[-149.7403, 61.0172],
						[-149.83, 61.0756],
						[-149.97, 61.1323],
						[-150.0741, 61.1564],
						[-150.0095, 61.2036],
						[-149.9233, 61.2114],
						[-149.812, 61.3179],
						[-149.7295, 61.3333],
						[-149.7008, 61.3849],
						[-149.5357, 61.4081],
						[-149.428, 61.4467],
						[-149.5428, 61.4897],
						[-149.6146, 61.4931],
						[-149.769, 61.4382],
						[-149.8085, 61.3961],
						[-149.8766, 61.384],
						[-149.9197, 61.3239],
						[-149.9197, 61.2638],
						[-149.9843, 61.2371],
						[-150.0741, 61.2535],
						[-150.2033, 61.2595],
						[-150.4689, 61.2449],
						[-150.5622, 61.2818],
						[-150.6627, 61.2955],
						[-150.713, 61.2509],
						[-151.0252, 61.1787],
						[-151.1652, 61.0464],
						[-151.2944, 61.0361],
						[-151.3626, 61.0095],
						[-151.4811, 61.0112],
						[-151.621, 60.9571],
						[-151.7933, 60.8634],
						[-151.7969, 60.8385],
						[-151.7036, 60.732],
						[-151.7108, 60.7122],
						[-151.8472, 60.7337],
						[-151.9154, 60.7183],
						[-152.023, 60.6736],
						[-152.0984, 60.5946],
						[-152.1953, 60.5696],
						[-152.3102, 60.5061],
						[-152.3317, 60.4434],
						[-152.303, 60.4142],
						[-152.2348, 60.3935],
						[-152.3784, 60.3454],
						[-152.4107, 60.2879],
						[-152.4573, 60.2844],
						[-152.5579, 60.2243],
						[-152.626, 60.2183],
						[-152.6584, 60.2423],
						[-152.7481, 60.2329],
						[-152.7337, 60.1745],
						[-152.6763, 60.1633],
						[-152.6871, 60.1375],
						[-152.5973, 60.1015],
						[-152.5686, 60.0714],
						[-152.6117, 60.0087],
						[-152.6799, 59.9683],
						[-152.705, 59.915],
						[-152.7947, 59.897],
						[-152.8091, 59.8781],
						[-152.9491, 59.8764],
						[-153.0029, 59.8867],
						[-153.1465, 59.8601],
						[-153.2111, 59.8626],
						[-153.2793, 59.8111],
						[-153.1967, 59.8248],
						[-153.1429, 59.8077],
						[-153.0891, 59.8334],
						[-153.0209, 59.8343],
						[-152.9957, 59.7913],
						[-153.0532, 59.6917],
						[-153.1214, 59.6779],
						[-153.2147, 59.6341],
						[-153.3152, 59.6659],
						[-153.2972, 59.6367],
						[-153.3439, 59.6212],
						[-153.4085, 59.6367],
						[-153.3834, 59.6668],
						[-153.3762, 59.7312],
						[-153.4552, 59.7922],
						[-153.4444, 59.69],
						[-153.4767, 59.6427],
						[-153.5413, 59.6298],
						[-153.588, 59.6513],
						[-153.6095, 59.6212],
						[-153.5521, 59.5972],
						[-153.5844, 59.5517],
						[-153.7603, 59.5431],
						[-153.6992, 59.4632],
						[-153.728, 59.4357],
						[-153.8249, 59.4176],
						[-153.8608, 59.4245],
						[-153.9469, 59.3859],
						[-153.99, 59.397],
						[-154.1192, 59.3652],
						[-154.1012, 59.3429],
						[-154.0295, 59.3274],
						[-154.112, 59.3],
						[-154.1371, 59.263],
						[-154.13, 59.2106],
						[-154.173, 59.1728],
						[-154.2592, 59.1427],
						[-154.173, 59.1204],
						[-154.1946, 59.0697],
						[-154.1587, 59.0182],
						[-154.0618, 59.0723],
						[-153.85, 59.0525],
						[-153.7926, 59.0714],
						[-153.7495, 59.0525],
						[-153.6957, 59.074],
						[-153.6167, 59.007],
						[-153.5485, 58.9838],
						[-153.4803, 58.995],
						[-153.3977, 58.9658],
						[-153.3331, 58.9202],
						[-153.3044, 58.8747],
						[-153.2506, 58.8558],
						[-153.3511, 58.8438],
						[-153.4013, 58.7424],
						[-153.4444, 58.7089],
						[-153.5521, 58.6874],
						[-153.5916, 58.6402],
						[-153.6777, 58.6118],
						[-153.85, 58.6118],
						[-153.9038, 58.5972],
						[-153.9182, 58.5165],
						[-153.9613, 58.4881],
						[-154.0582, 58.489],
						[-154.0761, 58.4718],
						[-154.0402, 58.404],
						[-154.0007, 58.3765],
						[-154.0977, 58.3455],
						[-154.1694, 58.3584],
						[-154.1766, 58.3198],
						[-154.1048, 58.2802],
						[-154.1443, 58.2107],
						[-154.2197, 58.1849],
						[-154.2412, 58.1565],
						[-154.2089, 58.1359],
						[-154.2915, 58.1359],
						[-154.3417, 58.0913],
						[-154.4351, 58.148],
						[-154.4674, 58.1419],
						[-154.4494, 58.093],
						[-154.4638, 58.0586],
						[-154.5391, 58.056],
						[-154.5822, 58.0191],
						[-154.6432, 58.0328],
						[-154.6432, 58.056],
						[-154.715, 58.0552],
						[-154.7294, 58.0217],
						[-154.8083, 58.0011],
						[-154.8909, 58.0277],
						[-154.9914, 58.0131],
						[-155.117, 57.9538],
						[-155.0632, 57.9091],
						[-155.0955, 57.8653],
						[-155.2391, 57.8275],
						[-155.3396, 57.8258],
						[-155.336, 57.7803],
						[-155.2857, 57.7588],
						[-155.3072, 57.7244],
						[-155.379, 57.7107],
						[-155.5082, 57.7614],
						[-155.5441, 57.7871],
						[-155.6159, 57.77],
						[-155.6339, 57.7158],
						[-155.6303, 57.6566],
						[-155.7236, 57.6334],
						[-155.7344, 57.55],
						[-155.9677, 57.544],
						[-156.0431, 57.5647],
						[-156.0502, 57.5011],
						[-156.0215, 57.4401],
						[-156.0933, 57.4401],
						[-156.1364, 57.4719],
						[-156.2118, 57.4745],
						[-156.2261, 57.4409],
						[-156.341, 57.4178],
						[-156.4809, 57.3387],
						[-156.5348, 57.3284],
						[-156.5527, 57.2906],
						[-156.5061, 57.2812],
						[-156.3374, 57.3361],
						[-156.323, 57.2932],
						[-156.3446, 57.2477],
						[-156.3984, 57.2416],
						[-156.3984, 57.215],
						[-156.3338, 57.1824],
						[-156.3697, 57.1497],
						[-156.4343, 57.1274],
						[-156.4415, 57.0853],
						[-156.5348, 57.0475],
						[-156.5635, 57.02],
						[-156.5563, 56.9839],
						[-156.6712, 56.996],
						[-156.7824, 56.9719],
						[-156.7968, 56.9118],
						[-156.8399, 56.9015],
						[-156.9081, 56.9659],
						[-156.9368, 56.9204],
						[-157.0337, 56.8843],
						[-157.0732, 56.8387],
						[-157.1593, 56.8336],
						[-157.1414, 56.7906],
						[-157.2024, 56.7674],
						[-157.2921, 56.8044],
						[-157.3783, 56.8619],
						[-157.4572, 56.8482],
						[-157.4572, 56.8044],
						[-157.4178, 56.8001],
						[-157.4142, 56.7692],
						[-157.5183, 56.7606],
						[-157.5649, 56.703],
						[-157.5434, 56.6755],
						[-157.4788, 56.6704],
						[-157.4608, 56.6257],
						[-157.59, 56.6223],
						[-157.6762, 56.6094],
						[-157.7372, 56.6755],
						[-157.9203, 56.6429],
						[-157.9849, 56.5991],
						[-158.0423, 56.5965],
						[-158.0746, 56.5544],
						[-158.0064, 56.5535],
						[-157.9095, 56.575],
						[-157.8377, 56.5604],
						[-157.8162, 56.514],
						[-157.8736, 56.4736],
						[-158.0279, 56.5114],
						[-158.1105, 56.5209],
						[-158.1284, 56.4608],
						[-158.2469, 56.4659],
						[-158.3294, 56.484],
						[-158.4012, 56.4556],
						[-158.4981, 56.38],
						[-158.491, 56.3422],
						[-158.3223, 56.325],
						[-158.2074, 56.2941],
						[-158.2182, 56.2692],
						[-158.333, 56.2331],
						[-158.3294, 56.2134],
						[-158.2648, 56.2099],
						[-158.1751, 56.2365],
						[-158.1177, 56.2305],
						[-158.3151, 56.1635],
						[-158.3976, 56.1094],
						[-158.394, 56.0647],
						[-158.4407, 56.1077],
						[-158.4766, 56.0931],
						[-158.4192, 56.0364],
						[-158.4407, 55.9934],
						[-158.4658, 56.0269],
						[-158.5017, 56.0252],
						[-158.5089, 55.9797],
						[-158.5986, 56.0484],
						[-158.5986, 56.13],
						[-158.6668, 56.0785],
						[-158.6381, 56.0158],
						[-158.6525, 55.9582],
						[-158.7386, 55.9531],
						[-158.7278, 56.0029],
						[-158.8535, 56.0037],
						[-158.9109, 55.935],
						[-159.0006, 55.9273],
						[-159.2698, 55.8903],
						[-159.3739, 55.8714],
						[-159.399, 55.8525],
						[-159.4098, 55.789],
						[-159.4708, 55.8285],
						[-159.4529, 55.8972],
						[-159.5354, 55.8809],
						[-159.4995, 55.8551],
						[-159.496, 55.7658],
						[-159.5534, 55.7048],
						[-159.5318, 55.6653],
						[-159.618, 55.5957],
						[-159.7328, 55.5699],
						[-159.7257, 55.6146],
						[-159.6395, 55.618],
						[-159.6431, 55.6524],
						[-159.679, 55.6558],
						[-159.6718, 55.7512],
						[-159.6072, 55.813],
						[-159.7113, 55.8465],
						[-159.8118, 55.8568],
						[-159.8585, 55.8414],
						[-159.8477, 55.8027],
						[-159.8908, 55.7847],
						[-159.9374, 55.8036],
						[-160.0272, 55.7924],
						[-160.0595, 55.722],
						[-160.1851, 55.6584],
						[-160.2784, 55.6412],
						[-160.4112, 55.6653],
						[-160.4328, 55.6404],
						[-160.3933, 55.6026],
						[-160.4651, 55.533],
						[-160.4615, 55.5063],
						[-160.5369, 55.4746],
						[-160.5799, 55.5648],
						[-160.6158, 55.5759],
						[-160.6661, 55.5441],
						[-160.7055, 55.5562],
						[-160.7702, 55.5295],
						[-160.6589, 55.5184],
						[-160.6661, 55.46],
						[-160.7809, 55.4514],
						[-160.8384, 55.4728],
						[-160.8204, 55.5063],
						[-160.8671, 55.527],
						[-160.946, 55.5081],
						[-161.0142, 55.4307],
						[-161.2332, 55.3577],
						[-161.3265, 55.3594],
						[-161.366, 55.3844],
						[-161.4593, 55.3594],
						[-161.506, 55.3629],
						[-161.4701, 55.4969],
						[-161.366, 55.5794],
						[-161.3624, 55.6189],
						[-161.4162, 55.6326],
						[-161.5275, 55.6309],
						[-161.6136, 55.606],
						[-161.6998, 55.5192],
						[-161.6854, 55.4084],
						[-161.829, 55.2881],
						[-161.8541, 55.2778],
						[-161.9044, 55.2048],
						[-161.9797, 55.2357],
						[-162.0479, 55.2254],
						[-162.0013, 55.1696],
						[-161.9654, 55.155],
						[-161.9618, 55.1069],
						[-162.0515, 55.0742],
						[-162.1197, 55.1026],
						[-162.1197, 55.1412],
						[-162.1772, 55.1541],
						[-162.2238, 55.1026],
						[-162.1879, 55.0605],
						[-162.2489, 55.0201],
						[-162.2992, 55.0433],
						[-162.4141, 55.0364],
						[-162.4715, 55.0519],
						[-162.5217, 55.1155],
						[-162.4069, 55.1206],
						[-162.4822, 55.161],
						[-162.511, 55.2503],
						[-162.5863, 55.2985],
						[-162.6258, 55.3045],
						[-162.6832, 55.2761],
						[-162.7191, 55.2203],
						[-162.694, 55.1971],
						[-162.6438, 55.1971],
						[-162.5792, 55.137],
						[-162.6186, 55.0974],
						[-162.5684, 55.0158],
						[-162.5684, 54.9712],
						[-162.6474, 54.9969],
						[-162.7191, 54.9806],
						[-162.7084, 54.9583],
						[-162.7694, 54.9325],
						[-162.834, 54.9265],
						[-162.913, 54.9505],
						[-162.9704, 55.0012],
						[-162.956, 55.0321],
						[-163.0027, 55.0803],
						[-163.0781, 55.112],
						[-163.1893, 55.0906],
						[-163.2252, 55.0425],
						[-163.0494, 54.9694],
						[-163.035, 54.9428],
						[-163.2145, 54.8474],
						[-163.3006, 54.8294],
						[-163.3724, 54.8011],
						[-163.315, 54.753],
						[-163.2181, 54.7547],
						[-163.1857, 54.7753],
						[-163.1068, 54.7323],
						[-163.0601, 54.661],
						[-163.1534, 54.6662],
						[-163.1247, 54.6954],
						[-163.1857, 54.7005],
						[-163.2216, 54.6765],
						[-163.2791, 54.6954],
						[-163.3401, 54.7487],
						[-163.4298, 54.7152],
						[-163.4226, 54.6559],
						[-163.4873, 54.655],
						[-163.5878, 54.6121],
						[-163.6703, 54.6275],
						[-163.8031, 54.6361],
						[-163.9646, 54.6318],
						[-164.0867, 54.6198],
						[-164.2338, 54.5854],
						[-164.3379, 54.5244],
						[-164.3523, 54.4652],
						[-164.4564, 54.4196],
						[-164.6394, 54.3913],
						[-164.7435, 54.3939],
						[-164.844, 54.4179],
						[-164.8763, 54.4437],
						[-164.9086, 54.5073],
						[-164.9445, 54.533],
						[-164.9481, 54.5803],
						[-164.9194, 54.6052],
						[-164.7614, 54.6404],
						[-164.7112, 54.6619],
						[-164.6286, 54.7564],
						[-164.5604, 54.8509],
						[-164.564, 54.8758],
						[-164.4851, 54.9222],
						[-164.4348, 54.9334],
						[-164.3451, 54.8938],
						[-164.2087, 54.9273],
						[-164.119, 54.9694],
						[-164.0292, 54.9694],
						[-163.8964, 55.039],
						[-163.7744, 55.0562],
						[-163.6452, 55.0442],
						[-163.5339, 55.0485],
						[-163.5303, 55.0167],
						[-163.4298, 54.9548],
						[-163.3975, 54.9024],
						[-163.4155, 54.8595],
						[-163.3186, 54.8801],
						[-163.3473, 54.9248],
						[-163.3401, 54.9565],
						[-163.2324, 54.9308],
						[-163.2862, 54.9669],
						[-163.2791, 55.033],
						[-163.315, 55.1266],
						[-163.2001, 55.1662],
						[-163.1068, 55.1842],
						[-162.956, 55.1713],
						[-162.8807, 55.1833],
						[-162.8448, 55.2426],
						[-162.9022, 55.2461],
						[-162.8878, 55.2701],
						[-162.8232, 55.2787],
						[-162.7981, 55.3113],
						[-162.7335, 55.3079],
						[-162.6402, 55.393],
						[-162.6294, 55.3655],
						[-162.5756, 55.3483],
						[-162.5002, 55.3895],
						[-162.5181, 55.4608],
						[-162.5899, 55.4531],
						[-162.493, 55.5115],
						[-162.2633, 55.6885],
						[-162.1449, 55.7331],
						[-162.0623, 55.7864],
						[-161.9941, 55.7976],
						[-161.8972, 55.8336],
						[-161.8075, 55.8921],
						[-161.7105, 55.9041],
						[-161.5849, 55.9376],
						[-161.4521, 55.9548],
						[-161.1327, 56.0149],
						[-161.025, 56.0072],
						[-160.9891, 56.0226],
						[-160.8276, 56.0269],
						[-160.7917, 56.0183],
						[-160.8635, 55.9952],
						[-160.8671, 55.9548],
						[-160.8168, 55.9531],
						[-160.8276, 55.911],
						[-160.9317, 55.8955],
						[-160.9424, 55.8225],
						[-160.806, 55.7383],
						[-160.7666, 55.7572],
						[-160.6697, 55.7237],
						[-160.6553, 55.74],
						[-160.7594, 55.7855],
						[-160.7702, 55.8586],
						[-160.7917, 55.8861],
						[-160.6984, 55.862],
						[-160.4938, 55.8646],
						[-160.4687, 55.7907],
						[-160.3861, 55.7967],
						[-160.2928, 55.7658],
						[-160.2677, 55.7847],
						[-160.3179, 55.819],
						[-160.221, 55.8319],
						[-160.422, 55.9084],
						[-160.5369, 55.9393],
						[-160.5333, 55.9891],
						[-160.5907, 55.9831],
						[-160.4507, 56.1257],
						[-160.3969, 56.2314],
						[-160.3574, 56.2795],
						[-160.2748, 56.3173],
						[-160.1456, 56.3998],
						[-160.081, 56.4109],
						[-159.9374, 56.4745],
						[-159.8298, 56.5441],
						[-159.5354, 56.6266],
						[-159.3883, 56.6901],
						[-159.2627, 56.7236],
						[-159.0186, 56.8147],
						[-158.9576, 56.8508],
						[-158.8535, 56.7924],
						[-158.7853, 56.7803],
						[-158.674, 56.7967],
						[-158.6453, 56.8473],
						[-158.692, 56.8877],
						[-158.6991, 56.9272],
						[-158.6453, 57.032],
						[-158.5412, 57.1248],
						[-158.394, 57.2365],
						[-158.3079, 57.2898],
						[-158.2289, 57.3215],
						[-158.0854, 57.3568],
						[-158.01, 57.4014],
						[-157.931, 57.4762],
						[-157.8808, 57.4891],
						[-157.7875, 57.5423],
						[-157.6798, 57.5638],
						[-157.6511, 57.5002],
						[-157.5865, 57.4873],
						[-157.5721, 57.5226],
						[-157.608, 57.6128],
						[-157.6905, 57.611],
						[-157.7121, 57.6403],
						[-157.7049, 57.7219],
						[-157.6726, 57.7725],
						[-157.5829, 58.1239],
						[-157.5326, 58.16],
						[-157.3962, 58.1737],
						[-157.3531, 58.2193],
						[-157.4249, 58.2115],
						[-157.547, 58.2777],
						[-157.5577, 58.3309],
						[-157.5254, 58.4143],
						[-157.4536, 58.5053],
						[-157.3567, 58.5337],
						[-157.2526, 58.6204],
						[-157.0768, 58.7081],
						[-157.0624, 58.757],
						[-156.9942, 58.8369],
						[-157.0122, 58.8756],
						[-156.9655, 58.9039],
						[-156.9763, 58.9409],
						[-156.9296, 58.9735],
						[-157.0014, 58.9735],
						[-157.0409, 58.946],
						[-157.0409, 58.9134],
						[-157.1163, 58.8678],
						[-157.2168, 58.8412],
						[-157.3531, 58.818],
						[-157.4285, 58.7914],
						[-157.5326, 58.7725],
						[-157.5506, 58.7545],
						[-157.6977, 58.7304],
						[-157.8556, 58.678],
						[-158.1392, 58.6153],
						[-158.2325, 58.6196],
						[-158.333, 58.6651],
						[-158.351, 58.7278],
						[-158.4228, 58.7699],
						[-158.5663, 58.8026],
						[-158.5591, 58.8421],
						[-158.5197, 58.8567],
						[-158.4874, 59.0001],
						[-158.5304, 58.9976],
						[-158.6202, 58.9108],
						[-158.7673, 58.8644],
						[-158.7889, 58.8146],
						[-158.7709, 58.7648],
						[-158.7996, 58.733],
						[-158.8499, 58.7227],
						[-158.8607, 58.6952],
						[-158.8283, 58.6265],
						[-158.7709, 58.5483],
						[-158.7027, 58.483],
						[-158.796, 58.4082],
						[-158.8965, 58.3902],
						[-159.0473, 58.4177],
						[-159.1873, 58.556],
						[-159.356, 58.7347],
						[-159.4493, 58.7974],
						[-159.5318, 58.8335],
						[-159.6431, 58.8447],
						[-159.5857, 58.9005],
						[-159.6431, 58.9383],
						[-159.7328, 58.9306],
						[-159.7544, 58.8558],
						[-159.8082, 58.8618],
						[-159.8046, 58.806],
						[-159.9087, 58.7802],
						[-159.9949, 58.8481],
						[-159.9841, 58.8704],
						[-160.0523, 58.8867],
						[-160.0918, 58.861],
						[-160.1492, 58.8661],
						[-160.1528, 58.9168],
						[-160.2318, 58.9013],
						[-160.3215, 58.9538],
						[-160.2569, 58.9941],
						[-160.3179, 59.0706],
						[-160.4758, 59.0259],
						[-160.6409, 58.9641],
						[-160.7522, 58.9108],
						[-160.824, 58.8292],
						[-160.8707, 58.8782],
						[-160.9999, 58.8498],
						[-161.3373, 58.7433],
						[-161.3732, 58.7081],
						[-161.3732, 58.666],
						[-161.5203, 58.6333],
						[-161.549, 58.611],
						[-161.628, 58.6024],
						[-161.6818, 58.5646],
						[-161.7536, 58.5517],
						[-161.768, 58.599],
						[-161.8721, 58.6376],
						[-162.0659, 58.6204],
						[-162.17, 58.6488],
						[-161.9941, 58.6892],
						[-161.9403, 58.6557],
						[-161.8757, 58.666],
						[-161.8577, 58.7089],
						[-161.768, 58.7751],
						[-161.7572, 58.8266],
						[-161.7859, 58.8996],
						[-161.7823, 58.9692],
						[-161.8398, 59.0328],
						[-161.8218, 59.0508],
						[-161.8792, 59.068],
						[-161.9833, 59.1513],
						[-162.0551, 59.2707],
						[-161.951, 59.3807],
						[-161.8757, 59.4005],
						[-161.7895, 59.4683],
						[-161.7034, 59.4907],
						[-161.7572, 59.5568],
						[-161.8218, 59.5946],
						[-161.8757, 59.6496],
						[-161.9116, 59.7415],
						[-162.091, 59.8807],
						[-162.1018, 59.9442],
						[-162.1915, 60.0018],
						[-162.2274, 60.0559],
						[-162.3351, 60.1289],
						[-162.371, 60.1667],
						[-162.4499, 60.1745],
						[-162.4894, 60.1427],
						[-162.4966, 60.0791],
						[-162.4751, 60.0473],
						[-162.5038, 59.9992],
						[-162.5469, 59.9812],
						[-162.6438, 59.9726],
						[-162.7084, 59.9906],
						[-162.7766, 59.9442],
						[-162.9058, 59.9193],
						[-163.0852, 59.8609],
						[-163.3473, 59.8197],
						[-163.6631, 59.7956],
						[-163.9323, 59.8042],
						[-164.1154, 59.8369],
						[-164.162, 59.8644],
						[-164.2087, 59.9494],
						[-164.1261, 59.9649],
						[-164.1908, 60.0241],
						[-164.3846, 60.0774],
						[-164.4994, 60.1702],
						[-164.5174, 60.1994],
						[-164.6358, 60.2432],
						[-164.7004, 60.2965],
						[-164.8512, 60.3033],
						[-164.9625, 60.3394],
						[-165.1311, 60.4339],
						[-165.0486, 60.4614],
						[-164.9625, 60.5087],
						[-164.9696, 60.5396],
						[-165.0558, 60.5447],
						[-165.1922, 60.4983],
						[-165.3645, 60.5069],
						[-165.4219, 60.5507],
						[-165.3824, 60.5782],
						[-165.2675, 60.5791],
						[-165.063, 60.6891],
						[-165.0271, 60.6856],
						[-164.966, 60.7243],
						[-165.0235, 60.7535],
						[-165.0378, 60.7887],
						[-164.923, 60.8093],
						[-165.0199, 60.8153],
						[-165.0019, 60.8755],
						[-164.905, 60.9425],
						[-164.966, 60.9451],
						[-165.0306, 60.9038],
						[-165.1742, 60.9399],
						[-165.1958, 60.9743],
						[-165.1168, 61.0164],
						[-165.0199, 61.0112],
						[-164.9517, 61.031],
						[-164.9517, 61.0696],
						[-165.0127, 61.0516],
						[-165.1204, 61.0791],
						[-165.1778, 61.1255],
						[-165.1706, 61.1452],
						[-165.2424, 61.1512],
						[-165.2891, 61.1813],
						[-165.325, 61.1693],
						[-165.368, 61.0842],
						[-165.4183, 61.0791],
						[-165.5547, 61.0928],
						[-165.5906, 61.1109],
						[-165.6265, 61.1675],
						[-165.6229, 61.2784],
						[-165.6624, 61.2947],
						[-165.8167, 61.3033],
						[-165.8813, 61.3351],
						[-165.9172, 61.4193],
						[-165.7665, 61.4571],
						[-165.7449, 61.4897],
						[-165.8059, 61.5292],
						[-165.9136, 61.5558],
						[-166.0751, 61.5326],
						[-166.0751, 61.4931],
						[-166.1505, 61.5129],
						[-166.1828, 61.5885],
						[-166.1541, 61.7148],
						[-166.1326, 61.7242],
						[-166.1397, 61.6323],
						[-165.8095, 61.6727],
						[-165.8095, 61.689],
						[-165.9351, 61.7062],
						[-166.0069, 61.7302],
						[-166.0931, 61.8007],
						[-166.0859, 61.8161],
						[-165.9567, 61.8325],
						[-165.7593, 61.8256],
						[-165.6408, 61.8471],
						[-165.7449, 61.9622],
						[-165.7593, 62.0017],
						[-165.7485, 62.0739],
						[-165.6731, 62.14],
						[-165.3106, 62.3788],
						[-165.2963, 62.4037],
						[-165.1993, 62.4699],
						[-165.0953, 62.5223],
						[-165.0773, 62.5824],
						[-165.0127, 62.64],
						[-164.9373, 62.6692],
						[-164.8691, 62.6657],
						[-164.8368, 62.6855],
						[-164.8763, 62.7843],
						[-164.783, 62.9458],
						[-164.7184, 63.0059],
						[-164.582, 63.0583],
						[-164.6466, 63.079],
						[-164.6322, 63.0979],
						[-164.4241, 63.2121],
						[-164.2087, 63.2516],
						[-164.0364, 63.2611],
						[-163.8857, 63.2224],
						[-163.7313, 63.213],
						[-163.6165, 63.1408],
						[-163.5303, 63.1357],
						[-163.5088, 63.1133],
						[-163.315, 63.0377],
						[-163.0529, 63.0583],
						[-162.834, 63.1649],
						[-162.8196, 63.2052],
						[-162.6617, 63.2302],
						[-162.4356, 63.3779],
						[-162.353, 63.4544],
						[-162.2705, 63.4879],
						[-162.3028, 63.5377],
						[-162.2525, 63.542],
						[-162.152, 63.5179],
						[-162.0731, 63.5136],
						[-162.0264, 63.4475],
						[-161.8398, 63.4475],
						[-161.6782, 63.4647],
						[-161.5849, 63.4475],
						[-161.3085, 63.4715],
						[-161.1363, 63.5042],
						[-161.104, 63.548],
						[-160.903, 63.658],
						[-160.7666, 63.7739],
						[-160.7666, 63.8349],
						[-160.8096, 63.9045],
						[-160.8922, 63.9861],
						[-160.9532, 64.0901],
						[-160.9568, 64.1914],
						[-160.9747, 64.2361],
						[-161.2296, 64.371],
						[-161.2619, 64.3985],
						[-161.409, 64.4225],
						[-161.506, 64.4234],
						[-161.4701, 64.5067],
						[-161.3732, 64.5351],
						[-161.3229, 64.5136],
						[-161.1973, 64.4964],
						[-161.0142, 64.5024],
						[-161.0501, 64.5411],
						[-160.9389, 64.5497],
						[-160.7917, 64.6193],
						[-160.7845, 64.7172],
						[-160.9353, 64.822],
						[-161.104, 64.8804],
						[-161.1937, 64.9216],
						[-161.1973, 64.8942],
						[-161.2942, 64.8529],
						[-161.366, 64.779],
						[-161.5167, 64.7533],
						[-161.6675, 64.7893],
						[-161.8792, 64.7095],
						[-162.17, 64.6802],
						[-162.2346, 64.6193],
						[-162.5397, 64.5308],
						[-162.6151, 64.4706],
						[-162.633, 64.3856],
						[-162.7909, 64.3255],
						[-162.8053, 64.4053],
						[-162.8591, 64.4998],
						[-162.9417, 64.5428],
						[-163.0422, 64.5402],
						[-163.0278, 64.5694],
						[-163.1427, 64.6089],
						[-163.175, 64.6399],
						[-163.3114, 64.5883],
						[-163.2539, 64.5497],
						[-163.1786, 64.5437],
						[-163.1211, 64.5102],
						[-163.035, 64.5196],
						[-163.0278, 64.4784],
						[-163.1068, 64.4088],
						[-163.175, 64.3993],
						[-163.2539, 64.4698],
						[-163.4514, 64.5351],
						[-163.5985, 64.5634],
						[-163.8282, 64.5746],
						[-163.9754, 64.5514],
						[-164.0723, 64.5617],
						[-164.2589, 64.5643],
						[-164.4205, 64.5454],
						[-164.8081, 64.4492],
						[-165.0163, 64.4346],
						[-165.4147, 64.4981],
						[-165.7521, 64.5368],
						[-165.9208, 64.5488],
						[-166.2366, 64.5832],
						[-166.4125, 64.651],
						[-166.4843, 64.7335],
						[-166.4807, 64.7971],
						[-166.4089, 64.828],
						[-166.4305, 64.883],
						[-166.531, 64.9371],
						[-166.6925, 64.9852],
						[-166.6961, 65.0359],
						[-166.8612, 65.0909],
						[-166.8971, 65.139],
						[-166.7571, 65.1106],
						[-166.6387, 65.1132],
						[-166.6063, 65.1364],
						[-166.5202, 65.1493],
						[-166.4628, 65.1768],
						[-166.4735, 65.224],
						[-166.3479, 65.2764],
						[-166.4843, 65.3306],
						[-166.5525, 65.3383],
						[-166.6566, 65.3245],
						[-166.7966, 65.3374],
						[-167.0263, 65.3821],
						[-167.3996, 65.4001],
						[-167.5755, 65.4448],
						[-167.7118, 65.4989],
						[-167.9093, 65.5505],
						[-168.0744, 65.5762],
						[-168.1282, 65.6561]
					]],
					[[
						[-169.0542, 52.863],
						[-168.9573, 52.8862],
						[-168.9609, 52.9369],
						[-168.8963, 52.9369],
						[-168.8604, 52.9678],
						[-168.8604, 53.0168],
						[-168.785, 53.0451],
						[-168.7671, 53.0941],
						[-168.803, 53.1079],
						[-168.7635, 53.1826],
						[-168.5984, 53.2736],
						[-168.541, 53.2513],
						[-168.4441, 53.2651],
						[-168.3436, 53.2625],
						[-168.3759, 53.3183],
						[-168.4189, 53.3226],
						[-168.3866, 53.3888],
						[-168.4082, 53.4111],
						[-168.3436, 53.4764],
						[-168.2, 53.5339],
						[-168.0062, 53.5657],
						[-167.9021, 53.5202],
						[-167.7908, 53.5211],
						[-167.7836, 53.5013],
						[-167.859, 53.4377],
						[-167.8411, 53.3862],
						[-168.0385, 53.3046],
						[-168.2718, 53.2427],
						[-168.34, 53.186],
						[-168.3436, 53.1551],
						[-168.4441, 53.0855],
						[-168.4979, 53.0357],
						[-168.5876, 53.0271],
						[-168.6881, 52.9661],
						[-168.742, 52.9515],
						[-168.7563, 52.9077],
						[-168.8102, 52.9257],
						[-168.9071, 52.8836],
						[-169.1009, 52.8244],
						[-169.0542, 52.863]
					]],
					[[
						[-169.2696, 52.7728],
						[-169.1655, 52.8149],
						[-169.1691, 52.7763],
						[-169.2624, 52.7548],
						[-169.2696, 52.7728]
					]],
					[[
						[-169.7613, 52.9781],
						[-169.747, 53.0211],
						[-169.6788, 53.0348],
						[-169.6644, 52.997],
						[-169.6967, 52.9584],
						[-169.7398, 52.9515],
						[-169.7613, 52.9781]
					]],
					[[
						[-169.7901, 56.618],
						[-169.6106, 56.6068],
						[-169.5065, 56.6111],
						[-169.4706, 56.5991],
						[-169.5675, 56.5407],
						[-169.6501, 56.5441],
						[-169.6824, 56.5836],
						[-169.7542, 56.5922],
						[-169.7901, 56.618]
					]],
					[[
						[-169.7757, 53.0795],
						[-169.747, 53.046],
						[-169.8008, 53.0572],
						[-169.7757, 53.0795]
					]],
					[[
						[-170.0054, 52.8467],
						[-169.9444, 52.8613],
						[-169.8618, 52.8587],
						[-169.869, 52.875],
						[-169.7721, 52.8948],
						[-169.7039, 52.8862],
						[-169.668, 52.8647],
						[-169.729, 52.7745],
						[-169.8367, 52.8175],
						[-169.9264, 52.7926],
						[-169.9946, 52.8046],
						[-170.0054, 52.8467]
					]],
					[[
						[-170.1131, 52.9017],
						[-170.0844, 52.924],
						[-170.0018, 52.9103],
						[-169.9982, 52.8836],
						[-170.0485, 52.857],
						[-170.0951, 52.8708],
						[-170.1131, 52.9017]
					]],
					[[
						[-170.1849, 52.723],
						[-170.1705, 52.7848],
						[-170.0521, 52.7694],
						[-170.0772, 52.7204],
						[-170.1849, 52.723]
					]],
					[[
						[-170.4218, 57.1695],
						[-170.3895, 57.2064],
						[-170.3105, 57.2193],
						[-170.2674, 57.2107],
						[-170.1633, 57.2296],
						[-170.1705, 57.1815],
						[-170.2854, 57.1283],
						[-170.3033, 57.1549],
						[-170.4218, 57.1695]
					]],
					[[
						[-170.8345, 52.6001],
						[-170.8166, 52.6362],
						[-170.673, 52.6981],
						[-170.5617, 52.6749],
						[-170.5582, 52.6517],
						[-170.6048, 52.6019],
						[-170.673, 52.6036],
						[-170.734, 52.5812],
						[-170.7879, 52.54],
						[-170.8417, 52.5581],
						[-170.8345, 52.6001]
					]],
					[[
						[-171.1504, 52.5847],
						[-171.1181, 52.5572],
						[-171.1755, 52.5589],
						[-171.1504, 52.5847]
					]],
					[[
						[-171.3119, 52.4936],
						[-171.2509, 52.5297],
						[-171.1935, 52.4979],
						[-171.2365, 52.4507],
						[-171.3047, 52.4498],
						[-171.3119, 52.4936]
					]],
					[[
						[-171.8395, 63.548],
						[-171.8324, 63.5798],
						[-171.7426, 63.6545],
						[-171.7534, 63.719],
						[-171.7247, 63.7344],
						[-171.7426, 63.7834],
						[-171.6673, 63.786],
						[-171.6385, 63.7499],
						[-171.6529, 63.7086],
						[-171.6098, 63.6794],
						[-171.3801, 63.6305],
						[-171.1037, 63.5892],
						[-170.9063, 63.5721],
						[-170.6981, 63.6468],
						[-170.49, 63.6966],
						[-170.2818, 63.6846],
						[-170.1777, 63.6253],
						[-170.0951, 63.6124],
						[-170.0413, 63.5231],
						[-170.0485, 63.4904],
						[-169.8582, 63.4423],
						[-169.6572, 63.4303],
						[-169.5783, 63.4028],
						[-169.546, 63.3728],
						[-169.4168, 63.3556],
						[-169.1009, 63.3384],
						[-169.0507, 63.3435],
						[-168.6845, 63.2963],
						[-168.7815, 63.1838],
						[-168.8604, 63.1468],
						[-169.0435, 63.1769],
						[-169.1978, 63.176],
						[-169.3773, 63.1511],
						[-169.5352, 63.0747],
						[-169.5711, 63.0223],
						[-169.5675, 62.9767],
						[-169.6393, 62.9372],
						[-169.747, 62.9561],
						[-169.7362, 62.9767],
						[-169.8295, 63.079],
						[-169.9444, 63.1322],
						[-170.1239, 63.1838],
						[-170.2638, 63.1795],
						[-170.3033, 63.2387],
						[-170.3643, 63.286],
						[-170.5582, 63.3547],
						[-170.8668, 63.414],
						[-171.1001, 63.4234],
						[-171.2688, 63.3856],
						[-171.3334, 63.3358],
						[-171.4627, 63.3066],
						[-171.5632, 63.335],
						[-171.739, 63.3659],
						[-171.8252, 63.4372],
						[-171.8503, 63.4853],
						[-171.8395, 63.548]
					]],
					[[
						[-172.622, 52.2986],
						[-172.5682, 52.3493],
						[-172.4497, 52.3914],
						[-172.4066, 52.3897],
						[-172.3097, 52.3562],
						[-172.3133, 52.321],
						[-172.4138, 52.2771],
						[-172.5287, 52.2539],
						[-172.6076, 52.2531],
						[-172.622, 52.2986]
					]],
					[[
						[-173.0635, 60.5026],
						[-173.042, 60.5619],
						[-172.9522, 60.6066],
						[-172.9127, 60.604],
						[-172.9271, 60.5542],
						[-172.884, 60.5078],
						[-172.7656, 60.4477],
						[-172.5933, 60.3935],
						[-172.5143, 60.3815],
						[-172.4246, 60.3927],
						[-172.22, 60.3119],
						[-172.2954, 60.2956],
						[-172.3277, 60.3274],
						[-172.5107, 60.3343],
						[-172.6041, 60.3197],
						[-172.7225, 60.3592],
						[-172.9307, 60.476],
						[-173.0635, 60.5026]
					]],
					[[
						[-173.0922, 60.6994],
						[-173.0635, 60.6951],
						[-173.0455, 60.6229],
						[-173.1173, 60.659],
						[-173.0922, 60.6994]
					]],
					[[
						[-174.047, 52.1328],
						[-173.9213, 52.1328],
						[-173.8029, 52.1062],
						[-173.7706, 52.132],
						[-173.7203, 52.1302],
						[-173.6019, 52.1534],
						[-173.5121, 52.1526],
						[-173.5337, 52.1285],
						[-173.4978, 52.1036],
						[-173.4691, 52.1165],
						[-173.3578, 52.0959],
						[-173.2753, 52.1096],
						[-173.2394, 52.1002],
						[-173.1245, 52.1096],
						[-173.1066, 52.0993],
						[-172.9594, 52.0933],
						[-173.0491, 52.0735],
						[-173.1066, 52.0787],
						[-173.1568, 52.0598],
						[-173.3147, 52.0589],
						[-173.3937, 52.0289],
						[-173.4224, 52.0426],
						[-173.4798, 52.0263],
						[-173.548, 52.0297],
						[-173.6126, 52.0512],
						[-173.7993, 52.0538],
						[-173.8316, 52.0409],
						[-173.9357, 52.0572],
						[-173.9716, 52.0993],
						[-174.0111, 52.0985],
						[-174.047, 52.1328]
					]],
					[[
						[-175.3283, 52.0272],
						[-175.1309, 52.0589],
						[-175.0914, 52.0349],
						[-175.0017, 52.0778],
						[-174.9909, 52.0589],
						[-174.9227, 52.0916],
						[-174.9048, 52.1165],
						[-174.8402, 52.0916],
						[-174.7433, 52.0985],
						[-174.7146, 52.1277],
						[-174.693, 52.107],
						[-174.5674, 52.1388],
						[-174.5638, 52.1749],
						[-174.4059, 52.1835],
						[-174.4525, 52.2187],
						[-174.2982, 52.2144],
						[-174.2515, 52.2436],
						[-174.2551, 52.2746],
						[-174.37, 52.2806],
						[-174.4525, 52.3055],
						[-174.4346, 52.3278],
						[-174.3592, 52.3141],
						[-174.3162, 52.345],
						[-174.3233, 52.3785],
						[-174.1869, 52.418],
						[-174.0685, 52.3905],
						[-173.9895, 52.3252],
						[-173.9859, 52.2986],
						[-174.0613, 52.2256],
						[-174.1833, 52.2325],
						[-174.1977, 52.1955],
						[-174.0793, 52.1277],
						[-174.09, 52.107],
						[-174.1439, 52.1251],
						[-174.2049, 52.1165],
						[-174.2192, 52.0933],
						[-174.2767, 52.0899],
						[-174.3485, 52.1096],
						[-174.4167, 52.028],
						[-174.5064, 52.0546],
						[-174.7361, 52.0074],
						[-174.7828, 52.0323],
						[-174.8868, 52.0426],
						[-174.9694, 52.0375],
						[-175.0161, 52.0074],
						[-175.095, 52.0005],
						[-175.2135, 52.0057],
						[-175.3283, 52.0272]
					]],
					[[
						[-175.5114, 51.9825],
						[-175.4504, 51.9971],
						[-175.4252, 51.9722],
						[-175.5042, 51.9687],
						[-175.5114, 51.9825]
					]],
					[[
						[-175.5401, 52.1835],
						[-175.4827, 52.1672],
						[-175.5222, 52.1569],
						[-175.5401, 52.1835]
					]],
					[[
						[-175.7411, 51.967],
						[-175.7232, 51.9748],
						[-175.5796, 51.9722],
						[-175.576, 51.9498],
						[-175.6442, 51.9524],
						[-175.7232, 51.9318],
						[-175.7411, 51.967]
					]],
					[[
						[-175.9529, 51.9833],
						[-175.9134, 51.9954],
						[-175.8129, 51.9945],
						[-175.759, 51.9172],
						[-175.8165, 51.9215],
						[-175.8775, 51.9636],
						[-175.9529, 51.9833]
					]],
					[[
						[-176.2005, 52.0787],
						[-176.1503, 52.1174],
						[-176.057, 52.1096],
						[-175.9744, 52.0409],
						[-176.0462, 52.0083],
						[-175.9924, 51.9601],
						[-176.039, 51.9309],
						[-176.1036, 51.9292],
						[-176.1682, 51.9481],
						[-176.1575, 51.9971],
						[-176.1826, 51.9988],
						[-176.2113, 52.065],
						[-176.2005, 52.0787]
					]],
					[[
						[-176.2364, 51.8261],
						[-176.2185, 51.8742],
						[-176.1754, 51.8828],
						[-176.1395, 51.8596],
						[-176.0713, 51.8682],
						[-176.1144, 51.8871],
						[-175.9924, 51.9129],
						[-175.9565, 51.8931],
						[-175.9636, 51.8459],
						[-176.0462, 51.8459],
						[-175.9995, 51.8012],
						[-176.1036, 51.8261],
						[-176.0964, 51.7918],
						[-176.1395, 51.8021],
						[-176.1575, 51.7686],
						[-176.1826, 51.8072],
						[-176.2364, 51.8261]
					]],
					[[
						[-176.9902, 51.6294],
						[-176.9507, 51.687],
						[-176.8969, 51.7007],
						[-176.8717, 51.7316],
						[-176.9184, 51.7883],
						[-176.904, 51.8115],
						[-176.7892, 51.8175],
						[-176.764, 51.8682],
						[-176.8107, 51.9275],
						[-176.7748, 51.9662],
						[-176.721, 51.9696],
						[-176.6564, 51.9524],
						[-176.5882, 52.0031],
						[-176.5559, 51.9911],
						[-176.5451, 51.9275],
						[-176.6169, 51.9026],
						[-176.6241, 51.8596],
						[-176.5774, 51.8425],
						[-176.5092, 51.8459],
						[-176.3979, 51.8682],
						[-176.2903, 51.8725],
						[-176.2651, 51.8175],
						[-176.2903, 51.7419],
						[-176.3441, 51.7316],
						[-176.4733, 51.7471],
						[-176.52, 51.7024],
						[-176.5846, 51.6921],
						[-176.6312, 51.6569],
						[-176.7138, 51.6835],
						[-176.7353, 51.6621],
						[-176.7138, 51.6208],
						[-176.7533, 51.6354],
						[-176.7999, 51.6131],
						[-176.8143, 51.6603],
						[-176.8645, 51.6852],
						[-176.9327, 51.5925],
						[-176.983, 51.6019],
						[-176.9902, 51.6294]
					]],
					[[
						[-177.464, 51.9138],
						[-177.4173, 51.9215],
						[-177.4101, 51.8992],
						[-177.4604, 51.8846],
						[-177.464, 51.9138]
					]],
					[[
						[-177.7044, 51.7076],
						[-177.6398, 51.7359],
						[-177.5537, 51.7213],
						[-177.4604, 51.7505],
						[-177.2809, 51.784],
						[-177.2055, 51.821],
						[-177.1984, 51.9309],
						[-177.1553, 51.9447],
						[-177.0979, 51.9361],
						[-177.044, 51.8983],
						[-177.1373, 51.8141],
						[-177.1194, 51.7402],
						[-177.1445, 51.7076],
						[-177.263, 51.681],
						[-177.3922, 51.7334],
						[-177.4891, 51.705],
						[-177.6327, 51.6964],
						[-177.6362, 51.6595],
						[-177.6721, 51.6638],
						[-177.7044, 51.7076]
					]],
					[[
						[-178.2249, 51.8803],
						[-178.1962, 51.9052],
						[-178.0921, 51.9198],
						[-177.9521, 51.9155],
						[-177.9126, 51.8794],
						[-177.9198, 51.8536],
						[-177.8516, 51.8261],
						[-177.7978, 51.8407],
						[-177.7332, 51.8261],
						[-177.6147, 51.8553],
						[-177.6506, 51.821],
						[-177.7978, 51.7935],
						[-177.8408, 51.7325],
						[-177.8265, 51.7059],
						[-177.8696, 51.6792],
						[-177.8983, 51.693],
						[-177.9306, 51.6552],
						[-177.9019, 51.6062],
						[-177.9306, 51.6019],
						[-177.9629, 51.65],
						[-178.0131, 51.6294],
						[-178.0706, 51.6706],
						[-178.1172, 51.6775],
						[-178.1029, 51.7016],
						[-177.9557, 51.723],
						[-177.9665, 51.778],
						[-178.0382, 51.7789],
						[-178.0849, 51.8081],
						[-178.2249, 51.8648],
						[-178.2249, 51.8803]
					]],
					[[
						[-178.6807, 51.6088],
						[-178.5659, 51.5985],
						[-178.5766, 51.583],
						[-178.6736, 51.589],
						[-178.6807, 51.6088]
					]],
					[[
						[-178.8566, 51.577],
						[-178.7597, 51.5624],
						[-178.7346, 51.5426],
						[-178.8243, 51.5469],
						[-178.8566, 51.577]
					]],
					[[
						[-178.871, 51.7952],
						[-178.8207, 51.8399],
						[-178.7489, 51.8098],
						[-178.7489, 51.7574],
						[-178.8171, 51.7488],
						[-178.871, 51.7952]
					]],
					[[
						[-178.993, 51.3811],
						[-178.9643, 51.4026],
						[-178.9284, 51.3837],
						[-178.9104, 51.3408],
						[-178.9535, 51.339],
						[-178.9894, 51.3116],
						[-178.993, 51.3811]
					]],
					[[
						[-179.1366, 51.2858],
						[-179.0935, 51.3012],
						[-179.072, 51.2506],
						[-179.1366, 51.2291],
						[-179.1366, 51.2858]
					]],
					[[
						[179.4841, 51.9825],
						[179.5272, 51.9808],
						[179.5703, 52.0108],
						[179.6636, 52.0229],
						[179.7749, 51.9705],
						[179.7426, 51.912],
						[179.6564, 51.876],
						[179.6134, 51.8717],
						[179.5236, 51.8966],
						[179.4841, 51.9215],
						[179.4841, 51.9825]
					]],
					[[
						[178.6263, 51.6371],
						[178.6443, 51.6578],
						[178.7376, 51.6328],
						[178.8058, 51.6354],
						[178.9027, 51.6148],
						[178.917, 51.5882],
						[179.0032, 51.5521],
						[179.1001, 51.4851],
						[179.1898, 51.4628],
						[179.2293, 51.4138],
						[179.3872, 51.4043],
						[179.4626, 51.376],
						[179.398, 51.3597],
						[179.3657, 51.3717],
						[179.2616, 51.3579],
						[179.2078, 51.3932],
						[179.0606, 51.455],
						[178.9529, 51.5418],
						[178.8453, 51.5787],
						[178.7842, 51.5719],
						[178.7053, 51.5933],
						[178.6263, 51.6371]
					]],
					[[
						[178.4468, 51.9782],
						[178.4791, 51.9876],
						[178.5509, 51.9739],
						[178.5904, 51.9455],
						[178.5402, 51.9034],
						[178.5043, 51.9],
						[178.4468, 51.9782]
					]],
					[[
						[178.2387, 51.8279],
						[178.3069, 51.8218],
						[178.3822, 51.7643],
						[178.3033, 51.7772],
						[178.2387, 51.8279]
					]],
					[[
						[178.0951, 52.0332],
						[178.1202, 52.0521],
						[178.1812, 52.0332],
						[178.192, 52.004],
						[178.131, 51.9868],
						[178.0951, 52.0332]
					]],
					[[
						[177.2049, 51.8966],
						[177.2911, 51.9198],
						[177.3449, 51.9627],
						[177.4849, 51.9851],
						[177.5064, 52.0392],
						[177.5639, 52.1217],
						[177.6034, 52.1371],
						[177.6751, 52.0924],
						[177.6321, 52.065],
						[177.6034, 52.016],
						[177.5316, 51.9705],
						[177.6069, 51.955],
						[177.6069, 51.9249],
						[177.5603, 51.9163],
						[177.4095, 51.9309],
						[177.3485, 51.9043],
						[177.3342, 51.8442],
						[177.2947, 51.8459],
						[177.2049, 51.8803],
						[177.2049, 51.8966]
					]],
					[[
						[175.8733, 52.3708],
						[175.9666, 52.3596],
						[175.902, 52.3364],
						[175.8733, 52.3708]
					]],
					[[
						[174.0679, 52.735],
						[174.1469, 52.7281],
						[174.1576, 52.7058],
						[174.0715, 52.7187],
						[174.0679, 52.735]
					]],
					[[
						[173.953, 52.7513],
						[174.0033, 52.7445],
						[174.0033, 52.7196],
						[173.953, 52.7513]
					]],
					[[
						[173.8669, 52.7754],
						[173.9387, 52.7522],
						[173.8956, 52.7505],
						[173.8669, 52.7754]
					]],
					[[
						[173.3572, 52.4051],
						[173.3788, 52.4318],
						[173.447, 52.455],
						[173.5295, 52.4498],
						[173.6228, 52.5074],
						[173.77, 52.5117],
						[173.709, 52.4773],
						[173.691, 52.4455],
						[173.7269, 52.3562],
						[173.6515, 52.3562],
						[173.5905, 52.4008],
						[173.4649, 52.3845],
						[173.3572, 52.4051]
					]],
					[[
						[172.4599, 52.9274],
						[172.6286, 53.0013],
						[172.747, 53.0108],
						[173.106, 52.9936],
						[173.2101, 52.9395],
						[173.2962, 52.9266],
						[173.4218, 52.8458],
						[173.429, 52.8304],
						[173.3034, 52.8235],
						[173.228, 52.8561],
						[173.167, 52.7952],
						[173.1347, 52.784],
						[172.9983, 52.7969],
						[172.905, 52.7617],
						[172.8081, 52.7891],
						[172.765, 52.8235],
						[172.7542, 52.8776],
						[172.6394, 52.9257],
						[172.5137, 52.9051],
						[172.4599, 52.9274]
					]],
					[[
						[-131.241, 55.0923],
						[-131.2087, 55.112],
						[-131.1728, 55.088],
						[-131.1908, 55.0433],
						[-131.2482, 55.0725],
						[-131.241, 55.0923]
					]],
					[[
						[-131.4851, 54.9505],
						[-131.2662, 54.9986],
						[-131.241, 54.9299],
						[-131.1944, 54.9179],
						[-131.2662, 54.8595],
						[-131.3523, 54.8586],
						[-131.4707, 54.9127],
						[-131.4851, 54.9505]
					]],
					[[
						[-131.6215, 54.9462],
						[-131.5282, 55.0021],
						[-131.4995, 54.9806],
						[-131.5928, 54.9308],
						[-131.6215, 54.9462]
					]],
					[[
						[-131.6466, 55.0356],
						[-131.5892, 55.0888],
						[-131.5964, 55.1258],
						[-131.5497, 55.143],
						[-131.6, 55.1799],
						[-131.6071, 55.24],
						[-131.5892, 55.2735],
						[-131.5497, 55.2813],
						[-131.4815, 55.2538],
						[-131.4636, 55.2237],
						[-131.4169, 55.2177],
						[-131.3559, 55.1833],
						[-131.3559, 55.0416],
						[-131.3882, 55.0124],
						[-131.4851, 55.0107],
						[-131.5318, 55.0382],
						[-131.6143, 55.0072],
						[-131.6466, 55.0356]
					]],
					[[
						[-131.8692, 55.3646],
						[-131.8548, 55.4213],
						[-131.8333, 55.4222],
						[-131.6466, 55.3062],
						[-131.6897, 55.2821],
						[-131.6933, 55.2237],
						[-131.7471, 55.1284],
						[-131.8297, 55.1919],
						[-131.862, 55.289],
						[-131.8692, 55.3646]
					]],
					[[
						[-132.4901, 56.5037],
						[-132.4758, 56.5449],
						[-132.4434, 56.5192],
						[-132.4901, 56.5037]
					]],
					[[
						[-132.5619, 56.3792],
						[-132.5547, 56.4041],
						[-132.4793, 56.4384],
						[-132.404, 56.4058],
						[-132.4076, 56.3869],
						[-132.4937, 56.3551],
						[-132.5619, 56.3792]
					]],
					[[
						[-132.6516, 56.4831],
						[-132.5798, 56.5114],
						[-132.5188, 56.5561],
						[-132.5368, 56.5132],
						[-132.6014, 56.4487],
						[-132.6516, 56.4831]
					]],
					[[
						[-132.8885, 54.9746],
						[-132.8239, 54.9797],
						[-132.8383, 54.9342],
						[-132.8885, 54.9746]
					]],
					[[
						[-133.0249, 56.2297],
						[-132.9998, 56.2752],
						[-132.9639, 56.2546],
						[-132.8849, 56.1713],
						[-132.849, 56.1128],
						[-132.8957, 56.1],
						[-132.9711, 56.1524],
						[-133.0034, 56.1558],
						[-133.0249, 56.2297]
					]],
					[[
						[-133.068, 56.3568],
						[-133.0034, 56.4307],
						[-132.928, 56.4565],
						[-132.8203, 56.4393],
						[-132.7342, 56.4582],
						[-132.6337, 56.4221],
						[-132.6193, 56.3912],
						[-132.6839, 56.3456],
						[-132.6624, 56.2752],
						[-132.7198, 56.2589],
						[-132.8778, 56.24],
						[-132.9603, 56.2958],
						[-133.0464, 56.3207],
						[-133.068, 56.3568]
					]],
					[[
						[-133.3192, 55.4265],
						[-133.2439, 55.4496],
						[-133.251, 55.4101],
						[-133.3085, 55.4007],
						[-133.3192, 55.4265]
					]],
					[[
						[-133.5633, 54.8689],
						[-133.531, 54.8689],
						[-133.4951, 54.759],
						[-133.5489, 54.7839],
						[-133.5633, 54.8689]
					]],
					[[
						[-133.6566, 55.6258],
						[-133.6243, 55.6455],
						[-133.6207, 55.6979],
						[-133.5489, 55.6721],
						[-133.5454, 55.6447],
						[-133.6243, 55.5828],
						[-133.6566, 55.6258]
					]],
					[[
						[-133.7105, 55.6747],
						[-133.6674, 55.6825],
						[-133.671, 55.6515],
						[-133.7105, 55.6747]
					]],
					[[
						[-133.7894, 55.4582],
						[-133.7607, 55.5373],
						[-133.732, 55.5587],
						[-133.6171, 55.551],
						[-133.5813, 55.5373],
						[-133.5884, 55.5072],
						[-133.6459, 55.4711],
						[-133.6171, 55.4574],
						[-133.5454, 55.4917],
						[-133.5274, 55.5287],
						[-133.4808, 55.5124],
						[-133.4126, 55.5708],
						[-133.3444, 55.5691],
						[-133.2869, 55.5356],
						[-133.3049, 55.484],
						[-133.3587, 55.4539],
						[-133.4269, 55.4668],
						[-133.4736, 55.4368],
						[-133.4126, 55.4179],
						[-133.4197, 55.3861],
						[-133.4987, 55.368],
						[-133.5777, 55.3251],
						[-133.61, 55.2418],
						[-133.6925, 55.3088],
						[-133.6315, 55.3612],
						[-133.6351, 55.4282],
						[-133.7392, 55.472],
						[-133.7894, 55.4582]
					]],
					[[
						[-133.8181, 55.9642],
						[-133.7787, 55.9831],
						[-133.6925, 56.0707],
						[-133.592, 56.0793],
						[-133.5525, 56.0965],
						[-133.6315, 56.1077],
						[-133.6351, 56.1481],
						[-133.5813, 56.1292],
						[-133.5489, 56.1584],
						[-133.6782, 56.2116],
						[-133.6279, 56.2365],
						[-133.6638, 56.3104],
						[-133.5813, 56.3525],
						[-133.4197, 56.3319],
						[-133.1649, 56.3173],
						[-133.0788, 56.2469],
						[-133.0393, 56.1841],
						[-133.0572, 56.1249],
						[-133.007, 56.1197],
						[-132.9603, 56.0613],
						[-132.8993, 56.0201],
						[-132.8383, 56.0244],
						[-132.6337, 55.9221],
						[-132.5906, 55.8775],
						[-132.4722, 55.7821],
						[-132.4614, 55.6739],
						[-132.3573, 55.6489],
						[-132.3286, 55.5785],
						[-132.2999, 55.551],
						[-132.1886, 55.5081],
						[-132.1419, 55.4582],
						[-132.1778, 55.4531],
						[-132.246, 55.4926],
						[-132.5152, 55.5768],
						[-132.526, 55.5502],
						[-132.6086, 55.4866],
						[-132.5691, 55.4797],
						[-132.5188, 55.5201],
						[-132.4901, 55.5038],
						[-132.4076, 55.5124],
						[-132.3896, 55.4806],
						[-132.3178, 55.4694],
						[-132.2819, 55.4445],
						[-132.4183, 55.4325],
						[-132.3896, 55.4015],
						[-132.2747, 55.4196],
						[-132.2245, 55.3749],
						[-132.1671, 55.3629],
						[-132.1276, 55.2881],
						[-132.0989, 55.2804],
						[-132.1419, 55.2383],
						[-132.2137, 55.2461],
						[-132.2066, 55.2186],
						[-132.1276, 55.1997],
						[-132.0881, 55.2065],
						[-132.0773, 55.2461],
						[-132.0271, 55.277],
						[-131.9948, 55.2589],
						[-131.9768, 55.1808],
						[-132.0307, 55.1516],
						[-132.0163, 55.1206],
						[-132.0953, 55.0399],
						[-132.1994, 55.0055],
						[-132.1348, 54.9858],
						[-132.0379, 55.0364],
						[-131.984, 55.0278],
						[-131.984, 54.8535],
						[-131.9589, 54.7916],
						[-132.0307, 54.7014],
						[-132.1635, 54.6937],
						[-132.2568, 54.7349],
						[-132.3071, 54.7186],
						[-132.3681, 54.7512],
						[-132.3717, 54.783],
						[-132.5081, 54.7813],
						[-132.447, 54.8268],
						[-132.3501, 54.8182],
						[-132.3465, 54.8517],
						[-132.4147, 54.8878],
						[-132.4829, 54.899],
						[-132.6086, 54.966],
						[-132.5763, 54.9986],
						[-132.5404, 55.0974],
						[-132.5942, 55.1052],
						[-132.6337, 55.0553],
						[-132.6086, 55.1421],
						[-132.5798, 55.1662],
						[-132.6193, 55.1997],
						[-132.6373, 55.1524],
						[-132.7342, 55.1344],
						[-132.6839, 55.0459],
						[-132.7485, 54.9961],
						[-132.9172, 55.0442],
						[-132.9316, 55.0742],
						[-132.8706, 55.1241],
						[-132.9352, 55.21],
						[-133.007, 55.2057],
						[-133.0429, 55.2486],
						[-133.1182, 55.2512],
						[-133.0752, 55.1842],
						[-133.0106, 55.1284],
						[-132.989, 55.0665],
						[-133.0141, 55.0356],
						[-132.9603, 55.021],
						[-132.8885, 54.8964],
						[-132.8024, 54.8741],
						[-132.8167, 54.9239],
						[-132.7306, 54.9394],
						[-132.6301, 54.8835],
						[-132.6157, 54.777],
						[-132.666, 54.7633],
						[-132.7306, 54.8268],
						[-132.7557, 54.8208],
						[-132.6732, 54.6748],
						[-132.7773, 54.6748],
						[-132.8706, 54.7023],
						[-132.8778, 54.7538],
						[-132.9495, 54.7882],
						[-133.1254, 54.9402],
						[-133.1613, 54.9591],
						[-133.1685, 55.0313],
						[-133.2116, 55.0399],
						[-133.2403, 55.0923],
						[-133.1362, 55.0992],
						[-133.2331, 55.1275],
						[-133.2223, 55.2297],
						[-133.3408, 55.2057],
						[-133.3767, 55.228],
						[-133.4413, 55.2117],
						[-133.4736, 55.2478],
						[-133.452, 55.3199],
						[-133.3336, 55.3457],
						[-133.2941, 55.2916],
						[-133.2331, 55.3088],
						[-133.2798, 55.3337],
						[-133.2582, 55.3672],
						[-133.208, 55.3844],
						[-133.1577, 55.3723],
						[-133.0788, 55.4101],
						[-133.1828, 55.4926],
						[-133.1326, 55.5029],
						[-133.19, 55.5304],
						[-133.1757, 55.5871],
						[-133.2618, 55.5691],
						[-133.4377, 55.6438],
						[-133.452, 55.6678],
						[-133.3946, 55.667],
						[-133.4269, 55.7099],
						[-133.4161, 55.74],
						[-133.5131, 55.7555],
						[-133.4915, 55.7065],
						[-133.5274, 55.6953],
						[-133.6423, 55.7288],
						[-133.6997, 55.7787],
						[-133.6566, 55.8233],
						[-133.5561, 55.8354],
						[-133.4233, 55.789],
						[-133.3228, 55.819],
						[-133.3838, 55.8766],
						[-133.4484, 55.8903],
						[-133.4915, 55.8732],
						[-133.5238, 55.8878],
						[-133.4951, 55.9135],
						[-133.5023, 55.9496],
						[-133.4772, 56.0037],
						[-133.4951, 56.0175],
						[-133.5418, 55.9771],
						[-133.6387, 55.9213],
						[-133.7176, 55.8929],
						[-133.8002, 55.9256],
						[-133.8181, 55.9642]
					]],
					[[
						[-133.8828, 57.3009],
						[-133.8289, 57.3224],
						[-133.7787, 57.294],
						[-133.8756, 57.2674],
						[-133.8828, 57.3009]
					]],
					[[
						[-133.9438, 55.9127],
						[-133.8935, 55.9367],
						[-133.854, 55.9316],
						[-133.8397, 55.8869],
						[-133.8684, 55.8457],
						[-133.9043, 55.8483],
						[-133.9438, 55.9127]
					]],
					[[
						[-134.3673, 55.911],
						[-134.2668, 55.9299],
						[-134.2632, 55.8929],
						[-134.152, 55.9213],
						[-134.1232, 55.9857],
						[-134.0981, 55.984],
						[-134.1304, 55.8972],
						[-134.2094, 55.8766],
						[-134.2812, 55.8285],
						[-134.3458, 55.8431],
						[-134.3171, 55.8689],
						[-134.3673, 55.911]
					]],
					[[
						[-134.414, 56.8482],
						[-134.353, 56.8929],
						[-134.2955, 56.9075],
						[-134.2704, 56.9358],
						[-134.152, 56.935],
						[-134.1484, 56.9573],
						[-134.0479, 56.9229],
						[-134.0084, 56.8516],
						[-133.9438, 56.8052],
						[-133.8325, 56.8035],
						[-133.8002, 56.7855],
						[-133.7643, 56.8078],
						[-133.8684, 56.8456],
						[-133.9151, 56.9143],
						[-133.9222, 56.9616],
						[-134.0479, 57.0295],
						[-134.0084, 57.075],
						[-133.8863, 57.0973],
						[-133.7033, 57.0655],
						[-133.6136, 57.0578],
						[-133.4449, 57.02],
						[-133.3336, 57.0028],
						[-133.3085, 57.0149],
						[-133.1039, 57.0054],
						[-132.9962, 56.9418],
						[-132.9459, 56.8714],
						[-132.9352, 56.8233],
						[-132.9029, 56.8035],
						[-132.8203, 56.7941],
						[-132.7629, 56.7528],
						[-132.7449, 56.7133],
						[-132.6265, 56.6687],
						[-132.5942, 56.6137],
						[-132.5404, 56.5845],
						[-132.6516, 56.5518],
						[-132.7234, 56.5157],
						[-132.8167, 56.4951],
						[-132.8742, 56.5089],
						[-132.9854, 56.5123],
						[-133.1398, 56.5329],
						[-133.1218, 56.4917],
						[-133.2044, 56.4479],
						[-133.3479, 56.4694],
						[-133.4161, 56.4943],
						[-133.4592, 56.4539],
						[-133.5131, 56.4367],
						[-133.6028, 56.435],
						[-133.6638, 56.4479],
						[-133.6746, 56.5226],
						[-133.7141, 56.5501],
						[-133.8217, 56.4951],
						[-133.7751, 56.4702],
						[-133.7823, 56.3929],
						[-133.8648, 56.3903],
						[-133.872, 56.3637],
						[-133.8361, 56.3199],
						[-133.8756, 56.2761],
						[-133.8828, 56.2228],
						[-133.9438, 56.1798],
						[-133.9294, 56.1463],
						[-133.9617, 56.0914],
						[-133.9976, 56.0819],
						[-134.0873, 56.0948],
						[-134.1376, 56.0063],
						[-134.1914, 56.0226],
						[-134.2345, 56.0673],
						[-134.2596, 56.1214],
						[-134.2632, 56.2151],
						[-134.2991, 56.2898],
						[-134.2919, 56.3542],
						[-134.2417, 56.3955],
						[-134.2525, 56.4444],
						[-134.1986, 56.5312],
						[-134.2417, 56.5553],
						[-134.3206, 56.5544],
						[-134.3027, 56.6205],
						[-134.3745, 56.6687],
						[-134.4032, 56.7331],
						[-134.396, 56.77],
						[-134.414, 56.8482]
					]],
					[[
						[-134.9595, 58.404],
						[-134.8052, 58.3215],
						[-134.7801, 58.2811],
						[-134.7298, 58.2734],
						[-134.7011, 58.1617],
						[-134.6329, 58.1626],
						[-134.5575, 58.1952],
						[-134.5181, 58.1754],
						[-134.4463, 58.1737],
						[-134.3709, 58.1488],
						[-134.1771, 58.16],
						[-134.1878, 58.0835],
						[-134.0981, 58.0191],
						[-134.0909, 57.9744],
						[-134.0156, 57.9298],
						[-133.9653, 57.855],
						[-133.9043, 57.8078],
						[-133.8971, 57.6858],
						[-133.8217, 57.6342],
						[-133.8074, 57.5784],
						[-133.8397, 57.5767],
						[-133.8576, 57.6171],
						[-133.994, 57.7201],
						[-134.0299, 57.8189],
						[-134.0981, 57.8507],
						[-134.1304, 57.8963],
						[-134.2058, 57.8963],
						[-134.1125, 57.8095],
						[-134.0981, 57.7803],
						[-134.1448, 57.7605],
						[-134.0191, 57.6566],
						[-133.9366, 57.6145],
						[-133.9438, 57.5612],
						[-133.8576, 57.4641],
						[-133.9258, 57.4693],
						[-133.872, 57.3817],
						[-133.872, 57.3585],
						[-133.9617, 57.3052],
						[-134.0335, 57.3276],
						[-134.0945, 57.3267],
						[-134.0802, 57.2975],
						[-134.1555, 57.2081],
						[-134.2919, 57.1368],
						[-134.3781, 57.1154],
						[-134.3853, 57.087],
						[-134.4965, 57.0312],
						[-134.6006, 57.0338],
						[-134.6329, 57.1102],
						[-134.6473, 57.2262],
						[-134.5719, 57.2949],
						[-134.5181, 57.3147],
						[-134.5755, 57.3413],
						[-134.5791, 57.4006],
						[-134.5288, 57.4057],
						[-134.4858, 57.3722],
						[-134.4642, 57.392],
						[-134.6078, 57.5131],
						[-134.6114, 57.5629],
						[-134.676, 57.6145],
						[-134.6939, 57.6849],
						[-134.7334, 57.7219],
						[-134.7047, 57.8292],
						[-134.7478, 57.8988],
						[-134.7585, 57.9804],
						[-134.798, 58.0586],
						[-134.7837, 58.0964],
						[-134.8555, 58.1763],
						[-134.9165, 58.215],
						[-134.9488, 58.2811],
						[-134.9703, 58.3679],
						[-134.9595, 58.404]
					]],
					[[
						[-135.0277, 58.5199],
						[-134.9021, 58.4813],
						[-134.8052, 58.3739],
						[-134.8411, 58.3696],
						[-134.9201, 58.4684],
						[-135.0026, 58.4847],
						[-135.0277, 58.5199]
					]],
					[[
						[-135.3508, 59.0199],
						[-135.2969, 58.9589],
						[-135.2969, 58.9194],
						[-135.3328, 58.9443],
						[-135.3508, 59.0199]
					]],
					[[
						[-135.6953, 57.3628],
						[-135.6379, 57.3722],
						[-135.6343, 57.3946],
						[-135.5338, 57.4504],
						[-135.5482, 57.5088],
						[-135.419, 57.5638],
						[-135.3544, 57.5535],
						[-135.2646, 57.4805],
						[-135.2108, 57.4891],
						[-135.1462, 57.4392],
						[-135.0636, 57.4186],
						[-134.9272, 57.422],
						[-134.8483, 57.41],
						[-134.8052, 57.3422],
						[-134.8124, 57.3001],
						[-134.8842, 57.3301],
						[-134.9524, 57.3284],
						[-134.956, 57.3095],
						[-134.8806, 57.288],
						[-134.8411, 57.2485],
						[-134.737, 57.0097],
						[-134.7406, 56.9487],
						[-134.6975, 56.9006],
						[-134.6975, 56.8508],
						[-134.6329, 56.7288],
						[-134.615, 56.6369],
						[-134.6329, 56.5407],
						[-134.6724, 56.5415],
						[-134.6437, 56.4719],
						[-134.6329, 56.2658],
						[-134.6652, 56.1704],
						[-134.7047, 56.1756],
						[-134.8052, 56.2357],
						[-134.8411, 56.3096],
						[-134.9165, 56.3603],
						[-135.06, 56.5424],
						[-135.0457, 56.5836],
						[-134.9667, 56.6034],
						[-135.0313, 56.6291],
						[-135.1246, 56.6025],
						[-135.1749, 56.6781],
						[-135.2144, 56.6652],
						[-135.2754, 56.7013],
						[-135.3185, 56.7485],
						[-135.3113, 56.7778],
						[-135.3615, 56.7589],
						[-135.3974, 56.7795],
						[-135.5051, 56.7812],
						[-135.5733, 56.8637],
						[-135.5051, 56.8662],
						[-135.4692, 56.8413],
						[-135.4333, 56.8525],
						[-135.4441, 56.9427],
						[-135.3759, 56.9882],
						[-135.3544, 57.0209],
						[-135.4943, 57.0947],
						[-135.4082, 57.1489],
						[-135.4226, 57.1678],
						[-135.3723, 57.1978],
						[-135.358, 57.2434],
						[-135.4764, 57.2571],
						[-135.5518, 57.2331],
						[-135.602, 57.3009],
						[-135.6953, 57.3628]
					]],
					[[
						[-135.7277, 58.3653],
						[-135.6702, 58.3808],
						[-135.602, 58.3747],
						[-135.5446, 58.3301],
						[-135.6487, 58.3249],
						[-135.7277, 58.3653]
					]],
					[[
						[-135.8533, 57.2408],
						[-135.8389, 57.282],
						[-135.8497, 57.3387],
						[-135.76, 57.3465],
						[-135.6379, 57.2932],
						[-135.5841, 57.2296],
						[-135.541, 57.2133],
						[-135.48, 57.2391],
						[-135.3974, 57.2305],
						[-135.4549, 57.16],
						[-135.541, 57.148],
						[-135.6379, 57.0097],
						[-135.681, 57.0149],
						[-135.8246, 56.9891],
						[-135.8569, 56.996],
						[-135.8461, 57.0836],
						[-135.7564, 57.124],
						[-135.7528, 57.1669],
						[-135.8317, 57.1703],
						[-135.8246, 57.1953],
						[-135.8712, 57.2219],
						[-135.8533, 57.2408]
					]],
					[[
						[-136.1548, 58.5972],
						[-136.1225, 58.6093],
						[-136.0973, 58.5586],
						[-136.1548, 58.5972]
					]],
					[[
						[-136.1548, 58.2837],
						[-136.0471, 58.3189],
						[-136.0363, 58.2734],
						[-136.0938, 58.2571],
						[-136.1548, 58.2837]
					]],
					[[
						[-136.5747, 57.9272],
						[-136.5568, 58.0767],
						[-136.4993, 58.105],
						[-136.4706, 58.0973],
						[-136.3665, 58.1488],
						[-136.3558, 58.1926],
						[-136.3988, 58.2717],
						[-136.284, 58.2699],
						[-136.284, 58.2236],
						[-136.2409, 58.172],
						[-136.1404, 58.2244],
						[-136.0363, 58.2193],
						[-135.9753, 58.2021],
						[-135.9143, 58.2442],
						[-135.7851, 58.2871],
						[-135.7348, 58.2399],
						[-135.5877, 58.2141],
						[-135.4979, 58.1686],
						[-135.541, 58.1016],
						[-135.6523, 58.0363],
						[-135.6379, 57.9942],
						[-135.5948, 57.9899],
						[-135.5661, 58.0414],
						[-135.4513, 58.1342],
						[-135.3974, 58.1445],
						[-135.2754, 58.0973],
						[-135.1103, 58.0887],
						[-135.0708, 58.0612],
						[-134.9667, 58.0474],
						[-134.9129, 57.9796],
						[-134.9272, 57.922],
						[-135.0062, 57.8842],
						[-135.139, 57.9263],
						[-135.1749, 57.9194],
						[-135.1318, 57.8851],
						[-135.0672, 57.8696],
						[-134.9488, 57.8052],
						[-135.0241, 57.7803],
						[-135.1139, 57.7786],
						[-135.0995, 57.7545],
						[-135.0241, 57.7433],
						[-134.9416, 57.764],
						[-134.9165, 57.6823],
						[-134.8662, 57.6231],
						[-134.8231, 57.5002],
						[-134.8734, 57.4641],
						[-135.0241, 57.4547],
						[-135.0852, 57.465],
						[-135.2431, 57.5466],
						[-135.4764, 57.6317],
						[-135.5733, 57.6746],
						[-135.6523, 57.6523],
						[-135.6846, 57.6772],
						[-135.6989, 57.6497],
						[-135.5913, 57.6025],
						[-135.5123, 57.5956],
						[-135.5877, 57.5689],
						[-135.5661, 57.5475],
						[-135.5661, 57.4856],
						[-135.5984, 57.4427],
						[-135.7097, 57.3679],
						[-135.8928, 57.4083],
						[-135.8963, 57.4409],
						[-136.0471, 57.514],
						[-136.1189, 57.5913],
						[-136.162, 57.5586],
						[-136.2302, 57.5956],
						[-136.266, 57.6634],
						[-136.2517, 57.6849],
						[-136.3163, 57.7588],
						[-136.3091, 57.7794],
						[-136.3665, 57.8284],
						[-136.4599, 57.8542],
						[-136.485, 57.8963],
						[-136.5747, 57.9272]
					]],
					[[
						[-145.6556, 60.3678],
						[-145.5551, 60.3557],
						[-145.5121, 60.3179],
						[-145.609, 60.3102],
						[-145.6556, 60.3678]
					]],
					[[
						[-146.011, 60.3961],
						[-145.9535, 60.4081],
						[-145.7454, 60.3858],
						[-145.7561, 60.3592],
						[-145.8315, 60.3506],
						[-146.011, 60.3961]
					]],
					[[
						[-147.3211, 60.8806],
						[-147.2277, 60.9107],
						[-147.1308, 60.9133],
						[-147.077, 60.8995],
						[-147.1416, 60.854],
						[-147.3211, 60.8806]
					]],
					[[
						[-147.5005, 60.6538],
						[-147.4862, 60.7277],
						[-147.3857, 60.7415],
						[-147.3103, 60.665],
						[-147.3498, 60.6272],
						[-147.4826, 60.6186],
						[-147.5005, 60.6538]
					]],
					[[
						[-147.5041, 60.2535],
						[-147.4969, 60.2655],
						[-147.3426, 60.3059],
						[-147.339, 60.275],
						[-147.4826, 60.2243],
						[-147.5041, 60.2535]
					]],
					[[
						[-147.9277, 59.7836],
						[-147.8989, 59.8016],
						[-147.8882, 59.8626],
						[-147.8056, 59.8729],
						[-147.8164, 59.909],
						[-147.741, 59.9554],
						[-147.6836, 59.9614],
						[-147.6944, 59.9967],
						[-147.5867, 60.0259],
						[-147.5544, 60.0516],
						[-147.4, 60.1161],
						[-147.3211, 60.2174],
						[-147.2242, 60.2552],
						[-147.2349, 60.2896],
						[-147.199, 60.3506],
						[-147.1021, 60.3798],
						[-147.0878, 60.3429],
						[-147.0016, 60.3446],
						[-147.0626, 60.3008],
						[-147.0303, 60.2862],
						[-146.9442, 60.3102],
						[-146.9155, 60.2913],
						[-146.955, 60.2595],
						[-147.1775, 60.1582],
						[-147.3534, 60.0473],
						[-147.3821, 59.9786],
						[-147.5005, 59.9271],
						[-147.3929, 59.8781],
						[-147.4611, 59.842],
						[-147.5436, 59.8351],
						[-147.6226, 59.848],
						[-147.6836, 59.7991],
						[-147.7159, 59.8042],
						[-147.8774, 59.7638],
						[-147.9277, 59.7836]
					]],
					[[
						[-147.9492, 60.2441],
						[-147.7913, 60.476],
						[-147.7518, 60.4408],
						[-147.7087, 60.452],
						[-147.7231, 60.5087],
						[-147.5651, 60.5808],
						[-147.5651, 60.5344],
						[-147.6082, 60.5069],
						[-147.619, 60.4365],
						[-147.6908, 60.4047],
						[-147.619, 60.3686],
						[-147.7051, 60.2853],
						[-147.6979, 60.2458],
						[-147.759, 60.1564],
						[-147.8451, 60.1951],
						[-147.8559, 60.2166],
						[-147.9456, 60.2226],
						[-147.9492, 60.2441]
					]],
					[[
						[-148.021, 60.7251],
						[-147.9671, 60.7518],
						[-147.9061, 60.7354],
						[-147.8451, 60.6942],
						[-147.9313, 60.6555],
						[-148.021, 60.7251]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "04",
			"properties": { "name": "Arizona" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-114.7195, 32.7189],
					[-114.7016, 32.7456],
					[-114.6154, 32.7344],
					[-114.5257, 32.7567],
					[-114.5329, 32.7911],
					[-114.4683, 32.8452],
					[-114.4647, 32.9131],
					[-114.5185, 33.0299],
					[-114.5759, 33.0368],
					[-114.6011, 33.0256],
					[-114.6477, 33.0471],
					[-114.6585, 33.0325],
					[-114.7087, 33.0909],
					[-114.6836, 33.1485],
					[-114.6764, 33.2704],
					[-114.7303, 33.3057],
					[-114.698, 33.3615],
					[-114.7267, 33.4053],
					[-114.6585, 33.413],
					[-114.558, 33.5316],
					[-114.5257, 33.5522],
					[-114.54, 33.5917],
					[-114.5257, 33.6862],
					[-114.497, 33.6965],
					[-114.5042, 33.7601],
					[-114.5293, 33.8151],
					[-114.5257, 33.8589],
					[-114.5042, 33.8641],
					[-114.5113, 33.9113],
					[-114.5365, 33.9285],
					[-114.436, 34.0281],
					[-114.436, 34.0874],
					[-114.4072, 34.1115],
					[-114.3247, 34.1364],
					[-114.2888, 34.1707],
					[-114.2278, 34.1888],
					[-114.2242, 34.2051],
					[-114.1345, 34.2609],
					[-114.138, 34.303],
					[-114.1775, 34.3494],
					[-114.2278, 34.3657],
					[-114.339, 34.4517],
					[-114.3857, 34.4577],
					[-114.3785, 34.5075],
					[-114.4719, 34.7128],
					[-114.5544, 34.7669],
					[-114.5795, 34.8262],
					[-114.637, 34.8752],
					[-114.6334, 35.0015],
					[-114.6118, 35.0831],
					[-114.6477, 35.102],
					[-114.5724, 35.1389],
					[-114.5724, 35.2008],
					[-114.6047, 35.3537],
					[-114.6262, 35.4095],
					[-114.6657, 35.4499],
					[-114.68, 35.4997],
					[-114.6585, 35.5306],
					[-114.6585, 35.6191],
					[-114.6908, 35.6518],
					[-114.68, 35.6853],
					[-114.7052, 35.7119],
					[-114.6944, 35.7557],
					[-114.7123, 35.8064],
					[-114.698, 35.8545],
					[-114.6657, 35.8751],
					[-114.7016, 35.9017],
					[-114.7446, 35.9851],
					[-114.7303, 36.022],
					[-114.7554, 36.0907],
					[-114.6657, 36.1174],
					[-114.6334, 36.1423],
					[-114.6154, 36.1303],
					[-114.5724, 36.1517],
					[-114.5113, 36.1509],
					[-114.5042, 36.1294],
					[-114.418, 36.1457],
					[-114.3714, 36.1431],
					[-114.3067, 36.0821],
					[-114.3139, 36.0581],
					[-114.2385, 36.0143],
					[-114.1524, 36.0237],
					[-114.0663, 36.1809],
					[-114.0483, 36.1938],
					[-114.0447, 36.3914],
					[-114.0519, 36.6251],
					[-114.0519, 37.0005],
					[-113.334, 37.0005],
					[-112.5372, 37.0005],
					[-111.4138, 37.0013],
					[-111.0656, 37.0022],
					[-110.4913, 37.0039],
					[-110.4698, 36.9979],
					[-109.6263, 36.9979],
					[-109.0448, 36.9988],
					[-109.0448, 35.5461],
					[-109.0484, 35.3425],
					[-109.0448, 35.3167],
					[-109.0448, 34.5221],
					[-109.0484, 33.9998],
					[-109.0448, 33.8752],
					[-109.0484, 33.7781],
					[-109.0484, 31.3324],
					[-109.2781, 31.3341],
					[-109.9996, 31.3333],
					[-110.459, 31.3324],
					[-111.0764, 31.3324],
					[-112.3649, 31.7413],
					[-113.1259, 31.9724],
					[-113.492, 32.0892],
					[-114.8128, 32.4939],
					[-114.7949, 32.5506],
					[-114.8128, 32.5652],
					[-114.8092, 32.6167],
					[-114.7805, 32.6304],
					[-114.7195, 32.7189]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "08",
			"properties": { "name": "Colorado" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-109.0484, 41.0003],
					[-108.1798, 41.0003],
					[-107.6271, 41.002],
					[-106.4534, 41.002],
					[-106.2165, 40.9977],
					[-105.7247, 40.9968],
					[-104.8561, 40.9977],
					[-104.831, 40.9994],
					[-104.0521, 41.0011],
					[-103.04, 41.002],
					[-102.5554, 41.002],
					[-102.0529, 41.002],
					[-102.0529, 40.0029],
					[-102.0493, 39.6756],
					[-102.0493, 39.3028],
					[-102.0457, 39.137],
					[-102.0457, 38.046],
					[-102.0421, 37.9283],
					[-102.0421, 36.9928],
					[-102.699, 36.9953],
					[-102.7779, 36.9996],
					[-103.0005, 37.0005],
					[-103.7327, 36.9979],
					[-104.3393, 36.9936],
					[-104.9997, 36.9936],
					[-105.1217, 36.9953],
					[-106.2021, 36.9945],
					[-106.8697, 36.9928],
					[-106.8769, 37.0005],
					[-107.4225, 36.9996],
					[-108.248, 36.9988],
					[-109.0448, 36.9988],
					[-109.0448, 37.4996],
					[-109.0412, 37.5305],
					[-109.0412, 38.1645],
					[-109.0592, 38.2753],
					[-109.0592, 38.7195],
					[-109.052, 38.905],
					[-109.052, 40.5003],
					[-109.0484, 40.5398],
					[-109.0484, 41.0003]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "12",
			"properties": { "name": "Florida" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-80.7504, 24.8577],
						[-80.7002, 24.9092],
						[-80.6822, 24.8809],
						[-80.7325, 24.8431],
						[-80.7504, 24.8577]
					]],
					[[
						[-80.8509, 24.8036],
						[-80.8186, 24.8371],
						[-80.7791, 24.8405],
						[-80.7971, 24.8121],
						[-80.8509, 24.8036]
					]],
					[[
						[-81.1237, 24.7073],
						[-80.9371, 24.7847],
						[-80.9155, 24.7589],
						[-81.0771, 24.6902],
						[-81.1237, 24.7073]
					]],
					[[
						[-81.8129, 24.569],
						[-81.7447, 24.6601],
						[-81.6729, 24.6996],
						[-81.5832, 24.7365],
						[-81.5724, 24.7563],
						[-81.4432, 24.813],
						[-81.3068, 24.7554],
						[-81.2924, 24.7108],
						[-81.2422, 24.6738],
						[-81.2565, 24.6644],
						[-81.4001, 24.6232],
						[-81.4109, 24.6463],
						[-81.5078, 24.642],
						[-81.5185, 24.6214],
						[-81.6837, 24.5587],
						[-81.8129, 24.5458],
						[-81.8129, 24.569]
					]],
					[[
						[-82.0139, 24.5433],
						[-81.8739, 24.5639],
						[-81.9098, 24.5269],
						[-81.9708, 24.5149],
						[-82.0175, 24.5218],
						[-82.0139, 24.5433]
					]],
					[[
						[-82.1646, 24.563],
						[-82.1431, 24.5931],
						[-82.1, 24.5845],
						[-82.118, 24.5493],
						[-82.1646, 24.563]
					]],
					[[
						[-82.2651, 26.6986],
						[-82.2472, 26.6832],
						[-82.179, 26.5019],
						[-82.1503, 26.4779],
						[-82.0892, 26.4555],
						[-82.0641, 26.4701],
						[-82.0139, 26.4521],
						[-82.0641, 26.4255],
						[-82.0964, 26.4246],
						[-82.1718, 26.4676],
						[-82.1969, 26.4916],
						[-82.2436, 26.5904],
						[-82.2651, 26.6557],
						[-82.2651, 26.6986]
					]],
					[[
						[-82.9327, 24.6326],
						[-82.8789, 24.6438],
						[-82.8645, 24.624],
						[-82.9363, 24.6154],
						[-82.9327, 24.6326]
					]],
					[[
						[-84.6699, 29.7947],
						[-84.5659, 29.8351],
						[-84.5659, 29.8102],
						[-84.6233, 29.7784],
						[-84.6628, 29.7732],
						[-84.6699, 29.7947]
					]],
					[[
						[-85.0037, 31.0008],
						[-84.9822, 30.9329],
						[-84.9427, 30.8883],
						[-84.9356, 30.817],
						[-84.914, 30.7525],
						[-84.8638, 30.7113],
						[-84.3756, 30.6898],
						[-83.4388, 30.6417],
						[-82.6994, 30.5979],
						[-82.2149, 30.5687],
						[-82.2328, 30.5567],
						[-82.2256, 30.5077],
						[-82.2005, 30.4854],
						[-82.2113, 30.4244],
						[-82.1718, 30.3591],
						[-82.039, 30.3703],
						[-82.039, 30.4347],
						[-82.0175, 30.4751],
						[-82.0067, 30.5773],
						[-82.0498, 30.6555],
						[-82.0354, 30.707],
						[-82.039, 30.7491],
						[-82.0103, 30.7628],
						[-82.0246, 30.7835],
						[-81.9816, 30.7766],
						[-81.9493, 30.8273],
						[-81.8954, 30.8213],
						[-81.8703, 30.7929],
						[-81.8093, 30.7903],
						[-81.7447, 30.7663],
						[-81.7195, 30.7448],
						[-81.6011, 30.7285],
						[-81.5401, 30.713],
						[-81.4898, 30.7259],
						[-81.4432, 30.7096],
						[-81.4288, 30.6984],
						[-81.4432, 30.6013],
						[-81.4324, 30.5223],
						[-81.4468, 30.5034],
						[-81.4109, 30.4819],
						[-81.3965, 30.4003],
						[-81.3929, 30.3033],
						[-81.3104, 29.9691],
						[-81.2637, 29.8583],
						[-81.2637, 29.8144],
						[-81.1632, 29.555],
						[-81.0483, 29.3076],
						[-80.9443, 29.1109],
						[-80.711, 28.7569],
						[-80.5746, 28.5851],
						[-80.5602, 28.531],
						[-80.5243, 28.4597],
						[-80.5889, 28.4107],
						[-80.6033, 28.3644],
						[-80.6069, 28.2896],
						[-80.5889, 28.1779],
						[-80.5674, 28.0955],
						[-80.51, 27.9709],
						[-80.3843, 27.7398],
						[-80.3305, 27.5972],
						[-80.3125, 27.5251],
						[-80.2946, 27.5001],
						[-80.2551, 27.3799],
						[-80.1941, 27.2493],
						[-80.1618, 27.1926],
						[-80.1403, 27.1118],
						[-80.0936, 27.0182],
						[-80.0326, 26.7966],
						[-80.0362, 26.5947],
						[-80.0613, 26.4444],
						[-80.0792, 26.264],
						[-80.09, 26.2322],
						[-80.1187, 25.9865],
						[-80.1187, 25.8413],
						[-80.1295, 25.7726],
						[-80.1546, 25.703],
						[-80.1546, 25.6652],
						[-80.1761, 25.6849],
						[-80.1654, 25.7288],
						[-80.1833, 25.7459],
						[-80.2408, 25.7245],
						[-80.2766, 25.6368],
						[-80.2982, 25.6222],
						[-80.3018, 25.5673],
						[-80.3305, 25.5329],
						[-80.3377, 25.4659],
						[-80.3197, 25.4375],
						[-80.3269, 25.398],
						[-80.3054, 25.3843],
						[-80.3341, 25.3387],
						[-80.3628, 25.3276],
						[-80.3664, 25.2855],
						[-80.291, 25.319],
						[-80.2013, 25.4856],
						[-80.1761, 25.5209],
						[-80.1761, 25.4856],
						[-80.2049, 25.4143],
						[-80.3269, 25.2236],
						[-80.3592, 25.1532],
						[-80.4489, 25.0733],
						[-80.571, 24.9539],
						[-80.6571, 24.8972],
						[-80.6679, 24.9075],
						[-80.5458, 25.0123],
						[-80.4956, 25.0467],
						[-80.4705, 25.0922],
						[-80.4597, 25.1618],
						[-80.4418, 25.1893],
						[-80.4633, 25.209],
						[-80.4956, 25.1996],
						[-80.5207, 25.2219],
						[-80.5423, 25.2065],
						[-80.6643, 25.1876],
						[-80.7145, 25.1523],
						[-80.7468, 25.1472],
						[-80.8115, 25.1858],
						[-80.8761, 25.1747],
						[-80.9012, 25.1394],
						[-80.9981, 25.124],
						[-81.0806, 25.1188],
						[-81.1417, 25.1635],
						[-81.1704, 25.2219],
						[-81.1632, 25.2898],
						[-81.1417, 25.3413],
						[-81.1201, 25.3387],
						[-81.1273, 25.3808],
						[-81.1704, 25.4642],
						[-81.2099, 25.5045],
						[-81.2027, 25.5338],
						[-81.2314, 25.587],
						[-81.2709, 25.6145],
						[-81.2888, 25.6875],
						[-81.3104, 25.703],
						[-81.3498, 25.6901],
						[-81.3822, 25.7769],
						[-81.4683, 25.8035],
						[-81.4719, 25.8172],
						[-81.5473, 25.8499],
						[-81.6226, 25.8971],
						[-81.6621, 25.886],
						[-81.6908, 25.8525],
						[-81.7483, 25.9607],
						[-81.8021, 26.0878],
						[-81.82, 26.2365],
						[-81.8452, 26.3301],
						[-81.8703, 26.3791],
						[-81.9708, 26.4761],
						[-82.0103, 26.4839],
						[-82.0139, 26.5285],
						[-82.0569, 26.5483],
						[-82.0569, 26.4933],
						[-82.1072, 26.4839],
						[-82.1108, 26.5406],
						[-82.1395, 26.6376],
						[-82.1826, 26.6815],
						[-82.1718, 26.7021],
						[-82.1251, 26.6995],
						[-82.0928, 26.666],
						[-82.0533, 26.8026],
						[-82.0605, 26.8765],
						[-82.0892, 26.8885],
						[-82.0892, 26.9229],
						[-82.0605, 26.9314],
						[-82.0749, 26.9581],
						[-82.118, 26.9546],
						[-82.1359, 26.9263],
						[-82.1754, 26.9168],
						[-82.1467, 26.7897],
						[-82.179, 26.7725],
						[-82.2508, 26.7631],
						[-82.2687, 26.7201],
						[-82.2687, 26.7845],
						[-82.2902, 26.8275],
						[-82.3513, 26.9082],
						[-82.4446, 27.0603],
						[-82.4769, 27.1411],
						[-82.5451, 27.2613],
						[-82.642, 27.3893],
						[-82.6922, 27.4374],
						[-82.7066, 27.4873],
						[-82.7425, 27.5397],
						[-82.7066, 27.5233],
						[-82.703, 27.4976],
						[-82.6492, 27.5233],
						[-82.6133, 27.5852],
						[-82.5702, 27.6092],
						[-82.5235, 27.6934],
						[-82.4769, 27.7226],
						[-82.4841, 27.7424],
						[-82.4338, 27.7647],
						[-82.3943, 27.8369],
						[-82.4123, 27.9013],
						[-82.4912, 27.9194],
						[-82.4733, 27.8223],
						[-82.5523, 27.8481],
						[-82.5307, 27.8773],
						[-82.5343, 27.9331],
						[-82.5523, 27.9666],
						[-82.6887, 28.0276],
						[-82.6851, 27.9718],
						[-82.7245, 27.9477],
						[-82.6851, 27.9159],
						[-82.6276, 27.9108],
						[-82.5882, 27.8163],
						[-82.624, 27.7802],
						[-82.624, 27.7269],
						[-82.6384, 27.7037],
						[-82.7138, 27.6986],
						[-82.7245, 27.6711],
						[-82.6994, 27.6384],
						[-82.7317, 27.6127],
						[-82.7461, 27.647],
						[-82.7389, 27.7183],
						[-82.7892, 27.7914],
						[-82.8215, 27.8137],
						[-82.8502, 27.8635],
						[-82.8286, 28.0199],
						[-82.8466, 28.0766],
						[-82.8609, 28.2175],
						[-82.8286, 28.2183],
						[-82.8286, 28.1384],
						[-82.8143, 28.062],
						[-82.782, 28.056],
						[-82.7963, 28.1874],
						[-82.764, 28.2192],
						[-82.7604, 28.2544],
						[-82.7317, 28.2922],
						[-82.7317, 28.3248],
						[-82.7066, 28.3678],
						[-82.7066, 28.4013],
						[-82.6743, 28.4417],
						[-82.6707, 28.5198],
						[-82.6528, 28.5911],
						[-82.6743, 28.6478],
						[-82.6671, 28.696],
						[-82.7138, 28.7209],
						[-82.6994, 28.7569],
						[-82.7317, 28.8506],
						[-82.6887, 28.9056],
						[-82.764, 29.0001],
						[-82.7533, 29.0267],
						[-82.782, 29.0739],
						[-82.8179, 29.0765],
						[-82.7999, 29.1049],
						[-82.8035, 29.147],
						[-82.8286, 29.1581],
						[-82.9973, 29.1779],
						[-83.0296, 29.1341],
						[-83.0548, 29.1306],
						[-83.0871, 29.2165],
						[-83.0763, 29.2552],
						[-83.1265, 29.2827],
						[-83.1696, 29.2904],
						[-83.1768, 29.3445],
						[-83.2019, 29.3944],
						[-83.2414, 29.433],
						[-83.2952, 29.4382],
						[-83.3132, 29.476],
						[-83.3993, 29.5172],
						[-83.3993, 29.6126],
						[-83.4137, 29.6701],
						[-83.4568, 29.6761],
						[-83.4927, 29.7088],
						[-83.5393, 29.7234],
						[-83.586, 29.7758],
						[-83.586, 29.8119],
						[-83.6183, 29.8419],
						[-83.6362, 29.8857],
						[-83.6865, 29.9235],
						[-83.787, 29.9768],
						[-83.9305, 30.0387],
						[-84.0239, 30.1031],
						[-84.0634, 30.1014],
						[-84.1567, 30.073],
						[-84.2679, 30.0979],
						[-84.2715, 30.0679],
						[-84.3649, 30.0086],
						[-84.3325, 29.9235],
						[-84.3433, 29.8995],
						[-84.3792, 29.8935],
						[-84.4331, 29.9064],
						[-84.451, 29.9287],
						[-84.5371, 29.9098],
						[-84.6484, 29.8471],
						[-84.8817, 29.7337],
						[-84.9032, 29.7354],
						[-84.8781, 29.7732],
						[-84.914, 29.7835],
						[-84.9391, 29.75],
						[-84.993, 29.7148],
						[-85.1222, 29.7157],
						[-85.0755, 29.6736],
						[-85.0684, 29.6409],
						[-85.0109, 29.622],
						[-84.9032, 29.6675],
						[-84.7992, 29.7019],
						[-84.7022, 29.7741],
						[-84.6915, 29.7629],
						[-84.7776, 29.6925],
						[-84.8781, 29.6555],
						[-85.0037, 29.5988],
						[-85.0468, 29.5868],
						[-85.0827, 29.6151],
						[-85.1401, 29.634],
						[-85.2227, 29.6779],
						[-85.2909, 29.6839],
						[-85.3519, 29.6598],
						[-85.3986, 29.7406],
						[-85.4165, 29.8428],
						[-85.3878, 29.8789],
						[-85.3878, 29.7887],
						[-85.3663, 29.7105],
						[-85.3411, 29.6916],
						[-85.3052, 29.7251],
						[-85.3017, 29.8084],
						[-85.3627, 29.8986],
						[-85.4273, 29.9502],
						[-85.4883, 29.9613],
						[-85.5421, 29.9957],
						[-85.5996, 30.0567],
						[-85.6965, 30.0971],
						[-85.7754, 30.1564],
						[-85.9226, 30.238],
						[-86.0913, 30.3033],
						[-86.2995, 30.3634],
						[-86.4108, 30.3806],
						[-86.6333, 30.396],
						[-86.7517, 30.3917],
						[-86.9204, 30.3703],
						[-87.1537, 30.3282],
						[-87.2686, 30.3153],
						[-87.2937, 30.3239],
						[-87.5198, 30.2801],
						[-87.466, 30.3024],
						[-87.4911, 30.323],
						[-87.4588, 30.3359],
						[-87.4301, 30.4063],
						[-87.3655, 30.4364],
						[-87.4373, 30.4802],
						[-87.4481, 30.5275],
						[-87.3942, 30.6151],
						[-87.405, 30.6744],
						[-87.5342, 30.7431],
						[-87.545, 30.7783],
						[-87.6275, 30.8462],
						[-87.6347, 30.8659],
						[-87.588, 30.9647],
						[-87.5988, 30.9974],
						[-87.003, 30.9991],
						[-86.5184, 30.9931],
						[-85.8939, 30.9931],
						[-85.4991, 30.9965],
						[-85.2442, 31.0008],
						[-85.0037, 31.0008]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "13",
			"properties": { "name": "Georgia" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-85.6067, 34.9843],
					[-85.4668, 34.9826],
					[-84.9786, 34.9877],
					[-84.3218, 34.9886],
					[-83.6183, 34.9868],
					[-83.6219, 34.992],
					[-83.1086, 35.0006],
					[-83.1194, 34.9387],
					[-83.2306, 34.8803],
					[-83.324, 34.7901],
					[-83.3204, 34.7592],
					[-83.3491, 34.7369],
					[-83.3383, 34.6819],
					[-83.2306, 34.6114],
					[-83.1696, 34.6054],
					[-83.0871, 34.5161],
					[-83.0045, 34.4723],
					[-82.904, 34.486],
					[-82.8753, 34.4757],
					[-82.8466, 34.4207],
					[-82.8358, 34.3657],
					[-82.7927, 34.34],
					[-82.782, 34.297],
					[-82.7425, 34.2524],
					[-82.7425, 34.2085],
					[-82.7174, 34.1501],
					[-82.6743, 34.1295],
					[-82.6456, 34.0719],
					[-82.5953, 34.0307],
					[-82.5558, 33.9439],
					[-82.5235, 33.9431],
					[-82.4553, 33.8812],
					[-82.3513, 33.8366],
					[-82.2472, 33.7524],
					[-82.2328, 33.7],
					[-82.2005, 33.6648],
					[-82.1969, 33.6304],
					[-82.1503, 33.5977],
					[-82.1072, 33.596],
					[-82.0462, 33.5642],
					[-81.9816, 33.4844],
					[-81.9277, 33.462],
					[-81.9134, 33.4414],
					[-81.9385, 33.4045],
					[-81.9241, 33.371],
					[-81.9385, 33.3435],
					[-81.838, 33.273],
					[-81.8523, 33.2473],
					[-81.8057, 33.2112],
					[-81.7662, 33.2155],
					[-81.7734, 33.1802],
					[-81.7447, 33.1416],
					[-81.6478, 33.0943],
					[-81.6155, 33.0952],
					[-81.558, 33.0454],
					[-81.4898, 33.0084],
					[-81.5078, 32.9509],
					[-81.4647, 32.8959],
					[-81.4575, 32.8538],
					[-81.4252, 32.8409],
					[-81.4252, 32.7679],
					[-81.4109, 32.7404],
					[-81.4288, 32.7017],
					[-81.4073, 32.6949],
					[-81.3929, 32.6536],
					[-81.4109, 32.6184],
					[-81.3678, 32.5772],
					[-81.2781, 32.5548],
					[-81.2781, 32.5351],
					[-81.1955, 32.4655],
					[-81.2099, 32.4363],
					[-81.156, 32.3461],
					[-81.1417, 32.3495],
					[-81.1201, 32.2842],
					[-81.156, 32.2464],
					[-81.1165, 32.1889],
					[-81.1309, 32.1666],
					[-81.113, 32.1133],
					[-81.0376, 32.0841],
					[-80.9981, 32.0987],
					[-80.9191, 32.0377],
					[-80.8868, 32.0343],
					[-80.8438, 32.0239],
					[-80.8617, 31.969],
					[-80.912, 31.9441],
					[-80.9478, 31.9569],
					[-80.9694, 31.9157],
					[-80.9335, 31.9088],
					[-80.9909, 31.8573],
					[-81.0663, 31.8771],
					[-81.0771, 31.8289],
					[-81.0376, 31.8195],
					[-81.0699, 31.7688],
					[-81.1309, 31.7224],
					[-81.1919, 31.7336],
					[-81.2027, 31.7198],
					[-81.1596, 31.6915],
					[-81.1309, 31.6958],
					[-81.1345, 31.6236],
					[-81.1596, 31.5704],
					[-81.2601, 31.548],
					[-81.2601, 31.5291],
					[-81.1991, 31.5377],
					[-81.1776, 31.5171],
					[-81.2601, 31.4046],
					[-81.2816, 31.3264],
					[-81.2565, 31.3152],
					[-81.2745, 31.2895],
					[-81.2924, 31.2061],
					[-81.3391, 31.1872],
					[-81.3678, 31.1365],
					[-81.4001, 31.134],
					[-81.4001, 31.073],
					[-81.4252, 31.0137],
					[-81.4503, 31.0154],
					[-81.4934, 30.9776],
					[-81.4468, 30.957],
					[-81.4073, 30.9776],
					[-81.4037, 30.908],
					[-81.4611, 30.7697],
					[-81.4432, 30.7096],
					[-81.4898, 30.7259],
					[-81.5401, 30.713],
					[-81.6011, 30.7285],
					[-81.7195, 30.7448],
					[-81.7447, 30.7663],
					[-81.8093, 30.7903],
					[-81.8703, 30.7929],
					[-81.8954, 30.8213],
					[-81.9493, 30.8273],
					[-81.9816, 30.7766],
					[-82.0246, 30.7835],
					[-82.0103, 30.7628],
					[-82.039, 30.7491],
					[-82.0354, 30.707],
					[-82.0498, 30.6555],
					[-82.0067, 30.5773],
					[-82.0175, 30.4751],
					[-82.039, 30.4347],
					[-82.039, 30.3703],
					[-82.1718, 30.3591],
					[-82.2113, 30.4244],
					[-82.2005, 30.4854],
					[-82.2256, 30.5077],
					[-82.2328, 30.5567],
					[-82.2149, 30.5687],
					[-82.6994, 30.5979],
					[-83.4388, 30.6417],
					[-84.3756, 30.6898],
					[-84.8638, 30.7113],
					[-84.914, 30.7525],
					[-84.9356, 30.817],
					[-84.9427, 30.8883],
					[-84.9822, 30.9329],
					[-85.0037, 31.0008],
					[-85.0109, 31.0532],
					[-85.0361, 31.1082],
					[-85.1078, 31.1864],
					[-85.0971, 31.2328],
					[-85.115, 31.2766],
					[-85.0899, 31.2946],
					[-85.0935, 31.3625],
					[-85.0648, 31.4312],
					[-85.0719, 31.4681],
					[-85.0468, 31.5171],
					[-85.0576, 31.6202],
					[-85.1258, 31.6949],
					[-85.1186, 31.7327],
					[-85.1401, 31.8393],
					[-85.1294, 31.8779],
					[-85.0791, 31.9398],
					[-85.054, 32.0136],
					[-85.0468, 32.0875],
					[-85.0576, 32.1356],
					[-85.0109, 32.1803],
					[-84.9607, 32.1983],
					[-84.9679, 32.219],
					[-84.9284, 32.219],
					[-84.8925, 32.2636],
					[-84.9356, 32.298],
					[-85.0002, 32.322],
					[-84.9643, 32.4226],
					[-84.9966, 32.4535],
					[-85.0073, 32.5239],
					[-85.0684, 32.5832],
					[-85.1042, 32.645],
					[-85.1222, 32.773],
					[-85.1689, 32.8117],
					[-85.1545, 32.8426],
					[-85.1868, 32.8701],
					[-85.3052, 33.4826],
					[-85.3591, 33.7498],
					[-85.4057, 34.0024],
					[-85.4704, 34.3279],
					[-85.5529, 34.7085],
					[-85.6067, 34.9843]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "18",
			"properties": { "name": "Indiana" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-87.5234, 41.7081],
					[-87.4696, 41.6729],
					[-87.4265, 41.6901],
					[-87.4014, 41.6772],
					[-87.4229, 41.6428],
					[-87.326, 41.6231],
					[-87.2219, 41.6239],
					[-87.1214, 41.6454],
					[-86.9348, 41.7098],
					[-86.8235, 41.7605],
					[-86.5005, 41.7597],
					[-85.7898, 41.7588],
					[-85.1186, 41.7597],
					[-84.8063, 41.7605],
					[-84.8063, 41.6961],
					[-84.8027, 41.4882],
					[-84.8027, 40.3027],
					[-84.8135, 39.9273],
					[-84.8135, 39.5485],
					[-84.8207, 39.3096],
					[-84.8207, 39.1052],
					[-84.8889, 39.0665],
					[-84.8961, 39.0493],
					[-84.8386, 38.9883],
					[-84.8315, 38.9617],
					[-84.8709, 38.9291],
					[-84.8602, 38.8973],
					[-84.7992, 38.8913],
					[-84.7848, 38.8698],
					[-84.8315, 38.8303],
					[-84.8099, 38.7925],
					[-84.8889, 38.7951],
					[-84.9463, 38.7753],
					[-84.9786, 38.7796],
					[-85.1042, 38.7255],
					[-85.1545, 38.692],
					[-85.2227, 38.7006],
					[-85.2586, 38.7375],
					[-85.3519, 38.7315],
					[-85.4093, 38.7375],
					[-85.4524, 38.7092],
					[-85.4381, 38.6018],
					[-85.4165, 38.5408],
					[-85.4739, 38.5064],
					[-85.4991, 38.4686],
					[-85.6067, 38.4394],
					[-85.6749, 38.3011],
					[-85.7431, 38.2676],
					[-85.7934, 38.2882],
					[-85.8293, 38.277],
					[-85.8508, 38.2229],
					[-85.9082, 38.1611],
					[-85.9047, 38.0863],
					[-85.9226, 38.0262],
					[-85.9513, 38.0056],
					[-86.0339, 37.9901],
					[-86.0482, 37.9592],
					[-86.0949, 38.009],
					[-86.1739, 38.0099],
					[-86.2672, 38.0571],
					[-86.2708, 38.1379],
					[-86.3461, 38.1954],
					[-86.3749, 38.1937],
					[-86.3713, 38.1645],
					[-86.3246, 38.1542],
					[-86.3282, 38.1327],
					[-86.3749, 38.131],
					[-86.4036, 38.1061],
					[-86.4323, 38.1259],
					[-86.4646, 38.1009],
					[-86.4323, 38.0863],
					[-86.4538, 38.0503],
					[-86.5184, 38.0425],
					[-86.5256, 37.9618],
					[-86.5077, 37.9291],
					[-86.5902, 37.9214],
					[-86.5974, 37.8673],
					[-86.6369, 37.8424],
					[-86.662, 37.863],
					[-86.6441, 37.9068],
					[-86.6799, 37.9154],
					[-86.7158, 37.8939],
					[-86.7517, 37.9128],
					[-86.7948, 37.9893],
					[-86.8558, 37.9875],
					[-86.9061, 37.9429],
					[-87.0102, 37.9197],
					[-87.0461, 37.8939],
					[-87.064, 37.8106],
					[-87.0891, 37.7874],
					[-87.1286, 37.7848],
					[-87.1573, 37.8381],
					[-87.2219, 37.8492],
					[-87.3009, 37.8982],
					[-87.3799, 37.936],
					[-87.4516, 37.9412],
					[-87.5019, 37.9094],
					[-87.5521, 37.9257],
					[-87.5737, 37.9678],
					[-87.6024, 37.9729],
					[-87.6275, 37.9162],
					[-87.588, 37.8613],
					[-87.6347, 37.8269],
					[-87.6742, 37.8295],
					[-87.6634, 37.8776],
					[-87.6814, 37.9034],
					[-87.8321, 37.8767],
					[-87.8716, 37.9214],
					[-87.9039, 37.9248],
					[-87.9398, 37.8905],
					[-87.9039, 37.8123],
					[-87.947, 37.7719],
					[-88.0295, 37.7994],
					[-88.069, 37.8011],
					[-88.0834, 37.8303],
					[-88.0403, 37.8226],
					[-88.0977, 37.9016],
					[-88.0116, 37.8948],
					[-88.0654, 37.9197],
					[-88.0116, 37.9772],
					[-88.008, 38.0288],
					[-88.0367, 38.0511],
					[-87.9685, 38.0674],
					[-87.9613, 38.1001],
					[-88.0044, 38.0838],
					[-87.9254, 38.1465],
					[-87.9757, 38.198],
					[-87.99, 38.2487],
					[-87.9577, 38.2401],
					[-87.9254, 38.2985],
					[-87.9075, 38.2685],
					[-87.8824, 38.3037],
					[-87.8536, 38.2753],
					[-87.807, 38.363],
					[-87.7783, 38.3707],
					[-87.7316, 38.4428],
					[-87.7388, 38.4755],
					[-87.6526, 38.5116],
					[-87.6526, 38.5683],
					[-87.6203, 38.6396],
					[-87.5952, 38.6671],
					[-87.545, 38.6774],
					[-87.4947, 38.7427],
					[-87.4983, 38.7787],
					[-87.5521, 38.8595],
					[-87.5127, 38.9548],
					[-87.5773, 38.9892],
					[-87.5737, 39.0571],
					[-87.6311, 39.1043],
					[-87.6419, 39.167],
					[-87.5737, 39.2186],
					[-87.606, 39.2615],
					[-87.5988, 39.3131],
					[-87.5773, 39.3406],
					[-87.5306, 39.3483],
					[-87.5306, 39.6464],
					[-87.5342, 39.6464],
					[-87.5342, 39.9874],
					[-87.5306, 39.9977],
					[-87.5306, 40.2503],
					[-87.527, 40.4625],
					[-87.527, 41.4899],
					[-87.5234, 41.7081]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "20",
			"properties": { "name": "Kansas" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-102.0529, 40.0029],
					[-101.1304, 40.002],
					[-100.7572, 40.002],
					[-99.8132, 40.0012],
					[-98.8333, 40.002],
					[-98.2482, 40.002],
					[-97.2468, 40.0012],
					[-96.3028, 40.0003],
					[-95.786, 40.0003],
					[-95.3086, 40.0003],
					[-95.2512, 39.9488],
					[-95.2045, 39.9393],
					[-95.2009, 39.9024],
					[-95.1543, 39.9075],
					[-95.1363, 39.8766],
					[-95.043, 39.8646],
					[-95.0143, 39.8998],
					[-94.9281, 39.8886],
					[-94.9425, 39.8646],
					[-94.9174, 39.8362],
					[-94.8779, 39.8268],
					[-94.8923, 39.7907],
					[-94.9246, 39.7898],
					[-94.9138, 39.7589],
					[-94.8707, 39.7727],
					[-94.8743, 39.7306],
					[-94.9102, 39.7254],
					[-94.9569, 39.746],
					[-94.9748, 39.6816],
					[-95.0286, 39.6653],
					[-95.0538, 39.6301],
					[-95.0466, 39.5948],
					[-95.1076, 39.5742],
					[-95.104, 39.533],
					[-95.0502, 39.4978],
					[-95.0466, 39.4729],
					[-94.9892, 39.4462],
					[-94.9461, 39.3938],
					[-94.8958, 39.3938],
					[-94.8779, 39.3758],
					[-94.9102, 39.3543],
					[-94.8887, 39.2864],
					[-94.8241, 39.2418],
					[-94.8348, 39.2177],
					[-94.781, 39.2065],
					[-94.763, 39.1799],
					[-94.7128, 39.1705],
					[-94.6805, 39.1842],
					[-94.6625, 39.1576],
					[-94.5908, 39.155],
					[-94.6087, 39.1198],
					[-94.6087, 38.7384],
					[-94.6123, 38.6353],
					[-94.6123, 38.0374],
					[-94.6159, 37.9927],
					[-94.6159, 37.3235],
					[-94.6195, 37.2402],
					[-94.6195, 36.9988],
					[-95.5132, 36.9996],
					[-96.2167, 36.9988],
					[-96.8233, 36.9988],
					[-97.6058, 36.9988],
					[-98.3523, 36.9979],
					[-99.2784, 36.9996],
					[-99.6481, 36.9996],
					[-100.2008, 37.0022],
					[-100.7356, 36.9988],
					[-101.4858, 36.9953],
					[-102.0421, 36.9928],
					[-102.0421, 37.9283],
					[-102.0457, 38.046],
					[-102.0457, 39.137],
					[-102.0493, 39.3028],
					[-102.0493, 39.6756],
					[-102.0529, 40.0029]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "23",
			"properties": { "name": "Maine" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-68.371, 44.1934],
						[-68.3207, 44.1994],
						[-68.3458, 44.1693],
						[-68.371, 44.1934]
					]],
					[[
						[-68.5002, 44.1599],
						[-68.475, 44.2312],
						[-68.4535, 44.202],
						[-68.3853, 44.1547],
						[-68.4392, 44.1161],
						[-68.4571, 44.1453],
						[-68.5002, 44.1599]
					]],
					[[
						[-68.5289, 44.3446],
						[-68.5181, 44.3807],
						[-68.4786, 44.3197],
						[-68.5289, 44.3446]
					]],
					[[
						[-68.8412, 44.2363],
						[-68.7909, 44.2381],
						[-68.7801, 44.2028],
						[-68.8412, 44.2363]
					]],
					[[
						[-68.9129, 43.8532],
						[-68.8842, 43.885],
						[-68.8806, 43.8206],
						[-68.9129, 43.8532]
					]],
					[[
						[-68.9452, 44.1126],
						[-68.9165, 44.1479],
						[-68.8268, 44.1865],
						[-68.7873, 44.1436],
						[-68.8196, 44.1367],
						[-68.7694, 44.0697],
						[-68.8089, 44.0362],
						[-68.8735, 44.025],
						[-68.9165, 44.0456],
						[-68.8986, 44.0671],
						[-68.9094, 44.1101],
						[-68.9452, 44.1126]
					]],
					[[
						[-68.9668, 44.2518],
						[-68.9201, 44.3102],
						[-68.9129, 44.3652],
						[-68.8806, 44.3867],
						[-68.8591, 44.3643],
						[-68.8914, 44.3343],
						[-68.8878, 44.3033],
						[-68.9165, 44.2432],
						[-68.9524, 44.2183],
						[-68.9668, 44.2518]
					]],
					[[
						[-70.704, 43.0594],
						[-70.8296, 43.129],
						[-70.8296, 43.1883],
						[-70.8081, 43.2244],
						[-70.9553, 43.3343],
						[-70.9876, 43.3799],
						[-70.9588, 43.4666],
						[-70.9732, 43.4752],
						[-70.9517, 43.5508],
						[-70.9732, 43.5706],
						[-70.9911, 43.8395],
						[-70.9911, 43.9159],
						[-71.0306, 44.6556],
						[-71.0845, 45.3059],
						[-71.0091, 45.3188],
						[-71.0091, 45.3471],
						[-70.9194, 45.3119],
						[-70.9229, 45.2792],
						[-70.8978, 45.2423],
						[-70.8583, 45.2294],
						[-70.8117, 45.3024],
						[-70.8045, 45.3763],
						[-70.826, 45.4004],
						[-70.783, 45.4313],
						[-70.7543, 45.4287],
						[-70.7112, 45.3909],
						[-70.6358, 45.3832],
						[-70.6322, 45.4167],
						[-70.7184, 45.488],
						[-70.7219, 45.5155],
						[-70.643, 45.6074],
						[-70.5927, 45.6306],
						[-70.5533, 45.6675],
						[-70.5245, 45.6667],
						[-70.4707, 45.7019],
						[-70.3846, 45.7345],
						[-70.4169, 45.7955],
						[-70.3415, 45.8522],
						[-70.2589, 45.8909],
						[-70.241, 45.939],
						[-70.2661, 45.9631],
						[-70.3164, 45.9631],
						[-70.3164, 46.0197],
						[-70.2805, 46.0524],
						[-70.3092, 46.0644],
						[-70.2553, 46.1082],
						[-70.2374, 46.1477],
						[-70.2912, 46.1855],
						[-70.2338, 46.2843],
						[-70.2051, 46.2998],
						[-70.2087, 46.3316],
						[-70.1477, 46.3591],
						[-70.0974, 46.4098],
						[-70.0579, 46.4166],
						[-69.9969, 46.695],
						[-69.2252, 47.4595],
						[-69.1786, 47.457],
						[-69.0816, 47.4243],
						[-69.0422, 47.4277],
						[-69.0529, 47.3779],
						[-69.0493, 47.2568],
						[-69.035, 47.2413],
						[-68.9022, 47.1786],
						[-68.8124, 47.2156],
						[-68.7191, 47.2413],
						[-68.6186, 47.2431],
						[-68.5791, 47.2877],
						[-68.4715, 47.2972],
						[-68.3817, 47.2869],
						[-68.3817, 47.3401],
						[-68.3243, 47.3599],
						[-68.2346, 47.3547],
						[-68.152, 47.3238],
						[-68.1377, 47.2963],
						[-68.0192, 47.2379],
						[-67.89, 47.1245],
						[-67.89, 47.1116],
						[-67.7895, 47.0678],
						[-67.7895, 46.5996],
						[-67.7787, 46.0043],
						[-67.7823, 45.9433],
						[-67.75, 45.9175],
						[-67.8039, 45.884],
						[-67.7536, 45.8239],
						[-67.8074, 45.7947],
						[-67.8039, 45.6778],
						[-67.7213, 45.6624],
						[-67.7105, 45.6796],
						[-67.6746, 45.6306],
						[-67.6064, 45.6065],
						[-67.4988, 45.5868],
						[-67.4485, 45.6031],
						[-67.4198, 45.5498],
						[-67.4162, 45.5017],
						[-67.4629, 45.5086],
						[-67.5024, 45.4897],
						[-67.4772, 45.4313],
						[-67.4198, 45.3772],
						[-67.4521, 45.3145],
						[-67.488, 45.2792],
						[-67.4054, 45.1796],
						[-67.4054, 45.1598],
						[-67.3408, 45.1255],
						[-67.2942, 45.1495],
						[-67.2906, 45.189],
						[-67.226, 45.1633],
						[-67.1578, 45.1607],
						[-67.1111, 45.1126],
						[-67.0896, 45.0688],
						[-67.1183, 45.0567],
						[-67.0824, 45.0293],
						[-67.0393, 44.9451],
						[-66.9855, 44.9124],
						[-66.9819, 44.8111],
						[-67.025, 44.7681],
						[-67.0609, 44.7698],
						[-67.0752, 44.7423],
						[-67.1901, 44.6453],
						[-67.2762, 44.6238],
						[-67.2619, 44.6049],
						[-67.3157, 44.598],
						[-67.2942, 44.6341],
						[-67.3085, 44.7071],
						[-67.4054, 44.6813],
						[-67.3624, 44.6315],
						[-67.4054, 44.5946],
						[-67.4449, 44.6057],
						[-67.4916, 44.5559],
						[-67.5382, 44.5714],
						[-67.5741, 44.5611],
						[-67.567, 44.531],
						[-67.5095, 44.4966],
						[-67.5885, 44.4477],
						[-67.6352, 44.4872],
						[-67.6567, 44.5362],
						[-67.7034, 44.5276],
						[-67.7141, 44.4949],
						[-67.7428, 44.4975],
						[-67.768, 44.5482],
						[-67.7931, 44.4949],
						[-67.8254, 44.4829],
						[-67.8541, 44.4193],
						[-67.8792, 44.4356],
						[-67.9008, 44.3944],
						[-67.9151, 44.4305],
						[-67.9869, 44.3867],
						[-68.0049, 44.4099],
						[-68.0479, 44.3308],
						[-68.1054, 44.3643],
						[-68.1054, 44.3953],
						[-68.1412, 44.3772],
						[-68.1807, 44.385],
						[-68.1735, 44.3283],
						[-68.231, 44.2673],
						[-68.1915, 44.2389],
						[-68.3171, 44.2252],
						[-68.3997, 44.2527],
						[-68.4571, 44.3446],
						[-68.4356, 44.3695],
						[-68.3566, 44.3927],
						[-68.3889, 44.4313],
						[-68.4356, 44.4013],
						[-68.4284, 44.4391],
						[-68.4643, 44.4365],
						[-68.4607, 44.3781],
						[-68.4786, 44.3781],
						[-68.4786, 44.4331],
						[-68.5648, 44.385],
						[-68.5468, 44.3549],
						[-68.5684, 44.318],
						[-68.5181, 44.2604],
						[-68.5289, 44.2217],
						[-68.6114, 44.1556],
						[-68.5791, 44.1461],
						[-68.6222, 44.1195],
						[-68.5827, 44.0714],
						[-68.6007, 44.0121],
						[-68.6581, 44.0035],
						[-68.6689, 44.0766],
						[-68.6473, 44.08],
						[-68.6581, 44.1272],
						[-68.7191, 44.1659],
						[-68.7335, 44.2209],
						[-68.6796, 44.2647],
						[-68.7478, 44.2991],
						[-68.7263, 44.3214],
						[-68.7622, 44.33],
						[-68.8268, 44.3119],
						[-68.8232, 44.409],
						[-68.7837, 44.4468],
						[-68.7801, 44.4898],
						[-68.8053, 44.5018],
						[-68.8304, 44.4623],
						[-68.8806, 44.4279],
						[-68.9201, 44.4563],
						[-68.9452, 44.4288],
						[-68.9991, 44.4253],
						[-68.9488, 44.3558],
						[-68.9596, 44.3145],
						[-69.0027, 44.2948],
						[-69.0386, 44.2338],
						[-69.0529, 44.1719],
						[-69.0996, 44.1049],
						[-69.0314, 44.0791],
						[-69.0781, 44.0551],
						[-69.0422, 44.0061],
						[-69.0781, 43.9735],
						[-69.175, 43.9769],
						[-69.2216, 43.9151],
						[-69.2755, 43.9142],
						[-69.2826, 43.8644],
						[-69.3221, 43.8566],
						[-69.3221, 43.9013],
						[-69.297, 43.9331],
						[-69.3293, 43.946],
						[-69.3401, 43.9202],
						[-69.3831, 43.909],
						[-69.3652, 43.9649],
						[-69.4298, 43.9571],
						[-69.4262, 43.9125],
						[-69.4836, 43.8807],
						[-69.5016, 43.8377],
						[-69.5447, 43.8816],
						[-69.5518, 43.8412],
						[-69.6057, 43.8137],
						[-69.6487, 43.836],
						[-69.6595, 43.7793],
						[-69.7062, 43.8231],
						[-69.7528, 43.7441],
						[-69.8354, 43.7209],
						[-69.8533, 43.7046],
						[-69.8713, 43.7759],
						[-69.9287, 43.7802],
						[-69.9826, 43.7441],
						[-70.0005, 43.7106],
						[-70.0723, 43.714],
						[-70.0974, 43.6719],
						[-70.1692, 43.6754],
						[-70.2051, 43.6333],
						[-70.2159, 43.5903],
						[-70.1979, 43.5654],
						[-70.2266, 43.5379],
						[-70.2733, 43.5628],
						[-70.3522, 43.5362],
						[-70.3846, 43.4967],
						[-70.363, 43.4391],
						[-70.3917, 43.4022],
						[-70.4276, 43.3893],
						[-70.4169, 43.361],
						[-70.4671, 43.3403],
						[-70.5174, 43.3438],
						[-70.5533, 43.3223],
						[-70.5927, 43.2381],
						[-70.5748, 43.2218],
						[-70.625, 43.1531],
						[-70.6214, 43.1342],
						[-70.6717, 43.0706],
						[-70.704, 43.0594]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "25",
			"properties": { "name": "Massachusetts" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-70.2769, 41.3104],
						[-70.2302, 41.2898],
						[-70.1261, 41.2941],
						[-70.0615, 41.3087],
						[-70.0184, 41.3688],
						[-69.961, 41.2786],
						[-69.9646, 41.2528],
						[-70.0005, 41.2391],
						[-70.1189, 41.2425],
						[-70.2661, 41.2941],
						[-70.2769, 41.3104]
					]],
					[[
						[-70.8332, 41.3533],
						[-70.7686, 41.3533],
						[-70.7004, 41.4307],
						[-70.6035, 41.4822],
						[-70.564, 41.4693],
						[-70.5389, 41.4092],
						[-70.4851, 41.386],
						[-70.4492, 41.4203],
						[-70.4528, 41.3482],
						[-70.5999, 41.349],
						[-70.7112, 41.3413],
						[-70.7758, 41.3009],
						[-70.8332, 41.3533]
					]],
					[[
						[-73.2632, 42.7459],
						[-72.4592, 42.727],
						[-71.7449, 42.7072],
						[-71.2926, 42.6969],
						[-71.2568, 42.7364],
						[-71.1814, 42.7373],
						[-71.185, 42.7905],
						[-71.1311, 42.8215],
						[-71.0629, 42.806],
						[-71.0306, 42.8593],
						[-70.9014, 42.8868],
						[-70.8476, 42.861],
						[-70.8189, 42.8722],
						[-70.8117, 42.8103],
						[-70.7794, 42.6935],
						[-70.6896, 42.6531],
						[-70.6466, 42.6892],
						[-70.5963, 42.66],
						[-70.6538, 42.5827],
						[-70.7363, 42.5766],
						[-70.8727, 42.5466],
						[-70.8296, 42.5036],
						[-70.8942, 42.4607],
						[-70.9194, 42.4684],
						[-70.9373, 42.4177],
						[-70.9517, 42.4529],
						[-70.9911, 42.4074],
						[-70.9517, 42.3438],
						[-70.9983, 42.3524],
						[-71.0163, 42.3284],
						[-70.9876, 42.306],
						[-71.0055, 42.2828],
						[-70.9553, 42.2708],
						[-70.9337, 42.3017],
						[-70.8835, 42.3086],
						[-70.8512, 42.2682],
						[-70.765, 42.2442],
						[-70.7219, 42.2081],
						[-70.7148, 42.1686],
						[-70.6394, 42.0887],
						[-70.643, 42.0457],
						[-70.6681, 42.0122],
						[-70.7112, 42.0079],
						[-70.7004, 41.9873],
						[-70.625, 41.9435],
						[-70.582, 41.9504],
						[-70.5533, 41.9298],
						[-70.5245, 41.8585],
						[-70.5425, 41.8155],
						[-70.4958, 41.7743],
						[-70.4133, 41.7442],
						[-70.2912, 41.7339],
						[-70.2589, 41.7141],
						[-70.1907, 41.7519],
						[-70.1225, 41.7588],
						[-70.0256, 41.7872],
						[-70.0041, 41.8086],
						[-70.0077, 41.8765],
						[-70.0292, 41.9289],
						[-70.0759, 41.9023],
						[-70.0759, 41.9856],
						[-70.0938, 42.0329],
						[-70.1477, 42.0621],
						[-70.18, 42.056],
						[-70.1979, 42.0225],
						[-70.2374, 42.0732],
						[-70.1907, 42.0827],
						[-70.1154, 42.0672],
						[-70.0328, 42.0174],
						[-69.9682, 41.9117],
						[-69.9359, 41.8095],
						[-69.9287, 41.6918],
						[-69.9826, 41.581],
						[-69.9897, 41.5432],
						[-70.0149, 41.5509],
						[-69.9718, 41.6471],
						[-69.9969, 41.6669],
						[-70.0902, 41.6626],
						[-70.2446, 41.6282],
						[-70.3522, 41.6351],
						[-70.3774, 41.6111],
						[-70.4384, 41.605],
						[-70.4922, 41.5518],
						[-70.6107, 41.5432],
						[-70.7327, 41.4865],
						[-70.7901, 41.4461],
						[-70.8655, 41.4221],
						[-70.9481, 41.4092],
						[-70.9265, 41.4315],
						[-70.8009, 41.4607],
						[-70.7435, 41.5011],
						[-70.6573, 41.5432],
						[-70.6394, 41.5776],
						[-70.6466, 41.6781],
						[-70.625, 41.7133],
						[-70.7184, 41.7356],
						[-70.7184, 41.6849],
						[-70.7435, 41.697],
						[-70.7578, 41.654],
						[-70.8081, 41.6566],
						[-70.8009, 41.6291],
						[-70.9122, 41.6196],
						[-70.9373, 41.5776],
						[-70.9301, 41.5398],
						[-70.9517, 41.5148],
						[-71.0414, 41.4951],
						[-71.0845, 41.5097],
						[-71.1204, 41.4977],
						[-71.1311, 41.66],
						[-71.1957, 41.6755],
						[-71.2244, 41.7107],
						[-71.2603, 41.7519],
						[-71.3285, 41.7803],
						[-71.3465, 41.8284],
						[-71.3393, 41.898],
						[-71.3824, 41.8928],
						[-71.3824, 42.0191],
						[-71.7987, 42.0079],
						[-71.8023, 42.0234],
						[-72.5274, 42.0346],
						[-72.6063, 42.0311],
						[-72.7571, 42.0363],
						[-72.7678, 42.0028],
						[-72.8181, 41.9976],
						[-72.8145, 42.0363],
						[-73.0083, 42.0389],
						[-73.4319, 42.0509],
						[-73.4857, 42.05],
						[-73.5072, 42.0861],
						[-73.3852, 42.4254],
						[-73.2632, 42.7459]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "27",
			"properties": { "name": "Minnesota" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-97.2289, 49.0007],
					[-96.931, 48.9998],
					[-95.9762, 48.9998],
					[-95.1543, 48.999],
					[-95.1543, 49.3847],
					[-95.0574, 49.3529],
					[-94.9569, 49.3701],
					[-94.8528, 49.3246],
					[-94.8241, 49.2945],
					[-94.7738, 49.121],
					[-94.7487, 49.0995],
					[-94.6841, 48.8839],
					[-94.7056, 48.8246],
					[-94.6913, 48.7782],
					[-94.641, 48.7413],
					[-94.5369, 48.7026],
					[-94.4508, 48.6923],
					[-94.4221, 48.7086],
					[-94.2641, 48.6992],
					[-94.2246, 48.6493],
					[-94.0057, 48.6433],
					[-93.8334, 48.6253],
					[-93.8047, 48.5686],
					[-93.8155, 48.5265],
					[-93.7939, 48.5162],
					[-93.6432, 48.5179],
					[-93.6252, 48.5308],
					[-93.5463, 48.5291],
					[-93.4637, 48.5462],
					[-93.4673, 48.5875],
					[-93.3489, 48.627],
					[-93.2089, 48.6425],
					[-93.1766, 48.6236],
					[-92.9863, 48.6236],
					[-92.7279, 48.5394],
					[-92.6346, 48.5428],
					[-92.631, 48.5007],
					[-92.6992, 48.4947],
					[-92.7136, 48.4629],
					[-92.6561, 48.4363],
					[-92.509, 48.4475],
					[-92.4551, 48.4011],
					[-92.4767, 48.3719],
					[-92.4551, 48.3298],
					[-92.369, 48.2207],
					[-92.2792, 48.2439],
					[-92.308, 48.316],
					[-92.2613, 48.3547],
					[-92.2075, 48.3452],
					[-92.0567, 48.359],
					[-91.9993, 48.3212],
					[-92.0065, 48.2653],
					[-91.9454, 48.2301],
					[-91.8952, 48.2378],
					[-91.8629, 48.2069],
					[-91.7157, 48.1992],
					[-91.6978, 48.1416],
					[-91.7121, 48.115],
					[-91.6404, 48.097],
					[-91.5578, 48.1081],
					[-91.5686, 48.0437],
					[-91.4896, 48.0677],
					[-91.4394, 48.0488],
					[-91.3712, 48.0695],
					[-91.2491, 48.0841],
					[-91.084, 48.1811],
					[-91.023, 48.1923],
					[-90.9763, 48.2198],
					[-90.8866, 48.2456],
					[-90.8364, 48.2344],
					[-90.8364, 48.1768],
					[-90.7753, 48.1622],
					[-90.7969, 48.1399],
					[-90.761, 48.0987],
					[-90.7035, 48.0961],
					[-90.5779, 48.121],
					[-90.5564, 48.0961],
					[-90.4667, 48.109],
					[-90.3733, 48.0935],
					[-90.3051, 48.1047],
					[-90.1328, 48.1116],
					[-90.0288, 48.0875],
					[-89.9965, 48.0308],
					[-89.8744, 47.9853],
					[-89.8206, 48.0153],
					[-89.7488, 48.0231],
					[-89.7021, 48.0059],
					[-89.6196, 48.0111],
					[-89.5801, 47.9956],
					[-89.4904, 48.0145],
					[-89.5873, 47.9664],
					[-89.6232, 47.9836],
					[-89.6375, 47.9544],
					[-89.6985, 47.9415],
					[-89.7919, 47.8916],
					[-89.9247, 47.8624],
					[-89.9749, 47.8306],
					[-90.1616, 47.7928],
					[-90.3338, 47.7465],
					[-90.3877, 47.7413],
					[-90.5384, 47.7026],
					[-90.5528, 47.6906],
					[-90.7359, 47.6245],
					[-90.8687, 47.5566],
					[-91.1271, 47.3994],
					[-91.1881, 47.3401],
					[-91.3855, 47.1872],
					[-91.4788, 47.1254],
					[-91.5722, 47.0901],
					[-91.6655, 47.0145],
					[-91.705, 47.0051],
					[-91.8055, 46.9338],
					[-91.8414, 46.9252],
					[-92.0603, 46.8101],
					[-92.0926, 46.7886],
					[-92.0639, 46.7457],
					[-92.0136, 46.7061],
					[-92.089, 46.7491],
					[-92.1393, 46.7396],
					[-92.15, 46.7147],
					[-92.2075, 46.7027],
					[-92.1752, 46.6864],
					[-92.2146, 46.6494],
					[-92.2936, 46.6632],
					[-92.2936, 46.0747],
					[-92.3295, 46.0661],
					[-92.351, 46.0155],
					[-92.4372, 46.0215],
					[-92.4731, 45.9734],
					[-92.5233, 45.9845],
					[-92.552, 45.9519],
					[-92.6418, 45.9321],
					[-92.7136, 45.8917],
					[-92.7853, 45.7646],
					[-92.8643, 45.7225],
					[-92.8894, 45.6246],
					[-92.8858, 45.5791],
					[-92.8105, 45.561],
					[-92.7746, 45.5687],
					[-92.7243, 45.5413],
					[-92.7279, 45.5146],
					[-92.6454, 45.4416],
					[-92.6489, 45.3986],
					[-92.7028, 45.3583],
					[-92.6992, 45.3334],
					[-92.7602, 45.2913],
					[-92.753, 45.2131],
					[-92.7674, 45.1856],
					[-92.7459, 45.1074],
					[-92.8033, 45.0576],
					[-92.7638, 45.0284],
					[-92.771, 44.9726],
					[-92.7494, 44.9373],
					[-92.7746, 44.9013],
					[-92.7638, 44.8368],
					[-92.8069, 44.7681],
					[-92.7889, 44.7372],
					[-92.7315, 44.714],
					[-92.6202, 44.6392],
					[-92.6238, 44.6186],
					[-92.5736, 44.6049],
					[-92.5413, 44.5671],
					[-92.3618, 44.5593],
					[-92.3187, 44.5447],
					[-92.2828, 44.4777],
					[-92.2326, 44.4451],
					[-92.0531, 44.4013],
					[-91.9742, 44.3678],
					[-91.9239, 44.3334],
					[-91.9239, 44.2879],
					[-91.8952, 44.275],
					[-91.8772, 44.2028],
					[-91.8162, 44.1642],
					[-91.7193, 44.129],
					[-91.7085, 44.104],
					[-91.5793, 44.0267],
					[-91.4394, 44.0018],
					[-91.3855, 43.9546],
					[-91.2778, 43.8377],
					[-91.2455, 43.7733],
					[-91.2742, 43.6762],
					[-91.2671, 43.6152],
					[-91.2312, 43.5835],
					[-91.2455, 43.5457],
					[-91.2168, 43.5001],
					[-91.949, 43.5001],
					[-92.753, 43.5001],
					[-93.1694, 43.4993],
					[-93.9698, 43.4993],
					[-94.6159, 43.5001],
					[-95.7393, 43.5001],
					[-96.4536, 43.5001],
					[-96.4536, 44.5439],
					[-96.45, 44.6307],
					[-96.4536, 44.8901],
					[-96.4536, 45.3016],
					[-96.4895, 45.3574],
					[-96.6187, 45.4081],
					[-96.6833, 45.4115],
					[-96.7336, 45.4588],
					[-96.7659, 45.5215],
					[-96.8592, 45.6057],
					[-96.8412, 45.6452],
					[-96.7515, 45.6985],
					[-96.6725, 45.732],
					[-96.6295, 45.7861],
					[-96.5792, 45.8256],
					[-96.5649, 45.9356],
					[-96.5792, 46.0266],
					[-96.5577, 46.0584],
					[-96.5972, 46.2199],
					[-96.6008, 46.3307],
					[-96.6474, 46.3539],
					[-96.7192, 46.4381],
					[-96.7371, 46.4802],
					[-96.7515, 46.5824],
					[-96.791, 46.6314],
					[-96.7982, 46.6649],
					[-96.7802, 46.7233],
					[-96.8018, 46.8195],
					[-96.7838, 46.8341],
					[-96.7623, 46.9346],
					[-96.7838, 46.9252],
					[-96.8233, 46.969],
					[-96.8197, 47.1159],
					[-96.8377, 47.1949],
					[-96.8448, 47.2929],
					[-96.8341, 47.3358],
					[-96.8592, 47.4243],
					[-96.8556, 47.6107],
					[-96.8735, 47.6133],
					[-96.8987, 47.6898],
					[-96.9382, 47.7688],
					[-96.9669, 47.7834],
					[-97.0063, 47.8702],
					[-97.0171, 47.92],
					[-97.0566, 47.9492],
					[-97.0745, 48.0531],
					[-97.132, 48.1399],
					[-97.1427, 48.2344],
					[-97.1176, 48.2799],
					[-97.1463, 48.3598],
					[-97.1356, 48.4045],
					[-97.1392, 48.4964],
					[-97.1643, 48.5471],
					[-97.1392, 48.6124],
					[-97.1068, 48.633],
					[-97.0961, 48.6863],
					[-97.1499, 48.755],
					[-97.1786, 48.8615],
					[-97.2002, 48.8821],
					[-97.2397, 48.9663],
					[-97.2289, 49.0007]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "34",
			"properties": { "name": "New Jersey" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-75.5603, 39.6301],
					[-75.5101, 39.6859],
					[-75.4778, 39.7134],
					[-75.4598, 39.7658],
					[-75.4167, 39.8019],
					[-75.3557, 39.8397],
					[-75.2193, 39.8612],
					[-75.1296, 39.8998],
					[-75.1296, 39.9591],
					[-75.0722, 39.9806],
					[-75.047, 40.0089],
					[-74.925, 40.0708],
					[-74.8604, 40.0836],
					[-74.8173, 40.1275],
					[-74.7814, 40.1206],
					[-74.7204, 40.1541],
					[-74.7707, 40.2151],
					[-74.8425, 40.2503],
					[-74.8676, 40.295],
					[-74.943, 40.3414],
					[-74.9681, 40.3998],
					[-75.0578, 40.4161],
					[-75.0722, 40.4548],
					[-75.065, 40.5364],
					[-75.1009, 40.5682],
					[-75.1619, 40.5639],
					[-75.1942, 40.5759],
					[-75.2014, 40.6472],
					[-75.1763, 40.673],
					[-75.205, 40.6919],
					[-75.1727, 40.7778],
					[-75.1332, 40.7735],
					[-75.0506, 40.8705],
					[-75.1368, 40.9736],
					[-75.0901, 41.0149],
					[-75.0255, 41.0398],
					[-74.9681, 41.0879],
					[-74.9824, 41.1085],
					[-74.9214, 41.1386],
					[-74.8819, 41.1807],
					[-74.8317, 41.2872],
					[-74.7527, 41.3465],
					[-74.6953, 41.3576],
					[-74.3794, 41.209],
					[-73.9057, 40.9977],
					[-73.8949, 40.9814],
					[-73.9667, 40.8207],
					[-74.0097, 40.7649],
					[-74.0241, 40.709],
					[-74.0456, 40.6901],
					[-74.0708, 40.6609],
					[-74.2036, 40.6309],
					[-74.2179, 40.5587],
					[-74.2502, 40.545],
					[-74.261, 40.502],
					[-74.261, 40.4651],
					[-74.1928, 40.441],
					[-74.1354, 40.4565],
					[-74.0492, 40.4187],
					[-73.999, 40.4109],
					[-73.9846, 40.4487],
					[-73.9703, 40.3714],
					[-73.981, 40.2795],
					[-74.0313, 40.1232],
					[-74.0779, 39.911],
					[-74.0959, 39.7624],
					[-74.2395, 39.5553],
					[-74.2789, 39.5141],
					[-74.3112, 39.5064],
					[-74.3005, 39.4789],
					[-74.3364, 39.4316],
					[-74.4082, 39.3775],
					[-74.4117, 39.3612],
					[-74.523, 39.3139],
					[-74.6379, 39.2212],
					[-74.7132, 39.1198],
					[-74.7922, 38.9918],
					[-74.864, 38.9402],
					[-74.9322, 38.9282],
					[-74.9717, 38.9402],
					[-74.9501, 39.0158],
					[-74.8963, 39.0992],
					[-74.8855, 39.1584],
					[-74.9609, 39.1902],
					[-75.0255, 39.1937],
					[-75.047, 39.2151],
					[-75.1117, 39.2126],
					[-75.1368, 39.1816],
					[-75.1691, 39.2023],
					[-75.1763, 39.2426],
					[-75.2409, 39.2744],
					[-75.2516, 39.3002],
					[-75.2875, 39.2899],
					[-75.3414, 39.3483],
					[-75.3665, 39.3414],
					[-75.4311, 39.3912],
					[-75.467, 39.4394],
					[-75.5352, 39.4608],
					[-75.5137, 39.5811],
					[-75.5567, 39.606],
					[-75.5603, 39.6301]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "37",
			"properties": { "name": "North Carolina" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-75.7254, 35.9361],
						[-75.6608, 35.9198],
						[-75.6177, 35.8562],
						[-75.6142, 35.8158],
						[-75.668, 35.8236],
						[-75.6608, 35.8622],
						[-75.7254, 35.9361]
					]],
					[[
						[-76.0126, 35.0685],
						[-75.9839, 35.12],
						[-75.9551, 35.12],
						[-75.7864, 35.1939],
						[-75.7326, 35.2042],
						[-75.6824, 35.2325],
						[-75.6393, 35.2343],
						[-75.5819, 35.2643],
						[-75.5352, 35.2729],
						[-75.5172, 35.3365],
						[-75.4778, 35.553],
						[-75.4778, 35.5994],
						[-75.5065, 35.681],
						[-75.528, 35.7712],
						[-75.5172, 35.7694],
						[-75.4778, 35.6784],
						[-75.4598, 35.5968],
						[-75.4706, 35.4799],
						[-75.5244, 35.2343],
						[-75.6106, 35.2274],
						[-75.79, 35.1724],
						[-75.9444, 35.1054],
						[-76.0126, 35.0685]
					]],
					[[
						[-81.6765, 36.5881],
						[-81.375, 36.5744],
						[-81.1776, 36.5718],
						[-80.8366, 36.5589],
						[-80.7038, 36.5624],
						[-80.2946, 36.5443],
						[-80.1223, 36.5426],
						[-79.5121, 36.5409],
						[-78.5107, 36.5409],
						[-78.2451, 36.5443],
						[-77.2975, 36.5443],
						[-76.9171, 36.5435],
						[-76.9171, 36.5521],
						[-76.8058, 36.5503],
						[-76.3751, 36.5503],
						[-75.8654, 36.5503],
						[-75.8582, 36.5005],
						[-75.7972, 36.29],
						[-75.7398, 36.1543],
						[-75.6572, 36.0203],
						[-75.5316, 35.7875],
						[-75.5639, 35.7995],
						[-75.6285, 35.9249],
						[-75.6788, 35.9937],
						[-75.7254, 36.0031],
						[-75.7577, 36.1526],
						[-75.8152, 36.2857],
						[-75.8439, 36.3055],
						[-75.8331, 36.339],
						[-75.851, 36.4155],
						[-75.8869, 36.4412],
						[-75.9013, 36.4825],
						[-75.991, 36.4936],
						[-75.9946, 36.528],
						[-76.0413, 36.5108],
						[-76.0197, 36.4584],
						[-75.9623, 36.4172],
						[-75.9228, 36.4258],
						[-75.9228, 36.3682],
						[-75.869, 36.2522],
						[-75.8367, 36.1998],
						[-75.8403, 36.1775],
						[-75.8008, 36.1131],
						[-75.8008, 36.0727],
						[-75.869, 36.1277],
						[-75.8654, 36.1595],
						[-75.9121, 36.2119],
						[-75.9228, 36.2445],
						[-75.9659, 36.2548],
						[-75.9444, 36.2222],
						[-75.9551, 36.1981],
						[-75.9049, 36.1646],
						[-76.0162, 36.1861],
						[-76.131, 36.2875],
						[-76.1849, 36.2978],
						[-76.1167, 36.2144],
						[-76.0808, 36.1998],
						[-76.0592, 36.1552],
						[-76.0915, 36.1354],
						[-76.1777, 36.1234],
						[-76.253, 36.1844],
						[-76.2279, 36.1303],
						[-76.192, 36.1071],
						[-76.2172, 36.095],
						[-76.3284, 36.1337],
						[-76.3751, 36.138],
						[-76.393, 36.1629],
						[-76.4576, 36.1835],
						[-76.3751, 36.1208],
						[-76.2997, 36.101],
						[-76.3248, 36.0847],
						[-76.411, 36.0779],
						[-76.4576, 36.0246],
						[-76.5151, 36.0057],
						[-76.5761, 36.0066],
						[-76.6048, 36.0332],
						[-76.6766, 36.0435],
						[-76.7197, 36.1474],
						[-76.7197, 36.1998],
						[-76.6766, 36.2668],
						[-76.6945, 36.278],
						[-76.7448, 36.2127],
						[-76.752, 36.1474],
						[-76.7232, 36.0667],
						[-76.6838, 36.0005],
						[-76.7017, 35.9645],
						[-76.673, 35.9352],
						[-76.5294, 35.9438],
						[-76.3966, 35.9842],
						[-76.3643, 35.9421],
						[-76.3177, 35.9473],
						[-76.271, 35.973],
						[-76.1777, 35.9937],
						[-76.0628, 35.9928],
						[-76.0126, 35.9576],
						[-76.0126, 35.9206],
						[-76.0628, 35.8536],
						[-76.052, 35.8064],
						[-76.0449, 35.6655],
						[-76.0126, 35.6698],
						[-75.9874, 35.7686],
						[-75.9767, 35.8974],
						[-75.9264, 35.9318],
						[-75.948, 35.9602],
						[-75.8977, 35.9773],
						[-75.808, 35.9593],
						[-75.7505, 35.8786],
						[-75.7254, 35.8227],
						[-75.7398, 35.778],
						[-75.7147, 35.6939],
						[-75.7434, 35.6724],
						[-75.729, 35.626],
						[-75.7757, 35.5796],
						[-75.8367, 35.571],
						[-75.8582, 35.5865],
						[-75.8941, 35.5727],
						[-75.9157, 35.5384],
						[-75.9515, 35.5306],
						[-75.9623, 35.4937],
						[-75.9874, 35.4842],
						[-76.0126, 35.4232],
						[-76.0592, 35.4104],
						[-76.07, 35.3708],
						[-76.131, 35.3494],
						[-76.1418, 35.3288],
						[-76.2351, 35.3502],
						[-76.3392, 35.3468],
						[-76.4325, 35.3623],
						[-76.4505, 35.3837],
						[-76.4864, 35.3717],
						[-76.5402, 35.4104],
						[-76.5869, 35.5092],
						[-76.4756, 35.5117],
						[-76.4576, 35.5504],
						[-76.5581, 35.5289],
						[-76.6012, 35.5384],
						[-76.6335, 35.51],
						[-76.6012, 35.461],
						[-76.5797, 35.3872],
						[-76.6048, 35.3872],
						[-76.7089, 35.4275],
						[-76.7591, 35.419],
						[-76.8309, 35.4482],
						[-76.9422, 35.4739],
						[-77.0247, 35.5152],
						[-77.0283, 35.4903],
						[-76.9673, 35.4379],
						[-76.8919, 35.4336],
						[-76.6658, 35.3459],
						[-76.5007, 35.3219],
						[-76.472, 35.2952],
						[-76.4684, 35.2609],
						[-76.4935, 35.2128],
						[-76.5402, 35.1664],
						[-76.5366, 35.1423],
						[-76.5689, 35.0968],
						[-76.6227, 35.0607],
						[-76.8022, 34.9645],
						[-76.9817, 35.0607],
						[-76.9781, 35.0049],
						[-76.8919, 34.9576],
						[-76.7627, 34.9207],
						[-76.6335, 34.9894],
						[-76.5869, 34.9911],
						[-76.4899, 35.0169],
						[-76.4756, 35.0702],
						[-76.4361, 35.0581],
						[-76.4253, 35.0015],
						[-76.3966, 34.9748],
						[-76.332, 34.9705],
						[-76.3643, 35.035],
						[-76.2925, 35.01],
						[-76.2854, 34.937],
						[-76.3464, 34.8726],
						[-76.411, 34.8614],
						[-76.411, 34.8322],
						[-76.4505, 34.815],
						[-76.5007, 34.7429],
						[-76.5258, 34.6819],
						[-76.5869, 34.6991],
						[-76.5833, 34.7678],
						[-76.6192, 34.7841],
						[-76.6156, 34.7137],
						[-76.673, 34.7077],
						[-76.5222, 34.6527],
						[-76.4397, 34.7583],
						[-76.3212, 34.8614],
						[-76.2351, 34.9259],
						[-76.0664, 35.077],
						[-76.0377, 35.059],
						[-76.1382, 34.9877],
						[-76.2315, 34.9052],
						[-76.3105, 34.852],
						[-76.3859, 34.785],
						[-76.4505, 34.7145],
						[-76.5258, 34.6157],
						[-76.5366, 34.5882],
						[-76.551, 34.6458],
						[-76.6766, 34.693],
						[-76.8166, 34.6939],
						[-77.0319, 34.6613],
						[-77.136, 34.6329],
						[-77.2078, 34.6054],
						[-77.3227, 34.5358],
						[-77.4626, 34.4714],
						[-77.556, 34.4173],
						[-77.6888, 34.3202],
						[-77.739, 34.273],
						[-77.8287, 34.163],
						[-77.879, 34.0677],
						[-77.9149, 33.9714],
						[-77.9472, 33.9122],
						[-77.9616, 33.8529],
						[-78.0082, 33.8589],
						[-78.019, 33.8881],
						[-78.0944, 33.9061],
						[-78.1769, 33.9139],
						[-78.2774, 33.9122],
						[-78.3851, 33.9019],
						[-78.5107, 33.8658],
						[-78.5394, 33.8512],
						[-78.8732, 34.1347],
						[-79.2465, 34.4456],
						[-79.3578, 34.5453],
						[-79.6736, 34.8047],
						[-79.8926, 34.8056],
						[-80.1582, 34.8116],
						[-80.7971, 34.8202],
						[-80.7827, 34.9362],
						[-80.9335, 35.1071],
						[-81.0412, 35.0444],
						[-81.0591, 35.0624],
						[-81.034, 35.1037],
						[-81.0412, 35.1492],
						[-81.2386, 35.1604],
						[-81.6155, 35.1741],
						[-82.2902, 35.199],
						[-82.3728, 35.1819],
						[-82.3907, 35.2154],
						[-82.4338, 35.1707],
						[-82.4589, 35.1784],
						[-82.5307, 35.1552],
						[-82.5523, 35.1595],
						[-82.6563, 35.1191],
						[-82.7568, 35.0676],
						[-82.782, 35.0856],
						[-83.1086, 35.0006],
						[-83.6219, 34.992],
						[-83.6183, 34.9868],
						[-84.3218, 34.9886],
						[-84.2895, 35.2248],
						[-84.2105, 35.266],
						[-84.1997, 35.2437],
						[-84.0992, 35.2471],
						[-84.0203, 35.3013],
						[-84.0382, 35.3502],
						[-84.0059, 35.3717],
						[-84.0203, 35.4095],
						[-83.9593, 35.4628],
						[-83.9126, 35.4757],
						[-83.8839, 35.5169],
						[-83.8265, 35.5238],
						[-83.7726, 35.5624],
						[-83.4855, 35.5684],
						[-83.446, 35.6114],
						[-83.367, 35.6389],
						[-83.3491, 35.6612],
						[-83.3132, 35.6552],
						[-83.2558, 35.6964],
						[-83.2414, 35.7265],
						[-83.184, 35.7299],
						[-83.1553, 35.7643],
						[-83.0799, 35.7892],
						[-82.9937, 35.7737],
						[-82.9004, 35.8725],
						[-82.9112, 35.9267],
						[-82.8753, 35.9524],
						[-82.8215, 35.9215],
						[-82.7497, 36.0057],
						[-82.6312, 36.0658],
						[-82.5953, 36.0315],
						[-82.6133, 35.9713],
						[-82.5594, 35.9541],
						[-82.4589, 36.0074],
						[-82.4051, 36.0873],
						[-82.3513, 36.1174],
						[-82.2902, 36.1354],
						[-82.2472, 36.1311],
						[-82.2149, 36.1595],
						[-82.1467, 36.1492],
						[-82.1323, 36.1062],
						[-82.0785, 36.1062],
						[-82.0282, 36.1303],
						[-81.9098, 36.3021],
						[-81.8344, 36.3476],
						[-81.7949, 36.3579],
						[-81.7662, 36.3381],
						[-81.7052, 36.3381],
						[-81.7411, 36.4017],
						[-81.716, 36.4576],
						[-81.6944, 36.4679],
						[-81.7088, 36.5366],
						[-81.6765, 36.5881]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "38",
			"properties": { "name": "North Dakota" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-104.0485, 48.9998],
					[-103.3738, 48.999],
					[-102.218, 48.999],
					[-101.4571, 48.999],
					[-101.1269, 48.999],
					[-100.4341, 48.9998],
					[-99.8598, 48.999],
					[-99.3753, 48.999],
					[-98.8692, 48.9998],
					[-97.9503, 49.0007],
					[-97.2289, 49.0007],
					[-97.2397, 48.9663],
					[-97.2002, 48.8821],
					[-97.1786, 48.8615],
					[-97.1499, 48.755],
					[-97.0961, 48.6863],
					[-97.1068, 48.633],
					[-97.1392, 48.6124],
					[-97.1643, 48.5471],
					[-97.1392, 48.4964],
					[-97.1356, 48.4045],
					[-97.1463, 48.3598],
					[-97.1176, 48.2799],
					[-97.1427, 48.2344],
					[-97.132, 48.1399],
					[-97.0745, 48.0531],
					[-97.0566, 47.9492],
					[-97.0171, 47.92],
					[-97.0063, 47.8702],
					[-96.9669, 47.7834],
					[-96.9382, 47.7688],
					[-96.8987, 47.6898],
					[-96.8735, 47.6133],
					[-96.8556, 47.6107],
					[-96.8592, 47.4243],
					[-96.8341, 47.3358],
					[-96.8448, 47.2929],
					[-96.8377, 47.1949],
					[-96.8197, 47.1159],
					[-96.8233, 46.969],
					[-96.7838, 46.9252],
					[-96.7623, 46.9346],
					[-96.7838, 46.8341],
					[-96.8018, 46.8195],
					[-96.7802, 46.7233],
					[-96.7982, 46.6649],
					[-96.791, 46.6314],
					[-96.7515, 46.5824],
					[-96.7371, 46.4802],
					[-96.7192, 46.4381],
					[-96.6474, 46.3539],
					[-96.6008, 46.3307],
					[-96.5972, 46.2199],
					[-96.5577, 46.0584],
					[-96.5792, 46.0266],
					[-96.5649, 45.9356],
					[-97.3186, 45.9347],
					[-97.9862, 45.9356],
					[-98.4134, 45.9364],
					[-99.3179, 45.9399],
					[-100.007, 45.9416],
					[-100.6495, 45.9433],
					[-101.5576, 45.9442],
					[-101.9991, 45.9442],
					[-103.0471, 45.945],
					[-103.6681, 45.945],
					[-104.045, 45.945],
					[-104.045, 48.3744],
					[-104.0485, 48.389],
					[-104.0485, 48.9998]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "40",
			"properties": { "name": "Oklahoma" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-103.0005, 37.0005],
					[-102.7779, 36.9996],
					[-102.699, 36.9953],
					[-102.0421, 36.9928],
					[-101.4858, 36.9953],
					[-100.7356, 36.9988],
					[-100.2008, 37.0022],
					[-99.6481, 36.9996],
					[-99.2784, 36.9996],
					[-98.3523, 36.9979],
					[-97.6058, 36.9988],
					[-96.8233, 36.9988],
					[-96.2167, 36.9988],
					[-95.5132, 36.9996],
					[-94.6195, 36.9988],
					[-94.6195, 36.4997],
					[-94.5979, 36.388],
					[-94.5728, 36.211],
					[-94.5513, 36.1019],
					[-94.501, 35.7969],
					[-94.4867, 35.7265],
					[-94.4328, 35.394],
					[-94.44, 35.12],
					[-94.4508, 34.8958],
					[-94.4544, 34.6939],
					[-94.4615, 34.5479],
					[-94.4687, 34.1896],
					[-94.4867, 33.6381],
					[-94.519, 33.6166],
					[-94.5872, 33.6794],
					[-94.6446, 33.6682],
					[-94.6482, 33.6879],
					[-94.7092, 33.6871],
					[-94.7738, 33.755],
					[-94.8276, 33.7412],
					[-94.8707, 33.7455],
					[-94.9461, 33.8125],
					[-94.9712, 33.8623],
					[-95.0502, 33.8641],
					[-95.1327, 33.9371],
					[-95.2296, 33.9611],
					[-95.2548, 33.9027],
					[-95.2943, 33.8752],
					[-95.3409, 33.8692],
					[-95.463, 33.8726],
					[-95.463, 33.8855],
					[-95.5383, 33.8795],
					[-95.5635, 33.9319],
					[-95.5993, 33.9345],
					[-95.6855, 33.8898],
					[-95.7573, 33.8924],
					[-95.7537, 33.8537],
					[-95.8003, 33.8615],
					[-95.8326, 33.8348],
					[-95.9367, 33.8872],
					[-95.9403, 33.8615],
					[-96.048, 33.8366],
					[-96.1018, 33.8477],
					[-96.0983, 33.8306],
					[-96.1485, 33.8374],
					[-96.1772, 33.7601],
					[-96.2275, 33.7481],
					[-96.2957, 33.7644],
					[-96.3208, 33.6948],
					[-96.3639, 33.6922],
					[-96.3675, 33.7111],
					[-96.4285, 33.779],
					[-96.5038, 33.7739],
					[-96.5326, 33.8228],
					[-96.572, 33.8194],
					[-96.6295, 33.8452],
					[-96.5864, 33.895],
					[-96.6761, 33.9087],
					[-96.7048, 33.8348],
					[-96.7695, 33.8271],
					[-96.7838, 33.8632],
					[-96.8305, 33.8752],
					[-96.852, 33.8469],
					[-96.8843, 33.8684],
					[-96.9058, 33.95],
					[-96.9238, 33.9594],
					[-96.974, 33.9362],
					[-96.9956, 33.9491],
					[-96.9848, 33.8864],
					[-97.0422, 33.8374],
					[-97.0889, 33.8537],
					[-97.0494, 33.8177],
					[-97.0961, 33.7988],
					[-97.0853, 33.7438],
					[-97.1248, 33.7172],
					[-97.1643, 33.7292],
					[-97.2073, 33.8099],
					[-97.1715, 33.8357],
					[-97.1786, 33.8924],
					[-97.2109, 33.9165],
					[-97.2468, 33.9001],
					[-97.254, 33.8641],
					[-97.3007, 33.8804],
					[-97.333, 33.8744],
					[-97.3725, 33.8194],
					[-97.4442, 33.8237],
					[-97.4622, 33.8495],
					[-97.4586, 33.9036],
					[-97.5017, 33.9199],
					[-97.5591, 33.8967],
					[-97.5986, 33.9182],
					[-97.5878, 33.9534],
					[-97.656, 33.9895],
					[-97.6883, 33.9869],
					[-97.7314, 33.9371],
					[-97.8355, 33.858],
					[-97.8785, 33.8503],
					[-97.936, 33.8795],
					[-97.9683, 33.8821],
					[-97.9539, 33.9379],
					[-97.9719, 33.9371],
					[-97.9467, 33.9886],
					[-97.9719, 34.0058],
					[-98.0185, 33.9938],
					[-98.0867, 34.0032],
					[-98.1226, 34.0814],
					[-98.0939, 34.1115],
					[-98.1083, 34.1544],
					[-98.1693, 34.114],
					[-98.2411, 34.1329],
					[-98.2949, 34.1329],
					[-98.3631, 34.157],
					[-98.4026, 34.1226],
					[-98.4134, 34.0848],
					[-98.4851, 34.0625],
					[-98.5713, 34.145],
					[-98.6467, 34.1647],
					[-98.6897, 34.1329],
					[-98.7651, 34.1364],
					[-98.8118, 34.1587],
					[-98.8584, 34.1613],
					[-98.9194, 34.1819],
					[-98.9876, 34.2214],
					[-99.0451, 34.1982],
					[-99.0774, 34.2111],
					[-99.1204, 34.2017],
					[-99.1276, 34.2188],
					[-99.1922, 34.2163],
					[-99.2102, 34.3365],
					[-99.2748, 34.3846],
					[-99.2604, 34.4035],
					[-99.3179, 34.4078],
					[-99.3825, 34.4568],
					[-99.3968, 34.3778],
					[-99.4399, 34.3743],
					[-99.5189, 34.4147],
					[-99.5799, 34.4164],
					[-99.6014, 34.3743],
					[-99.7091, 34.3872],
					[-99.7952, 34.4542],
					[-99.928, 34.5771],
					[-99.9998, 34.5608],
					[-99.9998, 36.4997],
					[-100.4413, 36.4997],
					[-101.0838, 36.4997],
					[-101.6222, 36.4997],
					[-102.2503, 36.5005],
					[-103.0041, 36.5005],
					[-103.0005, 36.6027],
					[-103.0005, 37.0005]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "42",
			"properties": { "name": "Pennsylvania" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-80.5207, 41.9779],
					[-80.3484, 42.0303],
					[-80.1869, 42.0938],
					[-80.1546, 42.1145],
					[-80.1187, 42.166],
					[-80.0721, 42.1686],
					[-80.0613, 42.1445],
					[-79.9321, 42.2064],
					[-79.7634, 42.27],
					[-79.7634, 42.0002],
					[-79.7598, 41.9985],
					[-78.8732, 41.9976],
					[-78.5969, 42.0002],
					[-77.9974, 41.9985],
					[-77.1252, 41.9994],
					[-76.7484, 42.0019],
					[-76.35, 41.9985],
					[-75.7434, 41.9976],
					[-75.3593, 41.9994],
					[-75.3091, 41.9487],
					[-75.2911, 41.9521],
					[-75.2624, 41.8662],
					[-75.2086, 41.8696],
					[-75.1117, 41.8404],
					[-75.0758, 41.7983],
					[-75.1045, 41.7708],
					[-75.0542, 41.7528],
					[-75.047, 41.6162],
					[-75.0758, 41.6059],
					[-74.986, 41.5088],
					[-74.9824, 41.4796],
					[-74.943, 41.4839],
					[-74.8891, 41.4556],
					[-74.8963, 41.4401],
					[-74.8066, 41.4427],
					[-74.7958, 41.4238],
					[-74.7384, 41.4307],
					[-74.742, 41.4049],
					[-74.6953, 41.3576],
					[-74.7527, 41.3465],
					[-74.8317, 41.2872],
					[-74.8819, 41.1807],
					[-74.9214, 41.1386],
					[-74.9824, 41.1085],
					[-74.9681, 41.0879],
					[-75.0255, 41.0398],
					[-75.0901, 41.0149],
					[-75.1368, 40.9736],
					[-75.0506, 40.8705],
					[-75.1332, 40.7735],
					[-75.1727, 40.7778],
					[-75.205, 40.6919],
					[-75.1763, 40.673],
					[-75.2014, 40.6472],
					[-75.1942, 40.5759],
					[-75.1619, 40.5639],
					[-75.1009, 40.5682],
					[-75.065, 40.5364],
					[-75.0722, 40.4548],
					[-75.0578, 40.4161],
					[-74.9681, 40.3998],
					[-74.943, 40.3414],
					[-74.8676, 40.295],
					[-74.8425, 40.2503],
					[-74.7707, 40.2151],
					[-74.7204, 40.1541],
					[-74.7814, 40.1206],
					[-74.8173, 40.1275],
					[-74.8604, 40.0836],
					[-74.925, 40.0708],
					[-75.047, 40.0089],
					[-75.0722, 39.9806],
					[-75.1296, 39.9591],
					[-75.1296, 39.8998],
					[-75.2193, 39.8612],
					[-75.3557, 39.8397],
					[-75.4167, 39.8019],
					[-75.4993, 39.8337],
					[-75.5783, 39.8388],
					[-75.6644, 39.8216],
					[-75.7182, 39.7924],
					[-75.79, 39.722],
					[-76.5689, 39.7211],
					[-76.9924, 39.7203],
					[-77.5344, 39.7203],
					[-78.3815, 39.7228],
					[-79.4762, 39.7211],
					[-80.0756, 39.7211],
					[-80.5207, 39.7211],
					[-80.5207, 40.6386],
					[-80.5207, 41.9779]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "46",
			"properties": { "name": "South Dakota" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-104.0593, 44.9975],
					[-104.0414, 45.0009],
					[-104.0414, 45.75],
					[-104.045, 45.945],
					[-103.6681, 45.945],
					[-103.0471, 45.945],
					[-101.9991, 45.9442],
					[-101.5576, 45.9442],
					[-100.6495, 45.9433],
					[-100.007, 45.9416],
					[-99.3179, 45.9399],
					[-98.4134, 45.9364],
					[-97.9862, 45.9356],
					[-97.3186, 45.9347],
					[-96.5649, 45.9356],
					[-96.5792, 45.8256],
					[-96.6295, 45.7861],
					[-96.6725, 45.732],
					[-96.7515, 45.6985],
					[-96.8412, 45.6452],
					[-96.8592, 45.6057],
					[-96.7659, 45.5215],
					[-96.7336, 45.4588],
					[-96.6833, 45.4115],
					[-96.6187, 45.4081],
					[-96.4895, 45.3574],
					[-96.4536, 45.3016],
					[-96.4536, 44.8901],
					[-96.45, 44.6307],
					[-96.4536, 44.5439],
					[-96.4536, 43.5001],
					[-96.5972, 43.5001],
					[-96.5792, 43.4812],
					[-96.6043, 43.4494],
					[-96.5254, 43.3945],
					[-96.529, 43.3],
					[-96.5792, 43.2957],
					[-96.5505, 43.251],
					[-96.5613, 43.2244],
					[-96.4751, 43.2218],
					[-96.468, 43.1505],
					[-96.4356, 43.1204],
					[-96.4608, 43.0637],
					[-96.511, 43.05],
					[-96.4931, 43.0019],
					[-96.5218, 42.9778],
					[-96.5003, 42.9598],
					[-96.5433, 42.9203],
					[-96.5433, 42.8515],
					[-96.5828, 42.8378],
					[-96.5936, 42.7931],
					[-96.6331, 42.7708],
					[-96.6295, 42.7055],
					[-96.5756, 42.6823],
					[-96.5146, 42.6308],
					[-96.4787, 42.556],
					[-96.4931, 42.5174],
					[-96.4464, 42.4907],
					[-96.5074, 42.4847],
					[-96.5254, 42.5105],
					[-96.6115, 42.5062],
					[-96.6366, 42.5517],
					[-96.712, 42.6084],
					[-96.6869, 42.6531],
					[-96.7264, 42.6668],
					[-96.8018, 42.6694],
					[-96.8053, 42.7038],
					[-96.9058, 42.7338],
					[-96.9489, 42.7192],
					[-96.9776, 42.7605],
					[-97.132, 42.7716],
					[-97.1499, 42.7957],
					[-97.2145, 42.8129],
					[-97.2181, 42.8455],
					[-97.3078, 42.867],
					[-97.3617, 42.855],
					[-97.4155, 42.8661],
					[-97.4407, 42.8464],
					[-97.5053, 42.8584],
					[-97.5627, 42.8472],
					[-97.6201, 42.8567],
					[-97.6847, 42.8421],
					[-97.7745, 42.8498],
					[-97.8462, 42.8679],
					[-97.875, 42.8584],
					[-97.9073, 42.7948],
					[-97.9503, 42.7699],
					[-98.0185, 42.7622],
					[-98.1298, 42.8215],
					[-98.259, 42.8747],
					[-98.4672, 42.9478],
					[-98.4995, 42.9984],
					[-98.9015, 42.9984],
					[-99.5691, 42.9976],
					[-100.1183, 42.9984],
					[-100.8864, 42.9976],
					[-101.6258, 42.9959],
					[-101.8483, 42.9993],
					[-102.4872, 42.9993],
					[-103.0005, 43.0002],
					[-104.0521, 43.001],
					[-104.0521, 43.2974],
					[-104.0557, 43.3043],
					[-104.0557, 44.8746],
					[-104.0593, 44.9975]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "48",
			"properties": { "name": "Texas" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-103.0041, 36.5005],
					[-102.2503, 36.5005],
					[-101.6222, 36.4997],
					[-101.0838, 36.4997],
					[-100.4413, 36.4997],
					[-99.9998, 36.4997],
					[-99.9998, 34.5608],
					[-99.928, 34.5771],
					[-99.7952, 34.4542],
					[-99.7091, 34.3872],
					[-99.6014, 34.3743],
					[-99.5799, 34.4164],
					[-99.5189, 34.4147],
					[-99.4399, 34.3743],
					[-99.3968, 34.3778],
					[-99.3825, 34.4568],
					[-99.3179, 34.4078],
					[-99.2604, 34.4035],
					[-99.2748, 34.3846],
					[-99.2102, 34.3365],
					[-99.1922, 34.2163],
					[-99.1276, 34.2188],
					[-99.1204, 34.2017],
					[-99.0774, 34.2111],
					[-99.0451, 34.1982],
					[-98.9876, 34.2214],
					[-98.9194, 34.1819],
					[-98.8584, 34.1613],
					[-98.8118, 34.1587],
					[-98.7651, 34.1364],
					[-98.6897, 34.1329],
					[-98.6467, 34.1647],
					[-98.5713, 34.145],
					[-98.4851, 34.0625],
					[-98.4134, 34.0848],
					[-98.4026, 34.1226],
					[-98.3631, 34.157],
					[-98.2949, 34.1329],
					[-98.2411, 34.1329],
					[-98.1693, 34.114],
					[-98.1083, 34.1544],
					[-98.0939, 34.1115],
					[-98.1226, 34.0814],
					[-98.0867, 34.0032],
					[-98.0185, 33.9938],
					[-97.9719, 34.0058],
					[-97.9467, 33.9886],
					[-97.9719, 33.9371],
					[-97.9539, 33.9379],
					[-97.9683, 33.8821],
					[-97.936, 33.8795],
					[-97.8785, 33.8503],
					[-97.8355, 33.858],
					[-97.7314, 33.9371],
					[-97.6883, 33.9869],
					[-97.656, 33.9895],
					[-97.5878, 33.9534],
					[-97.5986, 33.9182],
					[-97.5591, 33.8967],
					[-97.5017, 33.9199],
					[-97.4586, 33.9036],
					[-97.4622, 33.8495],
					[-97.4442, 33.8237],
					[-97.3725, 33.8194],
					[-97.333, 33.8744],
					[-97.3007, 33.8804],
					[-97.254, 33.8641],
					[-97.2468, 33.9001],
					[-97.2109, 33.9165],
					[-97.1786, 33.8924],
					[-97.1715, 33.8357],
					[-97.2073, 33.8099],
					[-97.1643, 33.7292],
					[-97.1248, 33.7172],
					[-97.0853, 33.7438],
					[-97.0961, 33.7988],
					[-97.0494, 33.8177],
					[-97.0889, 33.8537],
					[-97.0422, 33.8374],
					[-96.9848, 33.8864],
					[-96.9956, 33.9491],
					[-96.974, 33.9362],
					[-96.9238, 33.9594],
					[-96.9058, 33.95],
					[-96.8843, 33.8684],
					[-96.852, 33.8469],
					[-96.8305, 33.8752],
					[-96.7838, 33.8632],
					[-96.7695, 33.8271],
					[-96.7048, 33.8348],
					[-96.6761, 33.9087],
					[-96.5864, 33.895],
					[-96.6295, 33.8452],
					[-96.572, 33.8194],
					[-96.5326, 33.8228],
					[-96.5038, 33.7739],
					[-96.4285, 33.779],
					[-96.3675, 33.7111],
					[-96.3639, 33.6922],
					[-96.3208, 33.6948],
					[-96.2957, 33.7644],
					[-96.2275, 33.7481],
					[-96.1772, 33.7601],
					[-96.1485, 33.8374],
					[-96.0983, 33.8306],
					[-96.1018, 33.8477],
					[-96.048, 33.8366],
					[-95.9403, 33.8615],
					[-95.9367, 33.8872],
					[-95.8326, 33.8348],
					[-95.8003, 33.8615],
					[-95.7537, 33.8537],
					[-95.7573, 33.8924],
					[-95.6855, 33.8898],
					[-95.5993, 33.9345],
					[-95.5635, 33.9319],
					[-95.5383, 33.8795],
					[-95.463, 33.8855],
					[-95.463, 33.8726],
					[-95.3409, 33.8692],
					[-95.2943, 33.8752],
					[-95.2548, 33.9027],
					[-95.2296, 33.9611],
					[-95.1327, 33.9371],
					[-95.0502, 33.8641],
					[-94.9712, 33.8623],
					[-94.9461, 33.8125],
					[-94.8707, 33.7455],
					[-94.8276, 33.7412],
					[-94.7738, 33.755],
					[-94.7092, 33.6871],
					[-94.6482, 33.6879],
					[-94.6446, 33.6682],
					[-94.5872, 33.6794],
					[-94.519, 33.6166],
					[-94.4867, 33.6381],
					[-94.4579, 33.645],
					[-94.4579, 33.5986],
					[-94.4185, 33.5771],
					[-94.3862, 33.5823],
					[-94.3933, 33.5514],
					[-94.3574, 33.5436],
					[-94.3395, 33.5668],
					[-94.3108, 33.5514],
					[-94.3, 33.5763],
					[-94.2246, 33.5531],
					[-94.1852, 33.5926],
					[-94.1421, 33.578],
					[-94.1241, 33.5522],
					[-94.1026, 33.5703],
					[-94.0452, 33.5514],
					[-94.0452, 33.5514],
					[-94.0416, 33.5419],
					[-94.0416, 33.0196],
					[-94.0416, 31.9922],
					[-94.0129, 31.9819],
					[-93.9734, 31.92],
					[-93.9088, 31.8934],
					[-93.8765, 31.8221],
					[-93.8226, 31.7748],
					[-93.8298, 31.7456],
					[-93.8011, 31.6975],
					[-93.8262, 31.6614],
					[-93.8155, 31.6228],
					[-93.8406, 31.5987],
					[-93.8191, 31.5549],
					[-93.7868, 31.5274],
					[-93.7401, 31.5223],
					[-93.7293, 31.4922],
					[-93.7509, 31.469],
					[-93.7006, 31.4381],
					[-93.7042, 31.4106],
					[-93.6755, 31.3977],
					[-93.6647, 31.3556],
					[-93.6863, 31.3101],
					[-93.6432, 31.2697],
					[-93.6216, 31.2714],
					[-93.6001, 31.1743],
					[-93.5319, 31.1846],
					[-93.5534, 31.097],
					[-93.5247, 31.0704],
					[-93.5176, 31.024],
					[-93.5642, 31.006],
					[-93.5678, 30.9776],
					[-93.5247, 30.9304],
					[-93.557, 30.9115],
					[-93.5678, 30.8788],
					[-93.5534, 30.8247],
					[-93.5857, 30.7964],
					[-93.6181, 30.7422],
					[-93.6288, 30.6795],
					[-93.6719, 30.658],
					[-93.6791, 30.5996],
					[-93.7293, 30.5739],
					[-93.7401, 30.5395],
					[-93.715, 30.5189],
					[-93.697, 30.4407],
					[-93.7437, 30.3969],
					[-93.7652, 30.3333],
					[-93.7078, 30.2886],
					[-93.7042, 30.2431],
					[-93.7221, 30.2096],
					[-93.6898, 30.1417],
					[-93.7329, 30.0816],
					[-93.7006, 30.0653],
					[-93.7401, 30.0215],
					[-93.7903, 29.988],
					[-93.8155, 29.921],
					[-93.8621, 29.8574],
					[-93.9231, 29.8187],
					[-93.9303, 29.7973],
					[-93.898, 29.7715],
					[-93.837, 29.6907],
					[-93.837, 29.6787],
					[-94.0021, 29.6813],
					[-94.1349, 29.6469],
					[-94.5943, 29.4682],
					[-94.6948, 29.4158],
					[-94.7307, 29.3695],
					[-94.7846, 29.3755],
					[-94.7056, 29.4365],
					[-94.6733, 29.4768],
					[-94.6266, 29.476],
					[-94.5943, 29.4923],
					[-94.5656, 29.5318],
					[-94.5333, 29.5181],
					[-94.4938, 29.5249],
					[-94.5477, 29.5722],
					[-94.7415, 29.5258],
					[-94.7702, 29.5481],
					[-94.7092, 29.6255],
					[-94.6948, 29.6942],
					[-94.6948, 29.7577],
					[-94.7379, 29.762],
					[-94.7559, 29.7818],
					[-94.8169, 29.7569],
					[-94.8528, 29.7217],
					[-94.8671, 29.6787],
					[-94.921, 29.6581],
					[-94.9425, 29.6976],
					[-95.0071, 29.659],
					[-95.0143, 29.6289],
					[-94.982, 29.6014],
					[-95.0179, 29.5584],
					[-94.982, 29.5112],
					[-94.9102, 29.4966],
					[-94.9317, 29.4502],
					[-94.8923, 29.433],
					[-94.8851, 29.366],
					[-94.8923, 29.3085],
					[-94.921, 29.2818],
					[-94.9533, 29.2904],
					[-95.0215, 29.2294],
					[-95.1004, 29.1736],
					[-95.1112, 29.1959],
					[-95.1579, 29.1891],
					[-95.165, 29.1135],
					[-95.1435, 29.0911],
					[-95.0968, 29.1289],
					[-95.0035, 29.1848],
					[-94.9928, 29.2011],
					[-94.8779, 29.2861],
					[-94.8241, 29.3059],
					[-94.8241, 29.3445],
					[-94.7307, 29.3317],
					[-94.8025, 29.2793],
					[-95.0251, 29.1478],
					[-95.1112, 29.0885],
					[-95.1256, 29.0671],
					[-95.2404, 28.9889],
					[-95.384, 28.866],
					[-95.4378, 28.8592],
					[-95.6855, 28.7338],
					[-95.8111, 28.665],
					[-96.0013, 28.5886],
					[-96.2203, 28.4924],
					[-96.3423, 28.4176],
					[-96.389, 28.3815],
					[-96.4428, 28.318],
					[-96.6331, 28.2226],
					[-96.7192, 28.1642],
					[-96.8879, 28.031],
					[-97.0028, 27.9082],
					[-97.0422, 27.8369],
					[-97.0925, 27.7862],
					[-97.2109, 27.5964],
					[-97.2971, 27.4271],
					[-97.3366, 27.318],
					[-97.3725, 27.1531],
					[-97.3796, 27.0603],
					[-97.376, 26.9718],
					[-97.3473, 26.7957],
					[-97.2971, 26.6179],
					[-97.254, 26.5028],
					[-97.1966, 26.306],
					[-97.1571, 26.0827],
					[-97.1822, 26.1231],
					[-97.2002, 26.2494],
					[-97.2432, 26.4117],
					[-97.2827, 26.5423],
					[-97.3689, 26.556],
					[-97.3043, 26.5792],
					[-97.3366, 26.6505],
					[-97.3581, 26.7253],
					[-97.3904, 26.8009],
					[-97.4012, 26.8919],
					[-97.3976, 27.0105],
					[-97.4012, 27.1153],
					[-97.3617, 27.3524],
					[-97.3043, 27.507],
					[-97.2361, 27.6342],
					[-97.1858, 27.6943],
					[-97.1499, 27.769],
					[-97.1212, 27.7991],
					[-97.1284, 27.8395],
					[-97.0817, 27.9133],
					[-97.0171, 27.946],
					[-96.9848, 27.9847],
					[-96.9202, 28.0938],
					[-96.8197, 28.1436],
					[-96.791, 28.1891],
					[-96.7336, 28.1908],
					[-96.6079, 28.281],
					[-96.5254, 28.3231],
					[-96.4392, 28.3429],
					[-96.4536, 28.3798],
					[-96.4213, 28.4047],
					[-96.4536, 28.4185],
					[-96.511, 28.3833],
					[-96.5828, 28.3532],
					[-96.6223, 28.3188],
					[-96.6833, 28.3145],
					[-96.7048, 28.3489],
					[-96.7048, 28.4004],
					[-96.7695, 28.4107],
					[-96.791, 28.3841],
					[-96.791, 28.312],
					[-96.8089, 28.2905],
					[-96.7874, 28.2553],
					[-96.8018, 28.2243],
					[-96.8735, 28.1762],
					[-96.9346, 28.1238],
					[-96.9633, 28.123],
					[-97.0279, 28.1487],
					[-97.0387, 28.1857],
					[-97.1535, 28.1333],
					[-97.2145, 28.0757],
					[-97.1392, 28.0568],
					[-97.053, 28.1058],
					[-97.0243, 28.0422],
					[-97.0494, 28.0224],
					[-97.1858, 27.8309],
					[-97.2289, 27.8292],
					[-97.2504, 27.8764],
					[-97.3258, 27.8678],
					[-97.3402, 27.8833],
					[-97.455, 27.8738],
					[-97.4945, 27.8841],
					[-97.516, 27.8515],
					[-97.473, 27.824],
					[-97.4191, 27.8231],
					[-97.3796, 27.8378],
					[-97.394, 27.7828],
					[-97.3689, 27.7415],
					[-97.315, 27.7123],
					[-97.254, 27.6969],
					[-97.3222, 27.5714],
					[-97.3689, 27.4125],
					[-97.4119, 27.3215],
					[-97.4837, 27.2983],
					[-97.5088, 27.2751],
					[-97.5483, 27.2905],
					[-97.4981, 27.3086],
					[-97.4873, 27.3593],
					[-97.516, 27.3618],
					[-97.5376, 27.3352],
					[-97.6417, 27.2708],
					[-97.6273, 27.2433],
					[-97.5412, 27.2295],
					[-97.4514, 27.2622],
					[-97.4227, 27.2639],
					[-97.4407, 27.1642],
					[-97.4299, 27.1608],
					[-97.4442, 26.9873],
					[-97.4658, 26.8095],
					[-97.4801, 26.8069],
					[-97.4442, 26.6093],
					[-97.4155, 26.5535],
					[-97.4119, 26.4813],
					[-97.3796, 26.4804],
					[-97.2971, 26.2957],
					[-97.2791, 26.2708],
					[-97.3114, 26.2305],
					[-97.2971, 26.2012],
					[-97.3007, 26.148],
					[-97.272, 26.0861],
					[-97.2002, 26.0767],
					[-97.1966, 26.0466],
					[-97.1535, 26.0621],
					[-97.1463, 25.953],
					[-97.2289, 25.959],
					[-97.2755, 25.9521],
					[-97.2755, 25.9358],
					[-97.3509, 25.9255],
					[-97.3725, 25.8404],
					[-97.4442, 25.8499],
					[-97.4694, 25.8842],
					[-97.5232, 25.8868],
					[-97.5447, 25.9341],
					[-97.5842, 25.9375],
					[-97.6488, 26.0217],
					[-97.7601, 26.032],
					[-97.7996, 26.0604],
					[-97.8606, 26.0526],
					[-97.8857, 26.0664],
					[-97.9683, 26.0518],
					[-97.9826, 26.0672],
					[-98.0293, 26.0664],
					[-98.0401, 26.0415],
					[-98.076, 26.0681],
					[-98.1477, 26.0561],
					[-98.1765, 26.075],
					[-98.198, 26.0561],
					[-98.2482, 26.0732],
					[-98.277, 26.099],
					[-98.2662, 26.1205],
					[-98.3236, 26.1214],
					[-98.3344, 26.1591],
					[-98.3882, 26.1583],
					[-98.4636, 26.2227],
					[-98.4959, 26.2133],
					[-98.5856, 26.2545],
					[-98.6538, 26.2356],
					[-98.7005, 26.2657],
					[-98.7543, 26.3249],
					[-98.7902, 26.3318],
					[-98.7974, 26.3602],
					[-98.8261, 26.3705],
					[-98.8907, 26.3576],
					[-98.9661, 26.398],
					[-99.0092, 26.3954],
					[-99.0379, 26.4126],
					[-99.081, 26.3962],
					[-99.1133, 26.434],
					[-99.0917, 26.477],
					[-99.1276, 26.5251],
					[-99.1671, 26.5363],
					[-99.1779, 26.6205],
					[-99.1994, 26.6565],
					[-99.2102, 26.7244],
					[-99.2389, 26.7459],
					[-99.2425, 26.788],
					[-99.2676, 26.843],
					[-99.3286, 26.8799],
					[-99.3214, 26.9065],
					[-99.3681, 26.9289],
					[-99.3932, 26.9607],
					[-99.3753, 26.9778],
					[-99.4148, 27.0173],
					[-99.4471, 27.0234],
					[-99.4507, 27.068],
					[-99.4291, 27.0947],
					[-99.4399, 27.1514],
					[-99.4255, 27.1763],
					[-99.4399, 27.2502],
					[-99.4973, 27.2716],
					[-99.4937, 27.3034],
					[-99.5368, 27.3129],
					[-99.5045, 27.3386],
					[-99.4865, 27.4125],
					[-99.4973, 27.4391],
					[-99.483, 27.4907],
					[-99.5296, 27.4993],
					[-99.5117, 27.5646],
					[-99.5547, 27.6144],
					[-99.5799, 27.6024],
					[-99.605, 27.6419],
					[-99.6373, 27.6264],
					[-99.6696, 27.6599],
					[-99.7055, 27.6548],
					[-99.7701, 27.7321],
					[-99.8024, 27.7415],
					[-99.8132, 27.7742],
					[-99.8419, 27.7664],
					[-99.8778, 27.7991],
					[-99.8778, 27.842],
					[-99.9029, 27.8644],
					[-99.8921, 27.8996],
					[-99.9388, 27.9408],
					[-99.9316, 27.9812],
					[-99.9926, 27.995],
					[-100.0178, 28.0645],
					[-100.0537, 28.0843],
					[-100.0824, 28.1444],
					[-100.208, 28.19],
					[-100.2224, 28.2355],
					[-100.2475, 28.2338],
					[-100.2941, 28.2845],
					[-100.287, 28.3171],
					[-100.348, 28.403],
					[-100.3372, 28.4434],
					[-100.3695, 28.4769],
					[-100.3372, 28.5018],
					[-100.3803, 28.5113],
					[-100.4126, 28.5516],
					[-100.3982, 28.5851],
					[-100.4485, 28.6092],
					[-100.4449, 28.6375],
					[-100.4987, 28.6616],
					[-100.5131, 28.7054],
					[-100.5059, 28.7406],
					[-100.5346, 28.763],
					[-100.549, 28.8257],
					[-100.5705, 28.8265],
					[-100.6028, 28.9021],
					[-100.6315, 28.903],
					[-100.6531, 28.9434],
					[-100.6495, 29.0086],
					[-100.6746, 29.0997],
					[-100.7392, 29.1392],
					[-100.7751, 29.1736],
					[-100.7643, 29.1856],
					[-100.7966, 29.2466],
					[-100.8792, 29.2818],
					[-100.8864, 29.3076],
					[-100.9474, 29.3471],
					[-101.012, 29.3686],
					[-101.0587, 29.4408],
					[-101.0587, 29.4588],
					[-101.152, 29.4768],
					[-101.1735, 29.5146],
					[-101.2561, 29.5206],
					[-101.2453, 29.5696],
					[-101.2525, 29.6255],
					[-101.2704, 29.6297],
					[-101.292, 29.5722],
					[-101.3135, 29.5954],
					[-101.3063, 29.6409],
					[-101.3673, 29.6641],
					[-101.414, 29.7569],
					[-101.4427, 29.7535],
					[-101.475, 29.7809],
					[-101.5217, 29.7595],
					[-101.5468, 29.7973],
					[-101.5755, 29.7741],
					[-101.7083, 29.7629],
					[-101.7765, 29.7895],
					[-101.7981, 29.7827],
					[-101.8519, 29.8016],
					[-101.9309, 29.7895],
					[-101.9739, 29.8102],
					[-102.0349, 29.8041],
					[-102.0493, 29.7852],
					[-102.1426, 29.8033],
					[-102.1821, 29.8462],
					[-102.2288, 29.8437],
					[-102.3006, 29.878],
					[-102.3508, 29.8626],
					[-102.3867, 29.7672],
					[-102.5087, 29.7835],
					[-102.541, 29.7517],
					[-102.5662, 29.7612],
					[-102.6451, 29.7337],
					[-102.6703, 29.7423],
					[-102.699, 29.6959],
					[-102.6918, 29.6761],
					[-102.7384, 29.6418],
					[-102.7384, 29.5988],
					[-102.7636, 29.5988],
					[-102.7708, 29.5481],
					[-102.8102, 29.5224],
					[-102.8066, 29.494],
					[-102.8318, 29.4442],
					[-102.8246, 29.3995],
					[-102.8425, 29.3583],
					[-102.8784, 29.354],
					[-102.8892, 29.2921],
					[-102.9071, 29.2604],
					[-102.8713, 29.2415],
					[-102.8892, 29.2088],
					[-102.9538, 29.1762],
					[-102.9897, 29.183],
					[-103.0148, 29.1255],
					[-103.0758, 29.0885],
					[-103.101, 29.0576],
					[-103.1153, 28.9855],
					[-103.1656, 28.9786],
					[-103.2266, 28.9915],
					[-103.2804, 28.982],
					[-103.3307, 29.0215],
					[-103.4276, 29.0422],
					[-103.5245, 29.1375],
					[-103.6106, 29.1659],
					[-103.6465, 29.159],
					[-103.7255, 29.1916],
					[-103.7901, 29.2578],
					[-103.8368, 29.2784],
					[-103.9768, 29.2964],
					[-104.0378, 29.3205],
					[-104.106, 29.3729],
					[-104.1455, 29.3832],
					[-104.2136, 29.4528],
					[-104.2136, 29.4734],
					[-104.2639, 29.5138],
					[-104.3393, 29.5198],
					[-104.3823, 29.543],
					[-104.4003, 29.5722],
					[-104.508, 29.6392],
					[-104.5403, 29.6761],
					[-104.5654, 29.7706],
					[-104.6085, 29.8187],
					[-104.6336, 29.8703],
					[-104.6803, 29.9244],
					[-104.6803, 29.9751],
					[-104.7054, 30.0241],
					[-104.6838, 30.0859],
					[-104.6946, 30.1323],
					[-104.6874, 30.1795],
					[-104.7126, 30.238],
					[-104.752, 30.2637],
					[-104.7628, 30.3015],
					[-104.8095, 30.335],
					[-104.8131, 30.3608],
					[-104.8597, 30.39],
					[-104.8525, 30.4184],
					[-104.8992, 30.5704],
					[-104.9243, 30.6048],
					[-104.9674, 30.6082],
					[-105.0069, 30.6864],
					[-105.0607, 30.6864],
					[-105.111, 30.7431],
					[-105.154, 30.7517],
					[-105.1648, 30.7723],
					[-105.2187, 30.8015],
					[-105.2617, 30.7981],
					[-105.2868, 30.8221],
					[-105.3981, 30.8556],
					[-105.3981, 30.8891],
					[-105.556, 30.9905],
					[-105.6027, 31.0824],
					[-105.6458, 31.1142],
					[-105.7104, 31.1365],
					[-105.7427, 31.1649],
					[-105.775, 31.1666],
					[-105.7822, 31.1975],
					[-105.8683, 31.2886],
					[-105.9401, 31.3187],
					[-105.9545, 31.3651],
					[-106.0047, 31.3925],
					[-106.0801, 31.3986],
					[-106.2057, 31.4656],
					[-106.2452, 31.5412],
					[-106.2811, 31.5618],
					[-106.3026, 31.6202],
					[-106.3493, 31.6966],
					[-106.3816, 31.7319],
					[-106.4498, 31.7645],
					[-106.4893, 31.7482],
					[-106.5287, 31.7834],
					[-106.5467, 31.8075],
					[-106.6041, 31.8246],
					[-106.6005, 31.8444],
					[-106.6364, 31.8659],
					[-106.6472, 31.8985],
					[-106.6221, 31.914],
					[-106.6185, 32.0008],
					[-105.4304, 32.0008],
					[-104.5331, 31.9999],
					[-103.7219, 31.9999],
					[-103.0651, 32.0008],
					[-103.0651, 33.0419],
					[-103.0579, 33.2601],
					[-103.0579, 33.3881],
					[-103.0507, 33.7464],
					[-103.0435, 33.9457],
					[-103.0435, 35.2497],
					[-103.04, 35.6226],
					[-103.04, 36.3175],
					[-103.0435, 36.5005],
					[-103.0041, 36.5005]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "56",
			"properties": { "name": "Wyoming" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-104.0593, 44.9975],
					[-104.0557, 44.8746],
					[-104.0557, 43.3043],
					[-104.0521, 43.2974],
					[-104.0521, 43.001],
					[-104.0521, 41.0011],
					[-104.831, 40.9994],
					[-104.8561, 40.9977],
					[-105.7247, 40.9968],
					[-106.2165, 40.9977],
					[-106.4534, 41.002],
					[-107.6271, 41.002],
					[-108.1798, 41.0003],
					[-109.0484, 41.0003],
					[-109.5366, 40.9985],
					[-110.0498, 40.9977],
					[-110.2365, 40.9951],
					[-110.7139, 40.996],
					[-111.0477, 40.9977],
					[-111.0477, 41.3774],
					[-111.0441, 41.5655],
					[-111.0477, 42.0019],
					[-111.0477, 42.5827],
					[-111.0441, 42.7227],
					[-111.0441, 43.6591],
					[-111.0477, 43.6814],
					[-111.0477, 44.1247],
					[-111.0513, 44.354],
					[-111.0477, 44.4743],
					[-111.0548, 44.6246],
					[-111.0584, 44.8669],
					[-111.0548, 45.0009],
					[-110.7856, 45.0026],
					[-110.7067, 44.9923],
					[-110.4016, 44.994],
					[-110.3621, 45.0009],
					[-110.2006, 44.9966],
					[-110.1109, 45.0035],
					[-109.7986, 45.0018],
					[-109.1023, 45.0061],
					[-109.0628, 45],
					[-108.2696, 45],
					[-107.1353, 45],
					[-107.0851, 44.9966],
					[-106.5682, 44.9949],
					[-105.9293, 44.994],
					[-105.8468, 45],
					[-105.373, 45],
					[-104.4721, 44.9983],
					[-104.0593, 44.9975]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "09",
			"properties": { "name": "Connecticut" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-73.4857, 42.05],
					[-73.4319, 42.0509],
					[-73.0083, 42.0389],
					[-72.8145, 42.0363],
					[-72.8181, 41.9976],
					[-72.7678, 42.0028],
					[-72.7571, 42.0363],
					[-72.6063, 42.0311],
					[-72.5274, 42.0346],
					[-71.8023, 42.0234],
					[-71.7987, 42.0079],
					[-71.7987, 41.916],
					[-71.788, 41.7253],
					[-71.788, 41.5965],
					[-71.7987, 41.4169],
					[-71.8418, 41.41],
					[-71.831, 41.3447],
					[-71.8598, 41.3198],
					[-71.8849, 41.3362],
					[-71.9459, 41.3379],
					[-72.0213, 41.3173],
					[-72.0859, 41.3198],
					[-72.1325, 41.2992],
					[-72.19, 41.3233],
					[-72.2115, 41.2915],
					[-72.3874, 41.2614],
					[-72.3981, 41.2786],
					[-72.452, 41.2786],
					[-72.5489, 41.2503],
					[-72.5704, 41.2683],
					[-72.6638, 41.2691],
					[-72.6889, 41.2468],
					[-72.7535, 41.2666],
					[-72.8971, 41.2434],
					[-72.9078, 41.2949],
					[-72.9401, 41.2803],
					[-73.0083, 41.2099],
					[-73.0514, 41.2099],
					[-73.1304, 41.1472],
					[-73.177, 41.1669],
					[-73.2632, 41.1171],
					[-73.2883, 41.1283],
					[-73.3708, 41.1042],
					[-73.3529, 41.0853],
					[-73.3888, 41.0578],
					[-73.4929, 41.0484],
					[-73.5718, 41.002],
					[-73.6042, 41.0149],
					[-73.658, 40.9848],
					[-73.6544, 41.0131],
					[-73.7262, 41.1008],
					[-73.4821, 41.2125],
					[-73.5503, 41.2958],
					[-73.536, 41.4341],
					[-73.518, 41.6875],
					[-73.4857, 42.05]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "29",
			"properties": { "name": "Missouri" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-95.7645, 40.5853],
					[-95.165, 40.5776],
					[-94.5943, 40.5707],
					[-94.0344, 40.5733],
					[-93.5678, 40.5802],
					[-93.3453, 40.5802],
					[-92.6382, 40.5905],
					[-92.0926, 40.6017],
					[-91.7301, 40.6137],
					[-91.687, 40.581],
					[-91.6834, 40.5527],
					[-91.6188, 40.5407],
					[-91.6224, 40.5097],
					[-91.565, 40.4608],
					[-91.5291, 40.4591],
					[-91.5255, 40.4109],
					[-91.486, 40.3843],
					[-91.4178, 40.3783],
					[-91.4465, 40.3628],
					[-91.5075, 40.2391],
					[-91.5147, 40.1781],
					[-91.4932, 40.0364],
					[-91.4429, 39.9514],
					[-91.4178, 39.9273],
					[-91.4465, 39.8706],
					[-91.4286, 39.838],
					[-91.3747, 39.8087],
					[-91.3604, 39.7847],
					[-91.3712, 39.7323],
					[-91.2778, 39.6661],
					[-91.224, 39.6172],
					[-91.1845, 39.6052],
					[-91.1486, 39.5459],
					[-91.102, 39.539],
					[-91.0625, 39.4737],
					[-91.005, 39.4273],
					[-90.7933, 39.3096],
					[-90.7215, 39.2323],
					[-90.7107, 39.155],
					[-90.682, 39.088],
					[-90.7143, 39.0571],
					[-90.6748, 38.9841],
					[-90.6641, 38.9282],
					[-90.5851, 38.8689],
					[-90.5456, 38.875],
					[-90.5061, 38.9024],
					[-90.4738, 38.9591],
					[-90.4056, 38.9626],
					[-90.3087, 38.9239],
					[-90.2513, 38.9196],
					[-90.1149, 38.8492],
					[-90.1221, 38.7976],
					[-90.1652, 38.7727],
					[-90.2118, 38.722],
					[-90.1759, 38.643],
					[-90.1831, 38.6112],
					[-90.2621, 38.5287],
					[-90.2836, 38.4437],
					[-90.3446, 38.3853],
					[-90.3697, 38.3406],
					[-90.3733, 38.2736],
					[-90.3554, 38.2135],
					[-90.2908, 38.1705],
					[-90.219, 38.0941],
					[-90.1293, 38.0623],
					[-90.0898, 38.0159],
					[-90.0611, 38.0159],
					[-90, 37.9643],
					[-89.9426, 37.9704],
					[-89.9318, 37.9472],
					[-89.9749, 37.9265],
					[-89.9534, 37.8836],
					[-89.8816, 37.8793],
					[-89.8457, 37.9059],
					[-89.799, 37.8819],
					[-89.7955, 37.857],
					[-89.7416, 37.8467],
					[-89.6591, 37.7865],
					[-89.6662, 37.7522],
					[-89.616, 37.7496],
					[-89.5837, 37.7135],
					[-89.5155, 37.6929],
					[-89.5155, 37.6439],
					[-89.476, 37.5855],
					[-89.5227, 37.5786],
					[-89.5155, 37.5357],
					[-89.4222, 37.3974],
					[-89.4365, 37.3441],
					[-89.4904, 37.3338],
					[-89.5191, 37.2857],
					[-89.4581, 37.2402],
					[-89.4617, 37.1998],
					[-89.415, 37.125],
					[-89.3755, 37.0855],
					[-89.3791, 37.04],
					[-89.293, 36.9919],
					[-89.2571, 37.0151],
					[-89.3109, 37.0537],
					[-89.2607, 37.0641],
					[-89.1996, 37.0159],
					[-89.1709, 36.9704],
					[-89.1314, 36.9824],
					[-89.0991, 36.9644],
					[-89.1171, 36.8871],
					[-89.1386, 36.8476],
					[-89.1745, 36.8398],
					[-89.1781, 36.8072],
					[-89.1171, 36.7754],
					[-89.1278, 36.7514],
					[-89.1853, 36.7539],
					[-89.1996, 36.7161],
					[-89.1602, 36.6663],
					[-89.1889, 36.6414],
					[-89.214, 36.5804],
					[-89.2607, 36.5649],
					[-89.3253, 36.6319],
					[-89.3719, 36.6208],
					[-89.4186, 36.4988],
					[-89.4473, 36.4644],
					[-89.494, 36.4704],
					[-89.4868, 36.4971],
					[-89.4652, 36.5297],
					[-89.4832, 36.5718],
					[-89.5586, 36.5735],
					[-89.5729, 36.5478],
					[-89.5406, 36.4979],
					[-89.5191, 36.479],
					[-89.5442, 36.4241],
					[-89.5083, 36.3734],
					[-89.5263, 36.3416],
					[-89.6124, 36.3407],
					[-89.6124, 36.3089],
					[-89.537, 36.2754],
					[-89.5334, 36.2522],
					[-89.5873, 36.2394],
					[-89.695, 36.2531],
					[-89.7057, 36.2351],
					[-89.6052, 36.1715],
					[-89.6016, 36.1191],
					[-89.6806, 36.0847],
					[-89.6914, 36.0203],
					[-89.7344, 36.0005],
					[-90.3769, 35.9954],
					[-90.3195, 36.0899],
					[-90.2369, 36.1397],
					[-90.2369, 36.1595],
					[-90.1831, 36.205],
					[-90.1257, 36.229],
					[-90.1149, 36.266],
					[-90.0826, 36.272],
					[-90.0647, 36.3863],
					[-90.1436, 36.4241],
					[-90.1328, 36.4369],
					[-90.1508, 36.4979],
					[-91.4035, 36.4971],
					[-92.5628, 36.4979],
					[-93.0689, 36.4979],
					[-93.8657, 36.4988],
					[-94.6195, 36.4997],
					[-94.6195, 36.9988],
					[-94.6195, 37.2402],
					[-94.6159, 37.3235],
					[-94.6159, 37.9927],
					[-94.6123, 38.0374],
					[-94.6123, 38.6353],
					[-94.6087, 38.7384],
					[-94.6087, 39.1198],
					[-94.5908, 39.155],
					[-94.6625, 39.1576],
					[-94.6805, 39.1842],
					[-94.7128, 39.1705],
					[-94.763, 39.1799],
					[-94.781, 39.2065],
					[-94.8348, 39.2177],
					[-94.8241, 39.2418],
					[-94.8887, 39.2864],
					[-94.9102, 39.3543],
					[-94.8779, 39.3758],
					[-94.8958, 39.3938],
					[-94.9461, 39.3938],
					[-94.9892, 39.4462],
					[-95.0466, 39.4729],
					[-95.0502, 39.4978],
					[-95.104, 39.533],
					[-95.1076, 39.5742],
					[-95.0466, 39.5948],
					[-95.0538, 39.6301],
					[-95.0286, 39.6653],
					[-94.9748, 39.6816],
					[-94.9569, 39.746],
					[-94.9102, 39.7254],
					[-94.8743, 39.7306],
					[-94.8707, 39.7727],
					[-94.9138, 39.7589],
					[-94.9246, 39.7898],
					[-94.8923, 39.7907],
					[-94.8779, 39.8268],
					[-94.9174, 39.8362],
					[-94.9425, 39.8646],
					[-94.9281, 39.8886],
					[-95.0143, 39.8998],
					[-95.043, 39.8646],
					[-95.1363, 39.8766],
					[-95.1543, 39.9075],
					[-95.2009, 39.9024],
					[-95.2045, 39.9393],
					[-95.2512, 39.9488],
					[-95.3086, 40.0003],
					[-95.4199, 40.0484],
					[-95.3912, 40.1163],
					[-95.4773, 40.1816],
					[-95.4845, 40.2477],
					[-95.5563, 40.264],
					[-95.5527, 40.2915],
					[-95.6173, 40.3139],
					[-95.6604, 40.4084],
					[-95.6568, 40.4419],
					[-95.6963, 40.4711],
					[-95.6998, 40.5054],
					[-95.6532, 40.5415],
					[-95.6927, 40.557],
					[-95.6963, 40.5286],
					[-95.768, 40.5312],
					[-95.7645, 40.5853]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "54",
			"properties": { "name": "West Virginia" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-82.5953, 38.4222],
					[-82.5307, 38.405],
					[-82.4051, 38.4394],
					[-82.3225, 38.4489],
					[-82.3046, 38.491],
					[-82.2938, 38.5751],
					[-82.2508, 38.5992],
					[-82.1933, 38.5932],
					[-82.1718, 38.619],
					[-82.1826, 38.7057],
					[-82.2185, 38.7959],
					[-82.1431, 38.8406],
					[-82.1431, 38.8981],
					[-82.0928, 38.9737],
					[-82.0498, 38.9969],
					[-82.0282, 39.0287],
					[-81.9816, 38.9944],
					[-81.9421, 38.9935],
					[-81.899, 38.9256],
					[-81.9277, 38.8913],
					[-81.8882, 38.8741],
					[-81.8559, 38.893],
					[-81.8452, 38.9291],
					[-81.8129, 38.9463],
					[-81.7806, 38.9248],
					[-81.7662, 39.0201],
					[-81.8021, 39.045],
					[-81.8129, 39.082],
					[-81.7447, 39.0983],
					[-81.7554, 39.1808],
					[-81.7267, 39.216],
					[-81.6908, 39.2263],
					[-81.6801, 39.2736],
					[-81.5688, 39.2675],
					[-81.558, 39.3388],
					[-81.4683, 39.4041],
					[-81.4073, 39.3878],
					[-81.3857, 39.3431],
					[-81.3463, 39.3457],
					[-81.2709, 39.3861],
					[-81.2099, 39.393],
					[-81.1704, 39.4394],
					[-81.1309, 39.4462],
					[-81.1022, 39.4875],
					[-81.0448, 39.5364],
					[-80.9371, 39.6129],
					[-80.8796, 39.6206],
					[-80.8653, 39.6919],
					[-80.8294, 39.7143],
					[-80.8689, 39.7572],
					[-80.8258, 39.7984],
					[-80.8258, 39.8474],
					[-80.7899, 39.8672],
					[-80.8043, 39.9187],
					[-80.7576, 39.9093],
					[-80.7648, 39.9539],
					[-80.7397, 39.9711],
					[-80.7397, 40.0759],
					[-80.7074, 40.1017],
					[-80.7074, 40.1515],
					[-80.6715, 40.1927],
					[-80.6535, 40.2452],
					[-80.6176, 40.2649],
					[-80.5997, 40.3207],
					[-80.6105, 40.4024],
					[-80.5961, 40.4634],
					[-80.6284, 40.5355],
					[-80.6679, 40.5733],
					[-80.6356, 40.6163],
					[-80.5853, 40.6154],
					[-80.5207, 40.6386],
					[-80.5207, 39.7211],
					[-80.0756, 39.7211],
					[-79.4762, 39.7211],
					[-79.487, 39.2057],
					[-79.4511, 39.2117],
					[-79.3327, 39.3002],
					[-79.2896, 39.2993],
					[-79.2537, 39.3569],
					[-79.1424, 39.4084],
					[-79.0958, 39.4729],
					[-79.0563, 39.4711],
					[-78.9558, 39.4402],
					[-78.9163, 39.4866],
					[-78.7799, 39.6223],
					[-78.7333, 39.6137],
					[-78.7799, 39.6017],
					[-78.7333, 39.5863],
					[-78.7261, 39.5639],
					[-78.6543, 39.5347],
					[-78.5933, 39.5356],
					[-78.5681, 39.5201],
					[-78.4676, 39.5167],
					[-78.4174, 39.5493],
					[-78.4425, 39.5914],
					[-78.3959, 39.5905],
					[-78.4353, 39.618],
					[-78.3851, 39.6086],
					[-78.3528, 39.6404],
					[-78.2846, 39.6206],
					[-78.1841, 39.6954],
					[-78.0979, 39.6782],
					[-78.0369, 39.6361],
					[-78.0082, 39.6017],
					[-77.9436, 39.6189],
					[-77.8826, 39.5991],
					[-77.8323, 39.6026],
					[-77.8323, 39.5708],
					[-77.8898, 39.5579],
					[-77.8467, 39.4986],
					[-77.7785, 39.4634],
					[-77.7857, 39.4351],
					[-77.7534, 39.4247],
					[-77.7354, 39.3878],
					[-77.757, 39.3337],
					[-77.7211, 39.3217],
					[-77.7534, 39.283],
					[-77.8287, 39.1327],
					[-78.1087, 39.3122],
					[-78.3456, 39.466],
					[-78.36, 39.4127],
					[-78.342, 39.3887],
					[-78.3636, 39.3577],
					[-78.3384, 39.3483],
					[-78.421, 39.2572],
					[-78.3994, 39.2443],
					[-78.4389, 39.198],
					[-78.403, 39.167],
					[-78.4605, 39.1138],
					[-78.4856, 39.1078],
					[-78.5717, 39.0322],
					[-78.5502, 39.0184],
					[-78.6004, 38.9643],
					[-78.6256, 38.9823],
					[-78.6794, 38.9256],
					[-78.7153, 38.905],
					[-78.7153, 38.9359],
					[-78.7979, 38.875],
					[-78.8696, 38.7633],
					[-78.9953, 38.85],
					[-79.0276, 38.7908],
					[-79.0886, 38.7203],
					[-79.0922, 38.6593],
					[-79.1209, 38.6602],
					[-79.1568, 38.6112],
					[-79.2106, 38.4927],
					[-79.3111, 38.4119],
					[-79.4762, 38.4574],
					[-79.5373, 38.5511],
					[-79.6485, 38.5915],
					[-79.6988, 38.4875],
					[-79.688, 38.4317],
					[-79.7311, 38.3801],
					[-79.81, 38.3054],
					[-79.7885, 38.2676],
					[-79.8316, 38.2496],
					[-79.9213, 38.18],
					[-79.9464, 38.1319],
					[-79.9249, 38.107],
					[-79.9536, 38.0812],
					[-79.9751, 38.0296],
					[-80.011, 37.9858],
					[-80.0756, 37.942],
					[-80.1833, 37.8527],
					[-80.2587, 37.7565],
					[-80.2515, 37.7255],
					[-80.2946, 37.692],
					[-80.2802, 37.6568],
					[-80.2192, 37.6276],
					[-80.2623, 37.5932],
					[-80.3269, 37.564],
					[-80.3305, 37.5365],
					[-80.2838, 37.5331],
					[-80.2982, 37.5082],
					[-80.4741, 37.4231],
					[-80.51, 37.4816],
					[-80.553, 37.4738],
					[-80.6212, 37.4334],
					[-80.7684, 37.3725],
					[-80.7827, 37.3948],
					[-80.8581, 37.4274],
					[-80.8832, 37.3836],
					[-80.8509, 37.3467],
					[-80.9191, 37.3063],
					[-80.9801, 37.302],
					[-81.113, 37.2788],
					[-81.2242, 37.235],
					[-81.3211, 37.2994],
					[-81.3606, 37.3381],
					[-81.3893, 37.3201],
					[-81.418, 37.2728],
					[-81.5006, 37.2582],
					[-81.5078, 37.2324],
					[-81.5544, 37.2075],
					[-81.6765, 37.2015],
					[-81.7447, 37.2427],
					[-81.7447, 37.2634],
					[-81.7877, 37.2848],
					[-81.8523, 37.2874],
					[-81.8775, 37.3321],
					[-81.9277, 37.3587],
					[-81.9349, 37.436],
					[-81.9887, 37.4549],
					[-81.9923, 37.4833],
					[-81.9277, 37.5125],
					[-81.9672, 37.5374],
					[-82.0175, 37.534],
					[-82.118, 37.5597],
					[-82.1287, 37.5915],
					[-82.1574, 37.5924],
					[-82.1826, 37.6267],
					[-82.2149, 37.625],
					[-82.24, 37.6611],
					[-82.2938, 37.6705],
					[-82.3405, 37.7857],
					[-82.3764, 37.802],
					[-82.423, 37.8544],
					[-82.4195, 37.8836],
					[-82.4733, 37.8999],
					[-82.502, 37.9334],
					[-82.4625, 37.9807],
					[-82.5164, 37.9996],
					[-82.5487, 38.0683],
					[-82.5846, 38.107],
					[-82.6384, 38.1387],
					[-82.6384, 38.1714],
					[-82.6097, 38.1714],
					[-82.5989, 38.2186],
					[-82.6133, 38.2358],
					[-82.5738, 38.2642],
					[-82.5702, 38.314],
					[-82.5989, 38.3449],
					[-82.5953, 38.4222]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "17",
			"properties": { "name": "Illinois" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-91.4178, 40.3783],
					[-91.3747, 40.392],
					[-91.3819, 40.435],
					[-91.3676, 40.5123],
					[-91.407, 40.5424],
					[-91.3604, 40.6017],
					[-91.2491, 40.6386],
					[-91.1845, 40.6377],
					[-91.1199, 40.673],
					[-91.1163, 40.7254],
					[-91.0948, 40.7675],
					[-91.0912, 40.825],
					[-91.0553, 40.8482],
					[-91.005, 40.9049],
					[-90.9656, 40.9212],
					[-90.944, 41.0123],
					[-90.9476, 41.0965],
					[-90.9943, 41.1609],
					[-91.0409, 41.1661],
					[-91.1127, 41.2391],
					[-91.0732, 41.3052],
					[-91.0661, 41.3688],
					[-91.0445, 41.416],
					[-90.9763, 41.4341],
					[-90.9297, 41.4212],
					[-90.8471, 41.4556],
					[-90.7359, 41.4504],
					[-90.6497, 41.465],
					[-90.5923, 41.5131],
					[-90.5564, 41.5243],
					[-90.4631, 41.5234],
					[-90.3985, 41.5724],
					[-90.341, 41.5904],
					[-90.3446, 41.6471],
					[-90.3123, 41.6978],
					[-90.3123, 41.7425],
					[-90.28, 41.7674],
					[-90.1795, 41.8095],
					[-90.1831, 41.8447],
					[-90.1544, 41.9066],
					[-90.1652, 41.9564],
					[-90.14, 42.0088],
					[-90.1652, 42.0406],
					[-90.1616, 42.117],
					[-90.2082, 42.1523],
					[-90.3769, 42.215],
					[-90.4308, 42.2785],
					[-90.42, 42.3301],
					[-90.4774, 42.3842],
					[-90.5564, 42.416],
					[-90.5636, 42.4383],
					[-90.6425, 42.4684],
					[-90.6425, 42.5088],
					[-90.481, 42.5071],
					[-89.6662, 42.5053],
					[-89.4832, 42.501],
					[-88.7869, 42.4916],
					[-88.6397, 42.495],
					[-88.0941, 42.4959],
					[-87.7998, 42.4916],
					[-87.807, 42.3851],
					[-87.8357, 42.3017],
					[-87.7998, 42.2081],
					[-87.7244, 42.1076],
					[-87.6814, 42.0758],
					[-87.6706, 42.0294],
					[-87.6239, 41.91],
					[-87.6096, 41.8456],
					[-87.5593, 41.7657],
					[-87.5306, 41.7485],
					[-87.5234, 41.7081],
					[-87.527, 41.4899],
					[-87.527, 40.4625],
					[-87.5306, 40.2503],
					[-87.5306, 39.9977],
					[-87.5342, 39.9874],
					[-87.5342, 39.6464],
					[-87.5306, 39.6464],
					[-87.5306, 39.3483],
					[-87.5773, 39.3406],
					[-87.5988, 39.3131],
					[-87.606, 39.2615],
					[-87.5737, 39.2186],
					[-87.6419, 39.167],
					[-87.6311, 39.1043],
					[-87.5737, 39.0571],
					[-87.5773, 38.9892],
					[-87.5127, 38.9548],
					[-87.5521, 38.8595],
					[-87.4983, 38.7787],
					[-87.4947, 38.7427],
					[-87.545, 38.6774],
					[-87.5952, 38.6671],
					[-87.6203, 38.6396],
					[-87.6526, 38.5683],
					[-87.6526, 38.5116],
					[-87.7388, 38.4755],
					[-87.7316, 38.4428],
					[-87.7783, 38.3707],
					[-87.807, 38.363],
					[-87.8536, 38.2753],
					[-87.8824, 38.3037],
					[-87.9075, 38.2685],
					[-87.9254, 38.2985],
					[-87.9577, 38.2401],
					[-87.99, 38.2487],
					[-87.9757, 38.198],
					[-87.9254, 38.1465],
					[-88.0044, 38.0838],
					[-87.9613, 38.1001],
					[-87.9685, 38.0674],
					[-88.0367, 38.0511],
					[-88.008, 38.0288],
					[-88.0116, 37.9772],
					[-88.0654, 37.9197],
					[-88.0116, 37.8948],
					[-88.0977, 37.9016],
					[-88.0403, 37.8226],
					[-88.0834, 37.8303],
					[-88.069, 37.8011],
					[-88.0295, 37.7994],
					[-88.0582, 37.7427],
					[-88.1193, 37.7126],
					[-88.1587, 37.6645],
					[-88.1336, 37.5743],
					[-88.0618, 37.5159],
					[-88.0834, 37.473],
					[-88.2556, 37.4566],
					[-88.2987, 37.4472],
					[-88.3633, 37.4025],
					[-88.4136, 37.4249],
					[-88.4782, 37.3871],
					[-88.4854, 37.3398],
					[-88.5177, 37.284],
					[-88.5105, 37.2625],
					[-88.4495, 37.2067],
					[-88.4243, 37.15],
					[-88.4602, 37.0735],
					[-88.5141, 37.0649],
					[-88.5607, 37.0761],
					[-88.6289, 37.1208],
					[-88.7546, 37.1551],
					[-88.8048, 37.1886],
					[-88.9161, 37.2247],
					[-88.9843, 37.229],
					[-89.0309, 37.211],
					[-89.0776, 37.1749],
					[-89.1099, 37.119],
					[-89.1673, 37.0744],
					[-89.1817, 37.0271],
					[-89.1314, 36.9824],
					[-89.1709, 36.9704],
					[-89.1996, 37.0159],
					[-89.2607, 37.0641],
					[-89.3109, 37.0537],
					[-89.2571, 37.0151],
					[-89.293, 36.9919],
					[-89.3791, 37.04],
					[-89.3755, 37.0855],
					[-89.415, 37.125],
					[-89.4617, 37.1998],
					[-89.4581, 37.2402],
					[-89.5191, 37.2857],
					[-89.4904, 37.3338],
					[-89.4365, 37.3441],
					[-89.4222, 37.3974],
					[-89.5155, 37.5357],
					[-89.5227, 37.5786],
					[-89.476, 37.5855],
					[-89.5155, 37.6439],
					[-89.5155, 37.6929],
					[-89.5837, 37.7135],
					[-89.616, 37.7496],
					[-89.6662, 37.7522],
					[-89.6591, 37.7865],
					[-89.7416, 37.8467],
					[-89.7955, 37.857],
					[-89.799, 37.8819],
					[-89.8457, 37.9059],
					[-89.8816, 37.8793],
					[-89.9534, 37.8836],
					[-89.9749, 37.9265],
					[-89.9318, 37.9472],
					[-89.9426, 37.9704],
					[-90, 37.9643],
					[-90.0611, 38.0159],
					[-90.0898, 38.0159],
					[-90.1293, 38.0623],
					[-90.219, 38.0941],
					[-90.2908, 38.1705],
					[-90.3554, 38.2135],
					[-90.3733, 38.2736],
					[-90.3697, 38.3406],
					[-90.3446, 38.3853],
					[-90.2836, 38.4437],
					[-90.2621, 38.5287],
					[-90.1831, 38.6112],
					[-90.1759, 38.643],
					[-90.2118, 38.722],
					[-90.1652, 38.7727],
					[-90.1221, 38.7976],
					[-90.1149, 38.8492],
					[-90.2513, 38.9196],
					[-90.3087, 38.9239],
					[-90.4056, 38.9626],
					[-90.4738, 38.9591],
					[-90.5061, 38.9024],
					[-90.5456, 38.875],
					[-90.5851, 38.8689],
					[-90.6641, 38.9282],
					[-90.6748, 38.9841],
					[-90.7143, 39.0571],
					[-90.682, 39.088],
					[-90.7107, 39.155],
					[-90.7215, 39.2323],
					[-90.7933, 39.3096],
					[-91.005, 39.4273],
					[-91.0625, 39.4737],
					[-91.102, 39.539],
					[-91.1486, 39.5459],
					[-91.1845, 39.6052],
					[-91.224, 39.6172],
					[-91.2778, 39.6661],
					[-91.3712, 39.7323],
					[-91.3604, 39.7847],
					[-91.3747, 39.8087],
					[-91.4286, 39.838],
					[-91.4465, 39.8706],
					[-91.4178, 39.9273],
					[-91.4429, 39.9514],
					[-91.4932, 40.0364],
					[-91.5147, 40.1781],
					[-91.5075, 40.2391],
					[-91.4465, 40.3628],
					[-91.4178, 40.3783]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "35",
			"properties": { "name": "New Mexico" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-109.0448, 36.9988],
					[-108.248, 36.9988],
					[-107.4225, 36.9996],
					[-106.8769, 37.0005],
					[-106.8697, 36.9928],
					[-106.2021, 36.9945],
					[-105.1217, 36.9953],
					[-104.9997, 36.9936],
					[-104.3393, 36.9936],
					[-103.7327, 36.9979],
					[-103.0005, 37.0005],
					[-103.0005, 36.6027],
					[-103.0041, 36.5005],
					[-103.0435, 36.5005],
					[-103.04, 36.3175],
					[-103.04, 35.6226],
					[-103.0435, 35.2497],
					[-103.0435, 33.9457],
					[-103.0507, 33.7464],
					[-103.0579, 33.3881],
					[-103.0579, 33.2601],
					[-103.0651, 33.0419],
					[-103.0651, 32.0008],
					[-103.7219, 31.9999],
					[-104.5331, 31.9999],
					[-105.4304, 32.0008],
					[-106.6185, 32.0008],
					[-106.6221, 31.914],
					[-106.6472, 31.8985],
					[-106.6364, 31.8659],
					[-106.6005, 31.8444],
					[-106.6041, 31.8246],
					[-106.5467, 31.8075],
					[-106.5287, 31.7834],
					[-107.2969, 31.7834],
					[-108.2085, 31.7834],
					[-108.2085, 31.3333],
					[-109.0484, 31.3324],
					[-109.0484, 33.7781],
					[-109.0448, 33.8752],
					[-109.0484, 33.9998],
					[-109.0448, 34.5221],
					[-109.0448, 35.3167],
					[-109.0484, 35.3425],
					[-109.0448, 35.5461],
					[-109.0448, 36.9988]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "05",
			"properties": { "name": "Arkansas" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-94.6195, 36.4997],
					[-93.8657, 36.4988],
					[-93.0689, 36.4979],
					[-92.5628, 36.4979],
					[-91.4035, 36.4971],
					[-90.1508, 36.4979],
					[-90.1328, 36.4369],
					[-90.1436, 36.4241],
					[-90.0647, 36.3863],
					[-90.0826, 36.272],
					[-90.1149, 36.266],
					[-90.1257, 36.229],
					[-90.1831, 36.205],
					[-90.2369, 36.1595],
					[-90.2369, 36.1397],
					[-90.3195, 36.0899],
					[-90.3769, 35.9954],
					[-89.7344, 36.0005],
					[-89.7201, 35.9679],
					[-89.6555, 35.9258],
					[-89.6555, 35.8871],
					[-89.7416, 35.9069],
					[-89.7739, 35.8648],
					[-89.7021, 35.8339],
					[-89.7416, 35.8055],
					[-89.7811, 35.8047],
					[-89.8134, 35.76],
					[-89.8744, 35.7411],
					[-89.9067, 35.7591],
					[-89.9606, 35.7239],
					[-89.939, 35.6655],
					[-89.9067, 35.6509],
					[-89.8637, 35.6707],
					[-89.8529, 35.638],
					[-89.9462, 35.6019],
					[-89.9426, 35.5564],
					[-89.9103, 35.5478],
					[-89.9211, 35.5134],
					[-90, 35.5616],
					[-90.0323, 35.5538],
					[-90.0503, 35.504],
					[-90.018, 35.4679],
					[-90.0575, 35.4035],
					[-90.0862, 35.4791],
					[-90.1687, 35.4215],
					[-90.1795, 35.382],
					[-90.1364, 35.3769],
					[-90.1293, 35.4138],
					[-90.0754, 35.3837],
					[-90.1077, 35.3434],
					[-90.1077, 35.3047],
					[-90.1652, 35.2961],
					[-90.1508, 35.2557],
					[-90.097, 35.2497],
					[-90.0754, 35.2248],
					[-90.1185, 35.1879],
					[-90.0647, 35.138],
					[-90.1005, 35.1166],
					[-90.1436, 35.1346],
					[-90.1759, 35.1123],
					[-90.2082, 35.0264],
					[-90.298, 35.0375],
					[-90.3087, 34.9954],
					[-90.2477, 34.9482],
					[-90.2477, 34.9095],
					[-90.3123, 34.8717],
					[-90.4236, 34.8339],
					[-90.4379, 34.8846],
					[-90.4774, 34.8863],
					[-90.4846, 34.8614],
					[-90.4559, 34.8236],
					[-90.4738, 34.8021],
					[-90.4523, 34.7403],
					[-90.5025, 34.724],
					[-90.5205, 34.7532],
					[-90.5133, 34.8021],
					[-90.5492, 34.779],
					[-90.542, 34.7489],
					[-90.5672, 34.7369],
					[-90.5456, 34.7025],
					[-90.4738, 34.7008],
					[-90.4667, 34.6741],
					[-90.5169, 34.6312],
					[-90.5564, 34.6467],
					[-90.5528, 34.6888],
					[-90.5887, 34.6707],
					[-90.5887, 34.6157],
					[-90.5456, 34.5633],
					[-90.542, 34.5401],
					[-90.5887, 34.4963],
					[-90.5672, 34.425],
					[-90.6569, 34.3761],
					[-90.6569, 34.3219],
					[-90.6928, 34.3228],
					[-90.682, 34.3692],
					[-90.7502, 34.3683],
					[-90.7682, 34.3451],
					[-90.7394, 34.3065],
					[-90.7646, 34.2807],
					[-90.8328, 34.2678],
					[-90.8471, 34.2068],
					[-90.9045, 34.2438],
					[-90.9369, 34.2188],
					[-90.8866, 34.1819],
					[-90.8148, 34.1828],
					[-90.8076, 34.1613],
					[-90.8543, 34.1372],
					[-90.9117, 34.1656],
					[-90.9548, 34.1381],
					[-90.9225, 34.0943],
					[-90.883, 34.0969],
					[-90.8722, 34.0762],
					[-90.901, 34.0238],
					[-90.9871, 34.0187],
					[-90.962, 33.98],
					[-91.0015, 33.9663],
					[-91.0194, 34.0032],
					[-91.0768, 33.9835],
					[-91.0876, 33.9586],
					[-91.0086, 33.9293],
					[-91.0732, 33.8623],
					[-91.0481, 33.8151],
					[-90.9907, 33.7928],
					[-91.0266, 33.7635],
					[-91.0553, 33.779],
					[-91.1414, 33.7773],
					[-91.145, 33.7266],
					[-91.1091, 33.7043],
					[-91.0625, 33.7163],
					[-91.0338, 33.6733],
					[-91.0768, 33.6579],
					[-91.1343, 33.6768],
					[-91.1594, 33.7068],
					[-91.2204, 33.6931],
					[-91.2276, 33.669],
					[-91.1379, 33.6252],
					[-91.1307, 33.5969],
					[-91.2312, 33.5617],
					[-91.1845, 33.5075],
					[-91.2348, 33.4388],
					[-91.1702, 33.4526],
					[-91.1737, 33.4964],
					[-91.1235, 33.4732],
					[-91.1379, 33.4268],
					[-91.1989, 33.4182],
					[-91.2096, 33.4019],
					[-91.1737, 33.3813],
					[-91.1127, 33.3933],
					[-91.0768, 33.456],
					[-91.0589, 33.4285],
					[-91.1414, 33.3512],
					[-91.1414, 33.2962],
					[-91.0984, 33.2378],
					[-91.0876, 33.2739],
					[-91.0517, 33.285],
					[-91.0445, 33.2653],
					[-91.0912, 33.2206],
					[-91.084, 33.1562],
					[-91.0948, 33.1364],
					[-91.145, 33.1296],
					[-91.1845, 33.1416],
					[-91.2025, 33.1081],
					[-91.1199, 33.0548],
					[-91.1666, 33.0041],
					[-91.9526, 33.0076],
					[-92.5054, 33.0119],
					[-93.0689, 33.0179],
					[-93.3776, 33.0179],
					[-94.0416, 33.0196],
					[-94.0416, 33.5419],
					[-94.0452, 33.5514],
					[-94.0452, 33.5514],
					[-94.1026, 33.5703],
					[-94.1241, 33.5522],
					[-94.1421, 33.578],
					[-94.1852, 33.5926],
					[-94.2246, 33.5531],
					[-94.3, 33.5763],
					[-94.3108, 33.5514],
					[-94.3395, 33.5668],
					[-94.3574, 33.5436],
					[-94.3933, 33.5514],
					[-94.3862, 33.5823],
					[-94.4185, 33.5771],
					[-94.4579, 33.5986],
					[-94.4579, 33.645],
					[-94.4867, 33.6381],
					[-94.4687, 34.1896],
					[-94.4615, 34.5479],
					[-94.4544, 34.6939],
					[-94.4508, 34.8958],
					[-94.44, 35.12],
					[-94.4328, 35.394],
					[-94.4867, 35.7265],
					[-94.501, 35.7969],
					[-94.5513, 36.1019],
					[-94.5728, 36.211],
					[-94.5979, 36.388],
					[-94.6195, 36.4997]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "06",
			"properties": { "name": "California" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-118.6031, 33.4783],
						[-118.5457, 33.474],
						[-118.4452, 33.4285],
						[-118.3698, 33.4096],
						[-118.3662, 33.3881],
						[-118.3088, 33.3357],
						[-118.3268, 33.2988],
						[-118.3734, 33.3203],
						[-118.4667, 33.3263],
						[-118.4883, 33.3564],
						[-118.4883, 33.4199],
						[-118.5601, 33.4337],
						[-118.6031, 33.4783]
					]],
					[[
						[-118.6067, 33.0308],
						[-118.5744, 33.0342],
						[-118.4955, 32.9337],
						[-118.3519, 32.822],
						[-118.3878, 32.8255],
						[-118.4273, 32.8066],
						[-118.4883, 32.8444],
						[-118.5349, 32.9062],
						[-118.5852, 33.0084],
						[-118.6067, 33.0308]
					]],
					[[
						[-119.059, 33.4912],
						[-119.0303, 33.4972],
						[-119.0267, 33.462],
						[-119.0661, 33.468],
						[-119.059, 33.4912]
					]],
					[[
						[-119.4323, 34.029],
						[-119.4035, 34.0144],
						[-119.3641, 34.0264],
						[-119.3641, 34.0006],
						[-119.4251, 33.9972],
						[-119.4574, 34.0152],
						[-119.4323, 34.029]
					]],
					[[
						[-119.5794, 33.279],
						[-119.5292, 33.285],
						[-119.4574, 33.255],
						[-119.4287, 33.2284],
						[-119.4753, 33.2155],
						[-119.5471, 33.2335],
						[-119.5794, 33.279]
					]],
					[[
						[-119.9204, 34.0771],
						[-119.8558, 34.0711],
						[-119.8091, 34.0522],
						[-119.7553, 34.0565],
						[-119.6871, 34.0195],
						[-119.6189, 34.0161],
						[-119.5938, 34.0496],
						[-119.522, 34.0324],
						[-119.5615, 33.9955],
						[-119.662, 33.986],
						[-119.723, 33.9594],
						[-119.8737, 33.98],
						[-119.8773, 34.0324],
						[-119.9204, 34.0771]
					]],
					[[
						[-120.247, 34.0015],
						[-120.1681, 34.0084],
						[-120.1358, 34.0264],
						[-120.0891, 34.0195],
						[-120.0424, 34.0359],
						[-120.0424, 33.9946],
						[-119.9778, 33.9835],
						[-119.9742, 33.9422],
						[-120.1214, 33.8958],
						[-120.1788, 33.9276],
						[-120.247, 34.0015]
					]],
					[[
						[-120.448, 34.0376],
						[-120.3906, 34.0522],
						[-120.3691, 34.0762],
						[-120.3475, 34.0462],
						[-120.3009, 34.0238],
						[-120.3762, 34.0187],
						[-120.448, 34.0376]
					]],
					[[
						[-124.2132, 41.9985],
						[-123.6569, 41.9951],
						[-123.6246, 42.0002],
						[-123.3482, 41.9994],
						[-123.1472, 42.0097],
						[-123.0467, 42.0028],
						[-122.6339, 42.0045],
						[-122.5011, 42.0088],
						[-122.1601, 42.0079],
						[-121.9986, 42.0036],
						[-121.4458, 41.9968],
						[-121.0367, 41.9933],
						[-120.3296, 41.9933],
						[-119.9994, 41.9951],
						[-119.9994, 41.9942],
						[-119.9994, 40.7503],
						[-119.9958, 40.7211],
						[-119.9958, 40.0716],
						[-119.9994, 39.734],
						[-120.003, 39.7228],
						[-120.003, 39.3303],
						[-120.0065, 39.2254],
						[-119.9994, 38.9995],
						[-119.906, 38.9334],
						[-119.4933, 38.6499],
						[-119.0841, 38.3612],
						[-118.858, 38.2049],
						[-118.5026, 37.9489],
						[-118.054, 37.625],
						[-117.8314, 37.4652],
						[-117.3756, 37.1268],
						[-116.2521, 36.2772],
						[-115.4122, 35.6251],
						[-115.161, 35.4241],
						[-114.6334, 35.0015],
						[-114.637, 34.8752],
						[-114.5795, 34.8262],
						[-114.5544, 34.7669],
						[-114.4719, 34.7128],
						[-114.3785, 34.5075],
						[-114.3857, 34.4577],
						[-114.339, 34.4517],
						[-114.2278, 34.3657],
						[-114.1775, 34.3494],
						[-114.138, 34.303],
						[-114.1345, 34.2609],
						[-114.2242, 34.2051],
						[-114.2278, 34.1888],
						[-114.2888, 34.1707],
						[-114.3247, 34.1364],
						[-114.4072, 34.1115],
						[-114.436, 34.0874],
						[-114.436, 34.0281],
						[-114.5365, 33.9285],
						[-114.5113, 33.9113],
						[-114.5042, 33.8641],
						[-114.5257, 33.8589],
						[-114.5293, 33.8151],
						[-114.5042, 33.7601],
						[-114.497, 33.6965],
						[-114.5257, 33.6862],
						[-114.54, 33.5917],
						[-114.5257, 33.5522],
						[-114.558, 33.5316],
						[-114.6585, 33.413],
						[-114.7267, 33.4053],
						[-114.698, 33.3615],
						[-114.7303, 33.3057],
						[-114.6764, 33.2704],
						[-114.6836, 33.1485],
						[-114.7087, 33.0909],
						[-114.6585, 33.0325],
						[-114.6477, 33.0471],
						[-114.6011, 33.0256],
						[-114.5759, 33.0368],
						[-114.5185, 33.0299],
						[-114.4647, 32.9131],
						[-114.4683, 32.8452],
						[-114.5329, 32.7911],
						[-114.5257, 32.7567],
						[-114.6154, 32.7344],
						[-114.7016, 32.7456],
						[-114.7195, 32.7189],
						[-116.2127, 32.6107],
						[-117.1243, 32.5342],
						[-117.1351, 32.6184],
						[-117.1674, 32.6717],
						[-117.1961, 32.6889],
						[-117.2464, 32.6691],
						[-117.2571, 32.7258],
						[-117.2536, 32.7868],
						[-117.2823, 32.822],
						[-117.25, 32.8744],
						[-117.2823, 33.0127],
						[-117.3289, 33.1218],
						[-117.3612, 33.1682],
						[-117.5048, 33.334],
						[-117.5945, 33.3864],
						[-117.6304, 33.4302],
						[-117.6843, 33.462],
						[-117.7166, 33.4603],
						[-117.7848, 33.5419],
						[-117.8781, 33.5926],
						[-117.9283, 33.6055],
						[-118.0001, 33.6544],
						[-118.0899, 33.73],
						[-118.176, 33.7635],
						[-118.1832, 33.7232],
						[-118.2586, 33.7034],
						[-118.3232, 33.7154],
						[-118.3591, 33.7369],
						[-118.4129, 33.7421],
						[-118.4273, 33.7747],
						[-118.3949, 33.8039],
						[-118.3914, 33.8409],
						[-118.4416, 33.9405],
						[-118.4811, 33.9955],
						[-118.5421, 34.0384],
						[-118.6677, 34.0393],
						[-118.7467, 34.0324],
						[-118.8041, 34.0015],
						[-118.8544, 34.0341],
						[-118.9549, 34.0479],
						[-119.0877, 34.0986],
						[-119.1308, 34.1003],
						[-119.2169, 34.1458],
						[-119.2779, 34.267],
						[-119.3138, 34.2755],
						[-119.3748, 34.3211],
						[-119.3892, 34.3185],
						[-119.461, 34.3743],
						[-119.5363, 34.3958],
						[-119.5579, 34.413],
						[-119.6727, 34.4164],
						[-119.7302, 34.3958],
						[-119.7948, 34.4173],
						[-119.8737, 34.4087],
						[-120.0065, 34.4602],
						[-120.0891, 34.4602],
						[-120.1429, 34.4731],
						[-120.3009, 34.4671],
						[-120.4732, 34.4482],
						[-120.4767, 34.4748],
						[-120.5234, 34.5315],
						[-120.5808, 34.5573],
						[-120.6239, 34.5539],
						[-120.6454, 34.5814],
						[-120.5988, 34.7042],
						[-120.6383, 34.7558],
						[-120.6095, 34.858],
						[-120.6706, 34.9044],
						[-120.6383, 35.0032],
						[-120.6347, 35.1234],
						[-120.6993, 35.1715],
						[-120.7495, 35.1776],
						[-120.7603, 35.1595],
						[-120.8572, 35.2068],
						[-120.8967, 35.248],
						[-120.8608, 35.3605],
						[-120.868, 35.4035],
						[-120.9075, 35.449],
						[-121.0044, 35.461],
						[-121.1013, 35.5487],
						[-121.1659, 35.6354],
						[-121.252, 35.6569],
						[-121.313, 35.7136],
						[-121.331, 35.7832],
						[-121.3884, 35.8236],
						[-121.4135, 35.8554],
						[-121.4638, 35.8854],
						[-121.4853, 35.9705],
						[-121.5033, 36.0005],
						[-121.5751, 36.0254],
						[-121.6289, 36.1148],
						[-121.7186, 36.1955],
						[-121.8371, 36.2505],
						[-121.8766, 36.2892],
						[-121.9053, 36.3579],
						[-121.9017, 36.394],
						[-121.9412, 36.4808],
						[-121.934, 36.5598],
						[-121.9735, 36.5735],
						[-121.9376, 36.6371],
						[-121.873, 36.6045],
						[-121.8407, 36.6302],
						[-121.8155, 36.6826],
						[-121.7868, 36.8038],
						[-121.8622, 36.9318],
						[-121.9053, 36.9687],
						[-121.9412, 36.9781],
						[-121.9735, 36.9541],
						[-122.1063, 36.9558],
						[-122.2068, 37.0142],
						[-122.2857, 37.1019],
						[-122.336, 37.1173],
						[-122.3432, 37.1439],
						[-122.4042, 37.1955],
						[-122.4185, 37.2488],
						[-122.4006, 37.3596],
						[-122.4437, 37.436],
						[-122.4509, 37.4807],
						[-122.4939, 37.4927],
						[-122.5155, 37.5211],
						[-122.519, 37.5761],
						[-122.5011, 37.5992],
						[-122.4939, 37.6654],
						[-122.5155, 37.7805],
						[-122.4652, 37.8046],
						[-122.4078, 37.8114],
						[-122.3862, 37.7908],
						[-122.3611, 37.7152],
						[-122.3934, 37.7075],
						[-122.3539, 37.6156],
						[-122.3611, 37.5924],
						[-122.2642, 37.5726],
						[-122.1673, 37.5039],
						[-122.1386, 37.5082],
						[-122.0883, 37.4541],
						[-122.0453, 37.4566],
						[-122.0668, 37.491],
						[-122.1099, 37.5073],
						[-122.1458, 37.5821],
						[-122.1709, 37.6757],
						[-122.2463, 37.7221],
						[-122.2534, 37.7616],
						[-122.3324, 37.7839],
						[-122.3001, 37.8475],
						[-122.3252, 37.9059],
						[-122.3791, 37.9051],
						[-122.4293, 37.9635],
						[-122.4006, 37.9558],
						[-122.3683, 37.9781],
						[-122.3683, 38.0125],
						[-122.3001, 38.0107],
						[-122.2642, 38.0442],
						[-122.3001, 38.1052],
						[-122.3934, 38.143],
						[-122.4401, 38.1173],
						[-122.4903, 38.1121],
						[-122.4832, 38.0717],
						[-122.5011, 38.0322],
						[-122.4473, 37.9884],
						[-122.4903, 37.9669],
						[-122.5047, 37.9291],
						[-122.4365, 37.881],
						[-122.4616, 37.869],
						[-122.5011, 37.8939],
						[-122.476, 37.8329],
						[-122.5298, 37.8192],
						[-122.5621, 37.8518],
						[-122.6554, 37.9042],
						[-122.7021, 37.8939],
						[-122.7847, 37.9515],
						[-122.8205, 37.997],
						[-122.8816, 38.0253],
						[-122.939, 38.0322],
						[-123.0108, 38.0039],
						[-122.9605, 38.113],
						[-122.9498, 38.1542],
						[-122.9928, 38.2332],
						[-122.9677, 38.2504],
						[-123.0251, 38.3105],
						[-123.0646, 38.302],
						[-123.0682, 38.3595],
						[-123.1292, 38.4506],
						[-123.201, 38.4944],
						[-123.2513, 38.5107],
						[-123.3302, 38.5657],
						[-123.463, 38.7169],
						[-123.5133, 38.7418],
						[-123.542, 38.777],
						[-123.6533, 38.8543],
						[-123.7394, 38.954],
						[-123.6892, 39.021],
						[-123.6927, 39.0571],
						[-123.743, 39.1653],
						[-123.8004, 39.271],
						[-123.8256, 39.3612],
						[-123.8148, 39.4462],
						[-123.7681, 39.5596],
						[-123.7861, 39.6043],
						[-123.7861, 39.6601],
						[-123.8363, 39.7392],
						[-123.8507, 39.8319],
						[-123.9081, 39.8629],
						[-123.9296, 39.9101],
						[-124.0553, 40.0244],
						[-124.0804, 40.0295],
						[-124.0804, 40.0665],
						[-124.1091, 40.1034],
						[-124.1881, 40.1309],
						[-124.3639, 40.2606],
						[-124.3496, 40.3147],
						[-124.3639, 40.3749],
						[-124.4106, 40.4384],
						[-124.3783, 40.5226],
						[-124.3281, 40.6163],
						[-124.1773, 40.8439],
						[-124.1378, 40.9255],
						[-124.1127, 41.0278],
						[-124.1558, 41.0595],
						[-124.1594, 41.1429],
						[-124.145, 41.1446],
						[-124.1055, 41.2296],
						[-124.0624, 41.4392],
						[-124.0804, 41.5475],
						[-124.1342, 41.6574],
						[-124.1629, 41.7399],
						[-124.1952, 41.7365],
						[-124.2563, 41.7829],
						[-124.2204, 41.8464],
						[-124.2024, 41.9409],
						[-124.2132, 41.9985]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "10",
			"properties": { "name": "Delaware" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [[[
					[-75.5603, 39.6301],
					[-75.5567, 39.606],
					[-75.5603, 39.6301]
				]], [[
					[-75.79, 39.722],
					[-75.7182, 39.7924],
					[-75.6644, 39.8216],
					[-75.5783, 39.8388],
					[-75.4993, 39.8337],
					[-75.4167, 39.8019],
					[-75.4598, 39.7658],
					[-75.4778, 39.7134],
					[-75.5101, 39.6859],
					[-75.5316, 39.6919],
					[-75.6106, 39.6223],
					[-75.6034, 39.5888],
					[-75.5639, 39.5622],
					[-75.5603, 39.5201],
					[-75.589, 39.4961],
					[-75.589, 39.4608],
					[-75.5388, 39.4162],
					[-75.5137, 39.3663],
					[-75.4383, 39.3131],
					[-75.4024, 39.2547],
					[-75.3916, 39.2048],
					[-75.4096, 39.1748],
					[-75.4024, 39.0665],
					[-75.345, 39.0253],
					[-75.3198, 38.9883],
					[-75.3127, 38.9248],
					[-75.1906, 38.8071],
					[-75.1332, 38.7822],
					[-75.0973, 38.8028],
					[-75.0829, 38.7719],
					[-75.065, 38.661],
					[-75.0506, 38.4514],
					[-75.4275, 38.4523],
					[-75.6931, 38.46],
					[-75.7003, 38.5219],
					[-75.79, 39.6481],
					[-75.79, 39.722]
				]]]
			}
		},
		{
			"type": "Feature",
			"id": "11",
			"properties": { "name": "District of Columbia" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-77.1181, 38.9342],
					[-77.0427, 38.9944],
					[-76.9099, 38.893],
					[-77.0391, 38.7916],
					[-77.0391, 38.8715],
					[-77.1181, 38.9342]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "15",
			"properties": { "name": "Hawaii" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-156.0574, 19.7428],
						[-156.0502, 19.7806],
						[-155.982, 19.8459],
						[-155.939, 19.8519],
						[-155.8923, 19.9318],
						[-155.8349, 19.9765],
						[-155.8241, 20.0263],
						[-155.8851, 20.1071],
						[-155.8995, 20.1457],
						[-155.9031, 20.2359],
						[-155.8744, 20.2677],
						[-155.8098, 20.26],
						[-155.7451, 20.2325],
						[-155.7344, 20.205],
						[-155.6554, 20.1672],
						[-155.6016, 20.1268],
						[-155.5585, 20.132],
						[-155.4688, 20.1045],
						[-155.3288, 20.0383],
						[-155.2821, 20.022],
						[-155.2032, 19.9696],
						[-155.1457, 19.9207],
						[-155.0847, 19.8554],
						[-155.0955, 19.815],
						[-155.0919, 19.7377],
						[-155.0632, 19.7291],
						[-155.0057, 19.7394],
						[-154.9806, 19.6904],
						[-154.9842, 19.6415],
						[-154.9052, 19.571],
						[-154.8263, 19.5375],
						[-154.8083, 19.5195],
						[-154.8227, 19.4817],
						[-154.8694, 19.4379],
						[-154.9304, 19.3975],
						[-154.9806, 19.3494],
						[-155.0632, 19.3167],
						[-155.135, 19.2764],
						[-155.2067, 19.2609],
						[-155.2642, 19.2738],
						[-155.2965, 19.2661],
						[-155.3611, 19.2094],
						[-155.4185, 19.1879],
						[-155.4652, 19.1466],
						[-155.5154, 19.1329],
						[-155.5585, 19.0822],
						[-155.5549, 19.0539],
						[-155.5836, 19.0221],
						[-155.6016, 18.9714],
						[-155.6734, 18.9173],
						[-155.7272, 18.9697],
						[-155.8062, 19.0143],
						[-155.8851, 19.0393],
						[-155.921, 19.1209],
						[-155.9031, 19.2179],
						[-155.8887, 19.3477],
						[-155.9246, 19.4387],
						[-155.921, 19.4782],
						[-155.9533, 19.4886],
						[-155.9713, 19.5865],
						[-155.9964, 19.6432],
						[-156.0287, 19.6501],
						[-156.0574, 19.7428]
					]],
					[[
						[-156.6999, 20.9206],
						[-156.6819, 20.9799],
						[-156.6425, 21.028],
						[-156.5922, 21.0331],
						[-156.5456, 21.0048],
						[-156.5097, 20.9378],
						[-156.4738, 20.8948],
						[-156.3876, 20.9197],
						[-156.323, 20.9498],
						[-156.2297, 20.9318],
						[-156.2297, 20.918],
						[-156.1687, 20.8742],
						[-156.1328, 20.8613],
						[-156.1148, 20.827],
						[-156.0036, 20.7952],
						[-155.9856, 20.7677],
						[-155.9856, 20.723],
						[-156.0143, 20.6861],
						[-156.061, 20.6517],
						[-156.0825, 20.6543],
						[-156.1436, 20.6234],
						[-156.1938, 20.632],
						[-156.3015, 20.5864],
						[-156.3769, 20.5787],
						[-156.4379, 20.601],
						[-156.463, 20.7814],
						[-156.4881, 20.7986],
						[-156.5384, 20.778],
						[-156.6317, 20.821],
						[-156.6784, 20.8708],
						[-156.6999, 20.9206]
					]],
					[[
						[-156.7035, 20.5323],
						[-156.6784, 20.5572],
						[-156.5779, 20.6062],
						[-156.542, 20.5804],
						[-156.5563, 20.5426],
						[-156.5384, 20.528],
						[-156.585, 20.5117],
						[-156.603, 20.5246],
						[-156.6676, 20.5048],
						[-156.7035, 20.5323]
					]],
					[[
						[-157.0624, 20.9043],
						[-157.0373, 20.9275],
						[-156.9906, 20.9318],
						[-156.8973, 20.9154],
						[-156.8363, 20.8639],
						[-156.8076, 20.8201],
						[-156.8399, 20.7643],
						[-156.9081, 20.7393],
						[-156.9691, 20.735],
						[-156.9942, 20.7866],
						[-157.005, 20.8493],
						[-157.0552, 20.8768],
						[-157.0624, 20.9043]
					]],
					[[
						[-157.3101, 21.1019],
						[-157.2885, 21.1465],
						[-157.2562, 21.1714],
						[-157.2634, 21.2213],
						[-157.2024, 21.2196],
						[-157.1916, 21.2075],
						[-156.9978, 21.1826],
						[-156.9619, 21.2118],
						[-156.9476, 21.1757],
						[-156.8686, 21.1646],
						[-156.7717, 21.18],
						[-156.7107, 21.1586],
						[-156.7394, 21.1113],
						[-156.804, 21.0675],
						[-156.8758, 21.0495],
						[-157.0014, 21.077],
						[-157.0947, 21.1036],
						[-157.1486, 21.0916],
						[-157.2526, 21.0873],
						[-157.3101, 21.1019]
					]],
					[[
						[-158.2792, 21.5786],
						[-158.1248, 21.5864],
						[-158.0782, 21.6285],
						[-158.0638, 21.6585],
						[-158.0172, 21.6998],
						[-157.9669, 21.7127],
						[-157.9454, 21.6895],
						[-157.9238, 21.6293],
						[-157.8521, 21.5572],
						[-157.8377, 21.5125],
						[-157.8521, 21.4996],
						[-157.8413, 21.4592],
						[-157.7767, 21.412],
						[-157.7659, 21.461],
						[-157.7228, 21.4592],
						[-157.7372, 21.4043],
						[-157.7085, 21.3836],
						[-157.7121, 21.3587],
						[-157.6511, 21.2986],
						[-157.6941, 21.2668],
						[-157.7336, 21.2823],
						[-157.809, 21.2574],
						[-157.8305, 21.2797],
						[-157.8915, 21.3063],
						[-157.9813, 21.3158],
						[-158.089, 21.2986],
						[-158.1141, 21.302],
						[-158.1392, 21.3742],
						[-158.1787, 21.4034],
						[-158.1823, 21.43],
						[-158.2325, 21.4876],
						[-158.2325, 21.54],
						[-158.2792, 21.5786]
					]],
					[[
						[-159.7867, 22.0305],
						[-159.7831, 22.0649],
						[-159.7436, 22.0975],
						[-159.7293, 22.1396],
						[-159.6108, 22.2015],
						[-159.5821, 22.2238],
						[-159.4995, 22.2083],
						[-159.4888, 22.2298],
						[-159.4313, 22.2204],
						[-159.4026, 22.2324],
						[-159.3488, 22.2152],
						[-159.3129, 22.1834],
						[-159.295, 22.1448],
						[-159.295, 22.1052],
						[-159.3344, 22.0511],
						[-159.3308, 21.9601],
						[-159.3452, 21.936],
						[-159.4457, 21.869],
						[-159.4744, 21.8819],
						[-159.6036, 21.8922],
						[-159.6682, 21.9532],
						[-159.7544, 21.9781],
						[-159.7867, 22.0305]
					]],
					[[
						[-160.2461, 21.8492],
						[-160.2318, 21.8862],
						[-160.1887, 21.924],
						[-160.1205, 21.9626],
						[-160.1133, 21.9953],
						[-160.0738, 22.003],
						[-160.0667, 21.9764],
						[-160.0846, 21.9274],
						[-160.0774, 21.8965],
						[-160.1492, 21.8724],
						[-160.2066, 21.7891],
						[-160.2497, 21.8149],
						[-160.2461, 21.8492]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "19",
			"properties": { "name": "Iowa" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-96.4536, 43.5001],
					[-95.7393, 43.5001],
					[-94.6159, 43.5001],
					[-93.9698, 43.4993],
					[-93.1694, 43.4993],
					[-92.753, 43.5001],
					[-91.949, 43.5001],
					[-91.2168, 43.5001],
					[-91.2348, 43.4555],
					[-91.1989, 43.4125],
					[-91.2025, 43.3489],
					[-91.1055, 43.3137],
					[-91.0589, 43.2484],
					[-91.1235, 43.1969],
					[-91.145, 43.1522],
					[-91.1773, 43.1316],
					[-91.1737, 43.0388],
					[-91.145, 42.9632],
					[-91.145, 42.9048],
					[-91.102, 42.8833],
					[-91.0553, 42.7381],
					[-90.9369, 42.6832],
					[-90.7682, 42.6514],
					[-90.7071, 42.6342],
					[-90.6425, 42.5406],
					[-90.6425, 42.5088],
					[-90.6425, 42.4684],
					[-90.5636, 42.4383],
					[-90.5564, 42.416],
					[-90.4774, 42.3842],
					[-90.42, 42.3301],
					[-90.4308, 42.2785],
					[-90.3769, 42.215],
					[-90.2082, 42.1523],
					[-90.1616, 42.117],
					[-90.1652, 42.0406],
					[-90.14, 42.0088],
					[-90.1652, 41.9564],
					[-90.1544, 41.9066],
					[-90.1831, 41.8447],
					[-90.1795, 41.8095],
					[-90.28, 41.7674],
					[-90.3123, 41.7425],
					[-90.3123, 41.6978],
					[-90.3446, 41.6471],
					[-90.341, 41.5904],
					[-90.3985, 41.5724],
					[-90.4631, 41.5234],
					[-90.5564, 41.5243],
					[-90.5923, 41.5131],
					[-90.6497, 41.465],
					[-90.7359, 41.4504],
					[-90.8471, 41.4556],
					[-90.9297, 41.4212],
					[-90.9763, 41.4341],
					[-91.0445, 41.416],
					[-91.0661, 41.3688],
					[-91.0732, 41.3052],
					[-91.1127, 41.2391],
					[-91.0409, 41.1661],
					[-90.9943, 41.1609],
					[-90.9476, 41.0965],
					[-90.944, 41.0123],
					[-90.9656, 40.9212],
					[-91.005, 40.9049],
					[-91.0553, 40.8482],
					[-91.0912, 40.825],
					[-91.0948, 40.7675],
					[-91.1163, 40.7254],
					[-91.1199, 40.673],
					[-91.1845, 40.6377],
					[-91.2491, 40.6386],
					[-91.3604, 40.6017],
					[-91.407, 40.5424],
					[-91.3676, 40.5123],
					[-91.3819, 40.435],
					[-91.3747, 40.392],
					[-91.4178, 40.3783],
					[-91.486, 40.3843],
					[-91.5255, 40.4109],
					[-91.5291, 40.4591],
					[-91.565, 40.4608],
					[-91.6224, 40.5097],
					[-91.6188, 40.5407],
					[-91.6834, 40.5527],
					[-91.687, 40.581],
					[-91.7301, 40.6137],
					[-92.0926, 40.6017],
					[-92.6382, 40.5905],
					[-93.3453, 40.5802],
					[-93.5678, 40.5802],
					[-94.0344, 40.5733],
					[-94.5943, 40.5707],
					[-95.165, 40.5776],
					[-95.7645, 40.5853],
					[-95.7501, 40.6094],
					[-95.786, 40.6575],
					[-95.8326, 40.6712],
					[-95.8865, 40.7211],
					[-95.8793, 40.7529],
					[-95.8326, 40.7829],
					[-95.847, 40.8542],
					[-95.8111, 40.898],
					[-95.8362, 40.9212],
					[-95.8291, 40.9779],
					[-95.8614, 40.9951],
					[-95.8578, 41.0312],
					[-95.8829, 41.057],
					[-95.865, 41.0802],
					[-95.8829, 41.16],
					[-95.8398, 41.1747],
					[-95.926, 41.1953],
					[-95.9116, 41.2271],
					[-95.9296, 41.2812],
					[-95.8829, 41.3164],
					[-95.9547, 41.3396],
					[-95.9296, 41.3645],
					[-95.9331, 41.4642],
					[-96.0049, 41.4727],
					[-95.9978, 41.5389],
					[-96.0301, 41.5398],
					[-96.0372, 41.508],
					[-96.0911, 41.532],
					[-96.0803, 41.5801],
					[-96.1198, 41.6136],
					[-96.0947, 41.6471],
					[-96.1234, 41.6824],
					[-96.0839, 41.6978],
					[-96.1054, 41.7442],
					[-96.0767, 41.7614],
					[-96.066, 41.8009],
					[-96.109, 41.8207],
					[-96.1126, 41.8533],
					[-96.1629, 41.9057],
					[-96.1413, 41.9152],
					[-96.1306, 41.9719],
					[-96.188, 41.977],
					[-96.1844, 42.0028],
					[-96.2382, 42.0122],
					[-96.2203, 42.026],
					[-96.2741, 42.0475],
					[-96.2705, 42.1188],
					[-96.3495, 42.1669],
					[-96.3603, 42.2081],
					[-96.3244, 42.2296],
					[-96.3746, 42.3181],
					[-96.4141, 42.343],
					[-96.4141, 42.4083],
					[-96.3818, 42.4461],
					[-96.3962, 42.4839],
					[-96.4464, 42.4907],
					[-96.4931, 42.5174],
					[-96.4787, 42.556],
					[-96.5146, 42.6308],
					[-96.5756, 42.6823],
					[-96.6295, 42.7055],
					[-96.6331, 42.7708],
					[-96.5936, 42.7931],
					[-96.5828, 42.8378],
					[-96.5433, 42.8515],
					[-96.5433, 42.9203],
					[-96.5003, 42.9598],
					[-96.5218, 42.9778],
					[-96.4931, 43.0019],
					[-96.511, 43.05],
					[-96.4608, 43.0637],
					[-96.4356, 43.1204],
					[-96.468, 43.1505],
					[-96.4751, 43.2218],
					[-96.5613, 43.2244],
					[-96.5505, 43.251],
					[-96.5792, 43.2957],
					[-96.529, 43.3],
					[-96.5254, 43.3945],
					[-96.6043, 43.4494],
					[-96.5792, 43.4812],
					[-96.5972, 43.5001],
					[-96.4536, 43.5001]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "21",
			"properties": { "name": "Kentucky" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [[[
					[-89.1314, 36.9824],
					[-89.1817, 37.0271],
					[-89.1673, 37.0744],
					[-89.1099, 37.119],
					[-89.0776, 37.1749],
					[-89.0309, 37.211],
					[-88.9843, 37.229],
					[-88.9161, 37.2247],
					[-88.8048, 37.1886],
					[-88.7546, 37.1551],
					[-88.6289, 37.1208],
					[-88.5607, 37.0761],
					[-88.5141, 37.0649],
					[-88.4602, 37.0735],
					[-88.4243, 37.15],
					[-88.4495, 37.2067],
					[-88.5105, 37.2625],
					[-88.5177, 37.284],
					[-88.4854, 37.3398],
					[-88.4782, 37.3871],
					[-88.4136, 37.4249],
					[-88.3633, 37.4025],
					[-88.2987, 37.4472],
					[-88.2556, 37.4566],
					[-88.0834, 37.473],
					[-88.0618, 37.5159],
					[-88.1336, 37.5743],
					[-88.1587, 37.6645],
					[-88.1193, 37.7126],
					[-88.0582, 37.7427],
					[-88.0295, 37.7994],
					[-87.947, 37.7719],
					[-87.9039, 37.8123],
					[-87.9398, 37.8905],
					[-87.9039, 37.9248],
					[-87.8716, 37.9214],
					[-87.8321, 37.8767],
					[-87.6814, 37.9034],
					[-87.6634, 37.8776],
					[-87.6742, 37.8295],
					[-87.6347, 37.8269],
					[-87.588, 37.8613],
					[-87.6275, 37.9162],
					[-87.6024, 37.9729],
					[-87.5737, 37.9678],
					[-87.5521, 37.9257],
					[-87.5019, 37.9094],
					[-87.4516, 37.9412],
					[-87.3799, 37.936],
					[-87.3009, 37.8982],
					[-87.2219, 37.8492],
					[-87.1573, 37.8381],
					[-87.1286, 37.7848],
					[-87.0891, 37.7874],
					[-87.064, 37.8106],
					[-87.0461, 37.8939],
					[-87.0102, 37.9197],
					[-86.9061, 37.9429],
					[-86.8558, 37.9875],
					[-86.7948, 37.9893],
					[-86.7517, 37.9128],
					[-86.7158, 37.8939],
					[-86.6799, 37.9154],
					[-86.6441, 37.9068],
					[-86.662, 37.863],
					[-86.6369, 37.8424],
					[-86.5974, 37.8673],
					[-86.5902, 37.9214],
					[-86.5077, 37.9291],
					[-86.5256, 37.9618],
					[-86.5184, 38.0425],
					[-86.4538, 38.0503],
					[-86.4323, 38.0863],
					[-86.4646, 38.1009],
					[-86.4323, 38.1259],
					[-86.4036, 38.1061],
					[-86.3749, 38.131],
					[-86.3282, 38.1327],
					[-86.3246, 38.1542],
					[-86.3713, 38.1645],
					[-86.3749, 38.1937],
					[-86.3461, 38.1954],
					[-86.2708, 38.1379],
					[-86.2672, 38.0571],
					[-86.1739, 38.0099],
					[-86.0949, 38.009],
					[-86.0482, 37.9592],
					[-86.0339, 37.9901],
					[-85.9513, 38.0056],
					[-85.9226, 38.0262],
					[-85.9047, 38.0863],
					[-85.9082, 38.1611],
					[-85.8508, 38.2229],
					[-85.8293, 38.277],
					[-85.7934, 38.2882],
					[-85.7431, 38.2676],
					[-85.6749, 38.3011],
					[-85.6067, 38.4394],
					[-85.4991, 38.4686],
					[-85.4739, 38.5064],
					[-85.4165, 38.5408],
					[-85.4381, 38.6018],
					[-85.4524, 38.7092],
					[-85.4093, 38.7375],
					[-85.3519, 38.7315],
					[-85.2586, 38.7375],
					[-85.2227, 38.7006],
					[-85.1545, 38.692],
					[-85.1042, 38.7255],
					[-84.9786, 38.7796],
					[-84.9463, 38.7753],
					[-84.8889, 38.7951],
					[-84.8099, 38.7925],
					[-84.8315, 38.8303],
					[-84.7848, 38.8698],
					[-84.7992, 38.8913],
					[-84.8602, 38.8973],
					[-84.8709, 38.9291],
					[-84.8315, 38.9617],
					[-84.8386, 38.9883],
					[-84.8961, 39.0493],
					[-84.8889, 39.0665],
					[-84.8207, 39.1052],
					[-84.7561, 39.1464],
					[-84.731, 39.1447],
					[-84.6771, 39.0983],
					[-84.6017, 39.0743],
					[-84.5515, 39.0992],
					[-84.5012, 39.0966],
					[-84.4725, 39.1215],
					[-84.4438, 39.1146],
					[-84.4295, 39.0554],
					[-84.3254, 39.0279],
					[-84.2895, 38.9557],
					[-84.232, 38.8844],
					[-84.232, 38.8277],
					[-84.2141, 38.8054],
					[-84.0705, 38.7701],
					[-83.9521, 38.7865],
					[-83.848, 38.747],
					[-83.8372, 38.7177],
					[-83.7834, 38.6954],
					[-83.7654, 38.6525],
					[-83.6649, 38.6275],
					[-83.6147, 38.6842],
					[-83.5214, 38.7031],
					[-83.4675, 38.6756],
					[-83.367, 38.6585],
					[-83.3275, 38.6378],
					[-83.3096, 38.6009],
					[-83.2629, 38.6215],
					[-83.1553, 38.6207],
					[-83.1194, 38.6662],
					[-83.0548, 38.6954],
					[-83.0225, 38.7289],
					[-82.9686, 38.7289],
					[-82.8932, 38.7564],
					[-82.8681, 38.7281],
					[-82.8789, 38.6903],
					[-82.8466, 38.5949],
					[-82.7999, 38.5631],
					[-82.7245, 38.558],
					[-82.6492, 38.4901],
					[-82.6025, 38.46],
					[-82.5953, 38.4222],
					[-82.5989, 38.3449],
					[-82.5702, 38.314],
					[-82.5738, 38.2642],
					[-82.6133, 38.2358],
					[-82.5989, 38.2186],
					[-82.6097, 38.1714],
					[-82.6384, 38.1714],
					[-82.6384, 38.1387],
					[-82.5846, 38.107],
					[-82.5487, 38.0683],
					[-82.5164, 37.9996],
					[-82.4625, 37.9807],
					[-82.502, 37.9334],
					[-82.4733, 37.8999],
					[-82.4195, 37.8836],
					[-82.423, 37.8544],
					[-82.3764, 37.802],
					[-82.3405, 37.7857],
					[-82.2938, 37.6705],
					[-82.24, 37.6611],
					[-82.2149, 37.625],
					[-82.1826, 37.6267],
					[-82.1574, 37.5924],
					[-82.1287, 37.5915],
					[-82.118, 37.5597],
					[-82.0175, 37.534],
					[-81.9672, 37.5374],
					[-82.3548, 37.2651],
					[-82.4877, 37.2316],
					[-82.5666, 37.1963],
					[-82.6348, 37.1543],
					[-82.7245, 37.1156],
					[-82.7245, 37.0417],
					[-82.782, 37.0082],
					[-82.825, 37.0065],
					[-82.8681, 36.9781],
					[-82.8574, 36.9275],
					[-82.8789, 36.8888],
					[-82.9686, 36.8579],
					[-83.0727, 36.8544],
					[-83.1301, 36.7857],
					[-83.1373, 36.7428],
					[-83.1947, 36.7393],
					[-83.3132, 36.7093],
					[-83.4532, 36.6646],
					[-83.5285, 36.6663],
					[-83.647, 36.6242],
					[-83.6757, 36.601],
					[-83.6901, 36.583],
					[-83.988, 36.5899],
					[-84.2715, 36.5916],
					[-84.5443, 36.5959],
					[-84.8422, 36.6053],
					[-85.0253, 36.6191],
					[-85.2909, 36.6268],
					[-85.4883, 36.6148],
					[-85.8329, 36.6216],
					[-86.0339, 36.6302],
					[-86.3318, 36.6491],
					[-86.5077, 36.6526],
					[-86.5651, 36.6337],
					[-86.5902, 36.6526],
					[-87.1143, 36.6423],
					[-87.4445, 36.6405],
					[-87.8536, 36.6337],
					[-87.8501, 36.6637],
					[-88.069, 36.6783],
					[-88.0331, 36.5521],
					[-88.0546, 36.4971],
					[-88.5464, 36.5022],
					[-88.9986, 36.5022],
					[-89.3001, 36.5074],
					[-89.4186, 36.4988],
					[-89.3719, 36.6208],
					[-89.3253, 36.6319],
					[-89.2607, 36.5649],
					[-89.214, 36.5804],
					[-89.1889, 36.6414],
					[-89.1602, 36.6663],
					[-89.1996, 36.7161],
					[-89.1853, 36.7539],
					[-89.1278, 36.7514],
					[-89.1171, 36.7754],
					[-89.1781, 36.8072],
					[-89.1745, 36.8398],
					[-89.1386, 36.8476],
					[-89.1171, 36.8871],
					[-89.0991, 36.9644],
					[-89.1314, 36.9824]
				]], [[
					[-89.4868, 36.4971],
					[-89.5406, 36.4979],
					[-89.5729, 36.5478],
					[-89.5586, 36.5735],
					[-89.4832, 36.5718],
					[-89.4652, 36.5297],
					[-89.4868, 36.4971]
				]]]
			}
		},
		{
			"type": "Feature",
			"id": "24",
			"properties": { "name": "Maryland" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-75.9946, 37.9532],
						[-76.0449, 37.9532],
						[-76.0485, 38.015],
						[-76.0197, 38.0391],
						[-75.9803, 38.0047],
						[-75.9946, 37.9532]
					]],
					[[
						[-76.0951, 38.125],
						[-76.0879, 38.1929],
						[-76.0485, 38.204],
						[-76.0233, 38.1783],
						[-76.0054, 38.0769],
						[-76.0592, 38.0949],
						[-76.0449, 38.1181],
						[-76.0951, 38.125]
					]],
					[[
						[-76.3966, 38.771],
						[-76.3787, 38.7882],
						[-76.3607, 38.7487],
						[-76.3966, 38.771]
					]],
					[[
						[-79.4762, 39.7211],
						[-78.3815, 39.7228],
						[-77.5344, 39.7203],
						[-76.9924, 39.7203],
						[-76.5689, 39.7211],
						[-75.79, 39.722],
						[-75.79, 39.6481],
						[-75.7003, 38.5219],
						[-75.6931, 38.46],
						[-75.4275, 38.4523],
						[-75.0506, 38.4514],
						[-75.0542, 38.4145],
						[-75.0865, 38.3243],
						[-75.1045, 38.3114],
						[-75.1763, 38.1301],
						[-75.2409, 38.0271],
						[-75.6249, 37.9944],
						[-75.6465, 37.9472],
						[-75.668, 37.9506],
						[-75.7147, 37.9764],
						[-75.7829, 37.9729],
						[-75.8618, 37.918],
						[-75.8941, 37.9171],
						[-75.8977, 37.9747],
						[-75.8726, 38.0348],
						[-75.8116, 38.0588],
						[-75.8726, 38.0606],
						[-75.8654, 38.0992],
						[-75.8259, 38.1336],
						[-75.9013, 38.1413],
						[-75.9444, 38.113],
						[-75.9587, 38.137],
						[-75.9408, 38.1868],
						[-75.8475, 38.21],
						[-75.869, 38.2435],
						[-75.9013, 38.2324],
						[-75.9157, 38.2633],
						[-75.8618, 38.3595],
						[-75.9157, 38.3389],
						[-75.9408, 38.2985],
						[-75.9408, 38.247],
						[-75.9695, 38.2461],
						[-76.009, 38.3123],
						[-75.9695, 38.32],
						[-75.9731, 38.3655],
						[-76.0018, 38.3741],
						[-76.0162, 38.332],
						[-76.0485, 38.3037],
						[-76.0269, 38.2805],
						[-76.0592, 38.2272],
						[-76.0736, 38.2521],
						[-76.1346, 38.2324],
						[-76.1669, 38.2427],
						[-76.2243, 38.314],
						[-76.2566, 38.3252],
						[-76.2495, 38.3621],
						[-76.3356, 38.4918],
						[-76.2638, 38.5038],
						[-76.2459, 38.5373],
						[-76.2746, 38.5313],
						[-76.3069, 38.5751],
						[-76.2782, 38.6095],
						[-76.2351, 38.6284],
						[-76.2136, 38.6069],
						[-76.1705, 38.6293],
						[-76.1669, 38.5983],
						[-76.0879, 38.5906],
						[-76.0413, 38.5571],
						[-76.0269, 38.5657],
						[-76.0844, 38.625],
						[-76.1454, 38.637],
						[-76.1741, 38.6731],
						[-76.1992, 38.6705],
						[-76.2566, 38.7366],
						[-76.2746, 38.7126],
						[-76.3177, 38.7298],
						[-76.3212, 38.6791],
						[-76.3464, 38.686],
						[-76.3356, 38.7727],
						[-76.271, 38.8518],
						[-76.2207, 38.8131],
						[-76.192, 38.8217],
						[-76.2028, 38.9282],
						[-76.2315, 38.9428],
						[-76.2925, 38.9024],
						[-76.3356, 38.9059],
						[-76.332, 38.8646],
						[-76.3751, 38.85],
						[-76.3607, 38.9394],
						[-76.3033, 39.0261],
						[-76.2782, 38.9823],
						[-76.2028, 38.9729],
						[-76.1633, 38.9995],
						[-76.1849, 39.0459],
						[-76.1454, 39.0932],
						[-76.2028, 39.0854],
						[-76.2136, 39.0407],
						[-76.1992, 39.0141],
						[-76.2423, 39.0287],
						[-76.2315, 39.0828],
						[-76.253, 39.1335],
						[-76.2782, 39.1456],
						[-76.2207, 39.2624],
						[-76.1777, 39.2985],
						[-76.1849, 39.3191],
						[-76.1346, 39.3406],
						[-76.1095, 39.3723],
						[-76.0018, 39.3672],
						[-76.0413, 39.3878],
						[-75.9767, 39.4479],
						[-76.0126, 39.4531],
						[-75.9695, 39.5579],
						[-76.0951, 39.5373],
						[-76.1167, 39.4961],
						[-76.0736, 39.4754],
						[-76.0592, 39.4479],
						[-76.1023, 39.4359],
						[-76.2243, 39.3526],
						[-76.2495, 39.3792],
						[-76.2243, 39.4256],
						[-76.253, 39.4497],
						[-76.253, 39.4119],
						[-76.2854, 39.3681],
						[-76.2566, 39.3388],
						[-76.2818, 39.2993],
						[-76.3033, 39.3818],
						[-76.3571, 39.3938],
						[-76.3356, 39.3053],
						[-76.3643, 39.3122],
						[-76.3859, 39.2761],
						[-76.3428, 39.2572],
						[-76.4182, 39.2194],
						[-76.4433, 39.1954],
						[-76.4899, 39.2023],
						[-76.4971, 39.2246],
						[-76.5761, 39.2641],
						[-76.5653, 39.2246],
						[-76.533, 39.2134],
						[-76.5258, 39.1782],
						[-76.4289, 39.1318],
						[-76.4217, 39.0743],
						[-76.4397, 39.0528],
						[-76.393, 39.0115],
						[-76.4756, 38.9729],
						[-76.4505, 38.9411],
						[-76.4612, 38.9067],
						[-76.4935, 38.9102],
						[-76.5187, 38.8629],
						[-76.4971, 38.8174],
						[-76.5581, 38.7676],
						[-76.5258, 38.7246],
						[-76.533, 38.6782],
						[-76.5115, 38.6155],
						[-76.5187, 38.5391],
						[-76.4935, 38.4832],
						[-76.4146, 38.4145],
						[-76.3859, 38.3612],
						[-76.4182, 38.3226],
						[-76.4684, 38.3286],
						[-76.4361, 38.2925],
						[-76.4038, 38.3114],
						[-76.3751, 38.2994],
						[-76.4002, 38.259],
						[-76.3859, 38.2178],
						[-76.3212, 38.1387],
						[-76.3392, 38.1198],
						[-76.3212, 38.0365],
						[-76.3535, 38.0528],
						[-76.393, 38.1027],
						[-76.4217, 38.1061],
						[-76.4397, 38.1611],
						[-76.4684, 38.1533],
						[-76.4756, 38.1044],
						[-76.5007, 38.137],
						[-76.5294, 38.1344],
						[-76.5474, 38.1757],
						[-76.5904, 38.2143],
						[-76.673, 38.2341],
						[-76.7986, 38.2367],
						[-76.8273, 38.3475],
						[-76.8561, 38.3587],
						[-76.8704, 38.332],
						[-76.8345, 38.2736],
						[-76.8417, 38.2547],
						[-76.9207, 38.2916],
						[-76.9278, 38.3209],
						[-76.9745, 38.3475],
						[-77.0176, 38.4454],
						[-77.0894, 38.4076],
						[-77.2078, 38.3595],
						[-77.2509, 38.3827],
						[-77.276, 38.4815],
						[-77.2365, 38.5519],
						[-77.1827, 38.6009],
						[-77.1109, 38.6267],
						[-77.1288, 38.6353],
						[-77.1324, 38.6739],
						[-77.1073, 38.6971],
						[-77.0427, 38.7186],
						[-77.0391, 38.7916],
						[-76.9099, 38.893],
						[-77.0427, 38.9944],
						[-77.1181, 38.9342],
						[-77.1504, 38.966],
						[-77.2222, 38.9712],
						[-77.2509, 38.9858],
						[-77.2473, 39.027],
						[-77.3406, 39.0631],
						[-77.4626, 39.076],
						[-77.4842, 39.1095],
						[-77.5201, 39.1206],
						[-77.5165, 39.1705],
						[-77.4842, 39.1859],
						[-77.4591, 39.228],
						[-77.5416, 39.265],
						[-77.5631, 39.3036],
						[-77.617, 39.3028],
						[-77.6744, 39.3242],
						[-77.7211, 39.3217],
						[-77.757, 39.3337],
						[-77.7354, 39.3878],
						[-77.7534, 39.4247],
						[-77.7857, 39.4351],
						[-77.7785, 39.4634],
						[-77.8467, 39.4986],
						[-77.8898, 39.5579],
						[-77.8323, 39.5708],
						[-77.8323, 39.6026],
						[-77.8826, 39.5991],
						[-77.9436, 39.6189],
						[-78.0082, 39.6017],
						[-78.0369, 39.6361],
						[-78.0979, 39.6782],
						[-78.1841, 39.6954],
						[-78.2846, 39.6206],
						[-78.3528, 39.6404],
						[-78.3851, 39.6086],
						[-78.4353, 39.618],
						[-78.3959, 39.5905],
						[-78.4425, 39.5914],
						[-78.4174, 39.5493],
						[-78.4676, 39.5167],
						[-78.5681, 39.5201],
						[-78.5933, 39.5356],
						[-78.6543, 39.5347],
						[-78.7261, 39.5639],
						[-78.7333, 39.5863],
						[-78.7799, 39.6017],
						[-78.7333, 39.6137],
						[-78.7799, 39.6223],
						[-78.9163, 39.4866],
						[-78.9558, 39.4402],
						[-79.0563, 39.4711],
						[-79.0958, 39.4729],
						[-79.1424, 39.4084],
						[-79.2537, 39.3569],
						[-79.2896, 39.2993],
						[-79.3327, 39.3002],
						[-79.4511, 39.2117],
						[-79.487, 39.2057],
						[-79.4762, 39.7211]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "26",
			"properties": { "name": "Michigan" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-84.652, 45.8625],
						[-84.6233, 45.8771],
						[-84.591, 45.8256],
						[-84.3577, 45.7715],
						[-84.4187, 45.7217],
						[-84.5012, 45.7371],
						[-84.5515, 45.7895],
						[-84.652, 45.8625]
					]],
					[[
						[-85.395, 45.7784],
						[-85.377, 45.8127],
						[-85.3519, 45.7955],
						[-85.395, 45.7784]
					]],
					[[
						[-85.5242, 45.8299],
						[-85.4488, 45.7964],
						[-85.4632, 45.7655],
						[-85.5314, 45.7981],
						[-85.5242, 45.8299]
					]],
					[[
						[-85.6283, 45.598],
						[-85.6032, 45.6392],
						[-85.5924, 45.6985],
						[-85.5709, 45.7113],
						[-85.5673, 45.7603],
						[-85.5027, 45.7543],
						[-85.5062, 45.6813],
						[-85.4919, 45.61],
						[-85.5601, 45.5722],
						[-85.6283, 45.598]
					]],
					[[
						[-85.7001, 45.7363],
						[-85.6534, 45.7431],
						[-85.6714, 45.6967],
						[-85.6965, 45.6976],
						[-85.7001, 45.7363]
					]],
					[[
						[-85.8831, 45.4433],
						[-85.8365, 45.4287],
						[-85.8329, 45.378],
						[-85.8831, 45.4433]
					]],
					[[
						[-86.0662, 45.1401],
						[-86.0446, 45.1598],
						[-85.9764, 45.1384],
						[-85.998, 45.0559],
						[-86.059, 45.1006],
						[-86.0662, 45.1401]
					]],
					[[
						[-86.1559, 45.0104],
						[-86.1164, 45.0482],
						[-86.0949, 45.0078],
						[-86.1559, 45.0104]
					]],
					[[
						[-86.6907, 45.6169],
						[-86.6189, 45.5619],
						[-86.6476, 45.543],
						[-86.662, 45.5739],
						[-86.7123, 45.6108],
						[-86.6907, 45.6169]
					]],
					[[
						[-86.8235, 41.7605],
						[-86.7158, 41.8189],
						[-86.6189, 41.8937],
						[-86.4861, 42.1179],
						[-86.3641, 42.2433],
						[-86.2959, 42.3584],
						[-86.26, 42.4435],
						[-86.2241, 42.5947],
						[-86.2277, 42.6445],
						[-86.2062, 42.7192],
						[-86.2098, 42.8593],
						[-86.2313, 43.0156],
						[-86.2708, 43.1187],
						[-86.3964, 43.3163],
						[-86.4359, 43.397],
						[-86.4789, 43.5156],
						[-86.54, 43.6178],
						[-86.54, 43.6591],
						[-86.461, 43.7467],
						[-86.4359, 43.7896],
						[-86.4323, 43.8558],
						[-86.4646, 43.9709],
						[-86.5148, 44.0482],
						[-86.5005, 44.0757],
						[-86.4287, 44.1195],
						[-86.3426, 44.2492],
						[-86.2672, 44.3454],
						[-86.2528, 44.452],
						[-86.2205, 44.5671],
						[-86.26, 44.6633],
						[-86.2564, 44.6916],
						[-86.1595, 44.7286],
						[-86.0877, 44.7415],
						[-86.0662, 44.8214],
						[-86.0734, 44.8849],
						[-86.0375, 44.9159],
						[-85.9908, 44.9004],
						[-85.9334, 44.9691],
						[-85.8688, 44.9391],
						[-85.8077, 44.9502],
						[-85.779, 44.9777],
						[-85.7467, 45.0516],
						[-85.7108, 45.0653],
						[-85.657, 45.1461],
						[-85.6175, 45.1865],
						[-85.5852, 45.1804],
						[-85.5421, 45.2105],
						[-85.5314, 45.177],
						[-85.5996, 45.1495],
						[-85.6103, 45.1134],
						[-85.5673, 45.0439],
						[-85.6498, 44.9743],
						[-85.6032, 44.9906],
						[-85.6032, 44.927],
						[-85.6247, 44.921],
						[-85.6534, 44.8489],
						[-85.6283, 44.7672],
						[-85.5996, 44.7655],
						[-85.5314, 44.8901],
						[-85.5206, 44.9743],
						[-85.4739, 44.9915],
						[-85.5027, 44.8557],
						[-85.5565, 44.8179],
						[-85.578, 44.7604],
						[-85.5278, 44.7483],
						[-85.4991, 44.8042],
						[-85.4739, 44.8153],
						[-85.395, 44.9313],
						[-85.377, 44.9983],
						[-85.3806, 45.0464],
						[-85.3663, 45.1169],
						[-85.3878, 45.1899],
						[-85.3699, 45.2706],
						[-85.3088, 45.3127],
						[-85.2729, 45.3153],
						[-85.2083, 45.3566],
						[-85.1437, 45.3703],
						[-85.054, 45.3643],
						[-84.9607, 45.3763],
						[-84.9176, 45.3935],
						[-84.9212, 45.4218],
						[-85.0396, 45.4364],
						[-85.0899, 45.4768],
						[-85.1186, 45.5748],
						[-85.0612, 45.6392],
						[-85.0145, 45.6512],
						[-84.9427, 45.7105],
						[-84.9499, 45.7371],
						[-85.0145, 45.7603],
						[-84.7992, 45.7474],
						[-84.774, 45.7895],
						[-84.5551, 45.7019],
						[-84.4618, 45.6521],
						[-84.4151, 45.6693],
						[-84.3756, 45.6555],
						[-84.329, 45.6641],
						[-84.2141, 45.6349],
						[-84.128, 45.5619],
						[-84.1172, 45.5129],
						[-84.0562, 45.4897],
						[-83.9377, 45.4931],
						[-83.909, 45.4854],
						[-83.8049, 45.4193],
						[-83.7224, 45.4133],
						[-83.6003, 45.3523],
						[-83.4962, 45.3574],
						[-83.4209, 45.2904],
						[-83.3814, 45.2689],
						[-83.4137, 45.2457],
						[-83.3599, 45.1633],
						[-83.3204, 45.1375],
						[-83.2701, 45.0232],
						[-83.3419, 45.0413],
						[-83.3993, 45.0705],
						[-83.4532, 45.0353],
						[-83.4388, 44.9408],
						[-83.3957, 44.903],
						[-83.3204, 44.8806],
						[-83.3024, 44.83],
						[-83.2988, 44.7458],
						[-83.2737, 44.714],
						[-83.2881, 44.653],
						[-83.3168, 44.5954],
						[-83.3096, 44.5396],
						[-83.3275, 44.4442],
						[-83.3311, 44.3403],
						[-83.3742, 44.3274],
						[-83.4245, 44.2724],
						[-83.5106, 44.2741],
						[-83.5393, 44.2484],
						[-83.568, 44.1556],
						[-83.5824, 44.0568],
						[-83.6506, 44.0525],
						[-83.6793, 44.0362],
						[-83.6793, 43.9941],
						[-83.83, 43.9889],
						[-83.8767, 43.9589],
						[-83.909, 43.8936],
						[-83.927, 43.7871],
						[-83.9557, 43.751],
						[-83.909, 43.6728],
						[-83.8516, 43.6453],
						[-83.8193, 43.6737],
						[-83.8013, 43.641],
						[-83.7295, 43.623],
						[-83.6829, 43.5903],
						[-83.647, 43.6041],
						[-83.5465, 43.7072],
						[-83.4962, 43.7072],
						[-83.4711, 43.7286],
						[-83.4962, 43.7724],
						[-83.4639, 43.8102],
						[-83.4316, 43.885],
						[-83.4173, 43.842],
						[-83.3491, 43.8687],
						[-83.3311, 43.8936],
						[-83.4065, 43.9202],
						[-83.3204, 43.9176],
						[-83.2809, 43.9382],
						[-83.2629, 43.9735],
						[-83.1804, 43.9821],
						[-83.0584, 44.0061],
						[-83.026, 44.0448],
						[-82.9148, 44.0705],
						[-82.8753, 44.0448],
						[-82.7927, 44.0233],
						[-82.7389, 43.9898],
						[-82.6779, 43.8841],
						[-82.642, 43.8523],
						[-82.6169, 43.769],
						[-82.5989, 43.5903],
						[-82.5379, 43.4374],
						[-82.5235, 43.2252],
						[-82.4877, 43.1024],
						[-82.423, 43.0079],
						[-82.4159, 42.9667],
						[-82.4553, 42.9271],
						[-82.4805, 42.8026],
						[-82.4661, 42.7622],
						[-82.5092, 42.6651],
						[-82.5235, 42.6076],
						[-82.5882, 42.5509],
						[-82.642, 42.5543],
						[-82.6851, 42.5182],
						[-82.6815, 42.5749],
						[-82.7138, 42.5981],
						[-82.6779, 42.6119],
						[-82.624, 42.6651],
						[-82.6743, 42.6875],
						[-82.7281, 42.6832],
						[-82.7963, 42.654],
						[-82.8215, 42.6265],
						[-82.7892, 42.6033],
						[-82.782, 42.5646],
						[-82.8358, 42.568],
						[-82.8753, 42.5234],
						[-82.8717, 42.4512],
						[-82.8968, 42.3851],
						[-82.9579, 42.3395],
						[-83.0655, 42.3181],
						[-83.1301, 42.2347],
						[-83.123, 42.1256],
						[-83.1337, 42.0878],
						[-83.1876, 42.0664],
						[-83.1912, 42.0062],
						[-83.2486, 41.9727],
						[-83.2701, 41.9392],
						[-83.3168, 41.9358],
						[-83.3419, 41.8799],
						[-83.3814, 41.8705],
						[-83.4424, 41.8086],
						[-83.4245, 41.7408],
						[-83.4532, 41.733],
						[-83.7619, 41.7236],
						[-84.3613, 41.7064],
						[-84.8063, 41.6961],
						[-84.8063, 41.7605],
						[-85.1186, 41.7597],
						[-85.7898, 41.7588],
						[-86.5005, 41.7597],
						[-86.8235, 41.7605]
					]],
					[[
						[-89.2535, 47.8762],
						[-89.1781, 47.9355],
						[-88.8371, 48.0566],
						[-88.812, 48.0549],
						[-88.6576, 48.1391],
						[-88.5464, 48.1751],
						[-88.5248, 48.1657],
						[-88.4243, 48.1906],
						[-88.4279, 48.1665],
						[-88.471, 48.1528],
						[-88.5787, 48.0841],
						[-88.5787, 48.0583],
						[-88.6684, 48.0111],
						[-88.8515, 47.9655],
						[-89.0022, 47.9088],
						[-88.995, 47.8916],
						[-88.9125, 47.8916],
						[-89.1243, 47.8289],
						[-89.1889, 47.8315],
						[-89.2535, 47.8762]
					]],
					[[
						[-90.4164, 46.5661],
						[-90.3482, 46.6005],
						[-90.3051, 46.6031],
						[-90.1652, 46.6451],
						[-90.0288, 46.6744],
						[-89.9175, 46.7405],
						[-89.8493, 46.7955],
						[-89.7919, 46.8187],
						[-89.6734, 46.8333],
						[-89.6196, 46.8187],
						[-89.5155, 46.8419],
						[-89.4365, 46.8393],
						[-89.2499, 46.9037],
						[-89.1422, 46.9845],
						[-89.0632, 46.9888],
						[-88.9591, 47.0085],
						[-88.9233, 47.042],
						[-88.9053, 47.0858],
						[-88.8156, 47.1417],
						[-88.7653, 47.1554],
						[-88.6576, 47.2259],
						[-88.5751, 47.2456],
						[-88.4997, 47.2937],
						[-88.4602, 47.3401],
						[-88.3885, 47.3848],
						[-88.1803, 47.4578],
						[-87.9793, 47.4793],
						[-87.7998, 47.4733],
						[-87.7173, 47.4398],
						[-87.7137, 47.4011],
						[-87.7531, 47.4054],
						[-87.8142, 47.3848],
						[-87.8824, 47.396],
						[-87.9577, 47.3874],
						[-87.9398, 47.347],
						[-88.0546, 47.298],
						[-88.1623, 47.2164],
						[-88.2305, 47.1992],
						[-88.2485, 47.1357],
						[-88.2736, 47.1434],
						[-88.2987, 47.0987],
						[-88.349, 47.0764],
						[-88.3669, 47.0188],
						[-88.41, 46.9776],
						[-88.4423, 46.9724],
						[-88.4854, 46.8316],
						[-88.4638, 46.7869],
						[-88.4961, 46.7568],
						[-88.4566, 46.7594],
						[-88.3849, 46.8341],
						[-88.3741, 46.8719],
						[-88.349, 46.8608],
						[-88.2449, 46.9295],
						[-88.1444, 46.9664],
						[-88.1875, 46.9192],
						[-88.2305, 46.8986],
						[-88.2844, 46.8324],
						[-88.1767, 46.9046],
						[-88.0834, 46.92],
						[-88.0331, 46.9089],
						[-87.9003, 46.9097],
						[-87.8465, 46.884],
						[-87.8178, 46.8908],
						[-87.7567, 46.8608],
						[-87.728, 46.8273],
						[-87.6742, 46.8367],
						[-87.5952, 46.7826],
						[-87.5845, 46.7302],
						[-87.5234, 46.6881],
						[-87.5019, 46.6477],
						[-87.4516, 46.6056],
						[-87.3834, 46.5927],
						[-87.3906, 46.5249],
						[-87.3511, 46.5008],
						[-87.2578, 46.4879],
						[-87.1071, 46.4965],
						[-87.0173, 46.5335],
						[-86.9779, 46.5266],
						[-86.9276, 46.4647],
						[-86.8845, 46.4415],
						[-86.8163, 46.4381],
						[-86.7876, 46.4776],
						[-86.7302, 46.4716],
						[-86.6979, 46.439],
						[-86.6835, 46.4982],
						[-86.7087, 46.5438],
						[-86.6512, 46.561],
						[-86.6261, 46.5335],
						[-86.6476, 46.4854],
						[-86.5866, 46.463],
						[-86.4933, 46.5249],
						[-86.3497, 46.5781],
						[-86.138, 46.6726],
						[-86.0985, 46.6546],
						[-85.8795, 46.6907],
						[-85.7144, 46.6769],
						[-85.5888, 46.6744],
						[-85.4811, 46.6804],
						[-85.2586, 46.7534],
						[-85.1724, 46.7637],
						[-85.0648, 46.7577],
						[-84.9499, 46.7697],
						[-85.0289, 46.6975],
						[-85.0361, 46.5816],
						[-85.0289, 46.5429],
						[-85.0576, 46.5266],
						[-85.0253, 46.4828],
						[-84.9679, 46.4759],
						[-84.9356, 46.4897],
						[-84.914, 46.4673],
						[-84.8602, 46.4699],
						[-84.7992, 46.4458],
						[-84.6771, 46.4879],
						[-84.6305, 46.4845],
						[-84.591, 46.445],
						[-84.5946, 46.4149],
						[-84.5048, 46.439],
						[-84.4797, 46.433],
						[-84.4438, 46.4888],
						[-84.3756, 46.5094],
						[-84.2931, 46.4931],
						[-84.2536, 46.5008],
						[-84.2249, 46.5343],
						[-84.128, 46.53],
						[-84.11, 46.5043],
						[-84.1459, 46.4184],
						[-84.1208, 46.3153],
						[-84.1172, 46.2337],
						[-84.2213, 46.2319],
						[-84.25, 46.1761],
						[-84.1962, 46.1658],
						[-84.1782, 46.1838],
						[-84.1244, 46.1804],
						[-84.0957, 46.1477],
						[-84.0275, 46.1314],
						[-84.0741, 46.0971],
						[-83.988, 46.0326],
						[-83.9449, 46.0318],
						[-83.8731, 45.9931],
						[-83.8444, 46.0258],
						[-83.7942, 45.9957],
						[-83.7654, 46.018],
						[-83.8229, 46.1039],
						[-83.7726, 46.0911],
						[-83.6255, 46.1022],
						[-83.5824, 46.0893],
						[-83.5321, 46.0112],
						[-83.4747, 45.9845],
						[-83.5106, 45.9295],
						[-83.5609, 45.9124],
						[-83.6578, 45.9459],
						[-83.769, 45.9321],
						[-83.8803, 45.9682],
						[-83.9198, 45.9579],
						[-84.0167, 45.9588],
						[-84.1136, 45.9785],
						[-84.329, 45.9562],
						[-84.3756, 45.9321],
						[-84.5084, 45.9914],
						[-84.5623, 46.0326],
						[-84.6089, 46.0266],
						[-84.6556, 46.0524],
						[-84.6915, 46.0172],
						[-84.6843, 45.9734],
						[-84.7381, 45.9459],
						[-84.7346, 45.9072],
						[-84.7022, 45.8531],
						[-84.7453, 45.8359],
						[-84.8315, 45.872],
						[-84.8422, 45.8986],
						[-84.9176, 45.9304],
						[-84.9356, 45.9562],
						[-85.0145, 46.0112],
						[-85.1509, 46.0507],
						[-85.1976, 46.0447],
						[-85.316, 46.0868],
						[-85.3806, 46.0825],
						[-85.4273, 46.1022],
						[-85.4452, 46.085],
						[-85.5134, 46.0945],
						[-85.6032, 46.0301],
						[-85.6642, 45.9673],
						[-85.6965, 45.9605],
						[-85.8113, 45.9802],
						[-85.8939, 45.9673],
						[-85.9226, 45.9484],
						[-85.9154, 45.9192],
						[-85.998, 45.951],
						[-86.0734, 45.9656],
						[-86.1595, 45.9536],
						[-86.1954, 45.9631],
						[-86.2779, 45.9424],
						[-86.3246, 45.9064],
						[-86.3318, 45.8522],
						[-86.3641, 45.7904],
						[-86.4143, 45.7938],
						[-86.4395, 45.7603],
						[-86.5184, 45.7474],
						[-86.54, 45.7079],
						[-86.5866, 45.7053],
						[-86.5866, 45.6667],
						[-86.6297, 45.6589],
						[-86.6153, 45.6065],
						[-86.6871, 45.634],
						[-86.7194, 45.6684],
						[-86.6764, 45.6916],
						[-86.6728, 45.7208],
						[-86.6333, 45.7474],
						[-86.6297, 45.7818],
						[-86.583, 45.7784],
						[-86.5292, 45.8531],
						[-86.54, 45.89],
						[-86.583, 45.8986],
						[-86.6476, 45.8342],
						[-86.784, 45.8548],
						[-86.7733, 45.811],
						[-86.8199, 45.7706],
						[-86.8379, 45.7225],
						[-86.9456, 45.6959],
						[-86.9635, 45.6727],
						[-86.985, 45.7062],
						[-86.9743, 45.7534],
						[-86.9886, 45.811],
						[-87.0173, 45.8385],
						[-87.0532, 45.8222],
						[-87.0712, 45.7191],
						[-87.0604, 45.7088],
						[-87.1717, 45.6615],
						[-87.265, 45.5507],
						[-87.326, 45.4253],
						[-87.3942, 45.3694],
						[-87.466, 45.2732],
						[-87.6132, 45.1238],
						[-87.5916, 45.0954],
						[-87.6598, 45.1074],
						[-87.6885, 45.1478],
						[-87.7352, 45.1719],
						[-87.7424, 45.1968],
						[-87.6885, 45.2981],
						[-87.6491, 45.3394],
						[-87.6562, 45.3686],
						[-87.6957, 45.3901],
						[-87.7531, 45.3497],
						[-87.8501, 45.3411],
						[-87.8501, 45.3411],
						[-87.8752, 45.3815],
						[-87.8501, 45.4021],
						[-87.8608, 45.4347],
						[-87.8142, 45.4639],
						[-87.7926, 45.4983],
						[-87.8034, 45.5387],
						[-87.7747, 45.598],
						[-87.8249, 45.6624],
						[-87.7819, 45.6735],
						[-87.8106, 45.7113],
						[-87.8572, 45.7268],
						[-87.8788, 45.7552],
						[-87.9649, 45.7586],
						[-87.9972, 45.7955],
						[-88.0726, 45.7801],
						[-88.1372, 45.8187],
						[-88.0762, 45.8634],
						[-88.1013, 45.8814],
						[-88.1013, 45.9218],
						[-88.2449, 45.9631],
						[-88.2951, 45.951],
						[-88.3813, 45.9914],
						[-88.4243, 45.9785],
						[-88.4997, 45.9966],
						[-88.5069, 46.018],
						[-88.6038, 46.0163],
						[-88.6146, 45.9888],
						[-88.6648, 45.9888],
						[-88.6792, 46.0137],
						[-88.7402, 46.0275],
						[-88.7833, 46.0163],
						[-89.092, 46.1383],
						[-90.1221, 46.3367],
						[-90.1185, 46.3599],
						[-90.219, 46.5034],
						[-90.3159, 46.5171],
						[-90.3518, 46.5378],
						[-90.3518, 46.5378],
						[-90.3949, 46.5326],
						[-90.4164, 46.5661]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "28",
			"properties": { "name": "Mississippi" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-88.5105, 30.2173],
						[-88.4028, 30.2062],
						[-88.4531, 30.189],
						[-88.4961, 30.1967],
						[-88.5105, 30.2173]
					]],
					[[
						[-88.7725, 30.2457],
						[-88.7151, 30.2569],
						[-88.6074, 30.232],
						[-88.5859, 30.2191],
						[-88.672, 30.2234],
						[-88.7725, 30.2457]
					]],
					[[
						[-88.9879, 30.2165],
						[-88.934, 30.2225],
						[-88.8802, 30.2491],
						[-88.8945, 30.2242],
						[-88.9699, 30.201],
						[-88.9879, 30.2165]
					]],
					[[
						[-89.1602, 30.2388],
						[-89.0848, 30.238],
						[-89.0561, 30.2491],
						[-89.074, 30.2122],
						[-89.1099, 30.2062],
						[-89.1709, 30.2285],
						[-89.1602, 30.2388]
					]],
					[[
						[-91.1666, 33.0041],
						[-91.1199, 33.0548],
						[-91.2025, 33.1081],
						[-91.1845, 33.1416],
						[-91.145, 33.1296],
						[-91.0948, 33.1364],
						[-91.084, 33.1562],
						[-91.0912, 33.2206],
						[-91.0445, 33.2653],
						[-91.0517, 33.285],
						[-91.0876, 33.2739],
						[-91.0984, 33.2378],
						[-91.1414, 33.2962],
						[-91.1414, 33.3512],
						[-91.0589, 33.4285],
						[-91.0768, 33.456],
						[-91.1127, 33.3933],
						[-91.1737, 33.3813],
						[-91.2096, 33.4019],
						[-91.1989, 33.4182],
						[-91.1379, 33.4268],
						[-91.1235, 33.4732],
						[-91.1737, 33.4964],
						[-91.1702, 33.4526],
						[-91.2348, 33.4388],
						[-91.1845, 33.5075],
						[-91.2312, 33.5617],
						[-91.1307, 33.5969],
						[-91.1379, 33.6252],
						[-91.2276, 33.669],
						[-91.2204, 33.6931],
						[-91.1594, 33.7068],
						[-91.1343, 33.6768],
						[-91.0768, 33.6579],
						[-91.0338, 33.6733],
						[-91.0625, 33.7163],
						[-91.1091, 33.7043],
						[-91.145, 33.7266],
						[-91.1414, 33.7773],
						[-91.0553, 33.779],
						[-91.0266, 33.7635],
						[-90.9907, 33.7928],
						[-91.0481, 33.8151],
						[-91.0732, 33.8623],
						[-91.0086, 33.9293],
						[-91.0876, 33.9586],
						[-91.0768, 33.9835],
						[-91.0194, 34.0032],
						[-91.0015, 33.9663],
						[-90.962, 33.98],
						[-90.9871, 34.0187],
						[-90.901, 34.0238],
						[-90.8722, 34.0762],
						[-90.883, 34.0969],
						[-90.9225, 34.0943],
						[-90.9548, 34.1381],
						[-90.9117, 34.1656],
						[-90.8543, 34.1372],
						[-90.8076, 34.1613],
						[-90.8148, 34.1828],
						[-90.8866, 34.1819],
						[-90.9369, 34.2188],
						[-90.9045, 34.2438],
						[-90.8471, 34.2068],
						[-90.8328, 34.2678],
						[-90.7646, 34.2807],
						[-90.7394, 34.3065],
						[-90.7682, 34.3451],
						[-90.7502, 34.3683],
						[-90.682, 34.3692],
						[-90.6928, 34.3228],
						[-90.6569, 34.3219],
						[-90.6569, 34.3761],
						[-90.5672, 34.425],
						[-90.5887, 34.4963],
						[-90.542, 34.5401],
						[-90.5456, 34.5633],
						[-90.5887, 34.6157],
						[-90.5887, 34.6707],
						[-90.5528, 34.6888],
						[-90.5564, 34.6467],
						[-90.5169, 34.6312],
						[-90.4667, 34.6741],
						[-90.4738, 34.7008],
						[-90.5456, 34.7025],
						[-90.5672, 34.7369],
						[-90.542, 34.7489],
						[-90.5492, 34.779],
						[-90.5133, 34.8021],
						[-90.5205, 34.7532],
						[-90.5025, 34.724],
						[-90.4523, 34.7403],
						[-90.4738, 34.8021],
						[-90.4559, 34.8236],
						[-90.4846, 34.8614],
						[-90.4774, 34.8863],
						[-90.4379, 34.8846],
						[-90.4236, 34.8339],
						[-90.3123, 34.8717],
						[-90.2477, 34.9095],
						[-90.2477, 34.9482],
						[-90.3087, 34.9954],
						[-89.4868, 34.9937],
						[-88.9268, 34.9946],
						[-88.2018, 34.9954],
						[-88.1551, 34.9224],
						[-88.0977, 34.8923],
						[-88.1408, 34.5814],
						[-88.209, 34.029],
						[-88.2305, 33.8915],
						[-88.349, 32.9294],
						[-88.3669, 32.7473],
						[-88.4423, 32.1373],
						[-88.4746, 31.8942],
						[-88.4387, 31.2302],
						[-88.4172, 30.8668],
						[-88.4064, 30.5601],
						[-88.3956, 30.3694],
						[-88.41, 30.3419],
						[-88.4351, 30.3548],
						[-88.471, 30.3204],
						[-88.5356, 30.3436],
						[-88.5823, 30.3307],
						[-88.6002, 30.3565],
						[-88.6648, 30.3625],
						[-88.7294, 30.3428],
						[-88.812, 30.3849],
						[-88.8192, 30.3608],
						[-88.8945, 30.3935],
						[-88.9735, 30.3909],
						[-89.0848, 30.3677],
						[-89.1853, 30.3316],
						[-89.293, 30.3033],
						[-89.2786, 30.3496],
						[-89.3145, 30.3754],
						[-89.3647, 30.3522],
						[-89.3324, 30.3376],
						[-89.3288, 30.3033],
						[-89.4186, 30.2543],
						[-89.4473, 30.1856],
						[-89.4796, 30.1942],
						[-89.5263, 30.1804],
						[-89.5693, 30.1804],
						[-89.616, 30.2234],
						[-89.6303, 30.2801],
						[-89.6303, 30.3393],
						[-89.6842, 30.4055],
						[-89.6842, 30.4519],
						[-89.7201, 30.4888],
						[-89.7596, 30.5051],
						[-89.7739, 30.5386],
						[-89.8098, 30.5678],
						[-89.8242, 30.6495],
						[-89.8529, 30.6615],
						[-89.817, 30.7371],
						[-89.8313, 30.768],
						[-89.8062, 30.7895],
						[-89.7739, 30.8969],
						[-89.7488, 30.9132],
						[-89.7524, 30.9518],
						[-89.7273, 30.9707],
						[-89.7524, 31.0017],
						[-90.3446, 31],
						[-90.8256, 30.9991],
						[-91.6368, 30.9991],
						[-91.5901, 31.0171],
						[-91.565, 31.067],
						[-91.6224, 31.1099],
						[-91.626, 31.134],
						[-91.5901, 31.1735],
						[-91.6009, 31.2087],
						[-91.6439, 31.2345],
						[-91.6404, 31.2671],
						[-91.5757, 31.2611],
						[-91.5147, 31.2783],
						[-91.5075, 31.3152],
						[-91.5506, 31.347],
						[-91.5542, 31.3857],
						[-91.5793, 31.3994],
						[-91.547, 31.4329],
						[-91.5327, 31.39],
						[-91.504, 31.3651],
						[-91.4717, 31.3711],
						[-91.4717, 31.396],
						[-91.5183, 31.4604],
						[-91.5219, 31.524],
						[-91.4501, 31.5394],
						[-91.407, 31.5695],
						[-91.4214, 31.597],
						[-91.486, 31.5858],
						[-91.5183, 31.6116],
						[-91.5004, 31.6442],
						[-91.4645, 31.6202],
						[-91.3999, 31.6202],
						[-91.3963, 31.7113],
						[-91.3568, 31.7413],
						[-91.2742, 31.7456],
						[-91.2599, 31.7611],
						[-91.364, 31.7705],
						[-91.3389, 31.8513],
						[-91.2958, 31.8607],
						[-91.2886, 31.8238],
						[-91.2635, 31.8092],
						[-91.2455, 31.8332],
						[-91.2671, 31.8513],
						[-91.1809, 31.9183],
						[-91.1881, 31.9612],
						[-91.163, 31.9827],
						[-91.0948, 31.9947],
						[-91.0948, 32.0377],
						[-91.163, 32.06],
						[-91.145, 32.0815],
						[-91.0984, 32.048],
						[-91.0804, 32.0789],
						[-91.0374, 32.0978],
						[-91.005, 32.1571],
						[-91.0589, 32.1777],
						[-91.0517, 32.1245],
						[-91.1666, 32.1339],
						[-91.1594, 32.2018],
						[-91.0625, 32.219],
						[-91.0374, 32.2422],
						[-90.9871, 32.2155],
						[-90.9692, 32.2516],
						[-90.9763, 32.2971],
						[-90.9333, 32.2903],
						[-90.8758, 32.3581],
						[-90.8902, 32.3736],
						[-90.9117, 32.3392],
						[-90.9943, 32.3538],
						[-90.9943, 32.4037],
						[-90.9656, 32.4208],
						[-90.9943, 32.4509],
						[-91.0302, 32.4337],
						[-91.1127, 32.4758],
						[-91.102, 32.5256],
						[-91.084, 32.5265],
						[-91.023, 32.4853],
						[-90.9871, 32.4913],
						[-91.005, 32.5136],
						[-91.0804, 32.5566],
						[-91.0302, 32.5832],
						[-91.0015, 32.615],
						[-91.0266, 32.6468],
						[-91.0481, 32.6073],
						[-91.1199, 32.5849],
						[-91.1414, 32.5969],
						[-91.1522, 32.6416],
						[-91.1199, 32.6743],
						[-91.0625, 32.7026],
						[-91.0553, 32.7258],
						[-91.1666, 32.7516],
						[-91.163, 32.8126],
						[-91.145, 32.8444],
						[-91.0697, 32.889],
						[-91.0625, 32.9225],
						[-91.0948, 32.9844],
						[-91.1343, 32.9809],
						[-91.1307, 32.9234],
						[-91.1809, 32.901],
						[-91.2132, 32.9199],
						[-91.2025, 32.9612],
						[-91.1666, 33.0041]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "30",
			"properties": { "name": "Montana" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-116.0476, 49.0007],
					[-115.2077, 48.999],
					[-114.3749, 49.0015],
					[-113.693, 48.9973],
					[-113.011, 48.999],
					[-111.7619, 48.9973],
					[-111.0046, 48.9973],
					[-110.1719, 48.999],
					[-108.9946, 48.999],
					[-108.4885, 48.999],
					[-107.4404, 48.999],
					[-106.6257, 48.9998],
					[-106.1124, 48.999],
					[-105.0571, 48.999],
					[-104.648, 48.999],
					[-104.0485, 48.9998],
					[-104.0485, 48.389],
					[-104.045, 48.3744],
					[-104.045, 45.945],
					[-104.0414, 45.75],
					[-104.0414, 45.0009],
					[-104.0593, 44.9975],
					[-104.4721, 44.9983],
					[-105.373, 45],
					[-105.8468, 45],
					[-105.9293, 44.994],
					[-106.5682, 44.9949],
					[-107.0851, 44.9966],
					[-107.1353, 45],
					[-108.2696, 45],
					[-109.0628, 45],
					[-109.1023, 45.0061],
					[-109.7986, 45.0018],
					[-110.1109, 45.0035],
					[-110.2006, 44.9966],
					[-110.3621, 45.0009],
					[-110.4016, 44.994],
					[-110.7067, 44.9923],
					[-110.7856, 45.0026],
					[-111.0548, 45.0009],
					[-111.0584, 44.8669],
					[-111.0548, 44.6246],
					[-111.0477, 44.4743],
					[-111.123, 44.4941],
					[-111.1446, 44.5362],
					[-111.2307, 44.5868],
					[-111.2235, 44.6238],
					[-111.324, 44.7243],
					[-111.3851, 44.7552],
					[-111.4138, 44.7105],
					[-111.4856, 44.708],
					[-111.4676, 44.6796],
					[-111.525, 44.5954],
					[-111.4712, 44.5404],
					[-111.5861, 44.5628],
					[-111.6184, 44.549],
					[-111.7045, 44.5602],
					[-111.7153, 44.5439],
					[-111.823, 44.5095],
					[-111.8696, 44.5636],
					[-111.9486, 44.5568],
					[-111.9952, 44.5353],
					[-112.0311, 44.5465],
					[-112.1065, 44.5207],
					[-112.1352, 44.5396],
					[-112.1855, 44.5327],
					[-112.2285, 44.5628],
					[-112.286, 44.5688],
					[-112.3183, 44.5387],
					[-112.3506, 44.5387],
					[-112.3578, 44.4863],
					[-112.3865, 44.4477],
					[-112.4726, 44.4803],
					[-112.5013, 44.4631],
					[-112.5408, 44.4838],
					[-112.6593, 44.4855],
					[-112.7203, 44.5044],
					[-112.7813, 44.4846],
					[-112.8351, 44.4228],
					[-112.8136, 44.3772],
					[-112.8459, 44.3583],
					[-112.8854, 44.3961],
					[-112.95, 44.4167],
					[-113.0038, 44.4511],
					[-113.0254, 44.4966],
					[-113.0074, 44.5258],
					[-113.0828, 44.5954],
					[-113.0505, 44.6367],
					[-113.0684, 44.6796],
					[-113.133, 44.7733],
					[-113.2479, 44.8231],
					[-113.3412, 44.7853],
					[-113.3556, 44.8196],
					[-113.4561, 44.8652],
					[-113.4956, 44.9485],
					[-113.4453, 44.9597],
					[-113.4381, 45.0069],
					[-113.4525, 45.0593],
					[-113.5207, 45.0928],
					[-113.5135, 45.1152],
					[-113.5745, 45.128],
					[-113.5997, 45.1908],
					[-113.6858, 45.2535],
					[-113.6894, 45.2835],
					[-113.7396, 45.3299],
					[-113.7325, 45.3901],
					[-113.7612, 45.4064],
					[-113.7827, 45.4553],
					[-113.7612, 45.4811],
					[-113.7648, 45.5206],
					[-113.833, 45.5206],
					[-113.8078, 45.6022],
					[-113.8617, 45.6237],
					[-113.9047, 45.622],
					[-113.8976, 45.6443],
					[-113.9335, 45.6821],
					[-113.9873, 45.7045],
					[-114.0196, 45.6933],
					[-114.0124, 45.6581],
					[-114.0663, 45.628],
					[-114.1345, 45.5576],
					[-114.2027, 45.5352],
					[-114.2493, 45.5455],
					[-114.2601, 45.4957],
					[-114.3462, 45.4596],
					[-114.3642, 45.4906],
					[-114.4144, 45.5095],
					[-114.4611, 45.561],
					[-114.5508, 45.561],
					[-114.5365, 45.6065],
					[-114.5652, 45.6375],
					[-114.5006, 45.6693],
					[-114.497, 45.7105],
					[-114.5652, 45.7741],
					[-114.5006, 45.8505],
					[-114.4108, 45.8514],
					[-114.3857, 45.8892],
					[-114.4324, 45.9356],
					[-114.4108, 45.9777],
					[-114.479, 46.0009],
					[-114.4934, 46.0472],
					[-114.4611, 46.0971],
					[-114.5221, 46.1254],
					[-114.5149, 46.1675],
					[-114.4467, 46.1735],
					[-114.4503, 46.2414],
					[-114.4719, 46.2672],
					[-114.4252, 46.2878],
					[-114.4216, 46.3874],
					[-114.3749, 46.4433],
					[-114.4001, 46.5025],
					[-114.357, 46.5051],
					[-114.3211, 46.6108],
					[-114.3319, 46.6606],
					[-114.3606, 46.6692],
					[-114.4539, 46.6494],
					[-114.4683, 46.6314],
					[-114.5472, 46.6443],
					[-114.6154, 46.64],
					[-114.6441, 46.6735],
					[-114.6226, 46.707],
					[-114.6477, 46.7328],
					[-114.698, 46.7405],
					[-114.7123, 46.7147],
					[-114.7662, 46.6967],
					[-114.7877, 46.7113],
					[-114.7662, 46.7585],
					[-114.8882, 46.8084],
					[-114.9456, 46.8591],
					[-114.9313, 46.92],
					[-115.0031, 46.9716],
					[-115.0497, 46.9707],
					[-115.0713, 47.0223],
					[-115.1395, 47.0927],
					[-115.2005, 47.1391],
					[-115.2435, 47.1503],
					[-115.2615, 47.1821],
					[-115.301, 47.1881],
					[-115.2938, 47.2207],
					[-115.3261, 47.2559],
					[-115.4122, 47.2645],
					[-115.5271, 47.3032],
					[-115.5522, 47.3496],
					[-115.7209, 47.4243],
					[-115.7604, 47.4226],
					[-115.7173, 47.4535],
					[-115.6384, 47.4604],
					[-115.6276, 47.4793],
					[-115.685, 47.4853],
					[-115.703, 47.5343],
					[-115.7425, 47.5386],
					[-115.7353, 47.5678],
					[-115.6886, 47.5953],
					[-115.7317, 47.6425],
					[-115.7245, 47.6966],
					[-115.7712, 47.7173],
					[-115.7963, 47.7576],
					[-115.825, 47.7525],
					[-115.8537, 47.8281],
					[-115.9076, 47.8461],
					[-116.0476, 47.9767],
					[-116.0511, 48.2164],
					[-116.0476, 48.31],
					[-116.0476, 49.0007]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "33",
			"properties": { "name": "New Hampshire" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-71.5008, 45.0138],
					[-71.4901, 45.0722],
					[-71.4254, 45.1272],
					[-71.4362, 45.1401],
					[-71.3967, 45.2036],
					[-71.4434, 45.2346],
					[-71.3788, 45.244],
					[-71.3644, 45.2664],
					[-71.2855, 45.3024],
					[-71.2316, 45.25],
					[-71.1311, 45.2457],
					[-71.0845, 45.3059],
					[-71.0306, 44.6556],
					[-70.9911, 43.9159],
					[-70.9911, 43.8395],
					[-70.9732, 43.5706],
					[-70.9517, 43.5508],
					[-70.9732, 43.4752],
					[-70.9588, 43.4666],
					[-70.9876, 43.3799],
					[-70.9553, 43.3343],
					[-70.8081, 43.2244],
					[-70.8296, 43.1883],
					[-70.8296, 43.129],
					[-70.704, 43.0594],
					[-70.8117, 42.91],
					[-70.8189, 42.8722],
					[-70.8476, 42.861],
					[-70.9014, 42.8868],
					[-71.0306, 42.8593],
					[-71.0629, 42.806],
					[-71.1311, 42.8215],
					[-71.185, 42.7905],
					[-71.1814, 42.7373],
					[-71.2568, 42.7364],
					[-71.2926, 42.6969],
					[-71.7449, 42.7072],
					[-72.4592, 42.727],
					[-72.4915, 42.7725],
					[-72.513, 42.7639],
					[-72.5561, 42.8515],
					[-72.5238, 42.9142],
					[-72.5202, 42.9632],
					[-72.4735, 42.9718],
					[-72.4448, 43.0105],
					[-72.4663, 43.0551],
					[-72.434, 43.0835],
					[-72.452, 43.1393],
					[-72.4376, 43.2579],
					[-72.3946, 43.3129],
					[-72.4161, 43.3644],
					[-72.3802, 43.4804],
					[-72.3981, 43.5104],
					[-72.3802, 43.574],
					[-72.3264, 43.6024],
					[-72.3048, 43.696],
					[-72.2725, 43.7338],
					[-72.2043, 43.7707],
					[-72.1828, 43.8085],
					[-72.1864, 43.8635],
					[-72.1182, 43.9211],
					[-72.1182, 43.9941],
					[-72.0751, 44.0319],
					[-72.032, 44.0912],
					[-72.032, 44.1315],
					[-72.0679, 44.19],
					[-72.0464, 44.2389],
					[-72.0679, 44.2681],
					[-72.032, 44.3205],
					[-71.9064, 44.348],
					[-71.8705, 44.3369],
					[-71.8131, 44.3549],
					[-71.7951, 44.3996],
					[-71.709, 44.4116],
					[-71.6588, 44.4451],
					[-71.6336, 44.4838],
					[-71.5762, 44.5027],
					[-71.5977, 44.5533],
					[-71.5583, 44.5645],
					[-71.5511, 44.6272],
					[-71.587, 44.6599],
					[-71.6264, 44.7475],
					[-71.5726, 44.7922],
					[-71.5583, 44.8471],
					[-71.4936, 44.9116],
					[-71.5403, 44.988],
					[-71.5008, 45.0138]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "36",
			"properties": { "name": "New York" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-72.0356, 41.2554],
						[-72.0249, 41.2709],
						[-71.9279, 41.2898],
						[-72.0033, 41.2528],
						[-72.0356, 41.2554]
					]],
					[[
						[-72.1433, 41.0982],
						[-72.1254, 41.1154],
						[-72.0859, 41.1016],
						[-72.0859, 41.0587],
						[-72.1074, 41.0887],
						[-72.1433, 41.0982]
					]],
					[[
						[-72.2151, 41.1832],
						[-72.1577, 41.197],
						[-72.2007, 41.16],
						[-72.2151, 41.1832]
					]],
					[[
						[-74.0708, 40.6609],
						[-74.0528, 40.6034],
						[-74.0851, 40.5699],
						[-74.2215, 40.5029],
						[-74.261, 40.502],
						[-74.2502, 40.545],
						[-74.2179, 40.5587],
						[-74.2036, 40.6309],
						[-74.0708, 40.6609]
					]],
					[[
						[-76.3751, 43.8747],
						[-76.3177, 43.9176],
						[-76.3356, 43.879],
						[-76.3751, 43.8747]
					]],
					[[
						[-76.454, 43.891],
						[-76.411, 43.9254],
						[-76.3787, 43.9211],
						[-76.454, 43.891]
					]],
					[[
						[-79.7634, 42.27],
						[-79.6449, 42.3155],
						[-79.4547, 42.4108],
						[-79.3506, 42.489],
						[-79.2429, 42.532],
						[-79.1496, 42.5534],
						[-79.1101, 42.6136],
						[-79.0635, 42.6445],
						[-79.0491, 42.6892],
						[-78.9199, 42.7373],
						[-78.8517, 42.7837],
						[-78.8661, 42.8524],
						[-78.9127, 42.8868],
						[-78.9055, 42.9237],
						[-78.9307, 42.9555],
						[-79.0132, 42.9856],
						[-79.0096, 43.0646],
						[-79.0742, 43.0775],
						[-79.0419, 43.1436],
						[-79.0599, 43.251],
						[-79.0706, 43.2622],
						[-78.9701, 43.2811],
						[-78.8338, 43.3171],
						[-78.6328, 43.3575],
						[-78.4892, 43.3747],
						[-78.3707, 43.3764],
						[-78.2343, 43.3687],
						[-78.1051, 43.3756],
						[-77.7964, 43.3395],
						[-77.7139, 43.3232],
						[-77.6601, 43.2828],
						[-77.5775, 43.2433],
						[-77.5344, 43.2347],
						[-77.3909, 43.2768],
						[-77.2652, 43.2776],
						[-77.1109, 43.2879],
						[-77.0355, 43.2716],
						[-76.953, 43.2708],
						[-76.9027, 43.2922],
						[-76.795, 43.3094],
						[-76.7304, 43.3438],
						[-76.6981, 43.3446],
						[-76.6299, 43.4134],
						[-76.5222, 43.4683],
						[-76.4864, 43.4752],
						[-76.4182, 43.5216],
						[-76.3679, 43.5259],
						[-76.2961, 43.513],
						[-76.2351, 43.5293],
						[-76.2028, 43.5749],
						[-76.1956, 43.6496],
						[-76.2279, 43.8042],
						[-76.2997, 43.8386],
						[-76.2602, 43.8816],
						[-76.21, 43.891],
						[-76.2028, 43.8627],
						[-76.1274, 43.8979],
						[-76.1382, 43.9348],
						[-76.2064, 43.9752],
						[-76.2854, 43.9623],
						[-76.2674, 43.9872],
						[-76.3212, 44.031],
						[-76.3751, 44.0319],
						[-76.3715, 44.1006],
						[-76.3141, 44.1994],
						[-76.2459, 44.2037],
						[-76.1633, 44.2398],
						[-76.1633, 44.281],
						[-76.0987, 44.2999],
						[-76.0018, 44.3472],
						[-75.9695, 44.3429],
						[-75.9121, 44.3678],
						[-75.8223, 44.4322],
						[-75.808, 44.4717],
						[-75.7649, 44.5155],
						[-75.6177, 44.6195],
						[-75.5065, 44.7054],
						[-75.3055, 44.8265],
						[-75.3091, 44.8368],
						[-75.065, 44.9305],
						[-74.9717, 44.9837],
						[-74.9071, 44.9837],
						[-74.8281, 45.0155],
						[-74.7312, 44.9906],
						[-74.663, 44.9992],
						[-74.3364, 44.9923],
						[-74.1461, 44.9915],
						[-73.7621, 45.0035],
						[-73.6759, 45.0026],
						[-73.3421, 45.0112],
						[-73.3385, 44.9176],
						[-73.3637, 44.8918],
						[-73.378, 44.8377],
						[-73.3314, 44.7887],
						[-73.3673, 44.7415],
						[-73.3744, 44.6624],
						[-73.3888, 44.6358],
						[-73.3744, 44.5757],
						[-73.3062, 44.5001],
						[-73.2919, 44.4408],
						[-73.335, 44.3721],
						[-73.3098, 44.2741],
						[-73.3242, 44.2432],
						[-73.3924, 44.1891],
						[-73.4103, 44.1126],
						[-73.439, 44.0448],
						[-73.4067, 44.0216],
						[-73.4103, 43.9331],
						[-73.3744, 43.8755],
						[-73.3924, 43.8206],
						[-73.3493, 43.7716],
						[-73.4067, 43.6883],
						[-73.4319, 43.5903],
						[-73.396, 43.568],
						[-73.3708, 43.6247],
						[-73.3026, 43.6247],
						[-73.2955, 43.5792],
						[-73.2488, 43.5542],
						[-73.256, 43.2596],
						[-73.2775, 42.8971],
						[-73.2775, 42.8335],
						[-73.2919, 42.8017],
						[-73.2632, 42.7459],
						[-73.3852, 42.4254],
						[-73.5072, 42.0861],
						[-73.4857, 42.05],
						[-73.518, 41.6875],
						[-73.536, 41.4341],
						[-73.5503, 41.2958],
						[-73.4821, 41.2125],
						[-73.7262, 41.1008],
						[-73.6544, 41.0131],
						[-73.658, 40.9848],
						[-73.6831, 40.9487],
						[-73.7585, 40.9126],
						[-73.7657, 40.8448],
						[-73.8123, 40.8465],
						[-73.78, 40.7949],
						[-73.7154, 40.8697],
						[-73.6759, 40.8568],
						[-73.64, 40.8929],
						[-73.5647, 40.9161],
						[-73.5001, 40.9178],
						[-73.4857, 40.9461],
						[-73.4067, 40.9204],
						[-73.3996, 40.9556],
						[-73.3673, 40.9315],
						[-73.2955, 40.9247],
						[-73.2309, 40.9049],
						[-73.1483, 40.929],
						[-73.1411, 40.9659],
						[-73.0801, 40.9728],
						[-73.0442, 40.9625],
						[-72.775, 40.965],
						[-72.635, 40.9822],
						[-72.5848, 40.9977],
						[-72.4771, 41.0518],
						[-72.4448, 41.0862],
						[-72.4017, 41.0973],
						[-72.3551, 41.1403],
						[-72.2797, 41.1583],
						[-72.2366, 41.1566],
						[-72.3012, 41.1119],
						[-72.3335, 41.1068],
						[-72.2797, 41.0802],
						[-72.2618, 41.0424],
						[-72.2007, 41.032],
						[-72.1613, 41.0535],
						[-72.1038, 40.9917],
						[-72.05, 41.0063],
						[-71.9674, 41.0475],
						[-71.9603, 41.0716],
						[-71.8992, 41.081],
						[-71.8562, 41.0707],
						[-71.8741, 41.0527],
						[-72.1146, 40.9719],
						[-72.3946, 40.8663],
						[-72.574, 40.813],
						[-72.8648, 40.7331],
						[-73.055, 40.6661],
						[-73.2093, 40.6309],
						[-73.3062, 40.6206],
						[-73.3278, 40.6334],
						[-73.5575, 40.5819],
						[-73.7764, 40.5905],
						[-73.9415, 40.5432],
						[-73.9308, 40.5759],
						[-74.0133, 40.5742],
						[-74.0026, 40.5956],
						[-74.042, 40.6292],
						[-74.0169, 40.6592],
						[-74.0456, 40.6901],
						[-74.0241, 40.709],
						[-74.0097, 40.7649],
						[-73.9667, 40.8207],
						[-73.8949, 40.9814],
						[-73.9057, 40.9977],
						[-74.3794, 41.209],
						[-74.6953, 41.3576],
						[-74.742, 41.4049],
						[-74.7384, 41.4307],
						[-74.7958, 41.4238],
						[-74.8066, 41.4427],
						[-74.8963, 41.4401],
						[-74.8891, 41.4556],
						[-74.943, 41.4839],
						[-74.9824, 41.4796],
						[-74.986, 41.5088],
						[-75.0758, 41.6059],
						[-75.047, 41.6162],
						[-75.0542, 41.7528],
						[-75.1045, 41.7708],
						[-75.0758, 41.7983],
						[-75.1117, 41.8404],
						[-75.2086, 41.8696],
						[-75.2624, 41.8662],
						[-75.2911, 41.9521],
						[-75.3091, 41.9487],
						[-75.3593, 41.9994],
						[-75.7434, 41.9976],
						[-76.35, 41.9985],
						[-76.7484, 42.0019],
						[-77.1252, 41.9994],
						[-77.9974, 41.9985],
						[-78.5969, 42.0002],
						[-78.8732, 41.9976],
						[-79.7598, 41.9985],
						[-79.7634, 42.0002],
						[-79.7634, 42.27]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "39",
			"properties": { "name": "Ohio" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-82.7353, 41.6033],
						[-82.6779, 41.6179],
						[-82.6851, 41.587],
						[-82.7353, 41.6033]
					]],
					[[
						[-82.843, 41.6471],
						[-82.8215, 41.7245],
						[-82.782, 41.6944],
						[-82.7927, 41.6643],
						[-82.843, 41.6282],
						[-82.843, 41.6471]
					]],
					[[
						[-84.8063, 41.6961],
						[-84.3613, 41.7064],
						[-83.7619, 41.7236],
						[-83.4532, 41.733],
						[-83.4496, 41.7107],
						[-83.3742, 41.6867],
						[-83.3275, 41.7013],
						[-83.1947, 41.6308],
						[-83.105, 41.6136],
						[-83.0655, 41.5956],
						[-83.0009, 41.5389],
						[-82.9327, 41.514],
						[-82.8681, 41.5337],
						[-82.8609, 41.5767],
						[-82.8358, 41.5879],
						[-82.7856, 41.5406],
						[-82.7174, 41.5423],
						[-82.721, 41.5105],
						[-82.6169, 41.4281],
						[-82.5594, 41.3997],
						[-82.4805, 41.3817],
						[-82.3333, 41.4307],
						[-82.2543, 41.4341],
						[-82.1933, 41.4642],
						[-82.0103, 41.5157],
						[-81.8775, 41.4831],
						[-81.8093, 41.4959],
						[-81.7375, 41.4882],
						[-81.6334, 41.5406],
						[-81.5293, 41.6145],
						[-81.5006, 41.6231],
						[-81.4432, 41.6729],
						[-81.2852, 41.7605],
						[-81.2493, 41.7614],
						[-81.0089, 41.8542],
						[-80.9012, 41.8688],
						[-80.815, 41.898],
						[-80.5817, 41.9573],
						[-80.5207, 41.9779],
						[-80.5207, 40.6386],
						[-80.5853, 40.6154],
						[-80.6356, 40.6163],
						[-80.6679, 40.5733],
						[-80.6284, 40.5355],
						[-80.5961, 40.4634],
						[-80.6105, 40.4024],
						[-80.5997, 40.3207],
						[-80.6176, 40.2649],
						[-80.6535, 40.2452],
						[-80.6715, 40.1927],
						[-80.7074, 40.1515],
						[-80.7074, 40.1017],
						[-80.7397, 40.0759],
						[-80.7397, 39.9711],
						[-80.7648, 39.9539],
						[-80.7576, 39.9093],
						[-80.8043, 39.9187],
						[-80.7899, 39.8672],
						[-80.8258, 39.8474],
						[-80.8258, 39.7984],
						[-80.8689, 39.7572],
						[-80.8294, 39.7143],
						[-80.8653, 39.6919],
						[-80.8796, 39.6206],
						[-80.9371, 39.6129],
						[-81.0448, 39.5364],
						[-81.1022, 39.4875],
						[-81.1309, 39.4462],
						[-81.1704, 39.4394],
						[-81.2099, 39.393],
						[-81.2709, 39.3861],
						[-81.3463, 39.3457],
						[-81.3857, 39.3431],
						[-81.4073, 39.3878],
						[-81.4683, 39.4041],
						[-81.558, 39.3388],
						[-81.5688, 39.2675],
						[-81.6801, 39.2736],
						[-81.6908, 39.2263],
						[-81.7267, 39.216],
						[-81.7554, 39.1808],
						[-81.7447, 39.0983],
						[-81.8129, 39.082],
						[-81.8021, 39.045],
						[-81.7662, 39.0201],
						[-81.7806, 38.9248],
						[-81.8129, 38.9463],
						[-81.8452, 38.9291],
						[-81.8559, 38.893],
						[-81.8882, 38.8741],
						[-81.9277, 38.8913],
						[-81.899, 38.9256],
						[-81.9421, 38.9935],
						[-81.9816, 38.9944],
						[-82.0282, 39.0287],
						[-82.0498, 38.9969],
						[-82.0928, 38.9737],
						[-82.1431, 38.8981],
						[-82.1431, 38.8406],
						[-82.2185, 38.7959],
						[-82.1826, 38.7057],
						[-82.1718, 38.619],
						[-82.1933, 38.5932],
						[-82.2508, 38.5992],
						[-82.2938, 38.5751],
						[-82.3046, 38.491],
						[-82.3225, 38.4489],
						[-82.4051, 38.4394],
						[-82.5307, 38.405],
						[-82.5953, 38.4222],
						[-82.6025, 38.46],
						[-82.6492, 38.4901],
						[-82.7245, 38.558],
						[-82.7999, 38.5631],
						[-82.8466, 38.5949],
						[-82.8789, 38.6903],
						[-82.8681, 38.7281],
						[-82.8932, 38.7564],
						[-82.9686, 38.7289],
						[-83.0225, 38.7289],
						[-83.0548, 38.6954],
						[-83.1194, 38.6662],
						[-83.1553, 38.6207],
						[-83.2629, 38.6215],
						[-83.3096, 38.6009],
						[-83.3275, 38.6378],
						[-83.367, 38.6585],
						[-83.4675, 38.6756],
						[-83.5214, 38.7031],
						[-83.6147, 38.6842],
						[-83.6649, 38.6275],
						[-83.7654, 38.6525],
						[-83.7834, 38.6954],
						[-83.8372, 38.7177],
						[-83.848, 38.747],
						[-83.9521, 38.7865],
						[-84.0705, 38.7701],
						[-84.2141, 38.8054],
						[-84.232, 38.8277],
						[-84.232, 38.8844],
						[-84.2895, 38.9557],
						[-84.3254, 39.0279],
						[-84.4295, 39.0554],
						[-84.4438, 39.1146],
						[-84.4725, 39.1215],
						[-84.5012, 39.0966],
						[-84.5515, 39.0992],
						[-84.6017, 39.0743],
						[-84.6771, 39.0983],
						[-84.731, 39.1447],
						[-84.7561, 39.1464],
						[-84.8207, 39.1052],
						[-84.8207, 39.3096],
						[-84.8135, 39.5485],
						[-84.8135, 39.9273],
						[-84.8027, 40.3027],
						[-84.8027, 41.4882],
						[-84.8063, 41.6961]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "41",
			"properties": { "name": "Oregon" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-123.5492, 46.2594],
					[-123.4738, 46.268],
					[-123.4271, 46.2294],
					[-123.4307, 46.1821],
					[-123.3697, 46.146],
					[-123.28, 46.1452],
					[-123.1651, 46.189],
					[-123.1149, 46.1855],
					[-122.9031, 46.0833],
					[-122.878, 46.0309],
					[-122.8134, 45.9613],
					[-122.8098, 45.9124],
					[-122.7847, 45.8677],
					[-122.7954, 45.8101],
					[-122.7595, 45.7345],
					[-122.7739, 45.6993],
					[-122.7631, 45.6564],
					[-122.6447, 45.61],
					[-122.4796, 45.5799],
					[-122.4401, 45.5636],
					[-122.3791, 45.5756],
					[-122.3324, 45.5481],
					[-122.2678, 45.5438],
					[-122.1852, 45.5773],
					[-122.1027, 45.5833],
					[-121.9089, 45.6546],
					[-121.8658, 45.6933],
					[-121.812, 45.7071],
					[-121.7366, 45.6942],
					[-121.6325, 45.7045],
					[-121.532, 45.7268],
					[-121.4638, 45.701],
					[-121.4028, 45.6933],
					[-121.3382, 45.7053],
					[-121.2161, 45.671],
					[-121.1838, 45.6065],
					[-121.1336, 45.61],
					[-121.0654, 45.6529],
					[-120.9433, 45.6564],
					[-120.8967, 45.6426],
					[-120.8572, 45.6718],
					[-120.6885, 45.7156],
					[-120.6347, 45.7457],
					[-120.5593, 45.738],
					[-120.4839, 45.6942],
					[-120.405, 45.6993],
					[-120.2829, 45.7217],
					[-120.2111, 45.726],
					[-120.1717, 45.762],
					[-120.0712, 45.7852],
					[-119.9671, 45.8247],
					[-119.802, 45.8479],
					[-119.6691, 45.8565],
					[-119.6225, 45.9055],
					[-119.5722, 45.9253],
					[-119.4861, 45.9064],
					[-119.2564, 45.9399],
					[-119.1954, 45.9278],
					[-119.1272, 45.933],
					[-119.0267, 45.9691],
					[-118.9872, 46],
					[-118.4021, 46.0009],
					[-117.5048, 45.9983],
					[-116.9162, 45.9957],
					[-116.8587, 45.9038],
					[-116.8192, 45.8806],
					[-116.7618, 45.8162],
					[-116.6972, 45.8204],
					[-116.6613, 45.7801],
					[-116.5931, 45.7784],
					[-116.5357, 45.7371],
					[-116.5357, 45.6916],
					[-116.489, 45.6495],
					[-116.4639, 45.6031],
					[-116.5501, 45.5103],
					[-116.5536, 45.4631],
					[-116.5895, 45.4433],
					[-116.6757, 45.3145],
					[-116.6721, 45.2835],
					[-116.7044, 45.2397],
					[-116.7295, 45.1401],
					[-116.7762, 45.1057],
					[-116.7977, 45.0602],
					[-116.848, 45.0224],
					[-116.8587, 44.9786],
					[-116.8336, 44.9287],
					[-116.8659, 44.8703],
					[-116.9305, 44.805],
					[-116.9305, 44.787],
					[-117.0454, 44.7449],
					[-117.0956, 44.665],
					[-117.1243, 44.5817],
					[-117.1602, 44.525],
					[-117.2248, 44.4838],
					[-117.2141, 44.4271],
					[-117.2428, 44.3961],
					[-117.1889, 44.3369],
					[-117.2177, 44.2887],
					[-117.171, 44.2587],
					[-117.1028, 44.2802],
					[-117.0454, 44.2295],
					[-116.9736, 44.2398],
					[-116.9664, 44.1942],
					[-116.9018, 44.1796],
					[-116.8982, 44.153],
					[-116.9341, 44.0998],
					[-116.9772, 44.0851],
					[-116.9736, 44.0491],
					[-116.9341, 44.0216],
					[-116.9377, 43.9846],
					[-116.9808, 43.9151],
					[-116.9808, 43.8687],
					[-117.0203, 43.8592],
					[-117.0274, 43.8085],
					[-117.0274, 42.0002],
					[-117.6233, 41.9985],
					[-118.6031, 41.9942],
					[-118.6965, 41.9916],
					[-119.2097, 41.9933],
					[-119.9994, 41.9951],
					[-119.9994, 41.9942],
					[-119.9994, 41.9951],
					[-120.3296, 41.9933],
					[-121.0367, 41.9933],
					[-121.4458, 41.9968],
					[-121.9986, 42.0036],
					[-122.1601, 42.0079],
					[-122.5011, 42.0088],
					[-122.6339, 42.0045],
					[-123.0467, 42.0028],
					[-123.1472, 42.0097],
					[-123.3482, 41.9994],
					[-123.6246, 42.0002],
					[-123.6569, 41.9951],
					[-124.2132, 41.9985],
					[-124.2886, 42.0457],
					[-124.3424, 42.093],
					[-124.3675, 42.1523],
					[-124.3604, 42.1806],
					[-124.4106, 42.2502],
					[-124.4034, 42.2777],
					[-124.4286, 42.3318],
					[-124.4357, 42.44],
					[-124.3891, 42.5663],
					[-124.4142, 42.6582],
					[-124.4752, 42.733],
					[-124.5147, 42.7347],
					[-124.5542, 42.8404],
					[-124.5003, 42.9177],
					[-124.4573, 43.0002],
					[-124.4357, 43.0715],
					[-124.4357, 43.1161],
					[-124.4034, 43.1849],
					[-124.3819, 43.2699],
					[-124.4034, 43.306],
					[-124.3424, 43.3515],
					[-124.3137, 43.3884],
					[-124.2563, 43.5018],
					[-124.2024, 43.6676],
					[-124.1486, 43.9108],
					[-124.1235, 44.104],
					[-124.1091, 44.2655],
					[-124.1163, 44.2724],
					[-124.0804, 44.4417],
					[-124.084, 44.5009],
					[-124.0589, 44.659],
					[-124.0589, 44.738],
					[-124.0768, 44.7715],
					[-124.0481, 44.8497],
					[-124.005, 45.0447],
					[-123.9763, 45.1452],
					[-123.9619, 45.2801],
					[-123.9727, 45.3368],
					[-123.9619, 45.4304],
					[-123.9763, 45.4897],
					[-123.9584, 45.5103],
					[-123.9404, 45.6615],
					[-123.944, 45.7268],
					[-123.9835, 45.762],
					[-123.9619, 45.8368],
					[-123.9691, 45.9081],
					[-123.9942, 45.9467],
					[-123.9368, 45.9777],
					[-123.9332, 46.0713],
					[-124.0014, 46.2371],
					[-123.9045, 46.1692],
					[-123.8543, 46.1572],
					[-123.865, 46.1898],
					[-123.7825, 46.1984],
					[-123.7574, 46.213],
					[-123.7179, 46.189],
					[-123.6748, 46.2156],
					[-123.621, 46.2156],
					[-123.5492, 46.2594]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "47",
			"properties": { "name": "Tennessee" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-89.7344, 36.0005],
					[-89.6914, 36.0203],
					[-89.6806, 36.0847],
					[-89.6016, 36.1191],
					[-89.6052, 36.1715],
					[-89.7057, 36.2351],
					[-89.695, 36.2531],
					[-89.5873, 36.2394],
					[-89.5334, 36.2522],
					[-89.537, 36.2754],
					[-89.6124, 36.3089],
					[-89.6124, 36.3407],
					[-89.5263, 36.3416],
					[-89.5083, 36.3734],
					[-89.5442, 36.4241],
					[-89.5191, 36.479],
					[-89.5406, 36.4979],
					[-89.4868, 36.4971],
					[-89.494, 36.4704],
					[-89.4473, 36.4644],
					[-89.4186, 36.4988],
					[-89.3001, 36.5074],
					[-88.9986, 36.5022],
					[-88.5464, 36.5022],
					[-88.0546, 36.4971],
					[-88.0331, 36.5521],
					[-88.069, 36.6783],
					[-87.8501, 36.6637],
					[-87.8536, 36.6337],
					[-87.4445, 36.6405],
					[-87.1143, 36.6423],
					[-86.5902, 36.6526],
					[-86.5651, 36.6337],
					[-86.5077, 36.6526],
					[-86.3318, 36.6491],
					[-86.0339, 36.6302],
					[-85.8329, 36.6216],
					[-85.4883, 36.6148],
					[-85.2909, 36.6268],
					[-85.0253, 36.6191],
					[-84.8422, 36.6053],
					[-84.5443, 36.5959],
					[-84.2715, 36.5916],
					[-83.988, 36.5899],
					[-83.6901, 36.583],
					[-83.6757, 36.601],
					[-83.5573, 36.5976],
					[-83.2773, 36.5984],
					[-83.2594, 36.5941],
					[-82.6958, 36.5933],
					[-82.4769, 36.5959],
					[-81.9349, 36.5941],
					[-81.9241, 36.6165],
					[-81.6478, 36.6122],
					[-81.6765, 36.5881],
					[-81.7088, 36.5366],
					[-81.6944, 36.4679],
					[-81.716, 36.4576],
					[-81.7411, 36.4017],
					[-81.7052, 36.3381],
					[-81.7662, 36.3381],
					[-81.7949, 36.3579],
					[-81.8344, 36.3476],
					[-81.9098, 36.3021],
					[-82.0282, 36.1303],
					[-82.0785, 36.1062],
					[-82.1323, 36.1062],
					[-82.1467, 36.1492],
					[-82.2149, 36.1595],
					[-82.2472, 36.1311],
					[-82.2902, 36.1354],
					[-82.3513, 36.1174],
					[-82.4051, 36.0873],
					[-82.4589, 36.0074],
					[-82.5594, 35.9541],
					[-82.6133, 35.9713],
					[-82.5953, 36.0315],
					[-82.6312, 36.0658],
					[-82.7497, 36.0057],
					[-82.8215, 35.9215],
					[-82.8753, 35.9524],
					[-82.9112, 35.9267],
					[-82.9004, 35.8725],
					[-82.9937, 35.7737],
					[-83.0799, 35.7892],
					[-83.1553, 35.7643],
					[-83.184, 35.7299],
					[-83.2414, 35.7265],
					[-83.2558, 35.6964],
					[-83.3132, 35.6552],
					[-83.3491, 35.6612],
					[-83.367, 35.6389],
					[-83.446, 35.6114],
					[-83.4855, 35.5684],
					[-83.7726, 35.5624],
					[-83.8265, 35.5238],
					[-83.8839, 35.5169],
					[-83.9126, 35.4757],
					[-83.9593, 35.4628],
					[-84.0203, 35.4095],
					[-84.0059, 35.3717],
					[-84.0382, 35.3502],
					[-84.0203, 35.3013],
					[-84.0992, 35.2471],
					[-84.1997, 35.2437],
					[-84.2105, 35.266],
					[-84.2895, 35.2248],
					[-84.3218, 34.9886],
					[-84.9786, 34.9877],
					[-85.4668, 34.9826],
					[-85.6067, 34.9843],
					[-85.8257, 34.9886],
					[-86.3964, 34.992],
					[-86.5292, 34.9903],
					[-86.863, 34.992],
					[-87.4373, 35.0032],
					[-87.6634, 35.0032],
					[-88.2018, 35.0083],
					[-88.2018, 34.9954],
					[-88.9268, 34.9946],
					[-89.4868, 34.9937],
					[-90.3087, 34.9954],
					[-90.298, 35.0375],
					[-90.2082, 35.0264],
					[-90.1759, 35.1123],
					[-90.1436, 35.1346],
					[-90.1005, 35.1166],
					[-90.0647, 35.138],
					[-90.1185, 35.1879],
					[-90.0754, 35.2248],
					[-90.097, 35.2497],
					[-90.1508, 35.2557],
					[-90.1652, 35.2961],
					[-90.1077, 35.3047],
					[-90.1077, 35.3434],
					[-90.0754, 35.3837],
					[-90.1293, 35.4138],
					[-90.1364, 35.3769],
					[-90.1795, 35.382],
					[-90.1687, 35.4215],
					[-90.0862, 35.4791],
					[-90.0575, 35.4035],
					[-90.018, 35.4679],
					[-90.0503, 35.504],
					[-90.0323, 35.5538],
					[-90, 35.5616],
					[-89.9211, 35.5134],
					[-89.9103, 35.5478],
					[-89.9426, 35.5564],
					[-89.9462, 35.6019],
					[-89.8529, 35.638],
					[-89.8637, 35.6707],
					[-89.9067, 35.6509],
					[-89.939, 35.6655],
					[-89.9606, 35.7239],
					[-89.9067, 35.7591],
					[-89.8744, 35.7411],
					[-89.8134, 35.76],
					[-89.7811, 35.8047],
					[-89.7416, 35.8055],
					[-89.7021, 35.8339],
					[-89.7739, 35.8648],
					[-89.7416, 35.9069],
					[-89.6555, 35.8871],
					[-89.6555, 35.9258],
					[-89.7201, 35.9679],
					[-89.7344, 36.0005]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "49",
			"properties": { "name": "Utah" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-114.0411, 41.9933],
					[-113.8222, 41.9882],
					[-112.8818, 41.9994],
					[-112.1926, 42.0011],
					[-112.1747, 41.9968],
					[-111.4712, 41.9994],
					[-111.0477, 42.0019],
					[-111.0441, 41.5655],
					[-111.0477, 41.3774],
					[-111.0477, 40.9977],
					[-110.7139, 40.996],
					[-110.2365, 40.9951],
					[-110.0498, 40.9977],
					[-109.5366, 40.9985],
					[-109.0484, 41.0003],
					[-109.0484, 40.5398],
					[-109.052, 40.5003],
					[-109.052, 38.905],
					[-109.0592, 38.7195],
					[-109.0592, 38.2753],
					[-109.0412, 38.1645],
					[-109.0412, 37.5305],
					[-109.0448, 37.4996],
					[-109.0448, 36.9988],
					[-109.6263, 36.9979],
					[-110.4698, 36.9979],
					[-110.4913, 37.0039],
					[-111.0656, 37.0022],
					[-111.4138, 37.0013],
					[-112.5372, 37.0005],
					[-113.334, 37.0005],
					[-114.0519, 37.0005],
					[-114.0519, 37.7565],
					[-114.0483, 37.7659],
					[-114.0519, 37.9996],
					[-114.0483, 38.1482],
					[-114.0519, 38.4042],
					[-114.0483, 38.6774],
					[-114.0483, 40.1043],
					[-114.0447, 40.2323],
					[-114.0447, 40.7718],
					[-114.0411, 41.0003],
					[-114.0411, 41.9933]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "51",
			"properties": { "name": "Virginia" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-75.9982, 37.8484],
						[-75.9731, 37.8355],
						[-75.9982, 37.8123],
						[-75.9982, 37.8484]
					]],
					[[
						[-75.668, 37.9506],
						[-75.6465, 37.9472],
						[-75.6249, 37.9944],
						[-75.2409, 38.0271],
						[-75.2983, 37.9592],
						[-75.3521, 37.875],
						[-75.3809, 37.8518],
						[-75.3988, 37.8699],
						[-75.4526, 37.8638],
						[-75.5208, 37.7968],
						[-75.5639, 37.7367],
						[-75.6034, 37.6285],
						[-75.5998, 37.5735],
						[-75.6824, 37.4635],
						[-75.6572, 37.4515],
						[-75.7218, 37.3733],
						[-75.7649, 37.3054],
						[-75.7972, 37.296],
						[-75.7936, 37.247],
						[-75.8331, 37.1732],
						[-75.8977, 37.1182],
						[-75.9551, 37.1199],
						[-75.9408, 37.0898],
						[-75.9731, 37.0855],
						[-75.9767, 37.1577],
						[-76.0126, 37.2049],
						[-76.0269, 37.2573],
						[-76.0197, 37.3175],
						[-75.9874, 37.3682],
						[-75.9767, 37.4446],
						[-75.9408, 37.5348],
						[-75.9264, 37.6001],
						[-75.869, 37.668],
						[-75.8044, 37.7625],
						[-75.8187, 37.7917],
						[-75.7434, 37.8063],
						[-75.6895, 37.8621],
						[-75.6859, 37.8862],
						[-75.7577, 37.9042],
						[-75.668, 37.9506]
					]],
					[[
						[-75.9946, 37.9532],
						[-76.0341, 37.9154],
						[-76.0449, 37.9532],
						[-75.9946, 37.9532]
					]],
					[[
						[-81.9672, 37.5374],
						[-81.9277, 37.5125],
						[-81.9923, 37.4833],
						[-81.9887, 37.4549],
						[-81.9349, 37.436],
						[-81.9277, 37.3587],
						[-81.8775, 37.3321],
						[-81.8523, 37.2874],
						[-81.7877, 37.2848],
						[-81.7447, 37.2634],
						[-81.7447, 37.2427],
						[-81.6765, 37.2015],
						[-81.5544, 37.2075],
						[-81.5078, 37.2324],
						[-81.5006, 37.2582],
						[-81.418, 37.2728],
						[-81.3893, 37.3201],
						[-81.3606, 37.3381],
						[-81.3211, 37.2994],
						[-81.2242, 37.235],
						[-81.113, 37.2788],
						[-80.9801, 37.302],
						[-80.9191, 37.3063],
						[-80.8509, 37.3467],
						[-80.8832, 37.3836],
						[-80.8581, 37.4274],
						[-80.7827, 37.3948],
						[-80.7684, 37.3725],
						[-80.6212, 37.4334],
						[-80.553, 37.4738],
						[-80.51, 37.4816],
						[-80.4741, 37.4231],
						[-80.2982, 37.5082],
						[-80.2838, 37.5331],
						[-80.3305, 37.5365],
						[-80.3269, 37.564],
						[-80.2623, 37.5932],
						[-80.2192, 37.6276],
						[-80.2802, 37.6568],
						[-80.2946, 37.692],
						[-80.2515, 37.7255],
						[-80.2587, 37.7565],
						[-80.1833, 37.8527],
						[-80.0756, 37.942],
						[-80.011, 37.9858],
						[-79.9751, 38.0296],
						[-79.9536, 38.0812],
						[-79.9249, 38.107],
						[-79.9464, 38.1319],
						[-79.9213, 38.18],
						[-79.8316, 38.2496],
						[-79.7885, 38.2676],
						[-79.81, 38.3054],
						[-79.7311, 38.3801],
						[-79.688, 38.4317],
						[-79.6988, 38.4875],
						[-79.6485, 38.5915],
						[-79.5373, 38.5511],
						[-79.4762, 38.4574],
						[-79.3111, 38.4119],
						[-79.2106, 38.4927],
						[-79.1568, 38.6112],
						[-79.1209, 38.6602],
						[-79.0922, 38.6593],
						[-79.0886, 38.7203],
						[-79.0276, 38.7908],
						[-78.9953, 38.85],
						[-78.8696, 38.7633],
						[-78.7979, 38.875],
						[-78.7153, 38.9359],
						[-78.7153, 38.905],
						[-78.6794, 38.9256],
						[-78.6256, 38.9823],
						[-78.6004, 38.9643],
						[-78.5502, 39.0184],
						[-78.5717, 39.0322],
						[-78.4856, 39.1078],
						[-78.4605, 39.1138],
						[-78.403, 39.167],
						[-78.4389, 39.198],
						[-78.3994, 39.2443],
						[-78.421, 39.2572],
						[-78.3384, 39.3483],
						[-78.3636, 39.3577],
						[-78.342, 39.3887],
						[-78.36, 39.4127],
						[-78.3456, 39.466],
						[-78.1087, 39.3122],
						[-77.8287, 39.1327],
						[-77.7534, 39.283],
						[-77.7211, 39.3217],
						[-77.6744, 39.3242],
						[-77.617, 39.3028],
						[-77.5631, 39.3036],
						[-77.5416, 39.265],
						[-77.4591, 39.228],
						[-77.4842, 39.1859],
						[-77.5165, 39.1705],
						[-77.5201, 39.1206],
						[-77.4842, 39.1095],
						[-77.4626, 39.076],
						[-77.3406, 39.0631],
						[-77.2473, 39.027],
						[-77.2509, 38.9858],
						[-77.2222, 38.9712],
						[-77.1504, 38.966],
						[-77.1181, 38.9342],
						[-77.0391, 38.8715],
						[-77.0391, 38.7916],
						[-77.0427, 38.7186],
						[-77.1073, 38.6971],
						[-77.1324, 38.6739],
						[-77.1288, 38.6353],
						[-77.2006, 38.6172],
						[-77.2473, 38.6353],
						[-77.2473, 38.5906],
						[-77.3119, 38.4935],
						[-77.3262, 38.4489],
						[-77.3191, 38.3836],
						[-77.2652, 38.3329],
						[-77.1611, 38.3458],
						[-77.1396, 38.3681],
						[-77.0858, 38.3681],
						[-77.0427, 38.4007],
						[-77.0104, 38.375],
						[-77.0319, 38.3114],
						[-76.9637, 38.2564],
						[-76.9637, 38.2143],
						[-76.8381, 38.1636],
						[-76.7484, 38.1619],
						[-76.7232, 38.1379],
						[-76.7017, 38.1559],
						[-76.6156, 38.1482],
						[-76.6012, 38.1104],
						[-76.5438, 38.0769],
						[-76.5151, 38.0262],
						[-76.4684, 38.0133],
						[-76.4146, 37.9669],
						[-76.3428, 37.9472],
						[-76.2351, 37.8887],
						[-76.253, 37.8329],
						[-76.3069, 37.8123],
						[-76.3141, 37.7204],
						[-76.2997, 37.6955],
						[-76.3392, 37.6559],
						[-76.2925, 37.6362],
						[-76.3607, 37.6104],
						[-76.472, 37.6654],
						[-76.5115, 37.6422],
						[-76.5366, 37.6637],
						[-76.5366, 37.6989],
						[-76.5833, 37.7685],
						[-76.6515, 37.796],
						[-76.6802, 37.826],
						[-76.7232, 37.8363],
						[-76.7986, 37.9248],
						[-76.8381, 37.9343],
						[-76.8525, 37.9729],
						[-76.8991, 37.9987],
						[-76.9063, 37.9729],
						[-76.8058, 37.8965],
						[-76.734, 37.7985],
						[-76.6802, 37.7779],
						[-76.6766, 37.7565],
						[-76.6192, 37.7444],
						[-76.5833, 37.662],
						[-76.5438, 37.6164],
						[-76.4361, 37.613],
						[-76.411, 37.5821],
						[-76.2997, 37.5614],
						[-76.35, 37.5365],
						[-76.3535, 37.5048],
						[-76.3069, 37.4979],
						[-76.2925, 37.5168],
						[-76.2638, 37.4816],
						[-76.2459, 37.3871],
						[-76.2746, 37.3097],
						[-76.3392, 37.3639],
						[-76.4146, 37.4025],
						[-76.4469, 37.3664],
						[-76.4074, 37.3329],
						[-76.3823, 37.2857],
						[-76.35, 37.2737],
						[-76.4289, 37.253],
						[-76.4935, 37.2496],
						[-76.472, 37.2161],
						[-76.393, 37.2256],
						[-76.4002, 37.1603],
						[-76.35, 37.1706],
						[-76.3356, 37.1439],
						[-76.2925, 37.1268],
						[-76.271, 37.0847],
						[-76.3033, 37.0013],
						[-76.3177, 37.0142],
						[-76.3751, 36.9988],
						[-76.4289, 36.9696],
						[-76.4648, 37.028],
						[-76.5689, 37.0804],
						[-76.6192, 37.119],
						[-76.6048, 37.1603],
						[-76.6515, 37.2213],
						[-76.7304, 37.2135],
						[-76.7591, 37.1921],
						[-76.795, 37.2316],
						[-76.8596, 37.241],
						[-76.874, 37.2608],
						[-76.9494, 37.2307],
						[-76.9099, 37.2024],
						[-76.8668, 37.2092],
						[-76.8022, 37.1981],
						[-76.7484, 37.1508],
						[-76.7161, 37.1491],
						[-76.6874, 37.1989],
						[-76.6586, 37.0967],
						[-76.6551, 37.0391],
						[-76.5761, 37.0228],
						[-76.4864, 36.9524],
						[-76.4828, 36.8965],
						[-76.3859, 36.9],
						[-76.3535, 36.9232],
						[-76.35, 36.8948],
						[-76.3177, 36.8854],
						[-76.3284, 36.9592],
						[-76.2674, 36.9644],
						[-76.2207, 36.9395],
						[-76.0951, 36.9086],
						[-76.0341, 36.9318],
						[-75.9946, 36.9223],
						[-75.9659, 36.8123],
						[-75.9228, 36.6921],
						[-75.8905, 36.6311],
						[-75.8654, 36.5503],
						[-76.3751, 36.5503],
						[-76.8058, 36.5503],
						[-76.9171, 36.5521],
						[-76.9171, 36.5435],
						[-77.2975, 36.5443],
						[-78.2451, 36.5443],
						[-78.5107, 36.5409],
						[-79.5121, 36.5409],
						[-80.1223, 36.5426],
						[-80.2946, 36.5443],
						[-80.7038, 36.5624],
						[-80.8366, 36.5589],
						[-81.1776, 36.5718],
						[-81.375, 36.5744],
						[-81.6765, 36.5881],
						[-81.6478, 36.6122],
						[-81.9241, 36.6165],
						[-81.9349, 36.5941],
						[-82.4769, 36.5959],
						[-82.6958, 36.5933],
						[-83.2594, 36.5941],
						[-83.2773, 36.5984],
						[-83.5573, 36.5976],
						[-83.6757, 36.601],
						[-83.647, 36.6242],
						[-83.5285, 36.6663],
						[-83.4532, 36.6646],
						[-83.3132, 36.7093],
						[-83.1947, 36.7393],
						[-83.1373, 36.7428],
						[-83.1301, 36.7857],
						[-83.0727, 36.8544],
						[-82.9686, 36.8579],
						[-82.8789, 36.8888],
						[-82.8574, 36.9275],
						[-82.8681, 36.9781],
						[-82.825, 37.0065],
						[-82.782, 37.0082],
						[-82.7245, 37.0417],
						[-82.7245, 37.1156],
						[-82.6348, 37.1543],
						[-82.5666, 37.1963],
						[-82.4877, 37.2316],
						[-82.3548, 37.2651],
						[-81.9672, 37.5374]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "53",
			"properties": { "name": "Washington" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-122.5262, 47.359],
						[-122.5119, 47.4492],
						[-122.476, 47.5111],
						[-122.4329, 47.4664],
						[-122.4401, 47.4166],
						[-122.3719, 47.3891],
						[-122.4365, 47.3659],
						[-122.4544, 47.3436],
						[-122.519, 47.3333],
						[-122.5262, 47.359]
					]],
					[[
						[-122.6519, 48.5488],
						[-122.6519, 48.5832],
						[-122.5801, 48.548],
						[-122.6411, 48.5256],
						[-122.6519, 48.5488]
					]],
					[[
						[-122.7236, 48.7318],
						[-122.7021, 48.7438],
						[-122.6088, 48.645],
						[-122.6734, 48.6811],
						[-122.7236, 48.7318]
					]],
					[[
						[-122.7416, 48.584],
						[-122.7129, 48.609],
						[-122.6698, 48.5686],
						[-122.7236, 48.5402],
						[-122.7416, 48.584]
					]],
					[[
						[-122.7703, 48.2275],
						[-122.7236, 48.304],
						[-122.6734, 48.3547],
						[-122.6662, 48.4019],
						[-122.6088, 48.4114],
						[-122.5837, 48.395],
						[-122.5837, 48.353],
						[-122.5155, 48.3203],
						[-122.5047, 48.298],
						[-122.5585, 48.2817],
						[-122.6195, 48.2945],
						[-122.6231, 48.2696],
						[-122.6698, 48.2404],
						[-122.72, 48.2344],
						[-122.7093, 48.2138],
						[-122.6267, 48.2224],
						[-122.5872, 48.1863],
						[-122.5585, 48.1141],
						[-122.5729, 48.1021],
						[-122.537, 48.0162],
						[-122.5083, 48.0394],
						[-122.5262, 48.097],
						[-122.4903, 48.0944],
						[-122.4329, 48.0454],
						[-122.3755, 48.0342],
						[-122.3504, 47.9535],
						[-122.3791, 47.9037],
						[-122.4293, 47.9148],
						[-122.4724, 47.9887],
						[-122.5478, 47.9672],
						[-122.5406, 47.993],
						[-122.6088, 48.0308],
						[-122.598, 48.1107],
						[-122.616, 48.1588],
						[-122.6806, 48.1554],
						[-122.6913, 48.1811],
						[-122.7703, 48.2275]
					]],
					[[
						[-122.9785, 48.7937],
						[-122.939, 48.7902],
						[-122.817, 48.7447],
						[-122.8457, 48.7378],
						[-122.9246, 48.755],
						[-122.9785, 48.7937]
					]],
					[[
						[-123.0718, 48.7],
						[-123.0108, 48.7224],
						[-123.0036, 48.694],
						[-123.0718, 48.7]
					]],
					[[
						[-123.0897, 49.0024],
						[-123.0359, 49.0024],
						[-123.0215, 48.9775],
						[-123.0826, 48.9758],
						[-123.0897, 49.0024]
					]],
					[[
						[-123.2046, 48.5961],
						[-123.1795, 48.6218],
						[-123.1077, 48.6227],
						[-123.1005, 48.5978],
						[-123.0467, 48.5694],
						[-122.9857, 48.5617],
						[-123.0503, 48.621],
						[-122.9175, 48.7138],
						[-122.8744, 48.712],
						[-122.7416, 48.6622],
						[-122.8098, 48.6193],
						[-122.7703, 48.5626],
						[-122.7775, 48.5093],
						[-122.817, 48.4878],
						[-122.8026, 48.4286],
						[-122.8744, 48.4182],
						[-122.9282, 48.4612],
						[-122.9605, 48.4509],
						[-123.0395, 48.4603],
						[-123.1508, 48.5136],
						[-123.1723, 48.5789],
						[-123.2046, 48.5961]
					]],
					[[
						[-123.2369, 48.6888],
						[-123.1723, 48.6803],
						[-123.1077, 48.6339],
						[-123.2154, 48.6691],
						[-123.2369, 48.6888]
					]],
					[[
						[-117.031, 48.999],
						[-117.0346, 48.7498],
						[-117.0346, 48.371],
						[-117.0418, 48.1253],
						[-117.0418, 47.5222],
						[-117.0382, 47.4776],
						[-117.0382, 46.4063],
						[-117.0633, 46.3651],
						[-116.9915, 46.2998],
						[-116.9628, 46.2534],
						[-116.9628, 46.1993],
						[-116.9198, 46.1649],
						[-116.9808, 46.085],
						[-116.9556, 46.0756],
						[-116.9162, 45.9957],
						[-117.5048, 45.9983],
						[-118.4021, 46.0009],
						[-118.9872, 46],
						[-119.0267, 45.9691],
						[-119.1272, 45.933],
						[-119.1954, 45.9278],
						[-119.2564, 45.9399],
						[-119.4861, 45.9064],
						[-119.5722, 45.9253],
						[-119.6225, 45.9055],
						[-119.6691, 45.8565],
						[-119.802, 45.8479],
						[-119.9671, 45.8247],
						[-120.0712, 45.7852],
						[-120.1717, 45.762],
						[-120.2111, 45.726],
						[-120.2829, 45.7217],
						[-120.405, 45.6993],
						[-120.4839, 45.6942],
						[-120.5593, 45.738],
						[-120.6347, 45.7457],
						[-120.6885, 45.7156],
						[-120.8572, 45.6718],
						[-120.8967, 45.6426],
						[-120.9433, 45.6564],
						[-121.0654, 45.6529],
						[-121.1336, 45.61],
						[-121.1838, 45.6065],
						[-121.2161, 45.671],
						[-121.3382, 45.7053],
						[-121.4028, 45.6933],
						[-121.4638, 45.701],
						[-121.532, 45.7268],
						[-121.6325, 45.7045],
						[-121.7366, 45.6942],
						[-121.812, 45.7071],
						[-121.8658, 45.6933],
						[-121.9089, 45.6546],
						[-122.1027, 45.5833],
						[-122.1852, 45.5773],
						[-122.2678, 45.5438],
						[-122.3324, 45.5481],
						[-122.3791, 45.5756],
						[-122.4401, 45.5636],
						[-122.4796, 45.5799],
						[-122.6447, 45.61],
						[-122.7631, 45.6564],
						[-122.7739, 45.6993],
						[-122.7595, 45.7345],
						[-122.7954, 45.8101],
						[-122.7847, 45.8677],
						[-122.8098, 45.9124],
						[-122.8134, 45.9613],
						[-122.878, 46.0309],
						[-122.9031, 46.0833],
						[-123.1149, 46.1855],
						[-123.1651, 46.189],
						[-123.28, 46.1452],
						[-123.3697, 46.146],
						[-123.4307, 46.1821],
						[-123.4271, 46.2294],
						[-123.4738, 46.268],
						[-123.5492, 46.2594],
						[-123.6712, 46.2672],
						[-123.682, 46.2964],
						[-123.7251, 46.3015],
						[-123.7609, 46.2749],
						[-123.8076, 46.2835],
						[-123.8758, 46.2397],
						[-123.9081, 46.2457],
						[-123.9835, 46.3093],
						[-124.0194, 46.3161],
						[-124.0445, 46.2757],
						[-124.0804, 46.2672],
						[-124.0589, 46.3866],
						[-124.0553, 46.4931],
						[-124.0696, 46.6348],
						[-124.023, 46.5824],
						[-124.0301, 46.4965],
						[-124.0158, 46.3788],
						[-123.9548, 46.3788],
						[-123.9512, 46.4106],
						[-123.9871, 46.445],
						[-123.9871, 46.4974],
						[-123.944, 46.4656],
						[-123.8937, 46.5369],
						[-123.9619, 46.6366],
						[-123.9225, 46.6726],
						[-123.8327, 46.7182],
						[-123.8937, 46.75],
						[-123.9153, 46.7268],
						[-123.9799, 46.725],
						[-123.9655, 46.7053],
						[-124.023, 46.7087],
						[-124.0947, 46.7465],
						[-124.0983, 46.7938],
						[-124.1378, 46.9054],
						[-124.1091, 46.9123],
						[-124.0876, 46.8676],
						[-124.0481, 46.8943],
						[-123.8614, 46.9484],
						[-123.8973, 46.9716],
						[-124.0194, 46.9913],
						[-124.0266, 47.03],
						[-124.1235, 47.042],
						[-124.1522, 47.0214],
						[-124.1235, 46.9441],
						[-124.1809, 46.9261],
						[-124.1701, 46.9587],
						[-124.1845, 47.1357],
						[-124.2347, 47.2869],
						[-124.3209, 47.3556],
						[-124.3532, 47.5334],
						[-124.3711, 47.5996],
						[-124.425, 47.7387],
						[-124.4752, 47.7697],
						[-124.4896, 47.8169],
						[-124.5398, 47.8367],
						[-124.5578, 47.8633],
						[-124.626, 47.8882],
						[-124.669, 47.9827],
						[-124.6978, 48.0695],
						[-124.6942, 48.115],
						[-124.7336, 48.1631],
						[-124.6906, 48.2129],
						[-124.6583, 48.3315],
						[-124.7265, 48.371],
						[-124.7157, 48.3899],
						[-124.6547, 48.3908],
						[-124.5111, 48.3435],
						[-124.3962, 48.2885],
						[-124.3604, 48.2877],
						[-124.2706, 48.2542],
						[-124.2491, 48.2645],
						[-124.1019, 48.2172],
						[-124.1055, 48.2],
						[-123.9799, 48.1648],
						[-123.7789, 48.1554],
						[-123.7035, 48.1665],
						[-123.5922, 48.1348],
						[-123.5599, 48.1511],
						[-123.5241, 48.1356],
						[-123.4379, 48.1416],
						[-123.3948, 48.1141],
						[-123.2477, 48.1159],
						[-123.1328, 48.1768],
						[-123.1436, 48.1562],
						[-123.0646, 48.1202],
						[-123.0395, 48.0815],
						[-122.9785, 48.0961],
						[-122.9175, 48.0918],
						[-122.9282, 48.0643],
						[-122.878, 48.0471],
						[-122.8744, 47.9964],
						[-122.8385, 48.0016],
						[-122.8277, 48.0471],
						[-122.878, 48.0763],
						[-122.878, 48.1107],
						[-122.8349, 48.1348],
						[-122.7595, 48.1433],
						[-122.7488, 48.1167],
						[-122.8026, 48.0875],
						[-122.7667, 48.0446],
						[-122.7416, 48.0497],
						[-122.7344, 48.0909],
						[-122.6985, 48.103],
						[-122.6698, 48.0171],
						[-122.7236, 48.0085],
						[-122.6842, 47.9724],
						[-122.6554, 47.9054],
						[-122.6088, 47.8873],
						[-122.6375, 47.8659],
						[-122.6949, 47.8676],
						[-122.6877, 47.8315],
						[-122.7488, 47.8006],
						[-122.7811, 47.7035],
						[-122.8313, 47.6958],
						[-122.7918, 47.7928],
						[-122.8134, 47.8075],
						[-122.8816, 47.7207],
						[-122.8959, 47.6752],
						[-122.9713, 47.615],
						[-123.1077, 47.4578],
						[-123.1544, 47.3556],
						[-123.1113, 47.3625],
						[-123.0287, 47.3513],
						[-122.9857, 47.3745],
						[-122.9103, 47.3891],
						[-122.8995, 47.4217],
						[-123.0574, 47.3685],
						[-123.1185, 47.3917],
						[-122.9677, 47.5858],
						[-122.8564, 47.6494],
						[-122.7524, 47.6734],
						[-122.7416, 47.7362],
						[-122.6842, 47.7989],
						[-122.5729, 47.8573],
						[-122.6052, 47.9406],
						[-122.5262, 47.9062],
						[-122.476, 47.7456],
						[-122.5549, 47.7456],
						[-122.5442, 47.7112],
						[-122.5047, 47.6992],
						[-122.519, 47.6511],
						[-122.4939, 47.6348],
						[-122.4939, 47.5893],
						[-122.5442, 47.5566],
						[-122.5478, 47.524],
						[-122.4939, 47.5102],
						[-122.5298, 47.469],
						[-122.5514, 47.3942],
						[-122.537, 47.3762],
						[-122.5729, 47.3272],
						[-122.5478, 47.2851],
						[-122.6016, 47.2173],
						[-122.6985, 47.2843],
						[-122.6698, 47.3668],
						[-122.7272, 47.3307],
						[-122.7488, 47.2766],
						[-122.6411, 47.2053],
						[-122.677, 47.1915],
						[-122.6734, 47.1503],
						[-122.7129, 47.1279],
						[-122.7703, 47.1675],
						[-122.8421, 47.2577],
						[-122.799, 47.2894],
						[-122.7954, 47.3418],
						[-122.8205, 47.3633],
						[-122.8205, 47.3195],
						[-122.8636, 47.2705],
						[-122.8385, 47.2087],
						[-122.86, 47.1675],
						[-122.8134, 47.1795],
						[-122.7739, 47.1228],
						[-122.677, 47.1039],
						[-122.6375, 47.1357],
						[-122.6375, 47.164],
						[-122.5908, 47.1778],
						[-122.5621, 47.2456],
						[-122.5262, 47.2912],
						[-122.5334, 47.3169],
						[-122.4437, 47.2671],
						[-122.4078, 47.2886],
						[-122.4221, 47.3195],
						[-122.3252, 47.3487],
						[-122.3252, 47.3917],
						[-122.3539, 47.4415],
						[-122.3827, 47.4509],
						[-122.3611, 47.481],
						[-122.397, 47.5154],
						[-122.4221, 47.5764],
						[-122.3432, 47.609],
						[-122.4293, 47.6588],
						[-122.3719, 47.7293],
						[-122.3934, 47.8075],
						[-122.3396, 47.847],
						[-122.3073, 47.9492],
						[-122.2283, 47.9707],
						[-122.2319, 48.0299],
						[-122.2822, 48.0497],
						[-122.3647, 48.1236],
						[-122.3611, 48.1872],
						[-122.397, 48.2293],
						[-122.4509, 48.2327],
						[-122.4401, 48.2121],
						[-122.4796, 48.188],
						[-122.4437, 48.1305],
						[-122.3791, 48.0875],
						[-122.3755, 48.0574],
						[-122.4688, 48.1305],
						[-122.5119, 48.1339],
						[-122.537, 48.1837],
						[-122.5298, 48.2499],
						[-122.4652, 48.2705],
						[-122.4078, 48.2516],
						[-122.3719, 48.2877],
						[-122.4078, 48.3263],
						[-122.5083, 48.365],
						[-122.5549, 48.4062],
						[-122.5514, 48.4397],
						[-122.6554, 48.4114],
						[-122.6806, 48.4397],
						[-122.6662, 48.4784],
						[-122.7129, 48.4638],
						[-122.7021, 48.4973],
						[-122.616, 48.5213],
						[-122.5693, 48.5084],
						[-122.537, 48.4664],
						[-122.4688, 48.4724],
						[-122.5047, 48.5651],
						[-122.4437, 48.5703],
						[-122.4257, 48.5995],
						[-122.5011, 48.6562],
						[-122.519, 48.7129],
						[-122.4903, 48.7507],
						[-122.537, 48.7765],
						[-122.598, 48.7713],
						[-122.6375, 48.7361],
						[-122.6052, 48.6983],
						[-122.6734, 48.7327],
						[-122.6447, 48.7816],
						[-122.6806, 48.8031],
						[-122.7129, 48.7911],
						[-122.7165, 48.8469],
						[-122.7918, 48.8933],
						[-122.7524, 48.9113],
						[-122.7703, 48.9423],
						[-122.817, 48.956],
						[-122.7595, 49.0024],
						[-122.0991, 49.0024],
						[-121.7509, 48.9973],
						[-121.2305, 49.0015],
						[-120.7172, 48.9998],
						[-120.0353, 48.9998],
						[-119.4287, 48.9998],
						[-118.8364, 49.0007],
						[-118.1975, 49.0007],
						[-117.031, 48.999]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "55",
			"properties": { "name": "Wisconsin" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-86.9563, 45.3523],
						[-86.9348, 45.421],
						[-86.8235, 45.4064],
						[-86.9061, 45.2964],
						[-86.9563, 45.3523]
					]],
					[[
						[-87.3763, 45.1993],
						[-87.3332, 45.2114],
						[-87.326, 45.1573],
						[-87.3763, 45.177],
						[-87.3763, 45.1993]
					]],
					[[
						[-90.4667, 47.0025],
						[-90.4379, 47.0738],
						[-90.3949, 47.0773],
						[-90.4128, 47.0128],
						[-90.4667, 47.0025]
					]],
					[[
						[-90.5241, 46.8762],
						[-90.4487, 46.9046],
						[-90.499, 46.8642],
						[-90.5241, 46.8762]
					]],
					[[
						[-90.6533, 46.9252],
						[-90.6354, 46.9432],
						[-90.5277, 46.9682],
						[-90.5097, 46.957],
						[-90.5492, 46.9157],
						[-90.6354, 46.9063],
						[-90.6533, 46.9252]
					]],
					[[
						[-90.761, 46.8393],
						[-90.7502, 46.8625],
						[-90.6784, 46.8977],
						[-90.6748, 46.8814],
						[-90.761, 46.8393]
					]],
					[[
						[-90.7394, 46.9639],
						[-90.6892, 46.9175],
						[-90.7359, 46.9149],
						[-90.7646, 46.9467],
						[-90.7394, 46.9639]
					]],
					[[
						[-90.7753, 47.024],
						[-90.6497, 47.0549],
						[-90.6102, 47.0077],
						[-90.56, 47.0369],
						[-90.5528, 46.9991],
						[-90.6102, 46.9913],
						[-90.6712, 46.9493],
						[-90.7107, 46.9853],
						[-90.7682, 47.0025],
						[-90.7753, 47.024]
					]],
					[[
						[-90.7933, 46.7852],
						[-90.7323, 46.8006],
						[-90.6174, 46.8745],
						[-90.5672, 46.847],
						[-90.6748, 46.8187],
						[-90.6569, 46.7886],
						[-90.7179, 46.7852],
						[-90.7897, 46.7534],
						[-90.7933, 46.7852]
					]],
					[[
						[-90.9835, 46.9862],
						[-90.9297, 47.0008],
						[-90.9333, 46.963],
						[-90.9835, 46.9862]
					]],
					[[
						[-92.0136, 46.7061],
						[-91.9419, 46.6804],
						[-91.8198, 46.6898],
						[-91.6439, 46.7345],
						[-91.5757, 46.7577],
						[-91.5111, 46.7577],
						[-91.3389, 46.8178],
						[-91.2491, 46.841],
						[-91.2276, 46.8633],
						[-91.1666, 46.8444],
						[-91.1414, 46.8728],
						[-91.0517, 46.8814],
						[-90.9727, 46.9415],
						[-90.9225, 46.9312],
						[-90.8722, 46.9613],
						[-90.8364, 46.9578],
						[-90.7502, 46.8883],
						[-90.8005, 46.823],
						[-90.8866, 46.756],
						[-90.8543, 46.6933],
						[-90.9117, 46.6632],
						[-90.9512, 46.597],
						[-90.9045, 46.5833],
						[-90.7538, 46.646],
						[-90.7359, 46.6924],
						[-90.5851, 46.5996],
						[-90.5384, 46.5807],
						[-90.5061, 46.5893],
						[-90.4164, 46.5661],
						[-90.3949, 46.5326],
						[-90.3518, 46.5378],
						[-90.3518, 46.5378],
						[-90.3159, 46.5171],
						[-90.219, 46.5034],
						[-90.1185, 46.3599],
						[-90.1221, 46.3367],
						[-89.092, 46.1383],
						[-88.7833, 46.0163],
						[-88.7402, 46.0275],
						[-88.6792, 46.0137],
						[-88.6648, 45.9888],
						[-88.6146, 45.9888],
						[-88.6038, 46.0163],
						[-88.5069, 46.018],
						[-88.4997, 45.9966],
						[-88.4243, 45.9785],
						[-88.3813, 45.9914],
						[-88.2951, 45.951],
						[-88.2449, 45.9631],
						[-88.1013, 45.9218],
						[-88.1013, 45.8814],
						[-88.0762, 45.8634],
						[-88.1372, 45.8187],
						[-88.0726, 45.7801],
						[-87.9972, 45.7955],
						[-87.9649, 45.7586],
						[-87.8788, 45.7552],
						[-87.8572, 45.7268],
						[-87.8106, 45.7113],
						[-87.7819, 45.6735],
						[-87.8249, 45.6624],
						[-87.7747, 45.598],
						[-87.8034, 45.5387],
						[-87.7926, 45.4983],
						[-87.8142, 45.4639],
						[-87.8608, 45.4347],
						[-87.8501, 45.4021],
						[-87.8752, 45.3815],
						[-87.8501, 45.3411],
						[-87.8501, 45.3411],
						[-87.7531, 45.3497],
						[-87.6957, 45.3901],
						[-87.6562, 45.3686],
						[-87.6491, 45.3394],
						[-87.6885, 45.2981],
						[-87.7424, 45.1968],
						[-87.7352, 45.1719],
						[-87.6885, 45.1478],
						[-87.6598, 45.1074],
						[-87.5916, 45.0954],
						[-87.6275, 45.0456],
						[-87.6311, 44.9769],
						[-87.7639, 44.9657],
						[-87.8393, 44.9313],
						[-87.8285, 44.8909],
						[-87.868, 44.8403],
						[-87.9003, 44.8274],
						[-87.9398, 44.7561],
						[-87.9829, 44.72],
						[-88.008, 44.6307],
						[-87.9972, 44.6092],
						[-88.0439, 44.5662],
						[-87.9721, 44.5301],
						[-87.929, 44.5362],
						[-87.9039, 44.5817],
						[-87.8106, 44.6367],
						[-87.7747, 44.6392],
						[-87.7208, 44.6934],
						[-87.7208, 44.7243],
						[-87.6096, 44.8385],
						[-87.5163, 44.8695],
						[-87.4768, 44.8635],
						[-87.4373, 44.8927],
						[-87.3834, 44.8652],
						[-87.405, 44.9047],
						[-87.3619, 44.988],
						[-87.265, 45.0817],
						[-87.2399, 45.1676],
						[-87.1968, 45.1633],
						[-87.1214, 45.1908],
						[-87.1178, 45.2414],
						[-87.0568, 45.293],
						[-86.9779, 45.2904],
						[-86.985, 45.2157],
						[-87.0425, 45.2114],
						[-87.0496, 45.0894],
						[-87.0927, 45.055],
						[-87.1214, 45.0585],
						[-87.1394, 45.0129],
						[-87.1896, 44.9683],
						[-87.1717, 44.9313],
						[-87.2183, 44.8978],
						[-87.204, 44.8755],
						[-87.2686, 44.8471],
						[-87.3153, 44.7939],
						[-87.344, 44.7191],
						[-87.405, 44.6272],
						[-87.4373, 44.6049],
						[-87.4696, 44.5516],
						[-87.545, 44.3214],
						[-87.5127, 44.2406],
						[-87.5198, 44.1796],
						[-87.5629, 44.1444],
						[-87.6455, 44.1049],
						[-87.6562, 44.0516],
						[-87.685, 44.0199],
						[-87.7352, 43.8807],
						[-87.728, 43.8102],
						[-87.6993, 43.7673],
						[-87.7065, 43.6797],
						[-87.7819, 43.5783],
						[-87.807, 43.4615],
						[-87.8788, 43.3696],
						[-87.9111, 43.2355],
						[-87.8824, 43.1702],
						[-87.9003, 43.1376],
						[-87.8716, 43.0646],
						[-87.8967, 43.0156],
						[-87.8465, 42.9624],
						[-87.8465, 42.8893],
						[-87.8249, 42.8352],
						[-87.7675, 42.7845],
						[-87.7819, 42.7081],
						[-87.8213, 42.6162],
						[-87.7998, 42.4916],
						[-88.0941, 42.4959],
						[-88.6397, 42.495],
						[-88.7869, 42.4916],
						[-89.4832, 42.501],
						[-89.6662, 42.5053],
						[-90.481, 42.5071],
						[-90.6425, 42.5088],
						[-90.6425, 42.5406],
						[-90.7071, 42.6342],
						[-90.7682, 42.6514],
						[-90.9369, 42.6832],
						[-91.0553, 42.7381],
						[-91.102, 42.8833],
						[-91.145, 42.9048],
						[-91.145, 42.9632],
						[-91.1737, 43.0388],
						[-91.1773, 43.1316],
						[-91.145, 43.1522],
						[-91.1235, 43.1969],
						[-91.0589, 43.2484],
						[-91.1055, 43.3137],
						[-91.2025, 43.3489],
						[-91.1989, 43.4125],
						[-91.2348, 43.4555],
						[-91.2168, 43.5001],
						[-91.2455, 43.5457],
						[-91.2312, 43.5835],
						[-91.2671, 43.6152],
						[-91.2742, 43.6762],
						[-91.2455, 43.7733],
						[-91.2778, 43.8377],
						[-91.3855, 43.9546],
						[-91.4394, 44.0018],
						[-91.5793, 44.0267],
						[-91.7085, 44.104],
						[-91.7193, 44.129],
						[-91.8162, 44.1642],
						[-91.8772, 44.2028],
						[-91.8952, 44.275],
						[-91.9239, 44.2879],
						[-91.9239, 44.3334],
						[-91.9742, 44.3678],
						[-92.0531, 44.4013],
						[-92.2326, 44.4451],
						[-92.2828, 44.4777],
						[-92.3187, 44.5447],
						[-92.3618, 44.5593],
						[-92.5413, 44.5671],
						[-92.5736, 44.6049],
						[-92.6238, 44.6186],
						[-92.6202, 44.6392],
						[-92.7315, 44.714],
						[-92.7889, 44.7372],
						[-92.8069, 44.7681],
						[-92.7638, 44.8368],
						[-92.7746, 44.9013],
						[-92.7494, 44.9373],
						[-92.771, 44.9726],
						[-92.7638, 45.0284],
						[-92.8033, 45.0576],
						[-92.7459, 45.1074],
						[-92.7674, 45.1856],
						[-92.753, 45.2131],
						[-92.7602, 45.2913],
						[-92.6992, 45.3334],
						[-92.7028, 45.3583],
						[-92.6489, 45.3986],
						[-92.6454, 45.4416],
						[-92.7279, 45.5146],
						[-92.7243, 45.5413],
						[-92.7746, 45.5687],
						[-92.8105, 45.561],
						[-92.8858, 45.5791],
						[-92.8894, 45.6246],
						[-92.8643, 45.7225],
						[-92.7853, 45.7646],
						[-92.7136, 45.8917],
						[-92.6418, 45.9321],
						[-92.552, 45.9519],
						[-92.5233, 45.9845],
						[-92.4731, 45.9734],
						[-92.4372, 46.0215],
						[-92.351, 46.0155],
						[-92.3295, 46.0661],
						[-92.2936, 46.0747],
						[-92.2936, 46.6632],
						[-92.2146, 46.6494],
						[-92.1752, 46.6864],
						[-92.2075, 46.7027],
						[-92.15, 46.7147],
						[-92.1393, 46.7396],
						[-92.089, 46.7491],
						[-92.0136, 46.7061]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "60",
			"properties": { "name": "American Samoa" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-169.5209, -14.221],
						[-169.4275, -14.2115],
						[-169.4239, -14.2553],
						[-169.4562, -14.2484],
						[-169.485, -14.2725],
						[-169.5209, -14.221]
					]],
					[[
						[-169.686, -14.1643],
						[-169.6106, -14.1574],
						[-169.6249, -14.1754],
						[-169.6716, -14.1857],
						[-169.686, -14.1643]
					]],
					[[
						[-170.8453, -14.3197],
						[-170.7986, -14.2897],
						[-170.7412, -14.288],
						[-170.673, -14.2295],
						[-170.6587, -14.2467],
						[-170.5617, -14.245],
						[-170.5474, -14.2828],
						[-170.5725, -14.2716],
						[-170.6766, -14.2974],
						[-170.7592, -14.3739],
						[-170.7986, -14.3335],
						[-170.8453, -14.3197]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "66",
			"properties": { "name": "Guam" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [[[
					[144.6213, 13.4519],
					[144.6393, 13.4648],
					[144.7613, 13.4786],
					[144.8367, 13.5963],
					[144.8582, 13.6521],
					[144.8834, 13.6426],
					[144.9085, 13.6057],
					[144.9587, 13.5963],
					[144.9228, 13.5207],
					[144.808, 13.427],
					[144.7757, 13.3892],
					[144.7685, 13.2939],
					[144.7254, 13.2458],
					[144.6608, 13.2715],
					[144.6357, 13.3583],
					[144.6644, 13.4013],
					[144.6213, 13.4519]
				]]]
			}
		},
		{
			"type": "Feature",
			"id": "69",
			"properties": { "name": "Commonwealth of the Northern Mariana Islands" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[146.0319, 16.0042],
						[146.0678, 16.0411],
						[146.0822, 16.0274],
						[146.0535, 15.9887],
						[146.0319, 16.0042]
					]],
					[[
						[145.8166, 17.6038],
						[145.8345, 17.621],
						[145.8381, 17.5789],
						[145.8166, 17.6038]
					]],
					[[
						[145.7089, 18.0685],
						[145.7591, 18.1141],
						[145.7591, 18.1647],
						[145.7986, 18.1647],
						[145.7986, 18.108],
						[145.7663, 18.0969],
						[145.7304, 18.0496],
						[145.7089, 18.0685]
					]],
					[[
						[145.6874, 15.1323],
						[145.7017, 15.1563],
						[145.7089, 15.2431],
						[145.7412, 15.2328],
						[145.813, 15.2903],
						[145.8309, 15.2714],
						[145.7807, 15.2027],
						[145.795, 15.1486],
						[145.7484, 15.1546],
						[145.7484, 15.0919],
						[145.7053, 15.1073],
						[145.6874, 15.1323]
					]],
					[[
						[145.6371, 18.779],
						[145.6586, 18.8125],
						[145.6981, 18.7884],
						[145.6945, 18.7446],
						[145.6658, 18.7248],
						[145.6407, 18.7463],
						[145.6371, 18.779]
					]],
					[[
						[145.6335, 16.3676],
						[145.7053, 16.3693],
						[145.7161, 16.3384],
						[145.6479, 16.3315],
						[145.6335, 16.3676]
					]],
					[[
						[145.5833, 15.0163],
						[145.5976, 15.0549],
						[145.6443, 15.1013],
						[145.6622, 15.0549],
						[145.6479, 15.0343],
						[145.673, 15],
						[145.6658, 14.9476],
						[145.6335, 14.9226],
						[145.5833, 15.0163]
					]],
					[[
						[145.1202, 14.1246],
						[145.1597, 14.1684],
						[145.2351, 14.1985],
						[145.2889, 14.1907],
						[145.2818, 14.1607],
						[145.2387, 14.1495],
						[145.21, 14.116],
						[145.1705, 14.1108],
						[145.1526, 14.1306],
						[145.1202, 14.1246]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "31",
			"properties": { "name": "Nebraska" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-104.0521, 43.001],
					[-103.0005, 43.0002],
					[-102.4872, 42.9993],
					[-101.8483, 42.9993],
					[-101.6258, 42.9959],
					[-100.8864, 42.9976],
					[-100.1183, 42.9984],
					[-99.5691, 42.9976],
					[-98.9015, 42.9984],
					[-98.4995, 42.9984],
					[-98.4672, 42.9478],
					[-98.259, 42.8747],
					[-98.1298, 42.8215],
					[-98.0185, 42.7622],
					[-97.9503, 42.7699],
					[-97.9073, 42.7948],
					[-97.875, 42.8584],
					[-97.8462, 42.8679],
					[-97.7745, 42.8498],
					[-97.6847, 42.8421],
					[-97.6201, 42.8567],
					[-97.5627, 42.8472],
					[-97.5053, 42.8584],
					[-97.4407, 42.8464],
					[-97.4155, 42.8661],
					[-97.3617, 42.855],
					[-97.3078, 42.867],
					[-97.2181, 42.8455],
					[-97.2145, 42.8129],
					[-97.1499, 42.7957],
					[-97.132, 42.7716],
					[-96.9776, 42.7605],
					[-96.9489, 42.7192],
					[-96.9058, 42.7338],
					[-96.8053, 42.7038],
					[-96.8018, 42.6694],
					[-96.7264, 42.6668],
					[-96.6869, 42.6531],
					[-96.712, 42.6084],
					[-96.6366, 42.5517],
					[-96.6115, 42.5062],
					[-96.5254, 42.5105],
					[-96.5074, 42.4847],
					[-96.4464, 42.4907],
					[-96.3962, 42.4839],
					[-96.3818, 42.4461],
					[-96.4141, 42.4083],
					[-96.4141, 42.343],
					[-96.3746, 42.3181],
					[-96.3244, 42.2296],
					[-96.3603, 42.2081],
					[-96.3495, 42.1669],
					[-96.2705, 42.1188],
					[-96.2741, 42.0475],
					[-96.2203, 42.026],
					[-96.2382, 42.0122],
					[-96.1844, 42.0028],
					[-96.188, 41.977],
					[-96.1306, 41.9719],
					[-96.1413, 41.9152],
					[-96.1629, 41.9057],
					[-96.1126, 41.8533],
					[-96.109, 41.8207],
					[-96.066, 41.8009],
					[-96.0767, 41.7614],
					[-96.1054, 41.7442],
					[-96.0839, 41.6978],
					[-96.1234, 41.6824],
					[-96.0947, 41.6471],
					[-96.1198, 41.6136],
					[-96.0803, 41.5801],
					[-96.0911, 41.532],
					[-96.0372, 41.508],
					[-96.0301, 41.5398],
					[-95.9978, 41.5389],
					[-96.0049, 41.4727],
					[-95.9331, 41.4642],
					[-95.9296, 41.3645],
					[-95.9547, 41.3396],
					[-95.8829, 41.3164],
					[-95.9296, 41.2812],
					[-95.9116, 41.2271],
					[-95.926, 41.1953],
					[-95.8398, 41.1747],
					[-95.8829, 41.16],
					[-95.865, 41.0802],
					[-95.8829, 41.057],
					[-95.8578, 41.0312],
					[-95.8614, 40.9951],
					[-95.8291, 40.9779],
					[-95.8362, 40.9212],
					[-95.8111, 40.898],
					[-95.847, 40.8542],
					[-95.8326, 40.7829],
					[-95.8793, 40.7529],
					[-95.8865, 40.7211],
					[-95.8326, 40.6712],
					[-95.786, 40.6575],
					[-95.7501, 40.6094],
					[-95.7645, 40.5853],
					[-95.768, 40.5312],
					[-95.6963, 40.5286],
					[-95.6927, 40.557],
					[-95.6532, 40.5415],
					[-95.6998, 40.5054],
					[-95.6963, 40.4711],
					[-95.6568, 40.4419],
					[-95.6604, 40.4084],
					[-95.6173, 40.3139],
					[-95.5527, 40.2915],
					[-95.5563, 40.264],
					[-95.4845, 40.2477],
					[-95.4773, 40.1816],
					[-95.3912, 40.1163],
					[-95.4199, 40.0484],
					[-95.3086, 40.0003],
					[-95.786, 40.0003],
					[-96.3028, 40.0003],
					[-97.2468, 40.0012],
					[-98.2482, 40.002],
					[-98.8333, 40.002],
					[-99.8132, 40.0012],
					[-100.7572, 40.002],
					[-101.1304, 40.002],
					[-102.0529, 40.0029],
					[-102.0529, 41.002],
					[-102.5554, 41.002],
					[-103.04, 41.002],
					[-104.0521, 41.0011],
					[-104.0521, 43.001]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "45",
			"properties": { "name": "South Carolina" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-83.1086, 35.0006],
					[-82.782, 35.0856],
					[-82.7568, 35.0676],
					[-82.6563, 35.1191],
					[-82.5523, 35.1595],
					[-82.5307, 35.1552],
					[-82.4589, 35.1784],
					[-82.4338, 35.1707],
					[-82.3907, 35.2154],
					[-82.3728, 35.1819],
					[-82.2902, 35.199],
					[-81.6155, 35.1741],
					[-81.2386, 35.1604],
					[-81.0412, 35.1492],
					[-81.034, 35.1037],
					[-81.0591, 35.0624],
					[-81.0412, 35.0444],
					[-80.9335, 35.1071],
					[-80.7827, 34.9362],
					[-80.7971, 34.8202],
					[-80.1582, 34.8116],
					[-79.8926, 34.8056],
					[-79.6736, 34.8047],
					[-79.3578, 34.5453],
					[-79.2465, 34.4456],
					[-78.8732, 34.1347],
					[-78.5394, 33.8512],
					[-78.5861, 33.8443],
					[-78.7153, 33.8005],
					[-78.8122, 33.7438],
					[-78.9378, 33.6398],
					[-79.0024, 33.572],
					[-79.0276, 33.5333],
					[-79.085, 33.4835],
					[-79.1353, 33.4036],
					[-79.1819, 33.2541],
					[-79.1711, 33.2069],
					[-79.1963, 33.1656],
					[-79.2465, 33.1253],
					[-79.3291, 33.09],
					[-79.3614, 33.0067],
					[-79.4224, 33.0153],
					[-79.4834, 33.0016],
					[-79.5229, 33.0351],
					[-79.5803, 33.0067],
					[-79.6162, 32.9526],
					[-79.6054, 32.926],
					[-79.5767, 32.9346],
					[-79.5767, 32.9062],
					[-79.6306, 32.889],
					[-79.6952, 32.8504],
					[-79.7275, 32.8057],
					[-79.8495, 32.755],
					[-79.8854, 32.7877],
					[-79.9249, 32.7816],
					[-79.9285, 32.7542],
					[-79.8675, 32.7353],
					[-79.8854, 32.6846],
					[-79.9751, 32.6399],
					[-80.0003, 32.6055],
					[-80.0362, 32.6098],
					[-80.1223, 32.5909],
					[-80.1833, 32.5402],
					[-80.2049, 32.5557],
					[-80.3341, 32.4784],
					[-80.3628, 32.4964],
					[-80.4166, 32.4724],
					[-80.4238, 32.4982],
					[-80.4705, 32.4973],
					[-80.4848, 32.4612],
					[-80.431, 32.3899],
					[-80.4561, 32.3263],
					[-80.571, 32.2731],
					[-80.6392, 32.2559],
					[-80.6463, 32.2911],
					[-80.7145, 32.3255],
					[-80.754, 32.3066],
					[-80.7612, 32.2799],
					[-80.7181, 32.2671],
					[-80.6679, 32.2164],
					[-80.7217, 32.1605],
					[-80.8115, 32.1099],
					[-80.8438, 32.1099],
					[-80.9048, 32.0523],
					[-80.8868, 32.0343],
					[-80.9191, 32.0377],
					[-80.9981, 32.0987],
					[-81.0376, 32.0841],
					[-81.113, 32.1133],
					[-81.1309, 32.1666],
					[-81.1165, 32.1889],
					[-81.156, 32.2464],
					[-81.1201, 32.2842],
					[-81.1417, 32.3495],
					[-81.156, 32.3461],
					[-81.2099, 32.4363],
					[-81.1955, 32.4655],
					[-81.2781, 32.5351],
					[-81.2781, 32.5548],
					[-81.3678, 32.5772],
					[-81.4109, 32.6184],
					[-81.3929, 32.6536],
					[-81.4073, 32.6949],
					[-81.4288, 32.7017],
					[-81.4109, 32.7404],
					[-81.4252, 32.7679],
					[-81.4252, 32.8409],
					[-81.4575, 32.8538],
					[-81.4647, 32.8959],
					[-81.5078, 32.9509],
					[-81.4898, 33.0084],
					[-81.558, 33.0454],
					[-81.6155, 33.0952],
					[-81.6478, 33.0943],
					[-81.7447, 33.1416],
					[-81.7734, 33.1802],
					[-81.7662, 33.2155],
					[-81.8057, 33.2112],
					[-81.8523, 33.2473],
					[-81.838, 33.273],
					[-81.9385, 33.3435],
					[-81.9241, 33.371],
					[-81.9385, 33.4045],
					[-81.9134, 33.4414],
					[-81.9277, 33.462],
					[-81.9816, 33.4844],
					[-82.0462, 33.5642],
					[-82.1072, 33.596],
					[-82.1503, 33.5977],
					[-82.1969, 33.6304],
					[-82.2005, 33.6648],
					[-82.2328, 33.7],
					[-82.2472, 33.7524],
					[-82.3513, 33.8366],
					[-82.4553, 33.8812],
					[-82.5235, 33.9431],
					[-82.5558, 33.9439],
					[-82.5953, 34.0307],
					[-82.6456, 34.0719],
					[-82.6743, 34.1295],
					[-82.7174, 34.1501],
					[-82.7425, 34.2085],
					[-82.7425, 34.2524],
					[-82.782, 34.297],
					[-82.7927, 34.34],
					[-82.8358, 34.3657],
					[-82.8466, 34.4207],
					[-82.8753, 34.4757],
					[-82.904, 34.486],
					[-83.0045, 34.4723],
					[-83.0871, 34.5161],
					[-83.1696, 34.6054],
					[-83.2306, 34.6114],
					[-83.3383, 34.6819],
					[-83.3491, 34.7369],
					[-83.3204, 34.7592],
					[-83.324, 34.7901],
					[-83.2306, 34.8803],
					[-83.1194, 34.9387],
					[-83.1086, 35.0006]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "72",
			"properties": { "name": "Puerto Rico" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-65.338, 18.3486],
						[-65.2985, 18.3305],
						[-65.2555, 18.3417],
						[-65.2231, 18.3108],
						[-65.2842, 18.2799],
						[-65.3272, 18.2962],
						[-65.338, 18.3486]
					]],
					[[
						[-65.5749, 18.1158],
						[-65.399, 18.1622],
						[-65.2734, 18.1347],
						[-65.338, 18.1123],
						[-65.4134, 18.1063],
						[-65.4529, 18.0857],
						[-65.4816, 18.096],
						[-65.5426, 18.0814],
						[-65.5749, 18.1158]
					]],
					[[
						[-65.5928, 18.3924],
						[-65.5677, 18.3804],
						[-65.5677, 18.3426],
						[-65.5928, 18.3924]
					]],
					[[
						[-67.269, 18.3666],
						[-67.2152, 18.3821],
						[-67.1542, 18.4242],
						[-67.1685, 18.4783],
						[-67.1434, 18.5058],
						[-67.0932, 18.5161],
						[-67.0214, 18.5109],
						[-66.9604, 18.4895],
						[-66.7809, 18.4912],
						[-66.7091, 18.4723],
						[-66.623, 18.4946],
						[-66.5584, 18.4903],
						[-66.4686, 18.4688],
						[-66.4399, 18.486],
						[-66.3394, 18.486],
						[-66.2425, 18.4688],
						[-66.1851, 18.4697],
						[-66.16, 18.4534],
						[-66.1384, 18.474],
						[-66.0056, 18.4439],
						[-65.9913, 18.4603],
						[-65.9267, 18.4448],
						[-65.9051, 18.4508],
						[-65.7938, 18.4225],
						[-65.7436, 18.3804],
						[-65.7185, 18.3915],
						[-65.7005, 18.3683],
						[-65.6646, 18.3632],
						[-65.6144, 18.3821],
						[-65.6323, 18.3477],
						[-65.6216, 18.2756],
						[-65.5749, 18.2498],
						[-65.6, 18.2128],
						[-65.6395, 18.2292],
						[-65.6287, 18.2051],
						[-65.6646, 18.2068],
						[-65.6898, 18.1793],
						[-65.7364, 18.1802],
						[-65.7759, 18.1295],
						[-65.801, 18.0582],
						[-65.8297, 18.0531],
						[-65.8333, 18.0153],
						[-65.9231, 17.9757],
						[-65.9769, 17.968],
						[-66.02, 17.9783],
						[-66.0451, 17.9551],
						[-66.081, 17.9663],
						[-66.1564, 17.9294],
						[-66.1923, 17.9371],
						[-66.2246, 17.9122],
						[-66.2533, 17.9199],
						[-66.3179, 17.9766],
						[-66.361, 17.968],
						[-66.3861, 17.9388],
						[-66.4615, 17.9904],
						[-66.4902, 17.9904],
						[-66.5799, 17.962],
						[-66.6302, 17.9826],
						[-66.6732, 17.9663],
						[-66.763, 18.0067],
						[-66.7881, 17.9732],
						[-66.8383, 17.95],
						[-66.9137, 17.9474],
						[-66.9568, 17.9319],
						[-66.9855, 17.9672],
						[-67.0609, 17.974],
						[-67.1003, 17.9465],
						[-67.1685, 17.9629],
						[-67.2116, 17.9526],
						[-67.208, 17.9981],
						[-67.1721, 18.0247],
						[-67.2116, 18.035],
						[-67.2008, 18.0909],
						[-67.1829, 18.1037],
						[-67.1793, 18.1682],
						[-67.1542, 18.1948],
						[-67.1901, 18.2816],
						[-67.2367, 18.2996],
						[-67.269, 18.3666]
					]],
					[[
						[-67.9331, 18.1115],
						[-67.8613, 18.1218],
						[-67.8433, 18.0848],
						[-67.8972, 18.0522],
						[-67.9402, 18.0797],
						[-67.9331, 18.1115]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "78",
			"properties": { "name": "United States Virgin Islands" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-64.7996, 18.3374],
						[-64.753, 18.3709],
						[-64.7135, 18.3675],
						[-64.6596, 18.3434],
						[-64.7063, 18.34],
						[-64.7027, 18.2979],
						[-64.735, 18.3151],
						[-64.7817, 18.3108],
						[-64.7996, 18.3374]
					]],
					[[
						[-64.9073, 17.6811],
						[-64.8822, 17.712],
						[-64.8965, 17.7464],
						[-64.8714, 17.7721],
						[-64.8355, 17.761],
						[-64.8068, 17.7799],
						[-64.7458, 17.7825],
						[-64.7242, 17.7575],
						[-64.5843, 17.7618],
						[-64.5771, 17.7464],
						[-64.6919, 17.7051],
						[-64.8391, 17.6794],
						[-64.9073, 17.6811]
					]],
					[[
						[-65.0832, 18.3391],
						[-65.0616, 18.3821],
						[-65.0257, 18.3623],
						[-64.9827, 18.3847],
						[-64.9145, 18.3666],
						[-64.796, 18.3718],
						[-64.8032, 18.352],
						[-64.8535, 18.3486],
						[-64.8211, 18.3125],
						[-64.8786, 18.303],
						[-64.9324, 18.3228],
						[-64.9575, 18.3056],
						[-65.0006, 18.3477],
						[-65.0832, 18.3391]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "16",
			"properties": { "name": "Idaho" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-116.9162, 45.9957],
					[-116.9556, 46.0756],
					[-116.9808, 46.085],
					[-116.9198, 46.1649],
					[-116.9628, 46.1993],
					[-116.9628, 46.2534],
					[-116.9915, 46.2998],
					[-117.0633, 46.3651],
					[-117.0382, 46.4063],
					[-117.0382, 47.4776],
					[-117.0418, 47.5222],
					[-117.0418, 48.1253],
					[-117.0346, 48.371],
					[-117.0346, 48.7498],
					[-117.031, 48.999],
					[-116.4172, 48.9998],
					[-116.0476, 49.0007],
					[-116.0476, 48.31],
					[-116.0511, 48.2164],
					[-116.0476, 47.9767],
					[-115.9076, 47.8461],
					[-115.8537, 47.8281],
					[-115.825, 47.7525],
					[-115.7963, 47.7576],
					[-115.7712, 47.7173],
					[-115.7245, 47.6966],
					[-115.7317, 47.6425],
					[-115.6886, 47.5953],
					[-115.7353, 47.5678],
					[-115.7425, 47.5386],
					[-115.703, 47.5343],
					[-115.685, 47.4853],
					[-115.6276, 47.4793],
					[-115.6384, 47.4604],
					[-115.7173, 47.4535],
					[-115.7604, 47.4226],
					[-115.7209, 47.4243],
					[-115.5522, 47.3496],
					[-115.5271, 47.3032],
					[-115.4122, 47.2645],
					[-115.3261, 47.2559],
					[-115.2938, 47.2207],
					[-115.301, 47.1881],
					[-115.2615, 47.1821],
					[-115.2435, 47.1503],
					[-115.2005, 47.1391],
					[-115.1395, 47.0927],
					[-115.0713, 47.0223],
					[-115.0497, 46.9707],
					[-115.0031, 46.9716],
					[-114.9313, 46.92],
					[-114.9456, 46.8591],
					[-114.8882, 46.8084],
					[-114.7662, 46.7585],
					[-114.7877, 46.7113],
					[-114.7662, 46.6967],
					[-114.7123, 46.7147],
					[-114.698, 46.7405],
					[-114.6477, 46.7328],
					[-114.6226, 46.707],
					[-114.6441, 46.6735],
					[-114.6154, 46.64],
					[-114.5472, 46.6443],
					[-114.4683, 46.6314],
					[-114.4539, 46.6494],
					[-114.3606, 46.6692],
					[-114.3319, 46.6606],
					[-114.3211, 46.6108],
					[-114.357, 46.5051],
					[-114.4001, 46.5025],
					[-114.3749, 46.4433],
					[-114.4216, 46.3874],
					[-114.4252, 46.2878],
					[-114.4719, 46.2672],
					[-114.4503, 46.2414],
					[-114.4467, 46.1735],
					[-114.5149, 46.1675],
					[-114.5221, 46.1254],
					[-114.4611, 46.0971],
					[-114.4934, 46.0472],
					[-114.479, 46.0009],
					[-114.4108, 45.9777],
					[-114.4324, 45.9356],
					[-114.3857, 45.8892],
					[-114.4108, 45.8514],
					[-114.5006, 45.8505],
					[-114.5652, 45.7741],
					[-114.497, 45.7105],
					[-114.5006, 45.6693],
					[-114.5652, 45.6375],
					[-114.5365, 45.6065],
					[-114.5508, 45.561],
					[-114.4611, 45.561],
					[-114.4144, 45.5095],
					[-114.3642, 45.4906],
					[-114.3462, 45.4596],
					[-114.2601, 45.4957],
					[-114.2493, 45.5455],
					[-114.2027, 45.5352],
					[-114.1345, 45.5576],
					[-114.0663, 45.628],
					[-114.0124, 45.6581],
					[-114.0196, 45.6933],
					[-113.9873, 45.7045],
					[-113.9335, 45.6821],
					[-113.8976, 45.6443],
					[-113.9047, 45.622],
					[-113.8617, 45.6237],
					[-113.8078, 45.6022],
					[-113.833, 45.5206],
					[-113.7648, 45.5206],
					[-113.7612, 45.4811],
					[-113.7827, 45.4553],
					[-113.7612, 45.4064],
					[-113.7325, 45.3901],
					[-113.7396, 45.3299],
					[-113.6894, 45.2835],
					[-113.6858, 45.2535],
					[-113.5997, 45.1908],
					[-113.5745, 45.128],
					[-113.5135, 45.1152],
					[-113.5207, 45.0928],
					[-113.4525, 45.0593],
					[-113.4381, 45.0069],
					[-113.4453, 44.9597],
					[-113.4956, 44.9485],
					[-113.4561, 44.8652],
					[-113.3556, 44.8196],
					[-113.3412, 44.7853],
					[-113.2479, 44.8231],
					[-113.133, 44.7733],
					[-113.0684, 44.6796],
					[-113.0505, 44.6367],
					[-113.0828, 44.5954],
					[-113.0074, 44.5258],
					[-113.0254, 44.4966],
					[-113.0038, 44.4511],
					[-112.95, 44.4167],
					[-112.8854, 44.3961],
					[-112.8459, 44.3583],
					[-112.8136, 44.3772],
					[-112.8351, 44.4228],
					[-112.7813, 44.4846],
					[-112.7203, 44.5044],
					[-112.6593, 44.4855],
					[-112.5408, 44.4838],
					[-112.5013, 44.4631],
					[-112.4726, 44.4803],
					[-112.3865, 44.4477],
					[-112.3578, 44.4863],
					[-112.3506, 44.5387],
					[-112.3183, 44.5387],
					[-112.286, 44.5688],
					[-112.2285, 44.5628],
					[-112.1855, 44.5327],
					[-112.1352, 44.5396],
					[-112.1065, 44.5207],
					[-112.0311, 44.5465],
					[-111.9952, 44.5353],
					[-111.9486, 44.5568],
					[-111.8696, 44.5636],
					[-111.823, 44.5095],
					[-111.7153, 44.5439],
					[-111.7045, 44.5602],
					[-111.6184, 44.549],
					[-111.5861, 44.5628],
					[-111.4712, 44.5404],
					[-111.525, 44.5954],
					[-111.4676, 44.6796],
					[-111.4856, 44.708],
					[-111.4138, 44.7105],
					[-111.3851, 44.7552],
					[-111.324, 44.7243],
					[-111.2235, 44.6238],
					[-111.2307, 44.5868],
					[-111.1446, 44.5362],
					[-111.123, 44.4941],
					[-111.0477, 44.4743],
					[-111.0513, 44.354],
					[-111.0477, 44.1247],
					[-111.0477, 43.6814],
					[-111.0441, 43.6591],
					[-111.0441, 42.7227],
					[-111.0477, 42.5827],
					[-111.0477, 42.0019],
					[-111.4712, 41.9994],
					[-112.1747, 41.9968],
					[-112.1926, 42.0011],
					[-112.8818, 41.9994],
					[-113.8222, 41.9882],
					[-114.0411, 41.9933],
					[-114.4683, 41.9959],
					[-114.5975, 41.9942],
					[-114.8057, 42.0019],
					[-115.0318, 41.9959],
					[-115.3153, 41.9959],
					[-116.367, 41.9959],
					[-117.0274, 42.0002],
					[-117.0274, 43.8085],
					[-117.0203, 43.8592],
					[-116.9808, 43.8687],
					[-116.9808, 43.9151],
					[-116.9377, 43.9846],
					[-116.9341, 44.0216],
					[-116.9736, 44.0491],
					[-116.9772, 44.0851],
					[-116.9341, 44.0998],
					[-116.8982, 44.153],
					[-116.9018, 44.1796],
					[-116.9664, 44.1942],
					[-116.9736, 44.2398],
					[-117.0454, 44.2295],
					[-117.1028, 44.2802],
					[-117.171, 44.2587],
					[-117.2177, 44.2887],
					[-117.1889, 44.3369],
					[-117.2428, 44.3961],
					[-117.2141, 44.4271],
					[-117.2248, 44.4838],
					[-117.1602, 44.525],
					[-117.1243, 44.5817],
					[-117.0956, 44.665],
					[-117.0454, 44.7449],
					[-116.9305, 44.787],
					[-116.9305, 44.805],
					[-116.8659, 44.8703],
					[-116.8336, 44.9287],
					[-116.8587, 44.9786],
					[-116.848, 45.0224],
					[-116.7977, 45.0602],
					[-116.7762, 45.1057],
					[-116.7295, 45.1401],
					[-116.7044, 45.2397],
					[-116.6721, 45.2835],
					[-116.6757, 45.3145],
					[-116.5895, 45.4433],
					[-116.5536, 45.4631],
					[-116.5501, 45.5103],
					[-116.4639, 45.6031],
					[-116.489, 45.6495],
					[-116.5357, 45.6916],
					[-116.5357, 45.7371],
					[-116.5931, 45.7784],
					[-116.6613, 45.7801],
					[-116.6972, 45.8204],
					[-116.7618, 45.8162],
					[-116.8192, 45.8806],
					[-116.8587, 45.9038],
					[-116.9162, 45.9957]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "32",
			"properties": { "name": "Nevada" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-119.9994, 41.9942],
					[-119.9994, 41.9951],
					[-119.2097, 41.9933],
					[-118.6965, 41.9916],
					[-118.6031, 41.9942],
					[-117.6233, 41.9985],
					[-117.0274, 42.0002],
					[-116.367, 41.9959],
					[-115.3153, 41.9959],
					[-115.0318, 41.9959],
					[-114.8057, 42.0019],
					[-114.5975, 41.9942],
					[-114.4683, 41.9959],
					[-114.0411, 41.9933],
					[-114.0411, 41.0003],
					[-114.0447, 40.7718],
					[-114.0447, 40.2323],
					[-114.0483, 40.1043],
					[-114.0483, 38.6774],
					[-114.0519, 38.4042],
					[-114.0483, 38.1482],
					[-114.0519, 37.9996],
					[-114.0483, 37.7659],
					[-114.0519, 37.7565],
					[-114.0519, 37.0005],
					[-114.0519, 36.6251],
					[-114.0447, 36.3914],
					[-114.0483, 36.1938],
					[-114.0663, 36.1809],
					[-114.1524, 36.0237],
					[-114.2385, 36.0143],
					[-114.3139, 36.0581],
					[-114.3067, 36.0821],
					[-114.3714, 36.1431],
					[-114.418, 36.1457],
					[-114.5042, 36.1294],
					[-114.5113, 36.1509],
					[-114.5724, 36.1517],
					[-114.6154, 36.1303],
					[-114.6334, 36.1423],
					[-114.6657, 36.1174],
					[-114.7554, 36.0907],
					[-114.7303, 36.022],
					[-114.7446, 35.9851],
					[-114.7016, 35.9017],
					[-114.6657, 35.8751],
					[-114.698, 35.8545],
					[-114.7123, 35.8064],
					[-114.6944, 35.7557],
					[-114.7052, 35.7119],
					[-114.68, 35.6853],
					[-114.6908, 35.6518],
					[-114.6585, 35.6191],
					[-114.6585, 35.5306],
					[-114.68, 35.4997],
					[-114.6657, 35.4499],
					[-114.6262, 35.4095],
					[-114.6047, 35.3537],
					[-114.5724, 35.2008],
					[-114.5724, 35.1389],
					[-114.6477, 35.102],
					[-114.6118, 35.0831],
					[-114.6334, 35.0015],
					[-115.161, 35.4241],
					[-115.4122, 35.6251],
					[-116.2521, 36.2772],
					[-117.3756, 37.1268],
					[-117.8314, 37.4652],
					[-118.054, 37.625],
					[-118.5026, 37.9489],
					[-118.858, 38.2049],
					[-119.0841, 38.3612],
					[-119.4933, 38.6499],
					[-119.906, 38.9334],
					[-119.9994, 38.9995],
					[-120.0065, 39.2254],
					[-120.003, 39.3303],
					[-120.003, 39.7228],
					[-119.9994, 39.734],
					[-119.9958, 40.0716],
					[-119.9958, 40.7211],
					[-119.9994, 40.7503],
					[-119.9994, 41.9942]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "50",
			"properties": { "name": "Vermont" },
			"geometry": {
				"type": "Polygon",
				"coordinates": [[
					[-73.3421, 45.0112],
					[-73.0873, 45.0155],
					[-72.6745, 45.0155],
					[-72.531, 45.0078],
					[-72.3084, 45.0035],
					[-72.1038, 45.0052],
					[-71.5008, 45.0138],
					[-71.5403, 44.988],
					[-71.4936, 44.9116],
					[-71.5583, 44.8471],
					[-71.5726, 44.7922],
					[-71.6264, 44.7475],
					[-71.587, 44.6599],
					[-71.5511, 44.6272],
					[-71.5583, 44.5645],
					[-71.5977, 44.5533],
					[-71.5762, 44.5027],
					[-71.6336, 44.4838],
					[-71.6588, 44.4451],
					[-71.709, 44.4116],
					[-71.7951, 44.3996],
					[-71.8131, 44.3549],
					[-71.8705, 44.3369],
					[-71.9064, 44.348],
					[-72.032, 44.3205],
					[-72.0679, 44.2681],
					[-72.0464, 44.2389],
					[-72.0679, 44.19],
					[-72.032, 44.1315],
					[-72.032, 44.0912],
					[-72.0751, 44.0319],
					[-72.1182, 43.9941],
					[-72.1182, 43.9211],
					[-72.1864, 43.8635],
					[-72.1828, 43.8085],
					[-72.2043, 43.7707],
					[-72.2725, 43.7338],
					[-72.3048, 43.696],
					[-72.3264, 43.6024],
					[-72.3802, 43.574],
					[-72.3981, 43.5104],
					[-72.3802, 43.4804],
					[-72.4161, 43.3644],
					[-72.3946, 43.3129],
					[-72.4376, 43.2579],
					[-72.452, 43.1393],
					[-72.434, 43.0835],
					[-72.4663, 43.0551],
					[-72.4448, 43.0105],
					[-72.4735, 42.9718],
					[-72.5202, 42.9632],
					[-72.5238, 42.9142],
					[-72.5561, 42.8515],
					[-72.513, 42.7639],
					[-72.4915, 42.7725],
					[-72.4592, 42.727],
					[-73.2632, 42.7459],
					[-73.2919, 42.8017],
					[-73.2775, 42.8335],
					[-73.2775, 42.8971],
					[-73.256, 43.2596],
					[-73.2488, 43.5542],
					[-73.2955, 43.5792],
					[-73.3026, 43.6247],
					[-73.3708, 43.6247],
					[-73.396, 43.568],
					[-73.4319, 43.5903],
					[-73.4067, 43.6883],
					[-73.3493, 43.7716],
					[-73.3924, 43.8206],
					[-73.3744, 43.8755],
					[-73.4103, 43.9331],
					[-73.4067, 44.0216],
					[-73.439, 44.0448],
					[-73.4103, 44.1126],
					[-73.3924, 44.1891],
					[-73.3242, 44.2432],
					[-73.3098, 44.2741],
					[-73.335, 44.3721],
					[-73.2919, 44.4408],
					[-73.3062, 44.5001],
					[-73.3744, 44.5757],
					[-73.3888, 44.6358],
					[-73.3744, 44.6624],
					[-73.3673, 44.7415],
					[-73.3314, 44.7887],
					[-73.378, 44.8377],
					[-73.3637, 44.8918],
					[-73.3385, 44.9176],
					[-73.3421, 45.0112]
				]]
			}
		},
		{
			"type": "Feature",
			"id": "22",
			"properties": { "name": "Louisiana" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-88.8802, 30.0533],
						[-88.8335, 29.9991],
						[-88.8156, 29.9339],
						[-88.8263, 29.8471],
						[-88.8622, 29.7569],
						[-88.8766, 29.7595],
						[-88.8479, 29.8368],
						[-88.8335, 29.9227],
						[-88.8371, 29.9622],
						[-88.8802, 30.0533]
					]],
					[[
						[-89.3432, 30.0593],
						[-89.3037, 30.0919],
						[-89.1889, 30.1615],
						[-89.1817, 30.1495],
						[-89.2283, 30.0954],
						[-89.2212, 30.0713],
						[-89.1745, 30.043],
						[-89.2283, 30],
						[-89.2391, 30.0404],
						[-89.275, 30.0318],
						[-89.336, 30.0404],
						[-89.3432, 30.0593]
					]],
					[[
						[-89.6268, 29.5353],
						[-89.5657, 29.5438],
						[-89.598, 29.5078],
						[-89.6268, 29.5353]
					]],
					[[
						[-90.3769, 29.1083],
						[-90.3518, 29.1315],
						[-90.3051, 29.0757],
						[-90.3338, 29.0636],
						[-90.3769, 29.1083]
					]],
					[[
						[-90.56, 29.0937],
						[-90.42, 29.0653],
						[-90.4882, 29.0585],
						[-90.56, 29.0937]
					]],
					[[
						[-90.7502, 29.0559],
						[-90.6605, 29.0722],
						[-90.6533, 29.0576],
						[-90.7466, 29.0404],
						[-90.7502, 29.0559]
					]],
					[[
						[-92.0172, 29.5911],
						[-91.9813, 29.6143],
						[-91.9383, 29.61],
						[-91.9275, 29.6323],
						[-91.8485, 29.6272],
						[-91.766, 29.5748],
						[-91.7337, 29.5808],
						[-91.7121, 29.5559],
						[-91.766, 29.5249],
						[-91.7696, 29.4906],
						[-91.8198, 29.4743],
						[-92.0316, 29.5731],
						[-92.0172, 29.5911]
					]],
					[[
						[-94.0416, 33.0196],
						[-93.3776, 33.0179],
						[-93.0689, 33.0179],
						[-92.5054, 33.0119],
						[-91.9526, 33.0076],
						[-91.1666, 33.0041],
						[-91.2025, 32.9612],
						[-91.2132, 32.9199],
						[-91.1809, 32.901],
						[-91.1307, 32.9234],
						[-91.1343, 32.9809],
						[-91.0948, 32.9844],
						[-91.0625, 32.9225],
						[-91.0697, 32.889],
						[-91.145, 32.8444],
						[-91.163, 32.8126],
						[-91.1666, 32.7516],
						[-91.0553, 32.7258],
						[-91.0625, 32.7026],
						[-91.1199, 32.6743],
						[-91.1522, 32.6416],
						[-91.1414, 32.5969],
						[-91.1199, 32.5849],
						[-91.0481, 32.6073],
						[-91.0266, 32.6468],
						[-91.0015, 32.615],
						[-91.0302, 32.5832],
						[-91.0804, 32.5566],
						[-91.005, 32.5136],
						[-90.9871, 32.4913],
						[-91.023, 32.4853],
						[-91.084, 32.5265],
						[-91.102, 32.5256],
						[-91.1127, 32.4758],
						[-91.0302, 32.4337],
						[-90.9943, 32.4509],
						[-90.9656, 32.4208],
						[-90.9943, 32.4037],
						[-90.9943, 32.3538],
						[-90.9117, 32.3392],
						[-90.8902, 32.3736],
						[-90.8758, 32.3581],
						[-90.9333, 32.2903],
						[-90.9763, 32.2971],
						[-90.9692, 32.2516],
						[-90.9871, 32.2155],
						[-91.0374, 32.2422],
						[-91.0625, 32.219],
						[-91.1594, 32.2018],
						[-91.1666, 32.1339],
						[-91.0517, 32.1245],
						[-91.0589, 32.1777],
						[-91.005, 32.1571],
						[-91.0374, 32.0978],
						[-91.0804, 32.0789],
						[-91.0984, 32.048],
						[-91.145, 32.0815],
						[-91.163, 32.06],
						[-91.0948, 32.0377],
						[-91.0948, 31.9947],
						[-91.163, 31.9827],
						[-91.1881, 31.9612],
						[-91.1809, 31.9183],
						[-91.2671, 31.8513],
						[-91.2455, 31.8332],
						[-91.2635, 31.8092],
						[-91.2886, 31.8238],
						[-91.2958, 31.8607],
						[-91.3389, 31.8513],
						[-91.364, 31.7705],
						[-91.2599, 31.7611],
						[-91.2742, 31.7456],
						[-91.3568, 31.7413],
						[-91.3963, 31.7113],
						[-91.3999, 31.6202],
						[-91.4645, 31.6202],
						[-91.5004, 31.6442],
						[-91.5183, 31.6116],
						[-91.486, 31.5858],
						[-91.4214, 31.597],
						[-91.407, 31.5695],
						[-91.4501, 31.5394],
						[-91.5219, 31.524],
						[-91.5183, 31.4604],
						[-91.4717, 31.396],
						[-91.4717, 31.3711],
						[-91.504, 31.3651],
						[-91.5327, 31.39],
						[-91.547, 31.4329],
						[-91.5793, 31.3994],
						[-91.5542, 31.3857],
						[-91.5506, 31.347],
						[-91.5075, 31.3152],
						[-91.5147, 31.2783],
						[-91.5757, 31.2611],
						[-91.6404, 31.2671],
						[-91.6439, 31.2345],
						[-91.6009, 31.2087],
						[-91.5901, 31.1735],
						[-91.626, 31.134],
						[-91.6224, 31.1099],
						[-91.565, 31.067],
						[-91.5901, 31.0171],
						[-91.6368, 30.9991],
						[-90.8256, 30.9991],
						[-90.3446, 31],
						[-89.7524, 31.0017],
						[-89.7273, 30.9707],
						[-89.7524, 30.9518],
						[-89.7488, 30.9132],
						[-89.7739, 30.8969],
						[-89.8062, 30.7895],
						[-89.8313, 30.768],
						[-89.817, 30.7371],
						[-89.8529, 30.6615],
						[-89.8242, 30.6495],
						[-89.8098, 30.5678],
						[-89.7739, 30.5386],
						[-89.7596, 30.5051],
						[-89.7201, 30.4888],
						[-89.6842, 30.4519],
						[-89.6842, 30.4055],
						[-89.6303, 30.3393],
						[-89.6303, 30.2801],
						[-89.616, 30.2234],
						[-89.5693, 30.1804],
						[-89.5263, 30.1804],
						[-89.5873, 30.1503],
						[-89.616, 30.1564],
						[-89.677, 30.1082],
						[-89.6842, 30.0756],
						[-89.7273, 30.0627],
						[-89.7165, 30.0284],
						[-89.817, 30.043],
						[-89.8565, 30.0043],
						[-89.8385, 29.9459],
						[-89.7739, 29.9373],
						[-89.7273, 29.9588],
						[-89.7416, 29.9081],
						[-89.7129, 29.8789],
						[-89.6591, 29.8626],
						[-89.6124, 29.872],
						[-89.5909, 29.8969],
						[-89.5729, 29.9837],
						[-89.5801, 29.9949],
						[-89.494, 30.0412],
						[-89.4832, 30.079],
						[-89.4186, 30.0498],
						[-89.3719, 30.055],
						[-89.3719, 30.0369],
						[-89.4329, 30.0077],
						[-89.4329, 29.9785],
						[-89.3791, 29.9639],
						[-89.3683, 29.9115],
						[-89.3145, 29.9235],
						[-89.275, 29.994],
						[-89.2176, 29.9725],
						[-89.2319, 29.9253],
						[-89.2786, 29.9253],
						[-89.3217, 29.8875],
						[-89.2427, 29.8892],
						[-89.2535, 29.8643],
						[-89.3647, 29.8454],
						[-89.3181, 29.7887],
						[-89.2786, 29.8076],
						[-89.3037, 29.7569],
						[-89.336, 29.7792],
						[-89.3863, 29.7887],
						[-89.4293, 29.7414],
						[-89.4329, 29.6564],
						[-89.4652, 29.6521],
						[-89.4868, 29.726],
						[-89.5263, 29.7268],
						[-89.5334, 29.6701],
						[-89.4868, 29.6203],
						[-89.5334, 29.6486],
						[-89.5837, 29.6529],
						[-89.6124, 29.6383],
						[-89.6662, 29.6435],
						[-89.6016, 29.61],
						[-89.6016, 29.5825],
						[-89.6842, 29.6246],
						[-89.6842, 29.6031],
						[-89.6411, 29.5756],
						[-89.6842, 29.5636],
						[-89.6806, 29.5344],
						[-89.6411, 29.5035],
						[-89.5693, 29.494],
						[-89.5729, 29.4751],
						[-89.5298, 29.4545],
						[-89.5334, 29.4253],
						[-89.5083, 29.3858],
						[-89.476, 29.4115],
						[-89.4581, 29.3935],
						[-89.3791, 29.3918],
						[-89.3217, 29.3626],
						[-89.3109, 29.3884],
						[-89.2391, 29.3102],
						[-89.1996, 29.348],
						[-89.1135, 29.2526],
						[-89.1135, 29.2019],
						[-89.092, 29.1908],
						[-89.0273, 29.2148],
						[-89.0022, 29.1796],
						[-89.0884, 29.1667],
						[-89.0955, 29.1384],
						[-89.0381, 29.1358],
						[-89.0668, 29.0911],
						[-89.1063, 29.1117],
						[-89.1494, 29.0293],
						[-89.1422, 28.9915],
						[-89.2176, 29.0224],
						[-89.2248, 29.0696],
						[-89.2535, 29.0834],
						[-89.3647, 28.9648],
						[-89.4186, 28.9296],
						[-89.3935, 28.9872],
						[-89.3612, 29.0121],
						[-89.3432, 29.0533],
						[-89.3109, 29.0714],
						[-89.3109, 29.1178],
						[-89.2786, 29.1375],
						[-89.2822, 29.1813],
						[-89.3181, 29.2019],
						[-89.3324, 29.1487],
						[-89.3899, 29.1238],
						[-89.4329, 29.1487],
						[-89.4832, 29.2148],
						[-89.6052, 29.2518],
						[-89.6411, 29.2904],
						[-89.8421, 29.3188],
						[-89.8852, 29.3067],
						[-90.0575, 29.1839],
						[-90.2226, 29.0851],
						[-90.2477, 29.0937],
						[-90.2333, 29.1289],
						[-90.2764, 29.1427],
						[-90.2836, 29.1779],
						[-90.2621, 29.1805],
						[-90.3051, 29.2664],
						[-90.3446, 29.2896],
						[-90.3769, 29.2887],
						[-90.3733, 29.2646],
						[-90.402, 29.2337],
						[-90.3949, 29.299],
						[-90.4308, 29.3471],
						[-90.4774, 29.2921],
						[-90.5277, 29.3042],
						[-90.5528, 29.2853],
						[-90.5779, 29.3119],
						[-90.5995, 29.2887],
						[-90.5636, 29.2346],
						[-90.6066, 29.238],
						[-90.6318, 29.2148],
						[-90.6461, 29.1684],
						[-90.6748, 29.1744],
						[-90.6928, 29.1255],
						[-90.7215, 29.1358],
						[-90.7897, 29.1074],
						[-90.8112, 29.0422],
						[-90.8687, 29.0559],
						[-90.8507, 29.0739],
						[-90.8974, 29.1315],
						[-90.962, 29.1805],
						[-91.0015, 29.1693],
						[-91.0948, 29.1873],
						[-91.1307, 29.2157],
						[-91.2204, 29.226],
						[-91.2778, 29.2475],
						[-91.3353, 29.299],
						[-91.2922, 29.311],
						[-91.2671, 29.3609],
						[-91.2384, 29.3506],
						[-91.1953, 29.3059],
						[-91.1702, 29.3248],
						[-91.1737, 29.2664],
						[-91.1199, 29.2518],
						[-91.1343, 29.3411],
						[-91.2168, 29.3961],
						[-91.2168, 29.4322],
						[-91.2563, 29.4382],
						[-91.2886, 29.403],
						[-91.3353, 29.3918],
						[-91.3281, 29.427],
						[-91.364, 29.421],
						[-91.3532, 29.4545],
						[-91.3209, 29.4786],
						[-91.3568, 29.5129],
						[-91.4609, 29.47],
						[-91.4968, 29.5387],
						[-91.5327, 29.5318],
						[-91.5542, 29.6332],
						[-91.6475, 29.634],
						[-91.626, 29.6624],
						[-91.6224, 29.7354],
						[-91.7373, 29.7492],
						[-91.7839, 29.7406],
						[-91.8557, 29.7079],
						[-91.8808, 29.7569],
						[-91.8521, 29.7921],
						[-91.8234, 29.7895],
						[-91.8306, 29.8291],
						[-91.888, 29.8359],
						[-91.9383, 29.817],
						[-91.9706, 29.8342],
						[-91.9777, 29.7998],
						[-92.0639, 29.7689],
						[-92.1141, 29.7397],
						[-92.1357, 29.7672],
						[-92.2003, 29.7535],
						[-92.2003, 29.726],
						[-92.168, 29.7002],
						[-92.1034, 29.6933],
						[-92.1357, 29.6675],
						[-92.107, 29.6134],
						[-92.0352, 29.6315],
						[-92.0172, 29.616],
						[-92.0639, 29.604],
						[-92.0424, 29.5868],
						[-92.1572, 29.5816],
						[-92.2505, 29.5395],
						[-92.3223, 29.5318],
						[-92.6166, 29.5859],
						[-92.7423, 29.622],
						[-92.9469, 29.7088],
						[-93.0294, 29.7363],
						[-93.1766, 29.7706],
						[-93.295, 29.7749],
						[-93.3453, 29.7629],
						[-93.4745, 29.7689],
						[-93.6827, 29.7474],
						[-93.7437, 29.7363],
						[-93.837, 29.6907],
						[-93.898, 29.7715],
						[-93.9303, 29.7973],
						[-93.9231, 29.8187],
						[-93.8621, 29.8574],
						[-93.8155, 29.921],
						[-93.7903, 29.988],
						[-93.7401, 30.0215],
						[-93.7006, 30.0653],
						[-93.7329, 30.0816],
						[-93.6898, 30.1417],
						[-93.7221, 30.2096],
						[-93.7042, 30.2431],
						[-93.7078, 30.2886],
						[-93.7652, 30.3333],
						[-93.7437, 30.3969],
						[-93.697, 30.4407],
						[-93.715, 30.5189],
						[-93.7401, 30.5395],
						[-93.7293, 30.5739],
						[-93.6791, 30.5996],
						[-93.6719, 30.658],
						[-93.6288, 30.6795],
						[-93.6181, 30.7422],
						[-93.5857, 30.7964],
						[-93.5534, 30.8247],
						[-93.5678, 30.8788],
						[-93.557, 30.9115],
						[-93.5247, 30.9304],
						[-93.5678, 30.9776],
						[-93.5642, 31.006],
						[-93.5176, 31.024],
						[-93.5247, 31.0704],
						[-93.5534, 31.097],
						[-93.5319, 31.1846],
						[-93.6001, 31.1743],
						[-93.6216, 31.2714],
						[-93.6432, 31.2697],
						[-93.6863, 31.3101],
						[-93.6647, 31.3556],
						[-93.6755, 31.3977],
						[-93.7042, 31.4106],
						[-93.7006, 31.4381],
						[-93.7509, 31.469],
						[-93.7293, 31.4922],
						[-93.7401, 31.5223],
						[-93.7868, 31.5274],
						[-93.8191, 31.5549],
						[-93.8406, 31.5987],
						[-93.8155, 31.6228],
						[-93.8262, 31.6614],
						[-93.8011, 31.6975],
						[-93.8298, 31.7456],
						[-93.8226, 31.7748],
						[-93.8765, 31.8221],
						[-93.9088, 31.8934],
						[-93.9734, 31.92],
						[-94.0129, 31.9819],
						[-94.0416, 31.9922],
						[-94.0416, 33.0196]
					]]
				]
			}
		},
		{
			"type": "Feature",
			"id": "44",
			"properties": { "name": "Rhode Island" },
			"geometry": {
				"type": "MultiPolygon",
				"coordinates": [
					[[
						[-71.1957, 41.6755],
						[-71.1311, 41.66],
						[-71.1204, 41.4977],
						[-71.1921, 41.4581],
						[-71.2137, 41.5458],
						[-71.2065, 41.6007],
						[-71.2424, 41.6196],
						[-71.2352, 41.4831],
						[-71.2962, 41.4848],
						[-71.3034, 41.4547],
						[-71.3393, 41.4487],
						[-71.3429, 41.4959],
						[-71.2855, 41.5776],
						[-71.2568, 41.636],
						[-71.1957, 41.6755]
					]],
					[[
						[-71.3644, 41.6609],
						[-71.3465, 41.6669],
						[-71.307, 41.6222],
						[-71.3249, 41.6162],
						[-71.3644, 41.6609]
					]],
					[[
						[-71.4003, 41.4607],
						[-71.3788, 41.5045],
						[-71.3931, 41.5243],
						[-71.3752, 41.5733],
						[-71.3608, 41.5561],
						[-71.3608, 41.4831],
						[-71.4003, 41.4607]
					]],
					[[
						[-71.6121, 41.16],
						[-71.5726, 41.2288],
						[-71.5547, 41.2167],
						[-71.5511, 41.1515],
						[-71.6121, 41.16]
					]],
					[[
						[-71.8598, 41.3198],
						[-71.831, 41.3447],
						[-71.8418, 41.41],
						[-71.7987, 41.4169],
						[-71.788, 41.5965],
						[-71.788, 41.7253],
						[-71.7987, 41.916],
						[-71.7987, 42.0079],
						[-71.3824, 42.0191],
						[-71.3824, 41.8928],
						[-71.3393, 41.898],
						[-71.3465, 41.8284],
						[-71.3285, 41.7803],
						[-71.2603, 41.7519],
						[-71.2244, 41.7107],
						[-71.2352, 41.672],
						[-71.2855, 41.6368],
						[-71.307, 41.6729],
						[-71.2926, 41.703],
						[-71.3608, 41.7494],
						[-71.3896, 41.7837],
						[-71.3716, 41.6729],
						[-71.4506, 41.6875],
						[-71.4075, 41.654],
						[-71.4039, 41.5896],
						[-71.447, 41.5801],
						[-71.4183, 41.5346],
						[-71.4183, 41.4727],
						[-71.4542, 41.4341],
						[-71.4829, 41.3714],
						[-71.5259, 41.3765],
						[-71.6229, 41.3611],
						[-71.7198, 41.3319],
						[-71.8562, 41.3061],
						[-71.8598, 41.3198]
					]]
				]
			}
		}
	]
};
// src/spark/resultColumns.ts
function withId(column, id) {
	return id === void 0 ? column : {
		...column,
		id
	};
}
/**
* Metrics first, then calculated metrics: the order the compiler's SELECT emits them. Metric
* names reserve the dimension and breakdown fields exactly as the compiler does, so a qualified
* alias here is the one the query returns.
*/
function resultColumns(binding) {
	const reserved = (binding.dimensions ?? []).map((d) => typeof d === "string" ? d : d.field);
	if (binding.breakdown) reserved.push(binding.breakdown);
	const aliases = resolveMetricAliases(binding.metrics, { reserved });
	const metricColumns = binding.metrics.map((entry, i) => withId({
		kind: "metric",
		name: aliases[i],
		entry
	}, entry.id));
	const calculatedColumns = (binding.calculatedMetrics ?? []).map((entry) => withId({
		kind: "calculated",
		name: entry.alias,
		entry
	}, entry.id));
	return [...metricColumns, ...calculatedColumns];
}
/** The column carrying `id`, or undefined when no entry holds it (a stale reference is inert). */
function findResultColumn(columns, id) {
	if (!id) return void 0;
	return columns.find((c) => c.id === id);
}
const ID_LENGTH = 8;
/**
* A new result-column id: `c` + 8 random base-36 chars (design §1). Ids only need to be unique
* within one binding, so `taken` is checked rather than relying on randomness alone.
*/
function newResultColumnId(taken = []) {
	const used = new Set(taken);
	for (;;) {
		let id = "c";
		for (let i = 0; i < ID_LENGTH; i++) id += Math.floor(Math.random() * 36).toString(36);
		if (!used.has(id)) return id;
	}
}
// src/symbolicDates.ts
/**
* Symbolic date tokens resolved to absolute `YYYY-MM-DD`, so the rest of the
* runner only handles ISO dates: `TODAY`, `P-<N><U>` (N units back, D/W/M/Y),
* and `SCHOOLYEARSTART` (MM-DD of the school-year start, default `07-01`,
* configurable per project).
*/
/**
* Anything else — absolute ISO dates, empty strings, unrecognised garbage —
* returns unchanged. That pass-through contract is what lets the resolver be
* applied unconditionally. Resolution timezone is America/New_York; every
* Simplicity client is US-based. See SIM-985 and the scoping-framework doc.
*/
/** Default school-year start when none is configured (MM-DD). */
const DEFAULT_SCHOOL_YEAR_START = "07-01";
/** Timezone all resolutions happen in. */
const ET_TIMEZONE = "America/New_York";
const RELATIVE_TOKEN_REGEX = /^P-(\d+)([DWMY])$/;
const SCHOOL_YEAR_START_REGEX = /^(\d{2})-(\d{2})$/;
/**
* Resolve a possibly-symbolic date token into an ISO date string (`YYYY-MM-DD`).
*
* Returns the input unchanged when no token matches (including absolute ISO
* dates and arbitrary strings).
*/
function resolveSymbolicDate(token, options) {
	if (!token) return token;
	if (token === "TODAY") return formatInET(options.now);
	const relativeMatch = RELATIVE_TOKEN_REGEX.exec(token);
	if (relativeMatch) {
		const amount = Number.parseInt(relativeMatch[1], 10);
		const unit = relativeMatch[2];
		return subtractInET(options.now, amount, unit);
	}
	if (token === "SCHOOLYEARSTART") return resolveSchoolYearStart(options.now, options.schoolYearStart ?? DEFAULT_SCHOOL_YEAR_START);
	return token;
}
/**
* `true` if the string is a symbolic token that this module knows how to
* resolve. Useful for callers that want to skip resolution work.
*/
function isSymbolicDateToken(token) {
	if (!token) return false;
	if (token === "TODAY" || token === "SCHOOLYEARSTART") return true;
	return RELATIVE_TOKEN_REGEX.test(token);
}
/**
* Format a Date as `YYYY-MM-DD` in America/New_York. We use Intl rather than
* raw UTC arithmetic so DST transitions and the late-evening UTC→ET date
* boundary are handled correctly.
*/
function formatInET(date) {
	return new Intl.DateTimeFormat("en-CA", {
		timeZone: ET_TIMEZONE,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	}).format(date);
}
/**
* Extract the ET-local year / month / day for a given UTC instant.
* Returned as integers (1-based month, 1-based day).
*/
function getETDateParts(date) {
	const [y, m, d] = formatInET(date).split("-").map((p) => Number.parseInt(p, 10));
	return {
		year: y,
		month: m,
		day: d
	};
}
/**
* Subtract `amount` of `unit` from `now`, anchored to ET. Month and year
* arithmetic clamps to the last day of the month, so `2026-03-31` minus one
* month is `2026-02-28` rather than an invalid `Feb 31`.
*/
function subtractInET(now, amount, unit) {
	const { year, month, day } = getETDateParts(now);
	if (unit === "D" || unit === "W") {
		const days = unit === "W" ? amount * 7 : amount;
		const anchored = Date.UTC(year, month - 1, day);
		return formatUTCParts(/* @__PURE__ */ new Date(anchored - days * 864e5));
	}
	let targetYear = year;
	let targetMonth = month;
	if (unit === "M") targetMonth -= amount;
	else targetYear -= amount;
	while (targetMonth <= 0) {
		targetMonth += 12;
		targetYear -= 1;
	}
	const lastDay = daysInMonth(targetYear, targetMonth);
	const targetDay = Math.min(day, lastDay);
	return `${pad4(targetYear)}-${pad2(targetMonth)}-${pad2(targetDay)}`;
}
/**
* The most recent (or current) MM-DD anniversary relative to today. With the
* default `07-01`: on 2026-05-20 → 2025-07-01 (this school year began last
* July); on 2026-07-01 → 2026-07-01; on 2026-08-15 → 2026-07-01.
*/
function resolveSchoolYearStart(now, schoolYearStart) {
	const match = SCHOOL_YEAR_START_REGEX.exec(schoolYearStart);
	if (!match) return resolveSchoolYearStart(now, DEFAULT_SCHOOL_YEAR_START);
	const startMonth = Number.parseInt(match[1], 10);
	const startDay = Number.parseInt(match[2], 10);
	const { year, month, day } = getETDateParts(now);
	return `${pad4(month > startMonth || month === startMonth && day >= startDay ? year : year - 1)}-${pad2(startMonth)}-${pad2(startDay)}`;
}
function formatUTCParts(date) {
	return `${pad4(date.getUTCFullYear())}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}
function pad2(n) {
	return n < 10 ? `0${n}` : `${n}`;
}
function pad4(n) {
	return n.toString().padStart(4, "0");
}
function daysInMonth(year, month) {
	return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
// src/tableRunScope.ts
/** `column ≥ value`, plus the exclusive upper bound only when one was stamped (SIM-3297). */
function dateDimension(date) {
	const lower = `${date.column} ≥ ${date.value}`;
	return date.until == null ? lower : `${lower} < ${date.until}`;
}
/**
* Every dimension that can move a row count, in a stable order. Two runs share a key exactly when
* their counts are comparable. Column name is part of it: repointing the date predicate at a
* different column changes what the count means even when the resolved date is unchanged.
*/
function dimensions(scope) {
	return [
		["date", scope.date ? dateDimension(scope.date) : "none"],
		["schools", scope.school ? `${scope.school.count}/${scope.school.of ?? "?"}` : "none"],
		["students", scope.student ? String(scope.student.count) : "none"],
		["cascade", scope.cascade ? `${scope.cascade.parent}.${scope.cascade.joinColumn}:${scope.cascade.count ?? "?"}` : "none"],
		["unfiltered", String(scope.unfiltered)],
		["reference", String(scope.reference)],
		["disabled", scope.disabled.length > 0 ? [...scope.disabled].sort().join("+") : "none"],
		["mode", scope.mode ?? "none"],
		["floor", scope.minStartIndex == null ? "none" : String(scope.minStartIndex)]
	];
}
/**
* Comparison key for "did these two runs fetch under the same rules?". Null in, null out — an
* unstamped run is not the same scope as anything, including another unstamped run. Two nulls are
* two absences of evidence, and matching them would report a shared scope nobody recorded.
*/
function scopeKey(scope) {
	if (scope == null) return null;
	return dimensions(scope).map(([k, v]) => `${k}=${v}`).join("|");
}
/**
* The partition key for a volume baseline: scope alone, NOT scope × day-of-week. A day split puts
* most partitions under the 5-sample floor before scope is applied, and every scope change resets
* them (measured on SIM-2913). The cost: a weekday/weekend rhythm lands in one partition and widens
* the band. A band too wide misses anomalies; a partition too thin cannot fire at all.
*/
function volumePartitionKey(scope) {
	return scopeKey(scope);
}
/** True only when both runs are stamped and every dimension agrees. */
function sameScope(a, b) {
	const ka = scopeKey(a);
	return ka != null && ka === scopeKey(b);
}
/**
* What moved between two stamped runs, as `dimension: before → after` phrases.
*
* Empty when the scopes match, or when either side is unstamped — an absent
* stamp cannot support a claim about what changed.
*/
function diffScope(current, previous) {
	if (current == null || previous == null) return [];
	const before = new Map(dimensions(previous));
	return dimensions(current).filter(([k, v]) => before.get(k) !== v).map(([k, v]) => `${k}: ${before.get(k) ?? "none"} → ${v}`);
}
/**
* SIM-3301 — the stamp for a connector that fetches whole tables. `null` and `unfiltered: true`
* are not interchangeable: null records nothing, so the ledger cannot tell a deletion from a row
* never requested, while `true` claims the run saw every row and licenses reading absence as a
* deletion. Assert it only where that holds.
*/
function unfilteredScope(mode = null) {
	return {
		date: null,
		school: null,
		student: null,
		cascade: null,
		unfiltered: true,
		disabled: [],
		reference: false,
		mode,
		minStartIndex: null
	};
}
/**
* The stamp for a connector narrowed only by an opaque per-table filter map (DnA query params,
* PowerSchool-Enrollment resource filters). A filter yields `unfiltered: false` with no dimension,
* which the Seen Ledger still reads as whole-table (`isWideScope`) — so a caller whose filters
* narrow must stamp nothing rather than this (SIM-3585).
*/
function filterDerivedScope(filters, mode = null, minStartIndex = null) {
	const narrowed = filters != null && Object.keys(filters).length > 0;
	const fromRowZero = mode !== "incremental" && minStartIndex == null;
	return {
		...unfilteredScope(mode),
		unfiltered: !narrowed && fromRowZero,
		minStartIndex
	};
}
export { BIGQUERY_DESTINATION_TYPES, BINDING_DATE_SENTINEL_TODAY, BI_DEFAULT_ROW_LIMIT, BI_MAX_ROW_LIMIT, BRAND_NAME, CADENCE_MINUTES, CADENCE_PERIOD_MINUTES, CASCADE_LAYER_SEQUENCE, COMPONENT_META, COMPONENT_REGISTRY, COMPONENT_TYPES, CONDITIONAL_OPERATORS, DAY_MINUTES, DNA_LIST_RESOURCES, DOTTIE_FEEDBACK_REASONS, DOTTIE_FEEDBACK_REASON_CODES, ExpressionSyntaxError, FUNCTIONS, HOUR_MINUTES, METADATA_STEP_TABLE, METRIC_COMPARISON_LABELS, MIN_VOLUME_SAMPLES, PIPELINE_GRACE_MINUTES, PIPELINE_STATUS_SEVERITY, PORTAL_TITLE_SUFFIX, POWERSCHOOL_ENROLLMENT_SCHEMA_KNOWLEDGE, POWERSCHOOL_SCHEMA_KNOWLEDGE, SCHEDULE_TIMEZONE, SEMANTIC_ROLES, SENTINEL_DATE_OPERATORS, TABLE_HEADER_BG_DEFAULT, TABLE_HEADER_FONT_DEFAULTS, TERMINAL_RUN_PLAN_STATUSES, USA_GEOJSON, VOLUME_BAND_MADS, WEEKDAY_NAMES, WEEK_MINUTES, ZERO_IS_NORMAL_RATE, allFunctionNames, appendMetadataStep, applyFontStyle, applyMetricComparison, archiveConfigSchema, archiveMappingSchema, avelaConnectorConfigSchema, baseConnectorConfigSchema, baseDestinationConfigSchema, bigQueryDestinationConfigSchema, bigQueryExperimentalDestinationConfigSchema, bigQueryNoDeduplicationDestinationConfigSchema, cadencePeriodMinutes, cadenceWindowFromMinutes, cadenceWindowMinutes, calculatedMetricDefSchema, canonicalDnaResource, chartTextStyle, classifyVolume, collectFields, columnAlignSchema, columnFontSchema, columnMetadataSchema, columnWrapModeSchema, compileExpression, componentTypeSchema, computePipelineStatus, computeVolumeStats, conditionalOperatorSchema, connectorConfigSchema, connectorSchema, creatableDestinationTypeSchema, dataBindingSchema, deanslistConnectorConfigSchema, describeApiError, destinationConfigSchema, destinationSchema, destinationTypeSchema, diffScope, dimensionDefSchema, dnaConnectorConfigSchema, dnaTableNameFor, dottieFeedbackReasonCodeSchema, draftDataBindingSchema, emitSql, exampleConnectorConfigSchema, exampleDestinationConfigSchema, exportColumnSchema, exportTableSchema, fileDropConnectorConfigSchema, filterDefSchema, filterDerivedScope, findResultColumn, fontConfigSchema, formatZodError, getBindingCapabilities, getEnrollmentTableMetadata, getInheritDescriptor, hasProjectLevelScoping, ireadySftpConnectorConfigSchema, isAggregateName, isBigQueryDestinationType, isBindingDateSentinel, isDottieFeedbackReasonCode, isPercentComparison, isProjectSuppressed, isSemanticRole, isSymbolicDateToken, jobSchema, lookupFunction, metricAggregationSchema, metricAliasFor, metricComparisonSchema, metricDefSchema, metricFormatSchema, newResultColumnId, nextSlotStart, parseAtPhase, parseExpression, phaseMinutes, phaseMinutesOf, pickableFunctions, pipelineReasonSchema, pipelineStatusSchema, powerschoolEnrollmentConnectorConfigSchema, powerschoolsConnectorConfigSchema, previousSlotStart, resolveMetricAliases, resolveSortColumn, resolveSymbolicDate, resultColumns, runCadenceSchema, runExecutionStatusSchema, runPlanSchema, runPlanStatusSchema, runStatusSchema, runStepKindSchema, runStepPhaseSchema, runStepSchema, runStepStatusSchema, runStepSyncStateSchema, safeString, sameScope, scheduleMembershipsForTable, schoolmintConnectorConfigSchema, schoolmintEnrollConnectorConfigSchema, scopeKey, sessionContextV1Schema, skywardConnectorConfigSchema, sortDefSchema, sortDirectionSchema, streamStateSchema, strictestCadence, syncModeSchema, tableRunErrorKindSchema, tableRunScopeSchema, tableStyleConfigSchema, transformLayerSchema, transitiveInheritFromParents, unfilteredScope, validateMinimumBindings, volumePartitionKey, walk, worstPipelineStatus, zeroIsNormal, zonedWallClock };

//# sourceMappingURL=index.mjs.map